(function (window, document) {
  'use strict';

  var POLL_FAST_INTERVAL_MS = 1000;
  var POLL_SLOW_INTERVAL_MS = 2500;
  var POLL_FAST_WINDOW_MS = 20000;
  var POLL_TIMEOUT_MS = 10 * 60 * 1000;
  var EDITOR_IMAGE_LOAD_TIMEOUT_MS = 20 * 1000;
  var EDITOR_DECODE_TIMEOUT_MS = 10 * 1000;
  var EDITOR_CROPPER_READY_TIMEOUT_MS = 20 * 1000;
  var EDITOR_AUTO_RETRY_LIMIT = 1;
  var MAX_ORIGINAL_BYTES = 104857600;
  var PROGRESS_TICK_MS = 1000;
  var DEFAULT_EXPECTED_PREVIEW_SECONDS = 50;
  var DEFAULT_SLOW_AFTER_SECONDS = 60;
  var VERY_SLOW_OFFSET_SECONDS = 60;
  var MAX_WORKING_DRAFTS = 5;
  var CANONICAL_STYLE_LABELS = Object.freeze({
    'watercolor-demo': 'Watercolor',
    'modern-portrait': 'Modern Portrait',
    'storybook-cartoon': 'Storybook Art',
    'classic-oil-painting': 'Oil Painting',
    'pencil-sketch': 'Pencil Sketch',
    'soft-anime-portrait': 'Anime Portrait',
    '3d-animated-character': '3D Cartoon',
    'clean-vector-cartoon': 'Vector Art',
    'royal-pet-portrait': 'Royal Pet'
  });
  var DRAFT_SAVE_DEBOUNCE_MS = 1500;
  var RECIPE_CROP_TOLERANCE = 0.25 / 8192;
  var EDITOR_LAYOUT_MAX_FRAMES = 24;
  var EDITOR_LAYOUT_STABLE_FRAMES = 2;
  var EDITOR_LAYOUT_VERIFY_FRAMES = 2;
  var EDITOR_LAYOUT_FAILURE_LIMIT = 3;
  var EDITOR_SOURCE_TOLERANCE_PX = 0.75;
  var EDITOR_CENTER_TOLERANCE_PX = 1.25;
  var EDITOR_GEOMETRY_TOLERANCE_PX = 2;
  var CID_PATTERN = /^\d{14}-[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
  var CROPPER_ASSET_PROMISE_KEY = '__giftInOneAiCropperAssetsPromise';
  var PREVIEW_OVERLAY_LOAD_TIMEOUT_MS = 15000;
  var PREVIEW_OVERLAY_RETRY_DELAYS_MS = [400, 1200];
  var DRAFT_THUMBNAIL_MAX_EDGE = 480;
  var PREVIEW_OVERLAY_LOADING_MESSAGE =
    'Loading the clock-face preview layer. AI artwork can still be generated.';
  var PREVIEW_OVERLAY_UNAVAILABLE_MESSAGE =
    'AI artwork can still be generated, but the clock-face preview layer for this option is unavailable. Final confirmation is paused until the preview layer is restored.';
  var AI_EVENT_NAMES = {
    studio_open: true,
    preview_generate: true,
    preview_ready: true,
    use_design: true,
    add_to_cart: true,
    studio_close: true,
    fallback_standard: true,
    ai_error: true
  };

  function displayStyleLabel(styleId, styleLabel, fallbackLabel) {
    var normalizedId = String(styleId || '').trim();
    var normalizedLabel = String(styleLabel || '').trim();
    var canonicalLabel = CANONICAL_STYLE_LABELS[normalizedId] || '';
    if (
      canonicalLabel &&
      (
        !normalizedLabel ||
        normalizedLabel.toLowerCase() === normalizedId.toLowerCase()
      )
    ) return canonicalLabel;
    return normalizedLabel || canonicalLabel || fallbackLabel || normalizedId || 'AI design';
  }

  function assertManifestCapacity(manifest) {
    if (
      !manifest || !Number.isInteger(manifest.designLimit) ||
      manifest.designLimit !== MAX_WORKING_DRAFTS ||
      !Array.isArray(manifest.designs) ||
      manifest.designs.length > manifest.designLimit
    ) throw new Error('The saved design capacity response is incompatible.');
    return manifest;
  }

  function designLimitReachedMessage() {
    return MAX_WORKING_DRAFTS + '/' + MAX_WORKING_DRAFTS +
      ' designs used. Delete one to create another.';
  }

  function designCapacityMessage() {
    return 'Saved privately. Previews and finished designs share ' +
      MAX_WORKING_DRAFTS + ' spaces.';
  }

  function normalizedAssetUrl(url) {
    try {
      return new window.URL(String(url || ''), document.baseURI).href;
    } catch (_error) {
      return '';
    }
  }

  function matchingAsset(selector, attribute, url) {
    var expected = normalizedAssetUrl(url);
    if (!expected) return null;
    var nodes = document.querySelectorAll(selector);
    for (var index = 0; index < nodes.length; index += 1) {
      if (normalizedAssetUrl(nodes[index].getAttribute(attribute)) === expected) {
        return nodes[index];
      }
    }
    return null;
  }

  function ensureCropperAssets(root) {
    if (window[CROPPER_ASSET_PROMISE_KEY]) return window[CROPPER_ASSET_PROMISE_KEY];
    var scriptUrl = String(root && root.dataset.cropperScriptUrl || '');
    var styleUrl = String(root && root.dataset.cropperStyleUrl || '');
    if (!scriptUrl || !styleUrl) {
      return Promise.reject(new Error('The image editor assets are unavailable.'));
    }

    var stylePromise = new Promise(function (resolve, reject) {
      var existing = matchingAsset('link[rel~="stylesheet"][href]', 'href', styleUrl);
      if (existing) {
        resolve();
        return;
      }
      var link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = styleUrl;
      link.dataset.gioAiCropperStyle = 'true';
      link.onload = resolve;
      link.onerror = function () {
        link.remove();
        reject(new Error('The image editor styles could not be loaded.'));
      };
      document.head.appendChild(link);
    });

    var scriptPromise = new Promise(function (resolve, reject) {
      if (typeof window.Cropper === 'function') {
        resolve();
        return;
      }
      var existing = matchingAsset(
        'script[data-gio-ai-cropper-script="true"][src]',
        'src',
        scriptUrl
      );
      var script = existing || document.createElement('script');
      function loaded() {
        if (typeof window.Cropper === 'function') resolve();
        else {
          if (!existing) script.remove();
          reject(new Error('The image editor could not be loaded.'));
        }
      }
      script.addEventListener('load', loaded, { once: true });
      script.addEventListener('error', function () {
        if (!existing) script.remove();
        reject(new Error('The image editor could not be loaded.'));
      }, { once: true });
      if (!existing) {
        script.src = scriptUrl;
        script.async = true;
        script.dataset.gioAiCropperScript = 'true';
        document.head.appendChild(script);
      }
    });

    window[CROPPER_ASSET_PROMISE_KEY] = Promise.all([stylePromise, scriptPromise])
      .then(function () { return window.Cropper; })
      .catch(function (error) {
        window[CROPPER_ASSET_PROMISE_KEY] = null;
        throw error;
      });
    return window[CROPPER_ASSET_PROMISE_KEY];
  }

  function trackAiEvent(root, name, attributes) {
    if (!AI_EVENT_NAMES[name]) return;
    var detail = { event: 'giftinone_ai_' + name, ai_event: name };
    var allowed = ['view', 'source', 'reason', 'retry'];
    allowed.forEach(function (key) {
      var value = attributes && attributes[key];
      if (typeof value !== 'string' && typeof value !== 'boolean') return;
      var safe = String(value).toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 32);
      if (safe) detail[key] = safe;
    });
    if (Array.isArray(window.dataLayer)) window.dataLayer.push(detail);
    try {
      root.dispatchEvent(new window.CustomEvent('giftinone:ai-event', {
        bubbles: true,
        detail: detail
      }));
    } catch (_error) {}
  }

  function delay(ms) {
    return new Promise(function (resolve) {
      window.setTimeout(resolve, ms);
    });
  }

  function nextPollDelay(startedAt) {
    if (document.visibilityState === 'hidden') return POLL_SLOW_INTERVAL_MS;
    return Date.now() - startedAt < POLL_FAST_WINDOW_MS
      ? POLL_FAST_INTERVAL_MS
      : POLL_SLOW_INTERVAL_MS;
  }

  function setStatus(root, message, target, isError) {
    var dialog = root.querySelector('[data-ai-dialog]');
    var resolvedTarget = target || (dialog && dialog.dataset.aiView === 'editor'
      ? 'editor'
      : 'setup');
    var selector = resolvedTarget === 'page'
      ? '[data-ai-page-status]'
      : resolvedTarget === 'editor'
        ? '[data-ai-editor-status]'
        : '[data-ai-status]';
    var node = root.querySelector(selector);
    if (!node) return;
    // A regular workflow status supersedes an older overlay-specific notice.
    // The overlay loader marks its own notices again after calling setStatus.
    delete node.dataset.aiOverlayNotice;
    node.textContent = message || '';
    node.setAttribute('role', isError ? 'alert' : 'status');
    if (isError) {
      node.dataset.aiError = 'true';
      node.tabIndex = -1;
    } else {
      delete node.dataset.aiError;
      node.removeAttribute('tabindex');
    }
  }

  function setDialogView(root, view) {
    var setup = root.querySelector('[data-ai-setup]');
    var editor = root.querySelector('[data-ai-editor]');
    var dialog = root.querySelector('[data-ai-dialog]');
    if (setup) setup.hidden = view !== 'setup';
    if (editor) editor.hidden = view !== 'editor';
    if (dialog) dialog.dataset.aiView = view;
    var generating = root.dataset.aiGenerating === 'true';
    var currentIndex = view === 'editor' ? '3' : generating ? '2' : '1';
    var currentLabel = view === 'editor' ? 'Adjust' : generating ? 'Preview' : 'Style & photo';
    var steps = root.querySelector('[data-ai-steps]');
    if (steps) {
      steps.dataset.aiCurrentIndex = currentIndex;
      steps.dataset.aiCurrentLabel = currentLabel;
    }
    var title = root.querySelector('[data-ai-dialog-title]');
    var help = root.querySelector('[data-ai-dialog-help]');
    var designLibraryOpen = Boolean(
      view === 'setup' && root._giftInOneAiState &&
      root._giftInOneAiState.designLibraryOpen
    );
    if (title) {
      title.textContent = designLibraryOpen
        ? 'My designs'
        : view === 'editor'
          ? 'Adjust your design'
          : generating
            ? 'Creating your preview'
            : 'Create AI artwork';
    }
    if (help) {
      help.textContent = designLibraryOpen
        ? 'Edit or choose a saved design. Opening a design does not use another preview.'
        : view === 'editor'
          ? 'Position your artwork exactly as you want it to print.'
          : generating
            ? 'Your photo is being transformed.'
            : 'Choose a style and add your photo.';
    }
    Array.prototype.forEach.call(root.querySelectorAll('[data-ai-step]'), function (step) {
      var name = step.dataset.aiStep;
      var current = view === 'editor'
        ? name === 'adjust'
        : generating
          ? name === 'generate'
          : name === 'choose';
      if (current) step.setAttribute('aria-current', 'step');
      else step.removeAttribute('aria-current');
      step.dataset.aiComplete = (
        (view === 'editor' && (name === 'choose' || name === 'generate')) ||
        (generating && name === 'choose')
      ) ? 'true' : 'false';
    });
  }

  function setDesignLibraryOpen(root, state, open, shouldFocus) {
    var dialog = root.querySelector('[data-ai-dialog]');
    var setup = root.querySelector('[data-ai-setup]');
    var createView = root.querySelector('[data-ai-create-view]');
    var designsView = root.querySelector('[data-ai-designs-view]');
    var title = root.querySelector('[data-ai-dialog-title]');
    var help = root.querySelector('[data-ai-dialog-help]');
    var enabled = Boolean(open && designSlotCount(state) > 0);
    state.designLibraryOpen = enabled;
    if (setup) setup.dataset.aiSetupPanel = enabled ? 'designs' : 'create';
    if (createView) createView.hidden = enabled;
    if (designsView) designsView.hidden = !enabled;
    if (dialog) {
      if (enabled) dialog.dataset.aiLibraryOpen = 'true';
      else delete dialog.dataset.aiLibraryOpen;
    }
    Array.prototype.forEach.call(
      root.querySelectorAll('[data-ai-library-toggle]'),
      function (button) {
        button.setAttribute('aria-expanded', enabled ? 'true' : 'false');
      }
    );
    if (enabled) {
      if (title) title.textContent = 'My designs';
      if (help) {
        help.textContent = 'Edit or choose a saved design. Opening a design does not use another preview.';
      }
    } else {
      setDialogView(root, dialog && dialog.dataset.aiView || 'setup');
      syncSetupAction(root, state);
    }
    var body = root.querySelector('.giftinone-ai-dialog__body');
    if (body) {
      body.scrollTop = 0;
      body.scrollLeft = 0;
    }
    if (shouldFocus && !actionConfirmationIsOpen(root)) {
      window.requestAnimationFrame(function () {
        focusWithoutScrolling(enabled
          ? designLibraryItemFocusTarget(root)
          : setupFocusTarget(root));
      });
    }
    return enabled;
  }

  function openSavedDesignLibrary(root, state, trigger) {
    if (!state || designSlotCount(state) === 0) return false;
    setDialogView(root, 'setup');
    openAiDialog(root, state, trigger);
    setDesignLibraryOpen(root, state, true, true);
    return true;
  }

  function syncDesignLibraryUi(root, state) {
    var total = designSlotCount(state);
    Array.prototype.forEach.call(
      root.querySelectorAll('[data-ai-library-count]'),
      function (node) {
        node.textContent = total + '/' + MAX_WORKING_DRAFTS;
      }
    );
    Array.prototype.forEach.call(
      root.querySelectorAll('[data-ai-library-toggle]'),
      function (button) {
        button.hidden = total === 0;
      }
    );
    if (state.designLibraryOpen && total === 0) {
      setDesignLibraryOpen(root, state, false, true);
    }
    var create = root.querySelector('[data-ai-library-create]');
    var createNote = root.querySelector('[data-ai-library-create-note]');
    var createLocked = Boolean(
      total >= MAX_WORKING_DRAFTS || state.canGenerate === false ||
      state.stylesReady === false || state.variantUnavailable
    );
    if (create) {
      create.disabled = Boolean(
        createLocked || state.busy || state.sessionRestoring ||
        state.finalizingDraftCid || state.cartSubmitting || state.cartUnknown
      );
    }
    if (createNote) {
      if (total >= MAX_WORKING_DRAFTS) {
        createNote.textContent = '5/5 saved spaces used. Delete one to create another.';
      } else if (state.variantUnavailable) {
        createNote.textContent = 'Choose an available product option before creating another design.';
      } else if (state.canGenerate === false || state.stylesReady === false) {
        createNote.textContent = state.quotaRemaining === 0
          ? 'Daily preview limit reached. Your saved designs remain available.'
          : 'New AI generation is temporarily unavailable. Your saved designs remain available.';
      } else {
        createNote.textContent = 'Uses 1 daily preview and 1 saved-design space. Your current design stays saved.';
      }
    }
  }

  function syncFooterNoticeVisibility(root) {
    var copy = root.querySelector('[data-ai-footer-copy]');
    var hint = root.querySelector('[data-ai-generate-hint]');
    var quota = root.querySelector('[data-ai-quota-status]');
    if (!copy) return;
    copy.hidden = Boolean(
      (!hint || hint.hidden || !String(hint.textContent || '').trim()) &&
      (!quota || quota.hidden || !String(quota.textContent || '').trim())
    );
  }

  function resetDialogViewport(root) {
    [
      root.querySelector('[data-ai-dialog]'),
      root.querySelector('.giftinone-ai-dialog__shell'),
      root.querySelector('.giftinone-ai-dialog__body')
    ].forEach(function (node) {
      if (!node) return;
      node.scrollTop = 0;
      node.scrollLeft = 0;
    });
  }

  function setupFocusTarget(root) {
    var selectedCard = root.querySelector(
      '[data-ai-style-choice][aria-checked="true"]'
    );
    if (selectedCard) return selectedCard;
    var firstCard = root.querySelector('[data-ai-style-choice]');
    if (firstCard) return firstCard;
    var styleSelect = root.querySelector('[data-ai-style]');
    if (styleSelect && !styleSelect.hidden) return styleSelect;
    return root.querySelector('[data-ai-close]');
  }

  function designLibraryItemFocusTarget(root) {
    var selectors = [
      '[data-ai-designs-view] [data-ai-working-draft-select][aria-pressed="true"]:not([disabled])',
      '[data-ai-designs-view] [data-ai-design-cid][aria-pressed="true"]:not([disabled])',
      '[data-ai-designs-view] .giftinone-ai-working-draft-card__select:not([disabled])',
      '[data-ai-designs-view] [data-ai-design-cid]:not([disabled])',
      '[data-ai-designs-view] [data-ai-working-draft-remove]:not([disabled])',
      '[data-ai-designs-view] [data-ai-design-remove]:not([disabled])',
      '[data-ai-library-create]:not([disabled])',
      '[data-ai-close]:not([disabled])'
    ];
    for (var index = 0; index < selectors.length; index += 1) {
      var target = root.querySelector(selectors[index]);
      if (target) return target;
    }
    return null;
  }

  function focusWithoutScrolling(node) {
    if (!node || typeof node.focus !== 'function') return;
    try {
      node.focus({ preventScroll: true });
    } catch (_error) {
      node.focus();
    }
  }

  function actionConfirmationIsOpen(root) {
    var confirmation = root && root._giftInOneAiConfirmation;
    return Boolean(confirmation && confirmation.open);
  }

  function setActionConfirmationPending(root, pending) {
    var confirmation = root && root._giftInOneAiConfirmation;
    if (!confirmation) return;
    confirmation.pending = Boolean(pending);
    if (confirmation.surface) {
      if (pending) confirmation.surface.setAttribute('aria-busy', 'true');
      else confirmation.surface.removeAttribute('aria-busy');
    }
    if (confirmation.cancelButton) {
      confirmation.cancelButton.disabled = Boolean(pending);
    }
    if (confirmation.acceptButton) {
      confirmation.acceptButton.disabled = Boolean(pending);
      confirmation.acceptButton.textContent = pending
        ? String(confirmation.options && confirmation.options.pendingLabel || 'Working…')
        : String(confirmation.options && confirmation.options.acceptLabel || 'Continue');
    }
    if (confirmation.progress) {
      confirmation.progress.textContent = pending
        ? String(confirmation.options && confirmation.options.pendingStatus ||
          confirmation.options && confirmation.options.pendingLabel || 'Working')
        : '';
    }
    if (pending && confirmation.surface) {
      window.requestAnimationFrame(function () {
        if (confirmation.open && confirmation.pending) {
          focusWithoutScrolling(confirmation.surface);
        }
      });
    }
  }

  function dismissActionConfirmation(root, restoreTrigger, focusTarget, force) {
    var confirmation = root && root._giftInOneAiConfirmation;
    if (!confirmation || !confirmation.open) return false;
    if (confirmation.pending && !force) return false;
    var trigger = confirmation.trigger;
    var dialog = root.querySelector('[data-ai-dialog]');
    var shell = root.querySelector('.giftinone-ai-dialog__shell');
    confirmation.open = false;
    confirmation.pending = false;
    confirmation.options = null;
    confirmation.trigger = null;
    if (confirmation.layer) {
      confirmation.layer.hidden = true;
      confirmation.layer.setAttribute('aria-hidden', 'true');
    }
    if (confirmation.surface) confirmation.surface.removeAttribute('aria-busy');
    if (confirmation.error) {
      confirmation.error.textContent = '';
      confirmation.error.hidden = true;
    }
    if (confirmation.progress) confirmation.progress.textContent = '';
    if (confirmation.cancelButton) confirmation.cancelButton.disabled = false;
    if (confirmation.acceptButton) confirmation.acceptButton.disabled = false;
    if (dialog) delete dialog.dataset.aiConfirmationOpen;
    if (shell) {
      if (confirmation.shellHadInert) shell.setAttribute('inert', '');
      else shell.removeAttribute('inert');
      if (confirmation.shellAriaHidden === null) shell.removeAttribute('aria-hidden');
      else shell.setAttribute('aria-hidden', confirmation.shellAriaHidden);
    }
    confirmation.shellHadInert = false;
    confirmation.shellAriaHidden = null;

    var target = focusTarget || (restoreTrigger ? trigger : null);
    if (target) {
      window.requestAnimationFrame(function () {
        if (typeof target === 'function') target = target();
        if (!target || target.isConnected === false) return;
        focusWithoutScrolling(target);
      });
    }
    return true;
  }

  function cancelActionConfirmation(root) {
    return dismissActionConfirmation(root, true, null, false);
  }

  function showActionConfirmationError(root, error) {
    var confirmation = root && root._giftInOneAiConfirmation;
    if (!confirmation || !confirmation.open) return;
    var options = confirmation.options || {};
    var message = '';
    if (typeof options.errorMessage === 'function') {
      message = options.errorMessage(error);
    } else if (options.errorMessage) {
      message = options.errorMessage;
    } else {
      message = error && error.message || 'This action could not be completed. Please try again.';
    }
    setActionConfirmationPending(root, false);
    if (confirmation.error) {
      confirmation.error.textContent = String(message);
      confirmation.error.hidden = false;
    }
    window.requestAnimationFrame(function () {
      if (confirmation.open) focusWithoutScrolling(confirmation.acceptButton);
    });
  }

  function acceptActionConfirmation(root) {
    var confirmation = root && root._giftInOneAiConfirmation;
    if (!confirmation || !confirmation.open || confirmation.pending) return;
    var options = confirmation.options || {};
    if (confirmation.error) {
      confirmation.error.textContent = '';
      confirmation.error.hidden = true;
    }
    setActionConfirmationPending(root, true);
    var result;
    try {
      result = typeof options.action === 'function' ? options.action() : null;
    } catch (error) {
      showActionConfirmationError(root, error);
      return;
    }
    Promise.resolve(result).then(function (focusTarget) {
      if (!confirmation.open) return;
      setActionConfirmationPending(root, false);
      dismissActionConfirmation(root, false, focusTarget, true);
    }).catch(function (error) {
      if (!confirmation.open) return;
      showActionConfirmationError(root, error);
    });
  }

  function openActionConfirmation(root, options) {
    var confirmation = root && root._giftInOneAiConfirmation;
    var dialog = root && root.querySelector('[data-ai-dialog]');
    var shell = root && root.querySelector('.giftinone-ai-dialog__shell');
    if (
      !confirmation || !confirmation.layer || !dialogIsOpen(dialog) ||
      confirmation.open
    ) return false;
    confirmation.open = true;
    confirmation.pending = false;
    confirmation.options = options || {};
    confirmation.trigger = confirmation.options.trigger || document.activeElement;
    confirmation.shellHadInert = Boolean(shell && shell.hasAttribute('inert'));
    confirmation.shellAriaHidden = shell ? shell.getAttribute('aria-hidden') : null;
    if (confirmation.title) {
      confirmation.title.textContent = String(
        confirmation.options.title || 'Confirm this action'
      );
    }
    if (confirmation.message) {
      confirmation.message.textContent = String(confirmation.options.message || '');
    }
    if (confirmation.cancelButton) {
      confirmation.cancelButton.textContent = String(
        confirmation.options.cancelLabel || 'Cancel'
      );
    }
    if (confirmation.acceptButton) {
      confirmation.acceptButton.textContent = String(
        confirmation.options.acceptLabel || 'Continue'
      );
    }
    if (confirmation.error) {
      confirmation.error.textContent = '';
      confirmation.error.hidden = true;
    }
    if (confirmation.progress) confirmation.progress.textContent = '';
    if (shell) {
      shell.setAttribute('inert', '');
      shell.setAttribute('aria-hidden', 'true');
    }
    dialog.dataset.aiConfirmationOpen = 'true';
    confirmation.layer.hidden = false;
    confirmation.layer.setAttribute('aria-hidden', 'false');
    window.requestAnimationFrame(function () {
      if (!confirmation.open) return;
      focusWithoutScrolling(confirmation.cancelButton || confirmation.surface);
    });
    return true;
  }

  function initializeActionConfirmation(root) {
    var layer = root.querySelector('[data-ai-confirm]');
    if (!layer) return;
    var confirmation = {
      layer: layer,
      surface: layer.querySelector('[data-ai-confirm-surface]'),
      title: layer.querySelector('[data-ai-confirm-title]'),
      message: layer.querySelector('[data-ai-confirm-message]'),
      error: layer.querySelector('[data-ai-confirm-error]'),
      progress: layer.querySelector('[data-ai-confirm-progress]'),
      cancelButton: layer.querySelector('[data-ai-confirm-cancel]'),
      acceptButton: layer.querySelector('[data-ai-confirm-accept]'),
      open: false,
      pending: false,
      options: null,
      trigger: null,
      shellHadInert: false,
      shellAriaHidden: null
    };
    root._giftInOneAiConfirmation = confirmation;
    if (confirmation.cancelButton) {
      confirmation.cancelButton.addEventListener('click', function () {
        cancelActionConfirmation(root);
      });
    }
    if (confirmation.acceptButton) {
      confirmation.acceptButton.addEventListener('click', function () {
        acceptActionConfirmation(root);
      });
    }
    layer.addEventListener('click', function (event) {
      if (event.target === layer) cancelActionConfirmation(root);
    });
    layer.addEventListener('keydown', function (event) {
      if (!confirmation.open) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        cancelActionConfirmation(root);
        return;
      }
      if (event.key !== 'Tab') return;
      if (confirmation.pending) {
        event.preventDefault();
        focusWithoutScrolling(confirmation.surface);
        return;
      }
      var focusable = [confirmation.cancelButton, confirmation.acceptButton].filter(
        function (button) { return Boolean(button && !button.disabled); }
      );
      if (!focusable.length) {
        event.preventDefault();
        focusWithoutScrolling(confirmation.surface);
        return;
      }
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        focusWithoutScrolling(last);
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        focusWithoutScrolling(first);
      } else if (!layer.contains(document.activeElement)) {
        event.preventDefault();
        focusWithoutScrolling(first);
      }
    });
  }

  function lockDialogChrome(root) {
    var dialog = root.querySelector('[data-ai-dialog]');
    var shell = root.querySelector('.giftinone-ai-dialog__shell');
    [dialog, shell].forEach(function (node) {
      if (!node) return;
      node.scrollTop = 0;
      node.scrollLeft = 0;
    });
  }

  function newPreviewActionLabel(root, state) {
    if (!state || designSlotCount(state) === 0) return 'Generate preview';
    var style = currentStyleMetadata(root);
    return 'Generate new ' + displayStyleLabel(
      style.styleId,
      style.label,
      'AI'
    ) + ' preview';
  }

  function syncSetupAction(root, state) {
    var footer = root.querySelector('[data-ai-setup-footer]');
    var generate = root.querySelector('[data-ai-generate]');
    var manage = root.querySelector('[data-ai-manage-designs]');
    var hint = root.querySelector('[data-ai-generate-hint]');
    if (!footer || !generate) return false;
    var hasPendingPreview = Boolean(
      state && state.pendingPreviewUrl && !state.cropper
    );
    var manageNeeded = Boolean(
      state && !hasPendingPreview && !state.previewFailed &&
      designSlotCount(state) >= MAX_WORKING_DRAFTS
    );
    generate.hidden = manageNeeded;
    if (manage) manage.hidden = !manageNeeded;
    if (hasPendingPreview) {
      generate.textContent = 'Open generated preview';
    } else if (state && state.variantUnavailable && !state.sessionRestoring) {
      generate.textContent = variantAvailabilityLabel(state);
    } else if (manageNeeded) {
      generate.textContent = newPreviewActionLabel(root, state);
    } else if (state && state.previewFailed) {
      generate.textContent = 'Retry preview';
    } else if (state && !photoSourceIsReady(state)) {
      generate.textContent = 'Choose a photo';
    } else {
      generate.textContent = newPreviewActionLabel(root, state);
    }
    var activeAction = manageNeeded && manage ? manage : generate;
    var ready = Boolean(
      state && activeAction && !activeAction.disabled && !state.busy &&
      root.dataset.aiGenerating !== 'true'
    );
    footer.dataset.aiReady = ready ? 'true' : 'false';
    if (hint) {
      var message = '';
      if (hasPendingPreview) {
        message = 'Your AI image is already generated. Opening it again will not use another preview.';
      } else if (state && state.variantUnavailable && !state.sessionRestoring) {
        message = 'Choose an available size or product option.';
      } else if (
        state && state.previewOverlayUnavailable && !state.sessionRestoring &&
        !state.busy && root.dataset.aiGenerating !== 'true'
      ) {
        message = state.previewOverlayStatus === 'LOADING'
          ? PREVIEW_OVERLAY_LOADING_MESSAGE
          : PREVIEW_OVERLAY_UNAVAILABLE_MESSAGE;
      } else if (state && state.canGenerate === false && !state.sessionRestoring) {
        message = state.quotaRemaining === 0
          ? 'Daily preview limit reached. Saved designs are still available.'
          : state.stylesReady === false
            ? 'New AI generation is temporarily unavailable. Saved designs can still be edited or used.'
            : 'AI generation is temporarily unavailable. Saved designs can still be edited or used.';
      } else if (state && state.stylesReady === false && !state.sessionRestoring) {
        message = 'New AI generation is temporarily unavailable. Saved designs remain available.';
      } else if (state && state.previewSettlementPending) {
        message = 'Finalizing the previous preview refund. Retry will be available shortly.';
      } else if (manageNeeded) {
        message = designLimitReachedMessage();
      }
      hint.textContent = message;
      hint.hidden = !message;
    }
    syncFooterNoticeVisibility(root);
    return ready;
  }

  function revealSetupAction(root, state, shouldFocus) {
    var footer = root.querySelector('[data-ai-setup-footer]');
    var generate = root.querySelector('[data-ai-generate]');
    var manage = root.querySelector('[data-ai-manage-designs]');
    if (!footer || !generate || !syncSetupAction(root, state)) return;
    var action = manage && !manage.hidden ? manage : generate;
    footer.classList.remove('is-attention');
    // Restart the short visual cue when a photo or style changes.
    void footer.offsetWidth;
    footer.classList.add('is-attention');
    window.requestAnimationFrame(function () {
      lockDialogChrome(root);
      if (shouldFocus) focusWithoutScrolling(action);
      window.requestAnimationFrame(function () { lockDialogChrome(root); });
    });
  }

  function focusReadySummary(root) {
    var target = root.querySelector('[data-ai-final-title]') || root.querySelector('[data-ai-add]');
    window.requestAnimationFrame(function () { focusWithoutScrolling(target); });
  }

  function settleResetSetupDialog(root, state, runNumber, focusPhoto) {
    resetDialogViewport(root);
    window.requestAnimationFrame(function () {
      if (runNumber !== state.runNumber) return;
      var dialog = root.querySelector('[data-ai-dialog]');
      if (!dialog || !dialogIsOpen(dialog) || dialog.dataset.aiView !== 'setup') return;
      resetDialogViewport(root);
      window.requestAnimationFrame(function () {
        if (
          runNumber !== state.runNumber ||
          !dialogIsOpen(dialog) ||
          dialog.dataset.aiView !== 'setup'
        ) return;
        focusWithoutScrolling(state.designLibraryOpen
          ? designLibraryItemFocusTarget(root)
          : focusPhoto ? root.querySelector('[data-ai-file]') : setupFocusTarget(root));
        // Focus changes can scroll overflow:hidden ancestors programmatically.
        // Reset once more after focus so the header and progress steps remain visible.
        resetDialogViewport(root);
      });
    });
  }

  function formatElapsed(milliseconds) {
    var seconds = Math.max(0, Math.floor(Number(milliseconds || 0) / 1000));
    var minutes = Math.floor(seconds / 60);
    var remainder = String(seconds % 60).padStart(2, '0');
    return 'Elapsed ' + minutes + ':' + remainder;
  }

  function progressForState(state, now) {
    var elapsed = Math.max(0, now - Number(state.progressPhaseStartedAt || now));
    var expectedMilliseconds = Math.max(
      5000,
      Number(state.expectedPreviewSeconds || DEFAULT_EXPECTED_PREVIEW_SECONDS) * 1000
    );
    if (state.progressPhase === 'preparing') return 5;
    if (state.progressPhase === 'uploading') {
      return 5 + 15 * Math.max(0, Math.min(1, Number(state.uploadProgress || 0)));
    }
    if (state.progressPhase === 'queued') {
      return Math.min(
        35,
        25 + 10 * (1 - Math.exp(-elapsed / (expectedMilliseconds * 0.4)))
      );
    }
    if (state.progressPhase === 'running') {
      return Math.min(
        92,
        35 + 57 * (1 - Math.exp(-elapsed / (expectedMilliseconds * 0.7)))
      );
    }
    if (state.progressPhase === 'loading') return 95;
    if (state.progressPhase === 'complete') return 100;
    return Number(state.progressValue || 0);
  }

  function progressAdvisoryLevelForState(state, totalElapsed) {
    var slowAfterMilliseconds = Math.max(
      5000,
      Number(state.slowAfterSeconds || DEFAULT_SLOW_AFTER_SECONDS) * 1000
    );
    var verySlowAfterMilliseconds = slowAfterMilliseconds + VERY_SLOW_OFFSET_SECONDS * 1000;
    if (totalElapsed >= verySlowAfterMilliseconds) return 2;
    if (totalElapsed >= slowAfterMilliseconds) return 1;
    return 0;
  }

  function updateProgressUi(root, state) {
    var progress = root.querySelector('[data-ai-progress]');
    if (!progress || progress.hidden) return;
    var now = Date.now();
    var candidate = progressForState(state, now);
    state.progressValue = Math.max(
      Number(state.progressValue || 0),
      Math.round(candidate)
    );
    var value = Math.min(100, state.progressValue);
    var bar = root.querySelector('[data-ai-progress-bar]');
    var meter = root.querySelector('[data-ai-progress-meter]');
    var valueNode = root.querySelector('[data-ai-progress-value]');
    var elapsedNode = root.querySelector('[data-ai-progress-elapsed]');
    var advisory = root.querySelector('[data-ai-progress-advisory]');
    var liveNode = root.querySelector('[data-ai-progress-live]');
    if (bar) bar.style.width = value + '%';
    if (meter) {
      meter.setAttribute('aria-valuenow', String(value));
      meter.setAttribute('aria-valuetext', 'About ' + value + ' percent complete');
    }
    if (valueNode) valueNode.textContent = value + '%';
    var totalElapsed = Math.max(0, now - Number(state.progressStartedAt || now));
    if (elapsedNode) elapsedNode.textContent = formatElapsed(totalElapsed);
    if (advisory) {
      var advisoryLevel = progressAdvisoryLevelForState(state, totalElapsed);
      if (advisoryLevel === 2) {
        advisory.textContent =
          'This is taking longer than usual. You can keep waiting or use regular photo upload.';
        advisory.hidden = false;
      } else if (advisoryLevel === 1) {
        advisory.textContent = 'Still working—complex photos can take longer.';
        advisory.hidden = false;
      } else {
        advisory.textContent = '';
        advisory.hidden = true;
      }
      if (advisoryLevel !== state.progressAdvisoryLevel) {
        state.progressAdvisoryLevel = advisoryLevel;
        if (liveNode && advisoryLevel > 0) liveNode.textContent = advisory.textContent;
      }
    }
  }

  function safeProgressStartedAt(value) {
    if (typeof value !== 'string' || value.length > 40) return 0;
    var timestamp = Date.parse(value);
    var now = Date.now();
    if (
      !Number.isFinite(timestamp) ||
      timestamp < now - POLL_TIMEOUT_MS ||
      timestamp > now + 5000
    ) return 0;
    return timestamp;
  }

  function applyProgressResponse(state, response) {
    if (!response || typeof response !== 'object' || Array.isArray(response)) return;
    var expected = response.expectedPreviewSeconds;
    var slowAfter = response.slowAfterSeconds;
    if (
      typeof expected === 'number' && typeof slowAfter === 'number' &&
      Number.isInteger(expected) && expected >= 5 && expected <= 600 &&
      Number.isInteger(slowAfter) && slowAfter >= expected && slowAfter <= 900
    ) {
      state.expectedPreviewSeconds = expected;
      state.slowAfterSeconds = slowAfter;
    }
    var supportedPhases = [
      'UPLOAD_PENDING', 'PREPARING', 'AI_QUEUED', 'AI_GENERATING',
      'PREVIEW_READY', 'FINALIZING_QUEUED', 'FINALIZING',
      'COMPLETE', 'FAILED', 'PROCESSING'
    ];
    if (supportedPhases.indexOf(response.phase) !== -1) {
      var publicPhaseChanged = state.publicPhase !== response.phase;
      state.publicPhase = response.phase;
      var startedAt = safeProgressStartedAt(response.startedAt);
      if (startedAt) state.publicPhaseStartedAt = startedAt;
      else if (publicPhaseChanged) state.publicPhaseStartedAt = 0;
    }
  }

  function syncPreviewProgress(root, state, response) {
    applyProgressResponse(state, response);
    var startedAt = state.publicPhaseStartedAt || undefined;
    if (state.publicPhase === 'AI_QUEUED') {
      setProgressStage(
        root,
        state,
        'queued',
        'Waiting for AI',
        'Your photo is securely queued for generation.',
        25,
        startedAt
      );
      return true;
    } else if (state.publicPhase === 'AI_GENERATING') {
      setProgressStage(
        root,
        state,
        'running',
        'Creating your preview',
        'The AI is transforming your photo.',
        35,
        startedAt
      );
      return true;
    } else if (state.publicPhase === 'PREVIEW_READY') {
      setProgressStage(
        root,
        state,
        'loading',
        'Loading your preview',
        'Generation is complete. Preparing the image editor.',
        95,
        startedAt
      );
      return true;
    }
    return false;
  }

  function updateProgressSummary(root, state) {
    var style = root.querySelector('[data-ai-style-choice][aria-checked="true"]');
    var styleSelect = root.querySelector('[data-ai-style]');
    var styleNode = root.querySelector('[data-ai-progress-style]');
    var fileNode = root.querySelector('[data-ai-progress-file]');
    var image = root.querySelector('[data-ai-progress-photo]');
    var styleTitle = style && style.querySelector('.giftinone-ai-style-card__title');
    if (styleNode) {
      styleNode.textContent = styleTitle
        ? styleTitle.textContent
        : styleSelect && styleSelect.options && styleSelect.selectedIndex >= 0
          ? styleSelect.options[styleSelect.selectedIndex].textContent
          : 'Selected style';
    }
    if (fileNode) fileNode.textContent = state.selectedFileName || 'Selected photo';
    if (image) {
      if (state.fileObjectUrl) image.src = state.fileObjectUrl;
      else image.removeAttribute('src');
    }
  }

  function setProgressStage(
    root,
    state,
    phase,
    title,
    detail,
    minimumValue,
    phaseStartedAt
  ) {
    var progress = root.querySelector('[data-ai-progress]');
    if (!progress) return;
    var phaseChanged = state.progressPhase !== phase;
    state.progressPhase = phase;
    if (phaseChanged || phaseStartedAt) {
      state.progressPhaseStartedAt = Number(phaseStartedAt || Date.now());
    }
    state.progressValue = Math.max(
      Number(state.progressValue || 0),
      Number(minimumValue || 0)
    );
    var titleNode = root.querySelector('[data-ai-progress-title]');
    var detailNode = root.querySelector('[data-ai-progress-detail]');
    var liveNode = root.querySelector('[data-ai-progress-live]');
    if (titleNode && title) titleNode.textContent = title;
    if (detailNode && detail) detailNode.textContent = detail;
    // Only phase changes enter the live region. Percentage ticks remain silent.
    if (phaseChanged && liveNode && title) liveNode.textContent = title;
    updateProgressUi(root, state);
  }

  function beginEstimatedProgress(root, state, phase, title, detail, minimumValue) {
    var progress = root.querySelector('[data-ai-progress]');
    if (!progress) return;
    if (state.progressTimer) window.clearInterval(state.progressTimer);
    state.generationActive = true;
    state.previewReadyPending = false;
    state.progressStartedAt = Date.now();
    state.progressPhaseStartedAt = state.progressStartedAt;
    state.progressPhase = '';
    state.progressValue = 0;
    state.uploadProgress = 0;
    state.progressAdvisoryLevel = 0;
    root.dataset.aiGenerating = 'true';
    progress.hidden = false;
    updateProgressSummary(root, state);
    setProgressStage(root, state, phase, title, detail, minimumValue);
    state.progressTimer = window.setInterval(function () {
      updateProgressUi(root, state);
    }, PROGRESS_TICK_MS);
    setDialogView(root, 'setup');
    updateLauncher(root, state);
  }

  function endEstimatedProgress(root, state, completed) {
    if (completed) {
      setProgressStage(
        root,
        state,
        'complete',
        'Preview ready',
        'Your preview is ready to position.',
        100
      );
    }
    if (state.progressTimer) window.clearInterval(state.progressTimer);
    state.progressTimer = null;
    state.generationActive = false;
    state.previewReadyPending = false;
    delete root.dataset.aiGenerating;
    var progress = root.querySelector('[data-ai-progress]');
    if (progress) progress.hidden = true;
    setDialogView(root, root.querySelector('[data-ai-dialog]').dataset.aiView || 'setup');
    updateLauncher(root, state);
    flushDeferredPhotoSelection(root, state);
  }

  function updateLauncher(root, state) {
    var launcher = root.querySelector('[data-ai-open]');
    if (!launcher) return;
    if (root.dataset.aiMode === 'regular') {
      launcher.hidden = true;
      return;
    }
    if (
      (root.dataset.aiState === 'ready' || root.dataset.aiState === 'added') &&
      state.selectedDesignCid && !state.generationActive &&
      !state.previewReadyPending && !state.cropper && !state.editorOpening
    ) {
      launcher.hidden = true;
      return;
    }
    launcher.hidden = false;
    // Existing work may still be opened without generating another image.
    var stockBlocked = Boolean(state.variantUnavailable && !hasExistingAiWork(state));
    launcher.disabled = Boolean(state.finalizingDraftCid || stockBlocked);
    markStockBlocked(launcher, stockBlocked);
    if (stockBlocked) {
      launcher.textContent = variantAvailabilityLabel(state);
    } else if (state.previewReadyPending) {
      launcher.textContent = 'Preview ready — continue';
    } else if (state.generationActive) {
      launcher.textContent = 'View progress';
    } else if (state.editorOpening) {
      launcher.textContent = 'Opening preview…';
    } else if (state.pendingPreviewUrl && !state.cropper) {
      launcher.textContent = 'Open generated preview';
    } else if (state.cropper) {
      launcher.textContent = 'Continue editing';
    } else {
      launcher.textContent = 'Create AI artwork';
    }
  }

  function dialogIsOpen(dialog) {
    return Boolean(dialog && dialog.hasAttribute('open'));
  }

  function releaseDialogScrollLock() {
    if (!document.querySelector('[data-ai-dialog][open]')) {
      document.documentElement.classList.remove('giftinone-ai-dialog-open');
      document.body.classList.remove('giftinone-ai-dialog-open');
    }
  }

  function afterDialogClose(root, state) {
    var dialog = root.querySelector('[data-ai-dialog]');
    dismissActionConfirmation(root, false, null, true);
    trackAiEvent(root, 'studio_close', {
      view: dialog && dialog.dataset.aiView === 'editor' ? 'editor' : 'setup'
    });
    releaseDialogScrollLock();
    if (state.editorOpening && typeof state.abortEditorOpen === 'function') {
      state.abortEditorOpen();
    }
    if (state.designLibraryOpen) {
      setDesignLibraryOpen(root, state, false, false);
    }
    updateLauncher(root, state);
    if (state.restoreFocusOnClose && state.dialogTrigger) {
      var returnTarget = state.dialogTrigger;
      if (returnTarget.isConnected === false || returnTarget.hidden ||
          (typeof returnTarget.getClientRects === 'function' && !returnTarget.getClientRects().length)) {
        returnTarget = root.dataset.aiState === 'added'
          ? root.querySelector('[data-ai-another]')
          : root.dataset.aiState === 'ready'
            ? root.querySelector('[data-ai-add]')
            : root.querySelector('[data-ai-open]');
      }
      if (returnTarget) focusWithoutScrolling(returnTarget);
    }
    state.restoreFocusOnClose = false;
  }

  function openAiDialog(root, state, trigger) {
    var dialog = root.querySelector('[data-ai-dialog]');
    if (!dialog) return;
    var openedNow = !dialogIsOpen(dialog);
    if (!dialogIsOpen(dialog)) {
      state.dialogTrigger = trigger || document.activeElement;
      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
        dialog.setAttribute('role', 'dialog');
        dialog.setAttribute('aria-modal', 'true');
      }
    }
    document.documentElement.classList.add('giftinone-ai-dialog-open');
    document.body.classList.add('giftinone-ai-dialog-open');
    if (openedNow) {
      trackAiEvent(root, 'studio_open', {
        view: dialog.dataset.aiView === 'editor' ? 'editor' : 'setup'
      });
    }
    scheduleEditorViewportResize(root, state, true);
  }

  function closeAiDialog(root, state, restoreFocus) {
    var dialog = root.querySelector('[data-ai-dialog]');
    if (!dialog || !dialogIsOpen(dialog)) return;
    state.restoreFocusOnClose = Boolean(restoreFocus);
    if (typeof dialog.close === 'function') {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
      afterDialogClose(root, state);
    }
  }

  function apiRequest(root, path, method, token, body, extraOptions) {
    var headers = { Accept: 'application/json' };
    var requestOptions = extraOptions || {};
    var options = {
      method: method,
      mode: 'cors',
      cache: 'no-store',
      credentials: 'include',
      headers: headers
    };
    Object.keys(requestOptions.headers || {}).forEach(function (name) {
      headers[name] = requestOptions.headers[name];
    });
    if (requestOptions.keepalive) options.keepalive = true;
    if (token) headers['X-Customization-Token'] = token;
    if (body !== undefined) {
      headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(body);
    }
    var base = String(root.dataset.apiBase || '').replace(/\/+$/, '');
    return window.fetch(base + path, options).then(function (response) {
      return response.text().then(function (text) {
        var data = {};
        try {
          data = text ? JSON.parse(text) : {};
        } catch (error) {
          throw new Error('The AI service returned an invalid response.');
        }
        if (!response.ok || data.ok === false) {
          var apiError = new Error(
            data.message || 'The AI service is temporarily unavailable.'
          );
          apiError.code = data.code || 'AI_REQUEST_FAILED';
          apiError.status = response.status;
          apiError.data = data;
          throw apiError;
        }
        return data;
      });
    });
  }

  function sessionFirstJobRequest(root, path, method, token, body, extraOptions) {
    return apiRequest(root, path, method, '', body, extraOptions).catch(function (error) {
      var disabledDrainFallback = Boolean(
        error && error.status === 503 && error.code === 'AI_PREVIEW_DISABLED'
      );
      if (
        !token || !error ||
        (
          error.status !== 401 && error.status !== 403 &&
          !disabledDrainFallback
        )
      ) throw error;
      // A current Studio session owns every new AI job. Omitting the custom
      // header keeps normal POST /preview and GET status requests simple CORS
      // requests. The capability token remains a compatibility fallback for
      // an unusual browser/session-cookie failure.
      return apiRequest(root, path, method, token, body, extraOptions);
    });
  }

  function browserTimeZone() {
    try {
      return String(Intl.DateTimeFormat().resolvedOptions().timeZone || '');
    } catch (_error) {
      return '';
    }
  }

  function quotaMessage(remaining) {
    if (remaining === 5) return '5 free previews remaining today';
    if (remaining > 1) return remaining + ' free previews remaining today';
    if (remaining === 1) return '1 free preview remaining today';
    return 'No free previews remaining today';
  }

  function updateQuotaUi(root, state, refunded) {
    var node = root.querySelector('[data-ai-quota-status]');
    if (!node) return;
    var remaining = state.quotaRemaining;
    if (!Number.isInteger(remaining) || remaining > 5) {
      node.hidden = true;
      node.textContent = '';
      syncFooterNoticeVisibility(root);
      return;
    }
    var message = quotaMessage(remaining);
    if (refunded) message = 'This technical failure was not counted. ' + message;
    if (remaining === 0) {
      message += '. You can still edit or use your saved designs.';
    }
    node.textContent = message;
    node.hidden = false;
    syncFooterNoticeVisibility(root);
  }

  function applyQuota(root, state, response) {
    if (!response || typeof response !== 'object') return false;
    if (typeof response.canGenerate === 'boolean') state.canGenerate = response.canGenerate;
    var quota = response.quota;
    if (response.previewRefunded === true) {
      setStatus(root, 'This technical failure was not counted.', 'setup');
      setStatus(root, 'This technical failure was not counted.', 'page');
    }
    if (quota && Number.isInteger(quota.remaining) && quota.remaining > 5) {
      state.quotaRemaining = null;
      updateQuotaUi(root, state, false);
      return false;
    }
    if (!quota || !Number.isInteger(quota.remaining)) {
      if (response.canGenerate === true || response.previewRefunded === true) state.quotaRemaining = null;
      updateQuotaUi(root, state, false);
      return false;
    }
    state.quotaRemaining = Math.max(0, Math.min(5, quota.remaining));
    state.quotaResetsAt = typeof quota.resetsAt === 'string' ? quota.resetsAt : '';
    state.canGenerate = response.canGenerate === false
      ? false
      : state.quotaRemaining > 0;
    updateQuotaUi(root, state, quota.refunded === true);
    return true;
  }

  function setAutoSaveStatus(root, message, isError) {
    var node = root.querySelector('[data-ai-autosave-status]');
    if (!node) return;
    node.textContent = message || '';
    node.hidden = !message;
    if (isError) node.dataset.aiError = 'true';
    else delete node.dataset.aiError;
  }

  function setRestoreStatus(root, message, visible, isError) {
    var node = root.querySelector('[data-ai-restore-status]');
    if (!node) return;
    var copy = node.querySelector('span:last-child');
    if (copy) copy.textContent = message || '';
    node.hidden = !visible;
    if (isError) node.dataset.aiError = 'true';
    else delete node.dataset.aiError;
  }

  function normalizedContentType(file) {
    var type = String(file.type || '').toLowerCase().split(';')[0];
    if (type === 'image/jpg' || type === 'image/pjpeg') type = 'image/jpeg';
    if (type !== 'image/jpeg' && type !== 'image/png' && type !== 'image/webp') {
      throw new Error('Please upload a JPG, PNG or WebP image.');
    }
    return type;
  }

  function validateOriginalFile(file) {
    if (!file) throw new Error('Please choose a photo.');
    if (!Number.isFinite(Number(file.size)) || file.size <= 0) {
      throw new Error('The selected photo is empty. Please choose another file.');
    }
    if (file.size > MAX_ORIGINAL_BYTES) {
      throw new Error('The photo is larger than 100 MB. Please choose a smaller file.');
    }
    return normalizedContentType(file);
  }

  function humanFileSize(bytes) {
    var value = Number(bytes) || 0;
    if (value < 1024 * 1024) return Math.max(1, Math.round(value / 1024)) + ' KB';
    return (value / (1024 * 1024)).toFixed(value >= 10 * 1024 * 1024 ? 0 : 1) + ' MB';
  }

  function revokeFilePreview(state) {
    if (state.fileObjectUrl && window.URL && typeof window.URL.revokeObjectURL === 'function') {
      window.URL.revokeObjectURL(state.fileObjectUrl);
    }
    state.fileObjectUrl = '';
  }

  function updateFileUi(root, state, file) {
    var picker = root.querySelector('.giftinone-ai-file-picker');
    var preview = root.querySelector('[data-ai-file-preview]');
    var previewImage = root.querySelector('[data-ai-file-preview-image]');
    var previewName = root.querySelector('[data-ai-file-preview-name]');
    var meta = root.querySelector('[data-ai-file-meta]');
    var fileName = root.querySelector('[data-ai-file-name]');
    var action = root.querySelector('[data-ai-file-action]');
    revokeFilePreview(state);
    if (!file) {
      if (picker) picker.hidden = false;
      if (preview) preview.hidden = true;
      if (previewImage) previewImage.removeAttribute('src');
      if (previewName) previewName.textContent = '';
      if (meta) meta.textContent = '';
      if (fileName) fileName.textContent = 'Upload photo';
      if (action) action.textContent = 'Choose';
      renderDesignGallery(root, state);
      return;
    }
    if (picker) picker.hidden = true;
    if (preview) preview.hidden = false;
    if (previewName) previewName.textContent = 'Photo added';
    if (meta) meta.textContent = file.name || 'Selected photo';
    if (fileName) fileName.textContent = file.name || 'Selected photo';
    if (action) action.textContent = 'Change';
    if (previewImage && window.URL && typeof window.URL.createObjectURL === 'function') {
      state.fileObjectUrl = window.URL.createObjectURL(file);
      previewImage.src = state.fileObjectUrl;
    }
    renderDesignGallery(root, state);
  }

  function updateRestoredSourceUi(root, state, draft) {
    var picker = root.querySelector('.giftinone-ai-file-picker');
    var preview = root.querySelector('[data-ai-file-preview]');
    var previewImage = root.querySelector('[data-ai-file-preview-image]');
    var previewName = root.querySelector('[data-ai-file-preview-name]');
    var meta = root.querySelector('[data-ai-file-meta]');
    var fileName = root.querySelector('[data-ai-file-name]');
    var action = root.querySelector('[data-ai-file-action]');
    revokeFilePreview(state);
    if (picker) picker.hidden = true;
    if (preview) preview.hidden = false;
    if (previewName) previewName.textContent = 'Using saved photo';
    if (meta) meta.textContent = draft && draft.sourceLabel
      ? draft.sourceLabel
      : 'Saved photo';
    if (fileName) fileName.textContent = 'Saved photo';
    if (action) action.textContent = 'Change';
    if (previewImage) {
      try {
        var recoveryUrl = new window.URL(String(draft.originalUrl || ''));
        if (recoveryUrl.protocol === 'https:') previewImage.src = recoveryUrl.href;
        else previewImage.removeAttribute('src');
      } catch (_error) {
        previewImage.removeAttribute('src');
      }
    }
    renderDesignGallery(root, state);
  }

  function emptyPhotoSource() {
    return {
      kind: 'none',
      file: null,
      sourceCid: '',
      previewUrl: '',
      label: ''
    };
  }

  function currentPhotoSource(state) {
    var source = state && state.photoSource;
    if (
      source &&
      (source.kind === 'none' || source.kind === 'local' || source.kind === 'saved')
    ) return source;
    return emptyPhotoSource();
  }

  function photoSourceIsReady(state) {
    var source = currentPhotoSource(state);
    if (source.kind === 'local') return Boolean(source.file);
    return source.kind === 'saved' && CID_PATTERN.test(String(source.sourceCid || ''));
  }

  // Keep the old fields synchronized while the remaining editor/draft code is
  // gradually migrated. They are compatibility mirrors only; readiness and
  // generation always read state.photoSource.
  function syncLegacyPhotoState(state) {
    var source = currentPhotoSource(state);
    var localFile = source.kind === 'local' ? source.file || null : null;
    state.fileValid = Boolean(localFile);
    state.pendingFileObject = localFile;
    state.activeFileObject = localFile;
    state.forkSourceCid = source.kind === 'saved'
      ? String(source.sourceCid || '')
      : '';
    state.selectedFileName = source.kind === 'local'
      ? String(localFile && localFile.name || source.label || 'Selected photo')
      : source.kind === 'saved'
        ? String(source.label || 'Saved photo')
        : '';
  }

  function renderPhotoSourceUi(root, state) {
    if (!root || !state) return;
    var source = currentPhotoSource(state);
    if (source.kind === 'local' && source.file) {
      updateFileUi(root, state, source.file);
      return;
    }
    if (source.kind === 'saved') {
      updateRestoredSourceUi(root, state, {
        originalUrl: source.previewUrl || '',
        sourceLabel: source.label || 'Saved photo'
      });
      return;
    }
    updateFileUi(root, state, null);
  }

  function setPhotoSource(root, state, nextSource, options) {
    if (!state) return emptyPhotoSource();
    var source = nextSource || emptyPhotoSource();
    var normalized = emptyPhotoSource();
    if (source.kind === 'local' && source.file) {
      validateOriginalFile(source.file);
      normalized.kind = 'local';
      normalized.file = source.file;
      normalized.label = String(
        source.label || source.file.name || 'Selected photo'
      );
    } else if (
      source.kind === 'saved' &&
      CID_PATTERN.test(String(source.sourceCid || ''))
    ) {
      normalized.kind = 'saved';
      normalized.sourceCid = String(source.sourceCid);
      normalized.previewUrl = String(source.previewUrl || '');
      normalized.label = String(source.label || 'Saved photo');
    }
    state.photoSource = normalized;
    if (!options || options.incrementRevision !== false) {
      state.photoSourceRevision = Number(state.photoSourceRevision || 0) + 1;
    }
    syncLegacyPhotoState(state);
    if (root) {
      var fileInput = root.querySelector('[data-ai-file]');
      if (fileInput && normalized.kind !== 'local') fileInput.value = '';
      if (!options || options.render !== false) renderPhotoSourceUi(root, state);
      syncSetupAction(root, state);
    }
    return normalized;
  }

  function photoSourceFromDraft(draft) {
    if (!draft) return emptyPhotoSource();
    if (draft.fileObject) {
      return {
        kind: 'local',
        file: draft.fileObject,
        label: draft.fileObject.name || 'Selected photo'
      };
    }
    if (CID_PATTERN.test(String(draft.cid || ''))) {
      return {
        kind: 'saved',
        sourceCid: String(draft.cid),
        previewUrl: String(draft.originalUrl || ''),
        label: draft.styleLabel
          ? 'Saved photo from ' + draft.styleLabel
          : 'Saved photo'
      };
    }
    return emptyPhotoSource();
  }

  function setPhotoSourceFromDraft(root, state, draft, options) {
    return setPhotoSource(root, state, photoSourceFromDraft(draft), options);
  }

  function photoSourceDraft(state, source) {
    var selected = source || currentPhotoSource(state);
    if (selected.kind !== 'saved') return null;
    return workingDraftByCid(state, selected.sourceCid) ||
      readyDesignByCid(state, selected.sourceCid);
  }

  function clearPhotoSourceForCid(root, state, cid) {
    var source = currentPhotoSource(state);
    if (source.kind !== 'saved' || source.sourceCid !== String(cid || '')) return false;
    setPhotoSource(root, state, emptyPhotoSource());
    return true;
  }

  function queuePhotoFileSelection(root, state, fileInput, file) {
    if (!file) return false;
    try {
      validateOriginalFile(file);
    } catch (error) {
      if (fileInput) {
        fileInput.value = '';
        fileInput.setAttribute('aria-invalid', 'true');
      }
      setStatus(root, error.message, 'setup', true);
      return false;
    }
    state.deferredPhotoSelection = {
      file: file,
      sequence: Number(state.deferredPhotoSequence || 0) + 1
    };
    state.deferredPhotoSequence = state.deferredPhotoSelection.sequence;
    setStatus(
      root,
      'Photo selected. It will be ready when the current action finishes.',
      'setup'
    );
    return true;
  }

  function flushDeferredPhotoSelection(root, state) {
    if (
      !state || !state.deferredPhotoSelection || state.busy ||
      state.finalizingDraftCid || state.sessionRestoring ||
      state.cartSubmitting || state.cartUnknown || state.generationActive ||
      root.dataset.aiGenerating === 'true'
    ) return false;
    var pending = state.deferredPhotoSelection;
    state.deferredPhotoSelection = null;
    window.setTimeout(function () {
      if (!state.sessionReady || state.sessionFailed ||
          pending.sequence !== state.deferredPhotoSequence) return;
      if (
        (('isConnected' in root && !root.isConnected) ||
          (!('isConnected' in root) && !document.documentElement.contains(root))) ||
        state.busy || state.finalizingDraftCid ||
        state.sessionRestoring || state.generationActive
      ) {
        if (!state.deferredPhotoSelection) state.deferredPhotoSelection = pending;
        return;
      }
      applySelectedPhotoFile(root, state, pending.file, true);
    }, 0);
    return true;
  }

  function stylePresentationText(value, maxLength) {
    return String(value == null ? '' : value)
      .replace(/[\u0000-\u001f\u007f]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, maxLength);
  }

  function normalizedStyleThumbnailUrl(value) {
    if (!value || String(value).length > 2048) return '';
    try {
      var url = new window.URL(String(value), window.location.href);
      if (
        url.protocol !== 'https:' || url.username || url.password ||
        url.port || url.hash
      ) return '';
      var shopifyFile =
        url.hostname === 'cdn.shopify.com' &&
        (url.pathname.indexOf('/s/files/') === 0 ||
          url.pathname.indexOf('/cdn/shop/files/') === 0);
      var storefrontShopifyFile =
        url.pathname.indexOf('/cdn/shop/files/') === 0 &&
        (
          url.hostname === window.location.hostname.toLowerCase() ||
          url.hostname === 'giftinone.com' ||
          url.hostname === 'www.giftinone.com' ||
          url.hostname === 'gift-in-one.myshopify.com'
        );
      var giftInOneStyle =
        url.hostname === 'static.giftinone.com' &&
        url.pathname.indexOf('/aiStyles/') === 0;
      return shopifyFile || storefrontShopifyFile || giftInOneStyle
        ? url.href
        : '';
    } catch (_error) {
      return '';
    }
  }

  /**
   * Preview clock faces are Shopify Files and are presentation-only. Never
   * accept an arbitrary URL here and never send this value as a production
   * asset selector.
   */
  function normalizedPreviewOverlayUrl(value) {
    if (!value || String(value).length > 2048) return '';
    try {
      var url = new window.URL(String(value), window.location.href);
      if (
        url.protocol !== 'https:' || url.username || url.password ||
        url.port || url.hash
      ) return '';
      var cdnShopifyFile =
        url.hostname === 'cdn.shopify.com' &&
        (url.pathname.indexOf('/s/files/') === 0 ||
          url.pathname.indexOf('/cdn/shop/files/') === 0);
      return cdnShopifyFile ? url.href : '';
    } catch (_error) {
      return '';
    }
  }

  function readAiJson(root, selector, errorMessage) {
    var script = root.querySelector(selector);
    if (!script) throw new Error(errorMessage);
    try {
      var parsed = JSON.parse(script.textContent || '{}');
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error(errorMessage);
      }
      return parsed;
    } catch (_error) {
      throw new Error(errorMessage);
    }
  }

  function greatestCommonDivisor(left, right) {
    var a = Math.abs(Math.round(Number(left) || 0));
    var b = Math.abs(Math.round(Number(right) || 0));
    while (b) {
      var next = a % b;
      a = b;
      b = next;
    }
    return a || 1;
  }

  function normalizedRatio(width, height) {
    var ratioWidth = width;
    var ratioHeight = height;
    if (
      typeof ratioWidth !== 'number' || typeof ratioHeight !== 'number' ||
      !Number.isInteger(ratioWidth) || ratioWidth < 1 || ratioWidth > 100000 ||
      !Number.isInteger(ratioHeight) || ratioHeight < 1 || ratioHeight > 100000
    ) return null;
    var divisor = greatestCommonDivisor(ratioWidth, ratioHeight);
    return {
      aspectWidth: ratioWidth,
      aspectHeight: ratioHeight,
      aspectRatio: ratioWidth / ratioHeight,
      ratioKey: Math.round(ratioWidth / divisor) + ':' + Math.round(ratioHeight / divisor)
    };
  }

  function normalizeVariantRatioRules(source) {
    if (!Array.isArray(source)) {
      throw new Error('AI Variant ratio rules are not valid JSON rules.');
    }
    var rules = [];
    if (source.length < 1 || source.length > 250) {
      throw new Error('AI Variant ratio rules exceed the supported limit.');
    }
    source.forEach(function (entry) {
      if (
        !entry || typeof entry !== 'object' || Array.isArray(entry) ||
        Object.keys(entry).length !== 2 ||
        !Object.prototype.hasOwnProperty.call(entry, 'when') ||
        !Object.prototype.hasOwnProperty.call(entry, 'ratio')
      ) {
        throw new Error('An AI Variant ratio rule has an invalid structure.');
      }
      var when = entry.when;
      var ratio = entry.ratio;
      if (
        !when || typeof when !== 'object' || Array.isArray(when) ||
        !ratio || typeof ratio !== 'object' || Array.isArray(ratio)
      ) throw new Error('An AI Variant ratio rule has an invalid structure.');
      var names = Object.keys(when);
      if (!names.length || names.length > 3) {
        throw new Error('An AI Variant ratio rule must match one to three options.');
      }
      var normalizedWhen = Object.create(null);
      for (var index = 0; index < names.length; index += 1) {
        var name = String(names[index] || '');
        var rawValue = when[names[index]];
        var value = typeof rawValue === 'string' ? rawValue : '';
        if (
          !name || name !== name.trim() || !value || value !== value.trim() ||
          name.length > 255 || value.length > 255 || normalizedWhen[name]
        ) {
          throw new Error('An AI Variant ratio rule contains an invalid option.');
        }
        normalizedWhen[name] = value;
      }
      if (
        Object.keys(ratio).length !== 2 ||
        !Object.prototype.hasOwnProperty.call(ratio, 'width') ||
        !Object.prototype.hasOwnProperty.call(ratio, 'height')
      ) throw new Error('An AI Variant ratio rule contains an invalid ratio.');
      var normalized = normalizedRatio(ratio.width, ratio.height);
      if (!normalized) throw new Error('An AI Variant ratio rule contains an invalid ratio.');
      rules.push({
        when: normalizedWhen,
        aspectWidth: normalized.aspectWidth,
        aspectHeight: normalized.aspectHeight,
        aspectRatio: normalized.aspectRatio,
        ratioKey: normalized.ratioKey
      });
    });
    return rules;
  }

  /** Read only the storefront-safe subset emitted from the optional profile. */
  function readRenderProfile(root) {
    var payload = readAiJson(
      root,
      '[data-ai-render-profile]',
      'AI product configuration is unavailable.'
    );
    var type = String(payload.type || '').trim().toUpperCase();
    if (type !== 'FREEFORM' && type !== 'OVERLAY' && type !== 'VARIANT_RATIO') {
      throw new Error('The selected AI Render Profile type is unsupported.');
    }
    var profile = {
      type: type,
      resolution: type === 'FREEFORM' ? 'NO_PROFILE' : 'CONFIGURED',
      previewOptionName: '',
      previewOverlayMap: Object.create(null),
      variantRatioRules: [],
      diagnostic: ''
    };
    if (type === 'OVERLAY') {
      var rawPreviewOptionName = String(payload.previewOptionName || '');
      profile.previewOptionName = rawPreviewOptionName;
      var sourceMap = payload.previewOverlayMap;
      if (
        !profile.previewOptionName ||
        profile.previewOptionName !== profile.previewOptionName.trim() ||
        profile.previewOptionName.length > 255 ||
        !sourceMap || typeof sourceMap !== 'object' || Array.isArray(sourceMap)
      ) throw new Error('The AI preview overlay profile is invalid.');
      if (sourceMap && typeof sourceMap === 'object' && !Array.isArray(sourceMap)) {
        var overlayKeys = Object.keys(sourceMap);
        if (overlayKeys.length < 1 || overlayKeys.length > 250) {
          throw new Error('The AI preview overlay map has an invalid size.');
        }
        overlayKeys.forEach(function (optionValue) {
          var exactValue = String(optionValue || '');
          var rawUrl = sourceMap[optionValue];
          var safeUrl = typeof rawUrl === 'string'
            ? normalizedPreviewOverlayUrl(rawUrl)
            : '';
          if (
            !exactValue || exactValue !== exactValue.trim() || exactValue.length > 255 ||
            !rawUrl || rawUrl !== rawUrl.trim() || rawUrl.length > 2048 || !safeUrl
          ) {
            throw new Error('The AI preview overlay map contains an invalid entry.');
          }
          profile.previewOverlayMap[exactValue] = safeUrl;
        });
      }
      if (!Object.keys(profile.previewOverlayMap).length) {
        throw new Error('The AI preview overlay map is empty.');
      }
    } else if (type === 'VARIANT_RATIO') {
      try {
        profile.variantRatioRules = normalizeVariantRatioRules(payload.variantRatioRules);
      } catch (ratioConfigurationError) {
        // A ratio profile is only an optional composition hint. A malformed,
        // empty or partly malformed rule set must never remove AI from the
        // product. Invalidate the whole ratio set (instead of applying a
        // surprising partial subset) and let this Variant use FREEFORM.
        // Overlay profiles intentionally do not use this fallback: production
        // overlay mistakes remain blocking because the final asset is required.
        profile.type = 'FREEFORM';
        profile.resolution = 'INVALID_CONFIG';
        profile.variantRatioRules = [];
        profile.diagnostic = String(
          ratioConfigurationError && ratioConfigurationError.message ||
          'The AI Variant ratio rules are invalid.'
        );
        console.warn(
          'GiftInOne AI Variant Ratio Profile: using FREEFORM because the optional rules are invalid.',
          profile.diagnostic
        );
      }
    }
    return profile;
  }

  function readProductVariants(root, renderProfile) {
    var payload = readAiJson(
      root,
      '[data-ai-product-variants]',
      'Product options are unavailable.'
    );
    var optionNames = Array.isArray(payload.optionNames)
      ? payload.optionNames.map(function (name) { return String(name || ''); })
      : [];
    var variants = Array.isArray(payload.variants) ? payload.variants : [];
    if (!optionNames.length || !variants.length) {
      throw new Error('Product options are unavailable.');
    }
    if (
      renderProfile.type === 'OVERLAY' &&
      optionNames.indexOf(renderProfile.previewOptionName) < 0
    ) throw new Error('The AI preview option does not exist on this product.');

    var byId = Object.create(null);
    variants.forEach(function (variant) {
      var id = String(variant && variant.id || '');
      var values = variant && Array.isArray(variant.options) ? variant.options : [];
      if (!/^\d{1,25}$/.test(id) || values.length !== optionNames.length || byId[id]) return;
      var selectedOptions = optionNames.map(function (name, index) {
        return { name: name, value: String(values[index] == null ? '' : values[index]) };
      });
      byId[id] = {
        variantId: id,
        selectedOptions: selectedOptions,
        available: variant.available !== false
      };
    });
    if (!Object.keys(byId).length) {
      throw new Error('No available product Variant could be loaded.');
    }
    return byId;
  }

  function selectedVariantContract(root, state, productForm) {
    var variantInput = productForm && productForm.querySelector('[name="id"]');
    var variantId = String(variantInput && variantInput.value || '').trim();
    var matched = state.variantsById && state.variantsById[variantId];
    var productId = String(root.dataset.productId || '').trim();
    var availability = readSelectedVariantAvailability(root, state, productForm);
    if (!/^\d{1,25}$/.test(productId) || !matched ||
        availability.soldOut || availability.unavailable || availability.pending) {
      throw new Error('Choose an available product option before continuing.');
    }
    return {
      productId: productId,
      variantId: matched.variantId,
      // Keep the exact Shopify option value for Variant binding. For example,
      // 16" recommend stays unchanged here. Only the trusted AWS backend
      // normalizes the corresponding production token to 16 recommend.
      selectedOptions: matched.selectedOptions.map(function (option) {
        return { name: option.name, value: option.value };
      })
    };
  }

  function readSelectedVariantAvailability(root, state, productForm) {
    var input = productForm && productForm.querySelector('[name="id"]');
    var variantId = String(input && input.value || '').trim();
    var matched = state.variantsById && state.variantsById[variantId];
    var scope = root.closest('product-info');
    var selectedJson = scope && scope.querySelector('variant-selects [data-selected-variant]');
    var available = matched && matched.available;
    // A section response can contain newer stock than the initial product JSON.
    // Ignore a response belonging to a different selection while IDs catch up.
    if (selectedJson) {
      try {
        var selected = JSON.parse(selectedJson.textContent || 'null');
        if (selected && String(selected.id) === variantId &&
            typeof selected.available === 'boolean') available = selected.available;
      } catch (_error) {}
    }
    var nativeForm = productForm && productForm.closest('product-form');
    var submit = productForm && productForm.querySelector('[type="submit"][name="add"]');
    var nativeDisabled = nativeForm && typeof nativeForm.variantDisabled === 'boolean'
      ? nativeForm.variantDisabled : Boolean(submit && submit.disabled);
    // toggleSubmitButton(true) leaves old DOM text during a section request.
    // The native controller clears variantText until the response is applied.
    var nativeText = nativeForm && typeof nativeForm.variantText === 'string'
      ? nativeForm.variantText : String(submit && submit.textContent || '');
    function normalize(value) { return String(value || '').trim().toLowerCase().replace(/\s+/g, ' '); }
    var text = normalize(nativeText);
    var strings = window.variantStrings || {};
    var soldOutText = text && (text === normalize(strings.soldOut) || /^sold\s*out$/.test(text));
    var unavailableText = text && (text === normalize(strings.unavailable) || text === 'unavailable');
    var unavailable = Boolean(!variantId || !matched || nativeDisabled && unavailableText);
    var soldOut = Boolean(!unavailable && (available === false || nativeDisabled && soldOutText));
    var pending = Boolean(!unavailable && !soldOut && nativeDisabled && nativeForm &&
      typeof nativeForm.variantText === 'string' && !text);
    return { variantId: variantId, soldOut: soldOut, unavailable: unavailable, pending: pending };
  }

  function updateVariantAvailability(root, state, productForm) {
    var availability = readSelectedVariantAvailability(root, state, productForm);
    state.variantSoldOut = availability.soldOut;
    state.variantPending = availability.pending;
    state.variantUnavailable = availability.soldOut || availability.unavailable || availability.pending;
    return availability;
  }

  function variantAvailabilityLabel(state) {
    if (state.variantSoldOut) return (window.variantStrings || {}).soldOut || 'Sold out';
    if (state.variantPending) return 'Updating options…';
    return (window.variantStrings || {}).unavailable || 'Unavailable';
  }

  function hasExistingAiWork(state) {
    return Boolean(state.generationActive || state.previewReadyPending || state.pendingPreviewUrl ||
      state.cropper || state.editorOpening);
  }

  function markStockBlocked(button, blocked) {
    if (!button) return;
    if (blocked) button.setAttribute('data-ai-stock-blocked', 'true');
    else button.removeAttribute('data-ai-stock-blocked');
  }

  function syncVariantAvailabilityNotice(root, state) {
    var notice = root.querySelector('[data-ai-availability-status]');
    if (!notice) return;
    var visible = Boolean(root.dataset.aiMode === 'ai' && state.variantUnavailable);
    notice.hidden = !visible;
    notice.textContent = !visible ? '' : state.variantPending
      ? 'Updating product options…'
      : state.variantSoldOut
        ? 'This option is sold out. Choose another size or option.'
        : 'This combination is unavailable. Choose another size or option.';
  }

  function freeformRenderPolicy(resolution) {
    return {
      mode: 'FREEFORM',
      resolution: String(resolution || 'NO_PROFILE'),
      constrained: false,
      aspectRatio: NaN,
      signature: 'FREEFORM'
    };
  }

  function selectedOptionMap(contract) {
    var values = Object.create(null);
    (contract && Array.isArray(contract.selectedOptions)
      ? contract.selectedOptions
      : []
    ).forEach(function (option) {
      var name = String(option && option.name || '');
      if (name) values[name] = String(option && option.value == null ? '' : option.value);
    });
    return values;
  }

  /**
   * This is a UI hint only. The API repeats the same match against the exact
   * Shopify Variant and returns the authoritative public renderPolicy.
   */
  function clientRenderPolicyForContract(renderProfile, contract) {
    if (!renderProfile || renderProfile.type !== 'VARIANT_RATIO') {
      return freeformRenderPolicy(renderProfile && renderProfile.type === 'OVERLAY'
        ? 'AWAITING_SERVER_OVERLAY'
        : renderProfile && renderProfile.resolution || 'NO_PROFILE');
    }
    var optionValues = selectedOptionMap(contract);
    var matches = renderProfile.variantRatioRules.filter(function (rule) {
      return Object.keys(rule.when).every(function (name) {
        return Object.prototype.hasOwnProperty.call(optionValues, name) &&
          optionValues[name] === rule.when[name];
      });
    });
    if (matches.length !== 1) {
      return freeformRenderPolicy(matches.length ? 'AMBIGUOUS' : 'NO_MATCH');
    }
    var matched = matches[0];
    return {
      mode: 'VARIANT_RATIO',
      resolution: 'MATCHED',
      constrained: true,
      aspectWidth: matched.aspectWidth,
      aspectHeight: matched.aspectHeight,
      aspectRatio: matched.aspectRatio,
      signature: 'RATIO:' + matched.ratioKey,
      authoritative: false
    };
  }

  function normalizePublicRenderPolicy(response) {
    var source = response && response.renderPolicy;
    if (!source || typeof source !== 'object' || Array.isArray(source)) {
      throw new Error('The product render policy is missing from the server response.');
    }
    var mode = String(source.mode || '').trim().toUpperCase();
    var resolution = String(source.resolution || '').trim().toUpperCase();
    if (mode === 'FREEFORM') {
      var freeform = freeformRenderPolicy(resolution || 'NO_MATCH');
      freeform.authoritative = true;
      return freeform;
    }
    if (mode !== 'VARIANT_RATIO' && mode !== 'OVERLAY') {
      throw new Error('The server returned an invalid product render policy.');
    }
    var ratio = normalizedRatio(source.aspectWidth, source.aspectHeight);
    if (!ratio) throw new Error('The server returned an invalid production shape.');
    return {
      mode: mode,
      resolution: resolution || 'MATCHED',
      constrained: true,
      aspectWidth: ratio.aspectWidth,
      aspectHeight: ratio.aspectHeight,
      aspectRatio: ratio.aspectRatio,
      signature: 'RATIO:' + ratio.ratioKey,
      authoritative: true
    };
  }

  function rememberAuthoritativeRenderPolicy(state, response, contract, activate) {
    var policy = normalizePublicRenderPolicy(response);
    if (!contract || !variantBindingMatches(response.variantBinding, contract)) {
      throw new Error('The product render policy does not match the selected Variant.');
    }
    state.renderPoliciesByVariant[contract.variantId] = policy;
    if (activate !== false) state.activeRenderPolicy = policy;
    return policy;
  }

  function rememberPolicyFromJob(state, job, activate) {
    var binding = job && job.variantBinding;
    if (!binding || !Array.isArray(binding.selectedOptions)) {
      throw new Error('The product Variant binding is missing from the server response.');
    }
    return rememberAuthoritativeRenderPolicy(state, job, {
      productId: String(binding.productId || ''),
      variantId: String(binding.variantId || ''),
      selectedOptions: binding.selectedOptions.map(function (option) {
        return {
          name: String(option && option.name || ''),
          value: String(option && option.value == null ? '' : option.value)
        };
      })
    }, activate);
  }

  function effectiveRenderPolicyForContract(state, contract) {
    var cached = contract && state.renderPoliciesByVariant
      ? state.renderPoliciesByVariant[contract.variantId]
      : null;
    if (cached) return cached;
    if (
      state.renderProfile.type === 'OVERLAY' && state.activeRenderPolicy &&
      state.activeRenderPolicy.mode === 'OVERLAY' && state.activeRenderPolicy.constrained
    ) {
      var provisional = cloneRenderPolicy(state.activeRenderPolicy);
      provisional.authoritative = false;
      provisional.resolution = 'AWAITING_SERVER_OVERLAY';
      return provisional;
    }
    return clientRenderPolicyForContract(state.renderProfile, contract);
  }

  function sameRenderShape(left, right) {
    if (!left || !right) return false;
    return String(left.signature || '') === String(right.signature || '');
  }

  function activeEditorAspectRatio(state) {
    return state && state.activeRenderPolicy && state.activeRenderPolicy.constrained
      ? Number(state.activeRenderPolicy.aspectRatio)
      : NaN;
  }

  function previewOverlayForContract(state, contract) {
    if (
      !state || !state.renderProfile || state.renderProfile.type !== 'OVERLAY' ||
      !contract
    ) return '';
    var previewOption = contract.selectedOptions.filter(function (option) {
      return option.name === state.renderProfile.previewOptionName;
    })[0];
    return previewOption
      ? String(state.renderProfile.previewOverlayMap[previewOption.value] || '')
      : '';
  }

  function variantBindingMatches(binding, contract) {
    if (!binding || !contract) return false;
    var boundOptions = Array.isArray(binding.selectedOptions)
      ? binding.selectedOptions
      : [];
    if (
      String(binding.productId || '') !== String(contract.productId || '') ||
      String(binding.variantId || '') !== String(contract.variantId || '') ||
      boundOptions.length !== contract.selectedOptions.length
    ) return false;
    for (var index = 0; index < boundOptions.length; index += 1) {
      if (
        String(boundOptions[index] && boundOptions[index].name || '') !==
          String(contract.selectedOptions[index].name || '') ||
        String(boundOptions[index] && boundOptions[index].value || '') !==
          String(contract.selectedOptions[index].value || '')
      ) return false;
    }
    return true;
  }

  function previewOverlayBlocksAction(state, action) {
    if (
      !state || !state.renderProfile || state.renderProfile.type !== 'OVERLAY' ||
      state.previewOverlayUnavailable !== true
    ) return false;
    return action === 'confirm';
  }

  function recordPreviewOverlayStatus(state, status, overlayUrl) {
    if (!state) return false;
    var nextStatus = String(status || '').toUpperCase();
    if (
      nextStatus !== 'NOT_REQUIRED' && nextStatus !== 'MISSING' &&
      nextStatus !== 'LOADING' && nextStatus !== 'READY' &&
      nextStatus !== 'FAILED'
    ) return false;
    var expectedUrl = String(overlayUrl || '');
    if (
      nextStatus !== 'NOT_REQUIRED' && nextStatus !== 'MISSING' &&
      expectedUrl !== String(state.currentPreviewOverlayUrl || '')
    ) return false;
    state.previewOverlayStatus = nextStatus;
    state.previewOverlayUnavailable = Boolean(
      state.renderProfile && state.renderProfile.type === 'OVERLAY' &&
      nextStatus !== 'READY'
    );
    return true;
  }

  function syncPreviewOverlayNotice(root, state) {
    var status = String(state && state.previewOverlayStatus || '');
    var unavailable = previewOverlayBlocksAction(state, 'confirm');
    var message = status === 'LOADING'
      ? PREVIEW_OVERLAY_LOADING_MESSAGE
      : unavailable
        ? PREVIEW_OVERLAY_UNAVAILABLE_MESSAGE
        : '';
    ['page', 'editor'].forEach(function (target) {
      var selector = target === 'page'
        ? '[data-ai-page-status]'
        : '[data-ai-editor-status]';
      var node = root.querySelector(selector);
      if (!node) return;
      if (!message) {
        if (node.dataset.aiOverlayNotice === 'true') {
          setStatus(root, '', target, false);
        }
        return;
      }
      setStatus(root, message, target, status !== 'LOADING');
      node.dataset.aiOverlayNotice = 'true';
    });
  }

  function syncPreviewOverlayAvailabilityUi(root, state, rerenderCards) {
    var ready = Boolean(
      state && state.previewOverlayStatus === 'READY' &&
      state.currentPreviewOverlayUrl
    );
    var property = root.querySelector('[data-ai-preview-overlay-property]');
    if (property) {
      property.value = '';
      property.disabled = true;
    }
    var finalOverlay = root.querySelector('[data-ai-final-overlay]');
    if (finalOverlay) {
      if (ready && finalOverlay.getAttribute('src') !== state.currentPreviewOverlayUrl) {
        finalOverlay.src = state.currentPreviewOverlayUrl;
      }
      finalOverlay.hidden = true;
    }
    mountEditorBrowserOverlay(root, state);
    syncPreviewOverlayNotice(root, state);
    if (rerenderCards) {
      renderDesignGallery(root, state);
    }
    setBusy(root, Boolean(state.busy));
  }

  function loadPreviewOverlayImage(image, overlayUrl) {
    if (!image || !overlayUrl) {
      return Promise.reject(new Error('The clock-face preview layer is unavailable.'));
    }
    var cancelLoad = null;
    var loadPromise = new Promise(function (resolve, reject) {
      var finished = false;
      var verifying = false;
      var timeout = window.setTimeout(function () {
        fail();
      }, PREVIEW_OVERLAY_LOAD_TIMEOUT_MS);
      function cleanup() {
        window.clearTimeout(timeout);
        image.onload = null;
        image.onerror = null;
      }
      function fail() {
        if (finished) return;
        finished = true;
        cleanup();
        reject(new Error('The clock-face preview layer could not be loaded.'));
      }
      cancelLoad = fail;
      function verifyDecodedImage() {
        if (finished || verifying) return;
        verifying = true;
        var decodeResult;
        try {
          decodeResult = typeof image.decode === 'function'
            ? image.decode()
            : Promise.resolve();
        } catch (_decodeError) {
          fail();
          return;
        }
        Promise.resolve(decodeResult).then(function () {
          if (finished) return;
          if (!(Number(image.naturalWidth) > 0 && Number(image.naturalHeight) > 0)) {
            fail();
            return;
          }
          finished = true;
          cleanup();
          resolve(overlayUrl);
        }, fail);
      }
      image.onload = verifyDecodedImage;
      image.onerror = fail;
      // Re-assigning an identical failed URL does not reliably dispatch a new
      // load in every browser. Clear it first so retries use a fresh request.
      image.removeAttribute('src');
      image.src = overlayUrl;
      if (image.complete) {
        window.setTimeout(function () {
          if (Number(image.naturalWidth) > 0 && Number(image.naturalHeight) > 0) {
            verifyDecodedImage();
          } else {
            fail();
          }
        }, 0);
      }
    });
    loadPromise.cancel = function () {
      if (cancelLoad) cancelLoad();
    };
    return loadPromise;
  }

  function cancelPreviewOverlayLoad(state) {
    if (!state) return;
    if (state.previewOverlayAttemptCancel) {
      var cancelAttempt = state.previewOverlayAttemptCancel;
      state.previewOverlayAttemptCancel = null;
      cancelAttempt();
    }
    state.previewOverlayLoadSequence = Number(state.previewOverlayLoadSequence || 0) + 1;
    if (state.previewOverlayRetryTimer) {
      window.clearTimeout(state.previewOverlayRetryTimer);
      state.previewOverlayRetryTimer = null;
    }
    if (state.previewOverlayRetryResolve) {
      var resolveRetry = state.previewOverlayRetryResolve;
      state.previewOverlayRetryResolve = null;
      resolveRetry(false);
    }
    state.previewOverlayLoadPromise = null;
  }

  function beginPreviewOverlayLoad(root, state, image, overlayUrl) {
    if (state.previewOverlayAttemptCancel) {
      var cancelPreviousAttempt = state.previewOverlayAttemptCancel;
      state.previewOverlayAttemptCancel = null;
      cancelPreviousAttempt();
    }
    if (state.previewOverlayRetryTimer) {
      window.clearTimeout(state.previewOverlayRetryTimer);
      state.previewOverlayRetryTimer = null;
    }
    if (state.previewOverlayRetryResolve) {
      var resolvePreviousRetry = state.previewOverlayRetryResolve;
      state.previewOverlayRetryResolve = null;
      resolvePreviousRetry(false);
    }
    state.previewOverlayLoadSequence = Number(state.previewOverlayLoadSequence || 0) + 1;
    var loadSequence = state.previewOverlayLoadSequence;
    image.removeAttribute('src');
    image.hidden = true;
    recordPreviewOverlayStatus(state, 'LOADING', overlayUrl);
    // Clear every card immediately. This prevents the previous variant's
    // clock face remaining visible while the next one is still loading.
    syncPreviewOverlayAvailabilityUi(root, state, true);

    function loadAttempt(attemptIndex) {
      if (
        loadSequence !== state.previewOverlayLoadSequence ||
        overlayUrl !== state.currentPreviewOverlayUrl
      ) return Promise.resolve(false);
      var attemptPromise = loadPreviewOverlayImage(image, overlayUrl);
      var attemptCancel = typeof attemptPromise.cancel === 'function'
        ? attemptPromise.cancel
        : null;
      state.previewOverlayAttemptCancel = attemptCancel;
      return attemptPromise.then(function () {
        if (state.previewOverlayAttemptCancel === attemptCancel) {
          state.previewOverlayAttemptCancel = null;
        }
        return true;
      }).catch(function (error) {
        if (state.previewOverlayAttemptCancel === attemptCancel) {
          state.previewOverlayAttemptCancel = null;
        }
        if (
          loadSequence !== state.previewOverlayLoadSequence ||
          overlayUrl !== state.currentPreviewOverlayUrl
        ) return false;
        if (attemptIndex >= PREVIEW_OVERLAY_RETRY_DELAYS_MS.length) throw error;
        return new Promise(function (resolve) {
          state.previewOverlayRetryResolve = resolve;
          state.previewOverlayRetryTimer = window.setTimeout(function () {
            state.previewOverlayRetryTimer = null;
            state.previewOverlayRetryResolve = null;
            resolve(loadAttempt(attemptIndex + 1));
          }, PREVIEW_OVERLAY_RETRY_DELAYS_MS[attemptIndex]);
        });
      });
    }

    state.previewOverlayLoadPromise = loadAttempt(0).then(function (loaded) {
      if (
        loaded !== true || loadSequence !== state.previewOverlayLoadSequence ||
        overlayUrl !== state.currentPreviewOverlayUrl
      ) return false;
      recordPreviewOverlayStatus(state, 'READY', overlayUrl);
      state.previewOverlayLoadPromise = null;
      // Completion can occur after a caller that did not request a render.
      // Always rebuild cards so every surface receives the verified layer.
      syncPreviewOverlayAvailabilityUi(root, state, true);
      return true;
    }).catch(function (error) {
      if (
        loadSequence !== state.previewOverlayLoadSequence ||
        overlayUrl !== state.currentPreviewOverlayUrl
      ) return false;
      recordPreviewOverlayStatus(state, 'FAILED', overlayUrl);
      state.previewOverlayLoadPromise = null;
      state.previewOverlayAttemptCancel = null;
      state.previewOverlayRetryTimer = null;
      state.previewOverlayRetryResolve = null;
      syncPreviewOverlayAvailabilityUi(root, state, true);
      console.warn('GiftInOne AI preview overlay:', error.message);
      return false;
    });
    return state.previewOverlayLoadPromise;
  }

  function appendBrowserOverlay(container, overlayUrl, state) {
    if (!container || !overlayUrl) return null;
    container.classList.add('giftinone-ai-browser-preview-stack');
    var overlay = document.createElement('img');
    overlay.className = 'giftinone-ai-browser-overlay';
    overlay.alt = '';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.hidden = true;
    overlay.loading = 'eager';
    overlay.decoding = 'async';
    overlay.referrerPolicy = 'no-referrer';
    overlay.addEventListener('load', function () { overlay.hidden = false; }, { once: true });
    overlay.addEventListener('error', function () {
      overlay.hidden = true;
      if (
        !state || !state.root || container.isConnected === false ||
        state.previewOverlayStatus !== 'READY' ||
        state.currentPreviewOverlayUrl !== overlayUrl
      ) return;
      var finalOverlay = state.root.querySelector('[data-ai-final-overlay]');
      if (finalOverlay) {
        beginPreviewOverlayLoad(state.root, state, finalOverlay, overlayUrl);
      } else {
        recordPreviewOverlayStatus(state, 'FAILED', overlayUrl);
        syncPreviewOverlayAvailabilityUi(state.root, state, true);
      }
    }, { once: true });
    overlay.src = overlayUrl;
    container.appendChild(overlay);
    return overlay;
  }

  function mountEditorBrowserOverlay(root, state) {
    var cropBox = root.querySelector('.cropper-crop-box');
    if (!cropBox) return;
    var overlay = cropBox.querySelector('[data-ai-editor-browser-overlay]');
    if (!overlay) {
      overlay = document.createElement('img');
      overlay.dataset.aiEditorBrowserOverlay = '';
      overlay.className = 'giftinone-ai-editor-browser-overlay';
      overlay.alt = '';
      overlay.setAttribute('aria-hidden', 'true');
      overlay.referrerPolicy = 'no-referrer';
      cropBox.appendChild(overlay);
    }
    if (
      state.currentPreviewOverlayUrl &&
      state.previewOverlayStatus === 'READY'
    ) {
      overlay.src = state.currentPreviewOverlayUrl;
      overlay.hidden = false;
    } else {
      overlay.removeAttribute('src');
      overlay.hidden = true;
    }
  }

  function syncBrowserPreviewOverlay(
    root,
    state,
    productForm,
    rerenderCards,
    retryFailedOverlay
  ) {
    var contract;
    try {
      contract = selectedVariantContract(root, state, productForm);
    } catch (_error) {
      cancelPreviewOverlayLoad(state);
      state.currentVariantContract = null;
      state.currentPreviewOverlayUrl = '';
      recordPreviewOverlayStatus(
        state,
        state.renderProfile.type === 'OVERLAY' ? 'MISSING' : 'NOT_REQUIRED',
        ''
      );
      var invalidFinalOverlay = root.querySelector('[data-ai-final-overlay]');
      if (invalidFinalOverlay) {
        invalidFinalOverlay.removeAttribute('src');
        invalidFinalOverlay.hidden = true;
      }
      syncPreviewOverlayAvailabilityUi(root, state, rerenderCards);
      return false;
    }
    if (state.renderProfile.type !== 'OVERLAY') {
      var removedOverlay = Boolean(state.currentPreviewOverlayUrl);
      cancelPreviewOverlayLoad(state);
      state.currentVariantContract = contract;
      state.currentPreviewOverlayUrl = '';
      recordPreviewOverlayStatus(state, 'NOT_REQUIRED', '');
      var plainFinalOverlay = root.querySelector('[data-ai-final-overlay]');
      if (plainFinalOverlay) {
        plainFinalOverlay.removeAttribute('src');
        plainFinalOverlay.hidden = true;
      }
      syncPreviewOverlayAvailabilityUi(root, state, removedOverlay && rerenderCards);
      return true;
    }
    var nextUrl = previewOverlayForContract(state, contract);
    if (!nextUrl) {
      cancelPreviewOverlayLoad(state);
      state.currentVariantContract = contract;
      state.currentPreviewOverlayUrl = '';
      recordPreviewOverlayStatus(state, 'MISSING', '');
      var missingFinalOverlay = root.querySelector('[data-ai-final-overlay]');
      if (missingFinalOverlay) {
        missingFinalOverlay.removeAttribute('src');
        missingFinalOverlay.hidden = true;
      }
      syncPreviewOverlayAvailabilityUi(root, state, rerenderCards);
      return false;
    }
    var changed = nextUrl !== state.currentPreviewOverlayUrl;
    state.currentVariantContract = contract;
    state.currentPreviewOverlayUrl = nextUrl;
    var finalOverlay = root.querySelector('[data-ai-final-overlay]');
    if (changed) {
      if (!finalOverlay) {
        recordPreviewOverlayStatus(state, 'FAILED', nextUrl);
        syncPreviewOverlayAvailabilityUi(root, state, rerenderCards);
        return false;
      }
      beginPreviewOverlayLoad(root, state, finalOverlay, nextUrl);
      return false;
    }
    if (
      retryFailedOverlay === true && state.previewOverlayStatus === 'FAILED' &&
      !state.previewOverlayLoadPromise && finalOverlay
    ) {
      beginPreviewOverlayLoad(root, state, finalOverlay, nextUrl);
      return false;
    }
    syncPreviewOverlayAvailabilityUi(root, state, rerenderCards);
    return state.previewOverlayStatus === 'READY';
  }

  function ensureBrowserPreviewOverlay(root, state, productForm, expectedContract) {
    var ready = syncBrowserPreviewOverlay(root, state, productForm, true, true);
    if (state.renderProfile.type !== 'OVERLAY') return Promise.resolve(true);
    if (
      expectedContract &&
      !variantBindingMatches(state.currentVariantContract, expectedContract)
    ) return Promise.resolve(false);
    if (ready) return Promise.resolve(true);

    // A deliberate confirmation/add-to-cart attempt may retry a URL whose
    // complete automatic retry cycle has already failed.
    if (
      state.previewOverlayStatus === 'FAILED' &&
      !state.previewOverlayLoadPromise && state.currentPreviewOverlayUrl
    ) {
      var finalOverlay = root.querySelector('[data-ai-final-overlay]');
      if (finalOverlay) {
        beginPreviewOverlayLoad(
          root,
          state,
          finalOverlay,
          state.currentPreviewOverlayUrl
        );
      }
    }
    var expectedUrl = String(state.currentPreviewOverlayUrl || '');
    var loadPromise = state.previewOverlayLoadPromise;
    if (!loadPromise || !expectedUrl) return Promise.resolve(false);
    return Promise.resolve(loadPromise).then(function (loaded) {
      return Boolean(
        loaded && state.previewOverlayStatus === 'READY' &&
        state.currentPreviewOverlayUrl === expectedUrl &&
        (!expectedContract ||
          variantBindingMatches(state.currentVariantContract, expectedContract))
      );
    });
  }

  function syncVariantAdjustmentUi(root, state) {
    if (!root || !state || root.dataset.aiState !== 'ready') return;
    var add = root.querySelector('[data-ai-add]');
    var message = root.querySelector('[data-ai-final-message]');
    var stockBlocked = Boolean(state.variantUnavailable && !state.cartSubmitting && !state.cartUnknown);
    var wasStockBlocked = add && add.getAttribute('data-ai-stock-blocked') === 'true';
    if (add && stockBlocked) {
      if (!wasStockBlocked || add.textContent !== add.dataset.aiStockLabel) {
        add.dataset.aiStockPreviousLabel = add.textContent;
      }
      add.dataset.aiStockLabel = variantAvailabilityLabel(state);
    } else if (add && wasStockBlocked) {
      if (!state.cartSubmitting && !state.cartUnknown && add.textContent === add.dataset.aiStockLabel) {
        add.textContent = add.dataset.aiStockPreviousLabel || 'Add to cart';
      }
      delete add.dataset.aiStockPreviousLabel;
      delete add.dataset.aiStockLabel;
    }
    markStockBlocked(add, stockBlocked);
    if (stockBlocked) {
      if (add) {
        add.textContent = variantAvailabilityLabel(state);
        add.disabled = true;
      }
    } else if (state.variantNeedsReadjustment && !state.cartSubmitting && !state.cartUnknown) {
      if (add) {
        add.textContent = 'Adjust design for this shape';
        add.disabled = Boolean(
          state.busy || state.cartSubmitting || state.cartUnknown ||
          state.cartChecking || state.variantUnavailable ||
          previewOverlayBlocksAction(state, 'cart') || state.selectedDesignInCart
        );
      }
      if (message) {
        message.textContent =
          'This option uses a different image shape. Review the crop before adding it to your cart.';
      }
    } else if (add && !state.cartSubmitting && !state.cartUnknown &&
        !state.variantNeedsReadjustment && add.textContent === 'Adjust design for this shape') {
      add.textContent = 'Add to cart';
      if (message && message.textContent ===
          'This option uses a different image shape. Review the crop before adding it to your cart.') {
        message.textContent = '';
      }
    }
  }

  function syncVariantRenderPolicy(root, state, contract) {
    if (!contract) {
      state.variantNeedsReadjustment = false;
      return freeformRenderPolicy('NO_VARIANT');
    }
    var previous = state.activeRenderPolicy;
    var next = effectiveRenderPolicyForContract(state, contract);
    state.activeRenderPolicy = cloneRenderPolicy(next);

    if (state.cropper && !sameRenderShape(previous, next)) {
      try {
        state.cropper.setAspectRatio(next.constrained ? next.aspectRatio : NaN);
        if (!fitEditorCropToImage(
          state.cropper,
          next.constrained ? next.aspectRatio : NaN
        )) throw new Error('The image could not be fitted to the selected shape.');
        var activeDraft = updateWorkingDraftFromEditor(root, state, true, true);
        if (!activeDraft) {
          throw new Error('The adjusted crop could not be prepared safely.');
        }
        activeDraft.renderPolicy = cloneRenderPolicy(next);
        activeDraft.variantBinding = contract;
        setStatus(
          root,
          'The selected option uses a different shape. Review the crop, then choose “Add this design to cart”.',
          'editor'
        );
      } catch (_aspectUpdateError) {
        state.editorInvalid = true;
      }
    }

    var selected = readyDesignByCid(state, state.selectedDesignCid);
    state.variantNeedsReadjustment = Boolean(
      selected && selected.renderPolicy &&
      !sameRenderShape(selected.renderPolicy, next)
    );
    syncVariantAdjustmentUi(root, state);
    return next;
  }

  function normalizedApiStyle(style) {
    var styleId = stylePresentationText(style && style.styleId, 96);
    if (!/^[A-Za-z0-9][A-Za-z0-9._-]{1,95}$/.test(styleId)) return null;
    var apiLabel = stylePresentationText(style && style.label, 80);
    if (!apiLabel) return null;
    var shortDescription = stylePresentationText(
      style.shortDescription || style.description,
      160
    );
    return {
      styleId: styleId,
      // DynamoDB STYLE#CURRENT.label, returned by GET /ai/styles, is the
      // runtime name authority. Do not reinterpret a valid current label here.
      label: apiLabel,
      shortDescription: shortDescription,
      description: shortDescription,
      thumbnailUrl: normalizedStyleThumbnailUrl(style.thumbnailUrl),
      bestFor: '',
      badge: '',
      sortOrder: Number.isFinite(Number(style.sortOrder))
        ? Number(style.sortOrder)
        : 0
    };
  }

  function readStylePresentation(root) {
    var script = root.querySelector('[data-ai-style-presentation]');
    var fallback = { configured: false, styles: [] };
    if (!script) return fallback;
    try {
      var payload = JSON.parse(script.textContent || '{}');
      if (
        !payload || payload.configured !== true ||
        !Array.isArray(payload.styles)
      ) return fallback;
      var seen = Object.create(null);
      var styles = [];
      var invalidPresentation = false;
      payload.styles.forEach(function (style) {
        var styleId = stylePresentationText(style && style.styleId, 96);
        if (
          !/^[A-Za-z0-9][A-Za-z0-9._-]{1,95}$/.test(styleId) ||
          seen[styleId]
        ) {
          invalidPresentation = true;
          return;
        }
        seen[styleId] = true;
        styles.push({
          styleId: styleId,
          label: stylePresentationText(style.label, 80),
          shortDescription: stylePresentationText(style.shortDescription, 160),
          bestFor: stylePresentationText(style.bestFor, 80),
          badge: stylePresentationText(style.badge, 24),
          thumbnailUrl: normalizedStyleThumbnailUrl(style.thumbnailUrl)
        });
      });
      if (invalidPresentation) return fallback;
      return { configured: true, styles: styles };
    } catch (_error) {
      return fallback;
    }
  }

  // Metaobjects control membership and card content. The effective set is
  // intersected with the protected API; DynamoDB owns the runtime name/order.
  function mergeStylePresentation(root, apiStyles) {
    var normalizedApi = [];
    var apiById = Object.create(null);
    (Array.isArray(apiStyles) ? apiStyles : []).forEach(function (style) {
      var normalized = normalizedApiStyle(style);
      if (!normalized || apiById[normalized.styleId]) return;
      normalizedApi.push(normalized);
      apiById[normalized.styleId] = normalized;
    });
    var presentation = readStylePresentation(root);
    if (!presentation.configured) return [];
    var effective = [];
    presentation.styles.forEach(function (card) {
      var authorized = apiById[card.styleId];
      if (!authorized) return;
      var description = card.shortDescription || authorized.shortDescription;
      effective.push({
        styleId: authorized.styleId,
        // The public name and stable order come from the authorized DynamoDB
        // configuration. Shopify supplies only card copy and imagery.
        label: authorized.label,
        shortDescription: description,
        description: description,
        thumbnailUrl: card.thumbnailUrl || authorized.thumbnailUrl,
        bestFor: card.bestFor,
        badge: card.badge,
        sortOrder: authorized.sortOrder
      });
    });
    effective.sort(function (left, right) {
      return left.sortOrder - right.sortOrder ||
        left.label.localeCompare(right.label) ||
        left.styleId.localeCompare(right.styleId);
    });
    return effective;
  }

  function renderStyleChoices(root, styleSelect, styles) {
    var aiState = root._giftInOneAiState;
    var restoredDesign = aiState && readyDesignByCid(aiState, aiState.selectedDesignCid);
    var preferredStyleId = String(styleSelect.value || restoredDesign && restoredDesign.styleId || '');
    var cards = root.querySelector('[data-ai-style-cards]');
    var toggle = root.querySelector('[data-ai-style-toggle]');
    var summary = root.querySelector('[data-ai-style-summary]');
    var summaryTitle = root.querySelector('[data-ai-style-summary-title]');
    var summaryDescription = root.querySelector('[data-ai-style-summary-description]');
    var summaryBestFor = root.querySelector('[data-ai-style-summary-best-for]');
    var stylesById = {};
    styleSelect.textContent = '';
    styles.forEach(function (style) {
      stylesById[style.styleId] = style;
      var option = document.createElement('option');
      option.value = style.styleId;
      option.textContent = style.label || style.styleId;
      styleSelect.appendChild(option);
    });
    if (!stylesById[preferredStyleId] && restoredDesign && stylesById[restoredDesign.styleId]) {
      preferredStyleId = restoredDesign.styleId;
    }
    if (!cards) {
      if (stylesById[preferredStyleId]) styleSelect.value = preferredStyleId;
      styleSelect.hidden = false;
      return;
    }
    cards.textContent = '';
    styleSelect.hidden = true;
    cards.hidden = false;
    cards.setAttribute('role', 'radiogroup');
    cards.dataset.aiExpanded = 'false';
    var cardButtons = [];

    function styleCardIsVisible(card) {
      if (!card) return false;
      try {
        return window.getComputedStyle(card).display !== 'none';
      } catch (_error) {
        return true;
      }
    }

    function syncCompactStyleList() {
      var expanded = cards.dataset.aiExpanded === 'true';
      if (!toggle) return;
      toggle.hidden = styles.length <= 4;
      toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      toggle.textContent = expanded ? 'Show fewer styles' : 'View all styles';
    }

    function updateStyleSummary(style) {
      if (!summary || !style) return;
      var description = style.shortDescription || style.description || '';
      summaryTitle.textContent = style.label || style.styleId;
      summaryDescription.textContent = description;
      summaryDescription.hidden = !description;
      summaryBestFor.textContent = style.bestFor ? 'Best for: ' + style.bestFor : '';
      summaryBestFor.hidden = !style.bestFor;
      summary.hidden = false;
    }

    function selectCard(button, shouldFocus) {
      if (!styleCardIsVisible(button)) {
        cards.dataset.aiExpanded = 'true';
        syncCompactStyleList();
      }
      styleSelect.value = button.dataset.aiStyleChoice;
      cardButtons.forEach(function (card) {
        var selected = card === button;
        card.setAttribute('aria-checked', selected ? 'true' : 'false');
        card.tabIndex = selected ? 0 : -1;
      });
      updateStyleSummary(stylesById[button.dataset.aiStyleChoice]);
      if (shouldFocus) button.focus();
      var state = root._giftInOneAiState;
      if (state && photoSourceIsReady(state)) revealSetupAction(root, state, false);
    }

    styles.forEach(function (style, index) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'giftinone-ai-style-card';
      button.dataset.aiStyleChoice = style.styleId;
      if (index >= 4) button.dataset.aiCompactExtra = 'true';
      button.setAttribute('role', 'radio');
      button.setAttribute('aria-checked', index === 0 ? 'true' : 'false');
      button.setAttribute('aria-label', [
        style.label || style.styleId,
        style.shortDescription || style.description,
        style.bestFor ? 'Best for: ' + style.bestFor : ''
      ].filter(Boolean).join('. '));
      button.tabIndex = index === 0 ? 0 : -1;

      var media = document.createElement('span');
      media.className = 'giftinone-ai-style-card__media';
      var placeholder = document.createElement('span');
      placeholder.className = 'giftinone-ai-style-card__placeholder';
      placeholder.textContent = 'Example image coming soon';
      media.appendChild(placeholder);
      var thumbnailUrl = normalizedStyleThumbnailUrl(style.thumbnailUrl);
      if (thumbnailUrl) {
        var image = document.createElement('img');
        image.src = thumbnailUrl;
        image.alt = '';
        image.loading = 'lazy';
        image.decoding = 'async';
        image.referrerPolicy = 'no-referrer';
        image.width = 600;
        image.height = 400;
        try { image.fetchPriority = 'low'; } catch (_error) {}
        placeholder.hidden = true;
        image.addEventListener('error', function () {
          image.remove();
          placeholder.hidden = false;
        }, { once: true });
        media.appendChild(image);
      }
      if (style.badge) {
        var badge = document.createElement('span');
        badge.className = 'giftinone-ai-style-card__badge';
        badge.textContent = style.badge;
        media.appendChild(badge);
      }
      button.appendChild(media);

      var copy = document.createElement('span');
      copy.className = 'giftinone-ai-style-card__copy';
      var title = document.createElement('strong');
      title.className = 'giftinone-ai-style-card__title';
      title.textContent = style.label || style.styleId;
      copy.appendChild(title);
      button.appendChild(copy);
      var check = document.createElement('span');
      check.className = 'giftinone-ai-style-card__check';
      check.setAttribute('aria-hidden', 'true');
      check.textContent = '\u2713';
      button.appendChild(check);

      button.addEventListener('click', function () {
        selectCard(button, false);
      });
      button.addEventListener('keydown', function (event) {
        var supported = [
          'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'
        ];
        if (supported.indexOf(event.key) === -1) return;
        event.preventDefault();
        var visibleButtons = cardButtons.filter(styleCardIsVisible);
        var current = visibleButtons.indexOf(button);
        if (current < 0 || !visibleButtons.length) return;
        var last = visibleButtons.length - 1;
        var next = current;
        var columns = 2;
        try {
          var cardLeftPositions = [];
          visibleButtons.forEach(function (card) {
            var left = Math.round(card.getBoundingClientRect().left);
            if (cardLeftPositions.indexOf(left) === -1) cardLeftPositions.push(left);
          });
          var measuredColumns = cardLeftPositions.length;
          if (measuredColumns <= 1) {
            var columnDefinition = window.getComputedStyle(cards).gridTemplateColumns;
            measuredColumns = String(columnDefinition || '')
              .trim()
              .split(/\s+/)
              .filter(Boolean).length;
          }
          if (measuredColumns > 0) columns = measuredColumns;
        } catch (_error) {
          // Two columns is the established fallback for non-layout test/browser contexts.
        }
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = last;
        if (event.key === 'ArrowLeft') next = Math.max(0, current - 1);
        if (event.key === 'ArrowRight') next = Math.min(last, current + 1);
        if (event.key === 'ArrowUp' && current - columns >= 0) {
          next = current - columns;
        }
        if (event.key === 'ArrowDown' && current + columns <= last) {
          next = current + columns;
        }
        selectCard(visibleButtons[next], true);
      });
      cardButtons.push(button);
      cards.appendChild(button);
    });
    if (toggle) {
      toggle.onclick = function () {
        cards.dataset.aiExpanded = cards.dataset.aiExpanded === 'true'
          ? 'false'
          : 'true';
        syncCompactStyleList();
      };
    }
    syncCompactStyleList();
    var preferredCard = cardButtons.filter(function (button) {
      return button.dataset.aiStyleChoice === preferredStyleId;
    })[0] || cardButtons[0];
    if (preferredCard) selectCard(preferredCard, false);
  }

  function uploadOriginal(upload, file, state, onProgress) {
    if (!upload || upload.method !== 'POST' || !upload.uploadUrl || !upload.fields) {
      throw new Error('The upload authorization is invalid.');
    }
    var formData = new FormData();
    Object.keys(upload.fields).forEach(function (name) {
      formData.append(name, upload.fields[name]);
    });
    formData.append('file', file);
    return new Promise(function (resolve, reject) {
      var xhr = new window.XMLHttpRequest();
      var settled = false;
      state.activeUpload = xhr;

      function finish(error) {
        if (settled) return;
        settled = true;
        if (state.activeUpload === xhr) state.activeUpload = null;
        if (error) reject(error);
        else resolve();
      }

      xhr.open('POST', upload.uploadUrl, true);
      xhr.withCredentials = false;
      xhr.upload.onprogress = function (event) {
        if (!event.lengthComputable || !(event.total > 0)) return;
        if (typeof onProgress === 'function') {
          onProgress(Math.max(0, Math.min(1, event.loaded / event.total)));
        }
      };
      xhr.onload = function () {
        if (xhr.status >= 200 && xhr.status < 300) {
          if (typeof onProgress === 'function') onProgress(1);
          finish();
          return;
        }
        finish(new Error('The original photo could not be uploaded.'));
      };
      xhr.onerror = function () {
        finish(new Error('The original photo could not be uploaded.'));
      };
      xhr.onabort = function () {
        var error = new Error('AI_REQUEST_CANCELLED');
        error.code = 'AI_REQUEST_CANCELLED';
        finish(error);
      };
      xhr.send(formData);
    });
  }

  function abortActiveUpload(state) {
    if (!state.activeUpload || typeof state.activeUpload.abort !== 'function') return;
    var upload = state.activeUpload;
    state.activeUpload = null;
    try { upload.abort(); } catch (_error) {}
  }

  function assertJobOperationIdentity(job, operationCid, operationStyleId) {
    if (
      !job || String(job.cid || '') !== String(operationCid || '') ||
      !operationStyleId || String(job.styleId || '') !== String(operationStyleId)
    ) {
      throw new Error('The preview job response is invalid.');
    }
    return job;
  }

  function pollJob(root, state, runNumber, finalPhase, expectedCid, expectedToken) {
    var startedAt = Date.now();
    var operationCid = String(expectedCid || state.cid || '');
    var operationToken = expectedToken || state.token;
    var operationStyleId = String(state.jobStyleId || '');
    function poll() {
      if (
        runNumber !== state.runNumber ||
        (finalPhase && (
          operationCid !== state.cid ||
          operationCid !== state.activeWorkingDraftCid ||
          operationCid !== state.finalizingDraftCid
        ))
      ) {
        return Promise.reject(new Error('AI_REQUEST_CANCELLED'));
      }
      return sessionFirstJobRequest(
        root,
        '/ai/jobs/' + encodeURIComponent(operationCid),
        'GET',
        operationToken
      ).then(function (job) {
        if (
          runNumber !== state.runNumber ||
          (finalPhase && (
            operationCid !== state.cid ||
            operationCid !== state.activeWorkingDraftCid ||
            operationCid !== state.finalizingDraftCid
          ))
        ) throw new Error('AI_REQUEST_CANCELLED');
        // CID and style are immutable job identity in both preview and final
        // phases. Never let a response for another style be promoted under
        // the active draft's label or recipe.
        assertJobOperationIdentity(job, operationCid, operationStyleId);
        var policyMatchesCurrentVariant = Boolean(
          state.currentVariantContract &&
          variantBindingMatches(job.variantBinding, state.currentVariantContract)
        );
        var jobRenderPolicy = rememberPolicyFromJob(
          state,
          job,
          policyMatchesCurrentVariant
        );
        var activeDraft = workingDraftByCid(state, operationCid);
        if (activeDraft) activeDraft.renderPolicy = jobRenderPolicy;
        applyQuota(root, state, job);
        if (!finalPhase) {
          var usedPublicPhase = syncPreviewProgress(root, state, job);
          // Backward/forward compatibility when a response has no mapped phase.
          if (!usedPublicPhase && job.status === 'PREVIEW_RUNNING') {
            setProgressStage(
              root,
              state,
              'running',
              'Creating your preview',
              'The AI is transforming your photo.',
              35
            );
          } else if (!usedPublicPhase && job.status === 'PREVIEW_QUEUED') {
            setProgressStage(
              root,
              state,
              'queued',
              'Waiting for AI',
              'Your photo is securely queued for generation.',
              25
            );
          }
        }
        if (finalPhase && job.status === 'READY_FOR_ORDER' && job.previewFinalUrl) {
          return job;
        }
        if (!finalPhase && job.status === 'PREVIEW_READY' && job.aiPreviewUrl) {
          if (job.aiPreviewUrl !== expectedAiPreviewUrl(operationCid)) {
            throw new Error('The preview image response is invalid.');
          }
          if (!usedPublicPhase) {
            setProgressStage(
              root,
              state,
              'loading',
              'Loading your preview',
              'Generation is complete. Preparing the image editor.',
              95
            );
          }
          return job;
        }
        if (job.status === 'PREVIEW_FAILED' || job.status === 'PREVIEW_FINAL_FAILED') {
          var failure = new Error('The AI preview could not be generated.');
          failure.code = job.status;
          failure.attempt = Number(
            job.status === 'PREVIEW_FAILED'
              ? job.previewAttempt
              : job.previewFinalAttempt
          );
          failure.data = job;
          throw failure;
        }
        if (Date.now() - startedAt >= POLL_TIMEOUT_MS) {
          var timeout = new Error('The AI preview timed out.');
          timeout.code = 'AI_PREVIEW_TIMEOUT';
          throw timeout;
        }
        return delay(nextPollDelay(startedAt)).then(poll);
      });
    }
    // Keep the original fast status discovery path while the customer is
    // actively waiting: immediate first check, then 1s/2.5s polling.
    return poll();
  }

  function destroyEditor(state) {
    if (state.cropper && typeof state.cropper.destroy === 'function') {
      state.cropper.destroy();
    }
    state.cropper = null;
    state.editorRecipeSnapshot = null;
    state.editorViewSnapshot = null;
    state.editorPreviewUrl = '';
    state.editorDraftInteraction = false;
    state.editorCanonicalCid = '';
    state.editorCanonicalRevision = 0;
    state.editorInvalid = false;
    state.editorViewportWidth = 0;
    state.editorViewportHeight = 0;
    state.editorViewportRestorePending = false;
    state.editorViewportRetryCount = 0;
    state.editorViewportFailure = false;
  }

  function updateTransformSummary(root, state) {
    var summary = root.querySelector('[data-ai-transform-summary]');
    if (!summary) return;
    var transformed = Number(state.rotation || 0) !== 0 || Boolean(state.flipX);
    summary.hidden = !transformed;
    if (!transformed) {
      summary.textContent = '';
      return;
    }
    var parts = [];
    if (Number(state.rotation || 0) !== 0) parts.push('Rotated ' + state.rotation + '°');
    if (state.flipX) parts.push('Flipped');
    summary.textContent = parts.join(' · ');
  }

  function setDraftReturnState(root, state, visible) {
    var draft = root.querySelector('[data-ai-draft-return]');
    state.reviewingAlternateStyle = Boolean(visible && state.cropper);
    if (draft) draft.hidden = !state.reviewingAlternateStyle;
  }

  function beginDraftSyncSuppression(state, cid, runNumber) {
    state.suppressDraftSync = {
      cid: String(cid || ''),
      runNumber: Number(runNumber)
    };
    state.editorDraftInteraction = false;
  }

  function endDraftSyncSuppression(state, cid, runNumber) {
    var lock = state.suppressDraftSync;
    if (
      lock && lock.cid === String(cid || '') &&
      lock.runNumber === Number(runNumber)
    ) {
      state.suppressDraftSync = null;
      state.editorDraftInteraction = false;
      return true;
    }
    return false;
  }

  function syncEditorRecipeSnapshot(root, state, updateCanonicalDraft) {
    if (!state.cropper) {
      state.editorRecipeSnapshot = null;
      return null;
    }
    if (state.suppressDraftSync) return state.editorRecipeSnapshot;
    try {
      var captured = captureEditorDraft(root, state, false);
      state.editorRecipeSnapshot = captured.recipe;
      var draft = workingDraftByCid(state, state.activeWorkingDraftCid);
      if (
        updateCanonicalDraft && draft && draft.cid === state.cid &&
        state.restoringWorkingDraftCid !== draft.cid
      ) {
        draft.rotation = state.rotation;
        draft.flipX = state.flipX;
        draft.data = cloneEditorValue(captured.data);
        draft.canvas = cloneEditorValue(state.cropper.getCanvasData());
        draft.cropBox = cloneEditorValue(state.cropper.getCropBoxData());
        draft.container = cloneEditorValue(state.cropper.getContainerData());
        draft.recipe = cloneRecipe(state.editorRecipeSnapshot);
        draft.customerAdjusted = true;
        draft.autoCenteredRepair = false;
        draft.revision = Number(draft.revision || 0) + 1;
        draft.updatedAt = Date.now();
        state.editorCanonicalCid = draft.cid;
        state.editorCanonicalRevision = draft.revision;
        state.editorInvalid = false;
        scheduleDraftSave(root, state, draft);
      }
    } catch (_error) {
      state.editorRecipeSnapshot = null;
      if (updateCanonicalDraft) state.editorInvalid = true;
    }
    return state.editorRecipeSnapshot;
  }

  function captureEditorViewSnapshot(root, state) {
    if (!state.cropper) return null;
    var draft = workingDraftByCid(state, state.activeWorkingDraftCid);
    var canonicalDraft = draft && draft.cid === state.cid ? draft : null;
    var fallbackRecipe = canonicalDraft
      ? canonicalDraft.recipe
      : syncEditorRecipeSnapshot(root, state);
    state.editorViewSnapshot = {
      data: cloneEditorValue(
        canonicalDraft && canonicalDraft.data
          ? canonicalDraft.data
          : state.cropper.getData(false)
      ),
      canvas: state.cropper.getCanvasData(),
      cropBox: state.cropper.getCropBoxData(),
      container: state.cropper.getContainerData(),
      recipe: cloneRecipe(
        canonicalDraft && canonicalDraft.recipe
          ? canonicalDraft.recipe
          : fallbackRecipe
      )
    };
    return state.editorViewSnapshot;
  }

  function restoreEditorViewSnapshot(root, state) {
    var snapshot = state.editorViewSnapshot;
    if (!state.cropper || !snapshot) return false;
    var suppressionCid = String(state.cid || '');
    var suppressionRun = state.runNumber;
    beginDraftSyncSuppression(state, suppressionCid, suppressionRun);
    try {
      var canonicalDraft = workingDraftByCid(state, state.activeWorkingDraftCid);
      if (!canonicalDraft || canonicalDraft.cid !== state.cid) {
        endDraftSyncSuppression(state, suppressionCid, suppressionRun);
        return false;
      }
      restoreWorkingDraftSnapshot(root, state, canonicalDraft);
      state.editorViewSnapshot = null;
      return true;
    } catch (error) {
      endDraftSyncSuppression(state, suppressionCid, suppressionRun);
      throw error;
    }
  }

  function hydrateWorkingDraft(root, state, draft, options) {
    state.cid = draft.cid;
    state.token = draft.token;
    state.originalKey = draft.originalKey;
    state.originalUrl = draft.originalUrl;
    state.jobStyleId = draft.styleId || '';
    state.jobStyleLabel = displayStyleLabel(
      state.jobStyleId,
      draft.styleLabel,
      'AI preview'
    );
    state.rotation = Number(draft.rotation || 0);
    state.flipX = Boolean(draft.flipX);
    state.editorPreviewUrl = draft.previewUrl;
    setPhotoSourceFromDraft(root, state, draft, options);
    state.activeWorkingDraftCid = draft.cid;
    state.previewFailed = false;
    state.previewFinalFailed = draft.status === 'PREVIEW_FINAL_FAILED';
  }

  function recipeFromStoredDraftData(root, draft) {
    var data = draft.data;
    var naturalWidth = Number(draft.naturalWidth);
    var naturalHeight = Number(draft.naturalHeight);
    var rotation = normalizeQuarterRotation(data.rotate);
    var flipX = Number(data.scaleX) < 0;
    var sourceCrop = sourceCropFromTransformedCrop(
      data,
      naturalWidth,
      naturalHeight,
      rotation,
      flipX
    );
    // Mirror recipeFromEditor exactly. In particular, clamp width/height
    // against the unrounded x/y before normalizing all four values; otherwise
    // a crop touching a source edge can produce a one-pixel canvas mismatch.
    var x = clamp(sourceCrop.x / naturalWidth, 0, 1);
    var y = clamp(sourceCrop.y / naturalHeight, 0, 1);
    var width = clamp(sourceCrop.width / naturalWidth, 0, 1 - x);
    var height = clamp(sourceCrop.height / naturalHeight, 0, 1 - y);
    var crop = {
      x: normalized(x),
      y: normalized(y),
      width: normalized(width),
      height: normalized(height)
    };
    return {
      schema: 'ai-recipe/v1',
      mode: 'crop-overlay',
      crop: crop,
      rotation: rotation,
      flipX: flipX,
      flipY: false,
      fit: 'cover',
      canvas: rectangleCanvas(
        root,
        crop.width * naturalWidth,
        crop.height * naturalHeight,
        rotation
      ),
      background: { mode: 'opaque', color: '#ffffff' }
    };
  }

  function draftMatchesRenderPolicy(draft) {
    var policy = draft && draft.renderPolicy;
    if (!policy || !policy.constrained) return true;
    var expectedAspect = Number(policy.aspectRatio);
    var canvas = draft.recipe && draft.recipe.canvas || {};
    var data = draft.data || {};
    var canvasAspect = Number(canvas.width) / Number(canvas.height);
    var cropAspect = Number(data.width) / Number(data.height);
    return Boolean(
      Number.isFinite(expectedAspect) && expectedAspect > 0 &&
      Number.isFinite(canvasAspect) && canvasAspect > 0 &&
      Number.isFinite(cropAspect) && cropAspect > 0 &&
      Math.abs(canvasAspect / expectedAspect - 1) <= 0.005 &&
      Math.abs(cropAspect / expectedAspect - 1) <= 0.005
    );
  }

  function centeredInitialCropData(naturalWidth, naturalHeight, aspectRatio) {
    var sourceWidth = Number(naturalWidth);
    var sourceHeight = Number(naturalHeight);
    if (!(sourceWidth > 0) || !(sourceHeight > 0)) return null;

    var targetRatio = Number(aspectRatio);
    var x = 0;
    var y = 0;
    var width = sourceWidth;
    var height = sourceHeight;
    if (Number.isFinite(targetRatio) && targetRatio > 0) {
      if (sourceWidth / sourceHeight > targetRatio) {
        width = sourceHeight * targetRatio;
        x = (sourceWidth - width) / 2;
      } else {
        height = sourceWidth / targetRatio;
        y = (sourceHeight - height) / 2;
      }
    }
    return {
      x: x,
      y: y,
      width: width,
      height: height,
      rotate: 0,
      scaleX: 1,
      scaleY: 1
    };
  }

  function initialCropAspectRatioForDraft(draft) {
    var policy = draft && draft.renderPolicy;
    if (policy && policy.constrained) {
      var policyRatio = Number(policy.aspectRatio);
      if (Number.isFinite(policyRatio) && policyRatio > 0) return policyRatio;
    }
    return NaN;
  }

  function draftHasCustomerCropAdjustment(draft) {
    if (!draft) return false;
    if (draft.customerAdjusted === true) return true;
    if (Number(draft.serverRevision || 0) > 1) return true;
    return Boolean(
      Number(draft.revision || 0) > 1 && draft.autoCenteredRepair !== true
    );
  }

  function draftUsesCenteredInitialCrop(draft) {
    if (!draft || !draft.data || draftHasCustomerCropAdjustment(draft)) return false;
    if (
      normalizeQuarterRotation(draft.data.rotate) !== 0 ||
      Number(draft.data.scaleX === undefined ? 1 : draft.data.scaleX) < 0 ||
      Number(draft.data.scaleY === undefined ? 1 : draft.data.scaleY) !== 1
    ) return false;
    var expected = centeredInitialCropData(
      draft.naturalWidth,
      draft.naturalHeight,
      initialCropAspectRatioForDraft(draft)
    );
    if (!expected) return false;
    return ['x', 'y', 'width', 'height'].every(function (field) {
      return Number.isFinite(Number(draft.data[field])) &&
        Math.abs(Number(draft.data[field]) - Number(expected[field])) <=
          EDITOR_SOURCE_TOLERANCE_PX;
    });
  }

  function repairUnadjustedDraftCrop(root, draft) {
    if (!draft || !draft.data || draft.restoredFromSession ||
      draft.initializationComplete === true || draftHasCustomerCropAdjustment(draft)) return false;
    var rotation;
    try {
      rotation = normalizeQuarterRotation(draft.data.rotate);
    } catch (_rotationError) {
      return false;
    }
    if (
      rotation !== 0 ||
      Number(draft.data.scaleX === undefined ? 1 : draft.data.scaleX) < 0 ||
      Number(draft.data.scaleY === undefined ? 1 : draft.data.scaleY) !== 1
    ) return false;
    var expected = centeredInitialCropData(
      draft.naturalWidth,
      draft.naturalHeight,
      initialCropAspectRatioForDraft(draft)
    );
    if (!expected) return false;

    // Only repair the known initialization failure: the crop still has the
    // untouched default size, but its source-space origin was captured while
    // Cropper was reacting to an unstable dialog layout. A resized/zoomed crop
    // is customer work and is never recentered by this compatibility guard.
    if (
      Math.abs(Number(draft.data.width) - expected.width) >
        EDITOR_SOURCE_TOLERANCE_PX ||
      Math.abs(Number(draft.data.height) - expected.height) >
        EDITOR_SOURCE_TOLERANCE_PX
    ) return false;
    if (
      Math.abs(Number(draft.data.x) - expected.x) <=
        EDITOR_SOURCE_TOLERANCE_PX &&
      Math.abs(Number(draft.data.y) - expected.y) <=
        EDITOR_SOURCE_TOLERANCE_PX
    ) return false;

    var previousData = draft.data;
    var previousRecipe = draft.recipe;
    try {
      draft.data = cloneEditorValue(expected);
      draft.recipe = recipeFromStoredDraftData(root, draft);
      draft.rotation = 0;
      draft.flipX = false;
      draft.revision = Math.max(1, Number(draft.revision || 0)) + 1;
      draft.updatedAt = Date.now();
      draft.autoCenteredRepair = true;
      return true;
    } catch (_repairError) {
      draft.data = previousData;
      draft.recipe = previousRecipe;
      return false;
    }
  }

  function assertRestorableWorkingDraft(root, draft) {
    var previewUrl = draft && typeof draft.previewUrl === 'string'
      ? draft.previewUrl
      : '';
    var naturalWidth = Number(draft && draft.naturalWidth);
    var naturalHeight = Number(draft && draft.naturalHeight);
    var data = draft && draft.data;
    var recipe = draft && draft.recipe;
    var dataRotation = normalizeQuarterRotation(data && data.rotate);
    var dataFlipX = Number(data && data.scaleX) < 0;
    var dataScaleY = data && data.scaleY === undefined
      ? 1
      : Number(data && data.scaleY);
    var rotatedWidth = Math.abs(dataRotation) === 90
      ? naturalHeight
      : naturalWidth;
    var rotatedHeight = Math.abs(dataRotation) === 90
      ? naturalWidth
      : naturalHeight;
    var dataX = Number(data && data.x);
    var dataY = Number(data && data.y);
    var dataWidth = Number(data && data.width);
    var dataHeight = Number(data && data.height);
    var recipeCrop = recipe && recipe.crop || {};
    var recipeCanvas = recipe && recipe.canvas || {};
    if (
      !draft || !CID_PATTERN.test(String(draft.cid || '')) ||
      previewUrl !== expectedAiPreviewUrl(String(draft.cid || '')) ||
      !data || typeof data !== 'object' ||
      !recipe || typeof recipe !== 'object'
    ) throw editorDraftValidationError('draft-identity');
    if (
      !Number.isFinite(naturalWidth) || naturalWidth <= 0 ||
      !Number.isFinite(naturalHeight) || naturalHeight <= 0 ||
      !Number.isFinite(dataX) || !Number.isFinite(dataY) ||
      !Number.isFinite(dataWidth) || dataWidth <= 0 ||
      !Number.isFinite(dataHeight) || dataHeight <= 0
    ) throw editorDraftValidationError('invalid-crop');
    if (
      dataX < -0.5 || dataY < -0.5 ||
      dataX + dataWidth > rotatedWidth + 0.5 ||
      dataY + dataHeight > rotatedHeight + 0.5
    ) throw editorDraftValidationError('crop-out-of-bounds');
    if (
      dataScaleY !== 1 ||
      normalizeQuarterRotation(draft.rotation) !== dataRotation ||
      Boolean(draft.flipX) !== dataFlipX ||
      recipe.rotation !== dataRotation ||
      recipe.flipX !== dataFlipX || recipe.flipY !== false
    ) throw editorDraftValidationError('transform-mismatch');
    if (
      !Number.isFinite(recipeCrop.x) || recipeCrop.x < 0 || recipeCrop.x > 1 ||
      !Number.isFinite(recipeCrop.y) || recipeCrop.y < 0 || recipeCrop.y > 1 ||
      !Number.isFinite(recipeCrop.width) || recipeCrop.width <= 0 ||
      !Number.isFinite(recipeCrop.height) || recipeCrop.height <= 0 ||
      recipeCrop.x + recipeCrop.width > 1 + RECIPE_CROP_TOLERANCE ||
      recipeCrop.y + recipeCrop.height > 1 + RECIPE_CROP_TOLERANCE
    ) throw editorDraftValidationError('recipe-crop-invalid');
    if (
      !Number.isInteger(recipeCanvas.width) || recipeCanvas.width <= 0 ||
      !Number.isInteger(recipeCanvas.height) || recipeCanvas.height <= 0
    ) throw editorDraftValidationError('recipe-canvas-invalid');
    if (!draftMatchesRenderPolicy(draft)) {
      throw editorDraftValidationError('render-policy-mismatch');
    }
    var derivedRecipe;
    try {
      derivedRecipe = recipeFromStoredDraftData(root, draft);
    } catch (error) {
      throw editorDraftValidationError('recipe-derivation-failed', error.message);
    }
    if (!recipesEquivalent(recipe, derivedRecipe)) {
      throw editorDraftValidationError('recipe-data-mismatch');
    }
  }

  function editorLayoutDiagnostic(state, draft, error) {
    var actual = {};
    var image = {};
    var cropper = state && state.cropper;
    if (cropper) {
      try {
        actual = {
          container: cloneEditorValue(cropper.getContainerData()),
          canvas: cloneEditorValue(cropper.getCanvasData()),
          cropBox: cloneEditorValue(cropper.getCropBoxData()),
          data: cloneEditorValue(cropper.getData(false))
        };
        image = cropper.getImageData() || {};
      } catch (_diagnosticError) {}
    }
    var expected = draft && draft.data ? cloneEditorValue(draft.data)
      : centeredInitialCropData(image.naturalWidth, image.naturalHeight,
          state ? activeEditorAspectRatio(state) : NaN);
    var difference = {};
    ['x', 'y', 'width', 'height', 'rotate', 'scaleX', 'scaleY'].forEach(function (field) {
      var measured = Number(actual.data && actual.data[field]);
      var target = Number(expected && expected[field]);
      if (Number.isFinite(measured) && Number.isFinite(target)) {
        difference[field] = measured - target;
      }
    });
    return {
      reason: error ? String(error.layoutReason || error.code || 'restore-exception')
        : 'viewport-geometry-mismatch',
      message: error ? String(error.message || '') : '',
      expected: expected,
      actual: actual,
      difference: difference,
      naturalWidth: Number(draft && draft.naturalWidth || image.naturalWidth || 0),
      naturalHeight: Number(draft && draft.naturalHeight || image.naturalHeight || 0),
      displaySourceTolerance: editorSourceToleranceForCropper(cropper),
      savedSourceBoundaryTolerance: 0.5
    };
  }

  function restoreWorkingDraftViewport(cropper, draft) {
    var data = cloneEditorValue(draft.data);
    var rotation = normalizeQuarterRotation(data.rotate);
    var flipX = Number(data.scaleX) < 0;
    // Apply discrete transforms first so Cropper exposes the rotated natural
    // aspect. A tiny temporary crop box releases viewMode:1 constraints left
    // behind by the editor's default unrotated crop.
    cropper.setData({
      rotate: rotation,
      scaleX: flipX ? -1 : 1,
      scaleY: 1
    });
    try {
      if (typeof cropper.resize === 'function') cropper.resize();
    } catch (_resizeError) {}

    var container = cropper.getContainerData() || {};
    var canvas = cropper.getCanvasData() || {};
    var rotatedNaturalWidth = Number(canvas.naturalWidth);
    var rotatedNaturalHeight = Number(canvas.naturalHeight);
    if (!(rotatedNaturalWidth > 0) || !(rotatedNaturalHeight > 0)) {
      rotatedNaturalWidth = Math.abs(rotation) === 90
        ? Number(draft.naturalHeight)
        : Number(draft.naturalWidth);
      rotatedNaturalHeight = Math.abs(rotation) === 90
        ? Number(draft.naturalWidth)
        : Number(draft.naturalHeight);
    }
    var containerWidth = Number(container.width);
    var containerHeight = Number(container.height);
    if (
      containerWidth > 0 && containerHeight > 0 &&
      rotatedNaturalWidth > 0 && rotatedNaturalHeight > 0
    ) {
      var centerX = Number(container.left || 0) + containerWidth / 2;
      var centerY = Number(container.top || 0) + containerHeight / 2;
      try {
        cropper.setCropBoxData({
          left: centerX - 0.5,
          top: centerY - 0.5,
          width: 1,
          height: 1
        });
      } catch (_temporaryCropError) {}

      var scale = Math.min(
        (containerWidth * 0.85) / Number(data.width),
        (containerHeight * 0.85) / Number(data.height)
      );
      if (Number.isFinite(scale) && scale > 0) {
        var targetWidth = Number(data.width) * scale;
        var targetHeight = Number(data.height) * scale;
        var targetLeft = centerX - targetWidth / 2;
        var targetTop = centerY - targetHeight / 2;
        try {
          cropper.setCanvasData({
            left: targetLeft - Number(data.x) * scale,
            top: targetTop - Number(data.y) * scale,
            width: rotatedNaturalWidth * scale
          });
        } catch (_canvasHintError) {}
        try {
          cropper.setCropBoxData({
            left: targetLeft,
            top: targetTop,
            width: targetWidth,
            height: targetHeight
          });
        } catch (_cropBoxHintError) {}
      }
    }
    // This final source-space write is authoritative. Cropper may round its
    // responsive view, but that display rounding is never read back here.
    cropper.setData(data);
    return workingDraftViewportMatches(cropper, draft);
  }

  function editorSourceToleranceForCropper(cropper) {
    if (!cropper) return EDITOR_SOURCE_TOLERANCE_PX;
    try {
      var canvas = cropper.getCanvasData() || {};
      var scaleX = Number(canvas.width) / Number(canvas.naturalWidth);
      var scaleY = Number(canvas.height) / Number(canvas.naturalHeight);
      var displayScale = Math.min(Math.abs(scaleX), Math.abs(scaleY));
      if (!Number.isFinite(displayScale) || !(displayScale > 0)) {
        return EDITOR_SOURCE_TOLERANCE_PX;
      }
      // Cropper rounds parts of its responsive viewport in CSS pixels. A fixed
      // source-pixel tolerance becomes stricter as a large image is displayed
      // in a smaller editor (for example with DevTools open or on mobile).
      // Convert the existing visual geometry tolerance back to source pixels so
      // harmless display rounding is accepted without allowing visible drift.
      return Math.max(
        EDITOR_SOURCE_TOLERANCE_PX,
        EDITOR_GEOMETRY_TOLERANCE_PX / displayScale
      );
    } catch (_scaleError) {
      return EDITOR_SOURCE_TOLERANCE_PX;
    }
  }

  function editorViewportGeometryMatchesData(
    cropper,
    expectedData,
    requireCenteredCanvas
  ) {
    var container = cropper.getContainerData() || {};
    var cropBox = cropper.getCropBoxData() || {};
    var canvas = cropper.getCanvasData() || {};
    var containerWidth = Number(container.width);
    var containerHeight = Number(container.height);
    var cropWidth = Number(cropBox.width);
    var cropHeight = Number(cropBox.height);
    var canvasLeft = Number(canvas.left);
    var canvasTop = Number(canvas.top);
    var canvasWidth = Number(canvas.width);
    var canvasHeight = Number(canvas.height);
    var canvasNaturalWidth = Number(canvas.naturalWidth);
    var canvasNaturalHeight = Number(canvas.naturalHeight);
    if (
      !(containerWidth > 0) || !(containerHeight > 0) ||
      !(cropWidth > 0) || !(cropHeight > 0) ||
      !Number.isFinite(canvasLeft) || !Number.isFinite(canvasTop) ||
      !(canvasWidth > 0) || !(canvasHeight > 0) ||
      !(canvasNaturalWidth > 0) || !(canvasNaturalHeight > 0)
    ) return false;

    var expectedCenterX = Number(container.left || 0) + containerWidth / 2;
    var expectedCenterY = Number(container.top || 0) + containerHeight / 2;
    var cropLeft = Number(cropBox.left);
    var cropTop = Number(cropBox.top);
    var cropCenterX = cropLeft + cropWidth / 2;
    var cropCenterY = cropTop + cropHeight / 2;
    if (
      !Number.isFinite(cropLeft) || !Number.isFinite(cropTop) ||
      !Number.isFinite(cropCenterX) || !Number.isFinite(cropCenterY) ||
      Math.abs(cropCenterX - expectedCenterX) > EDITOR_CENTER_TOLERANCE_PX ||
      Math.abs(cropCenterY - expectedCenterY) > EDITOR_CENTER_TOLERANCE_PX
    ) return false;

    var scaleX = canvasWidth / canvasNaturalWidth;
    var scaleY = canvasHeight / canvasNaturalHeight;
    if (
      !Number.isFinite(scaleX) || !(scaleX > 0) ||
      !Number.isFinite(scaleY) || !(scaleY > 0) ||
      Math.abs(scaleX / scaleY - 1) > 0.002
    ) return false;
    var expectedLeft = canvasLeft + Number(expectedData.x) * scaleX;
    var expectedTop = canvasTop + Number(expectedData.y) * scaleY;
    var expectedWidth = Number(expectedData.width) * scaleX;
    var expectedHeight = Number(expectedData.height) * scaleY;
    if (
      !Number.isFinite(expectedLeft) || !Number.isFinite(expectedTop) ||
      !Number.isFinite(expectedWidth) || !Number.isFinite(expectedHeight) ||
      Math.abs(cropLeft - expectedLeft) > EDITOR_GEOMETRY_TOLERANCE_PX ||
      Math.abs(cropTop - expectedTop) > EDITOR_GEOMETRY_TOLERANCE_PX ||
      Math.abs(cropWidth - expectedWidth) > EDITOR_GEOMETRY_TOLERANCE_PX ||
      Math.abs(cropHeight - expectedHeight) > EDITOR_GEOMETRY_TOLERANCE_PX
    ) return false;
    if (
      cropLeft < canvasLeft - EDITOR_GEOMETRY_TOLERANCE_PX ||
      cropTop < canvasTop - EDITOR_GEOMETRY_TOLERANCE_PX ||
      cropLeft + cropWidth > canvasLeft + canvasWidth + EDITOR_GEOMETRY_TOLERANCE_PX ||
      cropTop + cropHeight > canvasTop + canvasHeight + EDITOR_GEOMETRY_TOLERANCE_PX
    ) return false;

    if (requireCenteredCanvas) {
      var canvasCenterX = canvasLeft + canvasWidth / 2;
      var canvasCenterY = canvasTop + canvasHeight / 2;
      if (
        Math.abs(canvasCenterX - expectedCenterX) > EDITOR_GEOMETRY_TOLERANCE_PX ||
        Math.abs(canvasCenterY - expectedCenterY) > EDITOR_GEOMETRY_TOLERANCE_PX
      ) return false;
    }
    return true;
  }

  function defaultEditorViewportMatches(cropper, aspectRatio) {
    if (!cropper) return false;
    try {
      var image = cropper.getImageData() || {};
      var expected = centeredInitialCropData(
        image.naturalWidth,
        image.naturalHeight,
        aspectRatio
      );
      var actual = cropper.getData(false) || {};
      if (!expected) return false;
      var sourceTolerance = editorSourceToleranceForCropper(cropper);
      var fields = ['x', 'y', 'width', 'height'];
      for (var index = 0; index < fields.length; index += 1) {
        var field = fields[index];
        if (
          !Number.isFinite(Number(actual[field])) ||
          Math.abs(Number(actual[field]) - Number(expected[field])) >
            sourceTolerance
        ) return false;
      }
      if (
        normalizeQuarterRotation(actual.rotate) !== 0 ||
        Number(actual.scaleX === undefined ? 1 : actual.scaleX) < 0 ||
        Number(actual.scaleY === undefined ? 1 : actual.scaleY) !== 1
      ) return false;
      return editorViewportGeometryMatchesData(cropper, expected, true);
    } catch (_defaultVerificationError) {
      return false;
    }
  }

  function workingDraftViewportMatches(cropper, draft) {
    if (!cropper || !draft || !draft.data) return false;
    try {
      var expected = draft.data;
      var actual = cropper.getData(false) || {};
      var sourceTolerance = editorSourceToleranceForCropper(cropper);
      var sourceFields = ['x', 'y', 'width', 'height'];
      for (var index = 0; index < sourceFields.length; index += 1) {
        var field = sourceFields[index];
        if (
          !Number.isFinite(Number(actual[field])) ||
          Math.abs(Number(actual[field]) - Number(expected[field])) >
            sourceTolerance
        ) return false;
      }
      if (
        normalizeQuarterRotation(actual.rotate) !==
          normalizeQuarterRotation(expected.rotate) ||
        (Number(actual.scaleX === undefined ? 1 : actual.scaleX) < 0) !==
          (Number(expected.scaleX === undefined ? 1 : expected.scaleX) < 0) ||
        Number(actual.scaleY === undefined ? 1 : actual.scaleY) !== 1
      ) return false;

      return editorViewportGeometryMatchesData(
        cropper,
        expected,
        draftUsesCenteredInitialCrop(draft)
      );
    } catch (_verificationError) {
      return false;
    }
  }

  function markEditorViewportFailure(root, state) {
    state.editorInvalid = true;
    state.editorViewportRestorePending = true;
    state.editorViewportFailure = true;
    var approve = root.querySelector('[data-ai-approve]');
    if (approve) approve.disabled = true;
    Array.prototype.forEach.call(
      root.querySelectorAll(
        '[data-ai-zoom-out], [data-ai-zoom-in], [data-ai-rotate-left], ' +
        '[data-ai-rotate-right], [data-ai-flip-x], [data-ai-reset]'
      ),
      function (control) { control.disabled = true; }
    );
    if (state.cropper && typeof state.cropper.disable === 'function') {
      state.cropper.disable();
    }
    syncEditorPrimaryAction(root, state);
    setStatus(
      root,
      'This design could not be positioned safely. Select it in My designs to reopen it.',
      'editor',
      true
    );
  }

  function clearEditorViewportFailure(root, state) {
    var recovered = Boolean(state.editorViewportFailure);
    state.editorViewportRestorePending = false;
    state.editorViewportRetryCount = 0;
    if (!recovered) return false;
    state.editorInvalid = false;
    state.editorViewportFailure = false;
    if (recovered) {
      setStatus(root, 'Design position restored safely.', 'editor');
    }
    return recovered;
  }

  function withTemporarilyEnabledCropper(state, callback) {
    var cropper = state && state.cropper;
    if (!cropper) return false;
    var restoreDisabled = Boolean(state.busy || state.editorViewportFailure);
    if (restoreDisabled && typeof cropper.enable === 'function') cropper.enable();
    try {
      return callback(cropper);
    } finally {
      if (restoreDisabled && typeof cropper.disable === 'function') cropper.disable();
    }
  }

  function scheduleEditorViewportResize(root, state, force) {
    if (!state || !state.cropper || state.editorResizeScheduled) return;
    if (state.editorInvalid && !state.editorViewportFailure) return;
    if (state.suppressDraftSync || state.editorDraftInteraction || state.busy) {
      state.editorViewportRestorePending = true;
      return;
    }
    var editor = root.querySelector('[data-ai-editor]');
    var stage = root.querySelector('.giftinone-ai-editor-frame');
    var dialog = root.querySelector('[data-ai-dialog]');
    if (!editor || editor.hidden || !stage || !dialogIsOpen(dialog)) return;
    state.editorResizeScheduled = true;
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        state.editorResizeScheduled = false;
        if (
          !state.cropper || state.suppressDraftSync || state.editorDraftInteraction ||
          state.busy || editor.hidden || !dialogIsOpen(dialog)
        ) return;
        var rect = stage.getBoundingClientRect();
        var width = Math.round(rect.width);
        var height = Math.round(rect.height);
        if (!(width > 0) || !(height > 0)) return;
        if (
          !force && !state.editorViewportRestorePending &&
          width === state.editorViewportWidth &&
          height === state.editorViewportHeight
        ) return;
        var draft = workingDraftByCid(state, state.activeWorkingDraftCid);
        if (!draft || draft.cid !== state.cid || !draft.data || !draft.recipe) return;
        var resizeCid = draft.cid;
        var resizeRun = state.runNumber;
        var viewportMatched = false;
        beginDraftSyncSuppression(state, resizeCid, resizeRun);
        try {
          viewportMatched = withTemporarilyEnabledCropper(state, function (cropper) {
            if (typeof cropper.resize === 'function') cropper.resize();
            // Reconstruct the responsive view from canonical source-space data.
            // Cropper's container geometry is never promoted back into the recipe.
            var matched = restoreWorkingDraftViewport(cropper, draft);
            if (!matched) matched = restoreWorkingDraftViewport(cropper, draft);
            return matched;
          });
          if (viewportMatched) {
            state.rotation = normalizeQuarterRotation(draft.rotation);
            state.flipX = Boolean(draft.flipX);
            state.editorRecipeSnapshot = cloneRecipe(draft.recipe);
            state.editorCanonicalCid = draft.cid;
            state.editorCanonicalRevision = Number(draft.revision || 1);
            state.editorViewportWidth = width;
            state.editorViewportHeight = height;
            var recovered = clearEditorViewportFailure(root, state);
            updateTransformSummary(root, state);
            if (recovered) setBusy(root, false);
          }
        } catch (_resizeError) {
          // A later ResizeObserver delivery or dialog reopen retries safely.
          viewportMatched = false;
        } finally {
          endDraftSyncSuppression(state, resizeCid, resizeRun);
        }
        if (!viewportMatched) {
          markEditorViewportFailure(root, state);
          state.editorViewportRetryCount = Number(state.editorViewportRetryCount || 0) + 1;
          if (state.editorViewportRetryCount <= 2) {
            window.requestAnimationFrame(function () {
              scheduleEditorViewportResize(root, state, true);
            });
          } else {
            destroyEditor(state);
            setDraftReturnState(root, state, false);
            setDialogView(root, 'setup');
            renderWorkingDrafts(root, state);
            setBusy(root, false);
            setDesignLibraryOpen(root, state, true, true);
            setStatus(
              root,
              'The editor layout was not stable. Select the saved design to try opening it again.',
              'setup',
              true
            );
          }
          return;
        }
        state.editorViewportRestorePending = false;
        state.editorViewportRetryCount = 0;
      });
    });
  }

  function restoreWorkingDraftSnapshot(root, state, draft) {
    if (!state.cropper) {
      throw new Error('This AI preview can no longer be restored.');
    }
    if (repairUnadjustedDraftCrop(root, draft)) {
      scheduleDraftSave(root, state, draft);
    }
    assertRestorableWorkingDraft(root, draft);
    var cropper = state.cropper;
    var image = cropper.getImageData();
    if (
      Number(image.naturalWidth) !== Number(draft.naturalWidth) ||
      Number(image.naturalHeight) !== Number(draft.naturalHeight)
    ) throw new Error('The preview dimensions changed. Please generate this style again.');

    state.rotation = normalizeQuarterRotation(draft.rotation);
    state.flipX = Boolean(draft.flipX);
    var restoreWhileBusy = Boolean(state.busy);
    if (restoreWhileBusy && typeof cropper.enable === 'function') {
      cropper.enable();
    }
    var viewportMatched = false;
    try {
      viewportMatched = restoreWorkingDraftViewport(cropper, draft);
    } finally {
      if (restoreWhileBusy && typeof cropper.disable === 'function') {
        cropper.disable();
      }
    }
    if (!viewportMatched) {
      throw new Error('This design could not be positioned safely.');
    }
    updateTransformSummary(root, state);
    // Source-space data and the recipe are canonical. Responsive Cropper
    // reconstruction is only a view of that data and must never gate opening,
    // replace the recipe, or accumulate display rounding across comparisons.
    state.editorRecipeSnapshot = cloneRecipe(draft.recipe);
    state.editorCanonicalCid = draft.cid;
    state.editorCanonicalRevision = Number(draft.revision || 1);
    state.editorInvalid = false;
    renderWorkingDrafts(root, state);
    return true;
  }

  function settleWorkingDraftEditorViewport(root, state, runNumber, expectedCid, callback) {
    resetDialogViewport(root);
    var frameCount = 0;
    var stableFrames = 0;
    var restoreAttempts = 0;
    var lastWidth = 0;
    var lastHeight = 0;
    var finished = false;

    function finish(value, reason) {
      if (finished) return;
      finished = true;
      if (typeof callback === 'function') callback(Boolean(value), String(reason || ''));
    }

    function nextFrame() {
      window.requestAnimationFrame(function () {
        if (finished) return;
        frameCount += 1;
        if (
          runNumber !== state.runNumber || expectedCid !== state.cid ||
          expectedCid !== state.activeWorkingDraftCid
        ) {
          finish(false, 'cancelled');
          return;
        }
        var dialog = root.querySelector('[data-ai-dialog]');
        var editor = root.querySelector('[data-ai-editor]');
        if (
          (dialog && !dialogIsOpen(dialog)) ||
          (editor && editor.hidden)
        ) {
          finish(false, 'deferred');
          return;
        }
        if (!state.cropper) {
          markEditorViewportFailure(root, state);
          finish(false, 'cropper-missing');
          return;
        }

        var stage = root.querySelector('.giftinone-ai-editor-frame');
        var rect = stage && stage.getBoundingClientRect
          ? stage.getBoundingClientRect()
          : null;
        var width = Number(rect && rect.width);
        var height = Number(rect && rect.height);
        if (width > 0 && height > 0) {
          if (
            Math.abs(width - lastWidth) <= 0.5 &&
            Math.abs(height - lastHeight) <= 0.5
          ) stableFrames += 1;
          else stableFrames = 1;
          lastWidth = width;
          lastHeight = height;
        } else {
          stableFrames = 0;
        }

        if (stableFrames < EDITOR_LAYOUT_STABLE_FRAMES) {
          if (frameCount < EDITOR_LAYOUT_MAX_FRAMES) nextFrame();
          else {
            markEditorViewportFailure(root, state);
            finish(false, 'frame-limit');
          }
          return;
        }

        var draft = workingDraftByCid(state, expectedCid);
        var matched = false;
        var restoreError = null;
        try {
          var repaired = repairUnadjustedDraftCrop(root, draft);
          if (repaired) scheduleDraftSave(root, state, draft);
          assertRestorableWorkingDraft(root, draft);
          matched = withTemporarilyEnabledCropper(state, function (cropper) {
            if (typeof cropper.resize === 'function') cropper.resize();
            return restoreWorkingDraftViewport(cropper, draft);
          });
        } catch (error) {
          restoreError = error;
          matched = false;
        }
        if (!matched) state.editorLayoutDiagnostic = editorLayoutDiagnostic(state, draft, restoreError);
        restoreAttempts += 1;
        if (!matched && restoreAttempts < 2 && frameCount < EDITOR_LAYOUT_MAX_FRAMES) {
          stableFrames = 0;
          nextFrame();
          return;
        }
        if (!matched) {
          markEditorViewportFailure(root, state);
          finish(false, 'verification-failed');
          return;
        }

        clearEditorViewportFailure(root, state);
        state.editorLayoutDiagnostic = null;
        state.editorViewportWidth = Math.round(width);
        state.editorViewportHeight = Math.round(height);
        resetDialogViewport(root);
        var list = root.querySelector('[data-ai-working-draft-list="editor"]');
        var active = list
          ? list.querySelector('[data-ai-working-draft-select][aria-pressed="true"]')
          : null;
        var scroller = list ? list.parentNode : null;
        var card = active && active.closest
          ? active.closest('.giftinone-ai-working-draft-card')
          : null;
        if (scroller) {
          scroller.scrollLeft = Math.max(0, Number(card && card.offsetLeft) - 8 || 0);
        }
        finish(true);
      });
    }

    nextFrame();
  }

  function settleInitialEditorViewport(root, state, runNumber, expectedCid, callback) {
    resetDialogViewport(root);
    var frameCount = 0;
    var stableFrames = 0;
    var layoutFailures = 0;
    var fitApplied = false;
    var verifyFrames = 0;
    var lastWidth = 0;
    var lastHeight = 0;
    var finished = false;

    function finish(value, reason) {
      if (finished) return;
      finished = true;
      if (typeof callback === 'function') callback(Boolean(value), String(reason || ''));
    }

    function failLayout(reason) {
      markEditorViewportFailure(root, state);
      finish(false, reason || 'layout-failed');
    }

    function scheduleNextFrame(resetStability) {
      if (finished) return false;
      if (frameCount >= EDITOR_LAYOUT_MAX_FRAMES) {
        failLayout('frame-limit');
        return false;
      }
      if (resetStability) stableFrames = 0;
      nextFrame();
      return true;
    }

    function nextFrame() {
      window.requestAnimationFrame(function () {
        if (finished) return;
        frameCount += 1;
        if (
          runNumber !== state.runNumber || expectedCid !== state.cid ||
          expectedCid !== state.activeWorkingDraftCid
        ) {
          finish(false, 'cancelled');
          return;
        }
        var dialog = root.querySelector('[data-ai-dialog]');
        var editor = root.querySelector('[data-ai-editor]');
        if (
          (dialog && !dialogIsOpen(dialog)) ||
          (editor && editor.hidden)
        ) {
          finish(false, 'deferred');
          return;
        }
        if (!state.cropper) {
          failLayout('cropper-missing');
          return;
        }
        var stage = root.querySelector('.giftinone-ai-editor-frame');
        var rect = stage && stage.getBoundingClientRect
          ? stage.getBoundingClientRect()
          : null;
        var width = Number(rect && rect.width);
        var height = Number(rect && rect.height);
        var sizeChanged = false;
        if (width > 0 && height > 0) {
          if (
            Math.abs(width - lastWidth) <= 0.5 &&
            Math.abs(height - lastHeight) <= 0.5
          ) stableFrames += 1;
          else {
            sizeChanged = lastWidth > 0 && lastHeight > 0;
            stableFrames = 1;
          }
          lastWidth = width;
          lastHeight = height;
        } else {
          stableFrames = 0;
          fitApplied = false;
          verifyFrames = 0;
        }

        if (fitApplied && sizeChanged) {
          fitApplied = false;
          verifyFrames = 0;
        }

        if (stableFrames < EDITOR_LAYOUT_STABLE_FRAMES) {
          scheduleNextFrame(false);
          return;
        }

        if (!fitApplied) {
          var applied = false;
          try {
            applied = withTemporarilyEnabledCropper(state, function (cropper) {
              if (typeof cropper.resize === 'function') cropper.resize();
              return fitInitialEditorCropToImage(
                cropper,
                activeEditorAspectRatio(state)
              );
            });
          } catch (_initialFitError) {
            applied = false;
          }
          if (!applied) {
            layoutFailures += 1;
            if (layoutFailures < EDITOR_LAYOUT_FAILURE_LIMIT) {
              scheduleNextFrame(true);
              return;
            }
            failLayout('fit-failed');
            return;
          }
          fitApplied = true;
          verifyFrames = 0;
          scheduleNextFrame(false);
          return;
        }

        verifyFrames += 1;
        if (
          verifyFrames < EDITOR_LAYOUT_VERIFY_FRAMES
        ) {
          scheduleNextFrame(false);
          return;
        }

        var matched = false;
        try {
          matched = withTemporarilyEnabledCropper(state, function (cropper) {
            return defaultEditorViewportMatches(
              cropper,
              activeEditorAspectRatio(state)
            );
          });
        } catch (_initialVerifyError) {
          matched = false;
        }
        if (!matched) {
          layoutFailures += 1;
        }
        if (!matched && layoutFailures < EDITOR_LAYOUT_FAILURE_LIMIT) {
          fitApplied = false;
          verifyFrames = 0;
          scheduleNextFrame(true);
          return;
        }
        if (!matched) {
          failLayout('verification-failed');
          return;
        }

        clearEditorViewportFailure(root, state);
        state.editorViewportWidth = Math.round(width);
        state.editorViewportHeight = Math.round(height);
        resetDialogViewport(root);
        finish(true);
      });
    }

    nextFrame();
  }

  function switchToWorkingDraft(root, state, cid, skipFlush, restorePolicy) {
    var target = workingDraftByCid(state, cid);
    if (!target || state.busy || state.cartSubmitting || state.cartUnknown) {
      return Promise.resolve(false);
    }
    if (state.cartCustomizationCids[target.cid]) {
      setStatus(
        root,
        'This exact design is already in your cart. Remove it from the cart before editing it.',
        'setup',
        true
      );
      return Promise.resolve(false);
    }
    repairUnadjustedDraftCrop(root, target);
    // Do not persist an automatic repair until the editor has opened and the
    // reconstructed viewport has passed verification. A failed open must not
    // mutate a previously finished server draft in the background.
    var currentDraft = workingDraftByCid(state, state.activeWorkingDraftCid);
    if (
      !skipFlush && currentDraft && !currentDraft.persistError &&
      currentDraft.cid !== target.cid
    ) {
      setBusy(root, true);
      return flushPendingDraftSave(root, state, currentDraft, false).then(function () {
        setBusy(root, false);
        return switchToWorkingDraft(root, state, cid, true, restorePolicy);
      }).catch(function (error) {
        setBusy(root, false);
        setStatus(root, error.message || 'The current design could not be saved securely.', 'editor', true);
        return false;
      });
    }
    if (state.editorOpening && state.activeWorkingDraftCid === target.cid) {
      return Promise.resolve(false);
    }
    if (
      state.cropper && state.cid === target.cid && state.editorInvalid &&
      state.restoringWorkingDraftCid !== target.cid
    ) {
      // A failed responsive restore is never reused. Keep the canonical draft
      // card, discard only the unsafe Cropper view, then open it from scratch.
      destroyEditor(state);
    }
    if (
      state.cropper && state.cid === target.cid &&
      state.restoringWorkingDraftCid !== target.cid
    ) {
      if (!target.persistError) updateWorkingDraftFromEditor(root, state, true);
      returnToCurrentPreview(root, state);
      setStatus(
        root,
        target.persistError
          ? 'Secure save is paused. Make another adjustment to retry, or delete this preview.'
          : (target.styleLabel || 'AI preview') + ' is already open.',
        'editor',
        target.persistError
      );
      return Promise.resolve(true);
    }
    if (
      state.cropper && !state.restoringWorkingDraftCid &&
      !(currentDraft && currentDraft.persistError)
    ) {
      updateWorkingDraftFromEditor(root, state, false);
    }
    if (state.editorOpening && typeof state.abortEditorOpen === 'function') {
      state.abortEditorOpen();
    }
    state.runNumber += 1;
    var runNumber = state.runNumber;
    var expectedCid = target.cid;
    var currentContractAtOpen = state.currentVariantContract;
    var syncCurrentPolicyAfterRestore = Boolean(
      !restorePolicy && target.renderPolicy && currentContractAtOpen &&
      !variantBindingMatches(target.variantBinding, currentContractAtOpen)
    );
    // Canonical data must first be reconstructed under the exact shape it was
    // saved with. If the customer changed Variant, the successful callback
    // below then refits that verified view to the current product shape.
    state.activeRenderPolicy = cloneRenderPolicy(
      restorePolicy || target.renderPolicy ||
      effectiveRenderPolicyForContract(state, currentContractAtOpen)
    );
    state.restoringWorkingDraftCid = expectedCid;
    beginDraftSyncSuppression(state, expectedCid, runNumber);
    destroyEditor(state);
    hydrateWorkingDraft(root, state, target);
    state.editorOpening = true;
    setDialogView(root, 'editor');
    resetDialogViewport(root);
    openAiDialog(root, state);
    renderWorkingDrafts(root, state);
    setBusy(root, true);
    setStatus(root, 'Opening ' + (target.styleLabel || 'AI preview') + '…', 'editor');
    return openEditor(root, state, target.previewUrl, runNumber).then(function () {
      if (
        runNumber !== state.runNumber || expectedCid !== state.cid ||
        expectedCid !== state.activeWorkingDraftCid
      ) {
        if (state.restoringWorkingDraftCid === expectedCid) {
          state.restoringWorkingDraftCid = '';
          endDraftSyncSuppression(state, expectedCid, runNumber);
          state.editorOpening = false;
          setBusy(root, false);
        }
        return false;
      }
      hydrateWorkingDraft(root, state, target);
      return new Promise(function (resolve) {
        settleWorkingDraftEditorViewport(
          root,
          state,
          runNumber,
          expectedCid,
          function (settled, settleReason) {
            if (!settled) {
              if (state.restoringWorkingDraftCid === expectedCid) {
                state.restoringWorkingDraftCid = '';
                endDraftSyncSuppression(state, expectedCid, runNumber);
                state.editorOpening = false;
                if (settleReason === 'deferred') {
                  setBusy(root, false);
                  updateLauncher(root, state);
                  resolve(false);
                  return;
                }
                destroyEditor(state);
                setDialogView(root, 'setup');
                renderWorkingDrafts(root, state);
                setBusy(root, false);
                setDesignLibraryOpen(root, state, true, true);
                setStatus(
                  root,
                  'The editor layout was not stable. Select the saved design to try opening it again.',
                  'setup',
                  true
                );
              }
              resolve(false);
              return;
            }
            try {
              restoreWorkingDraftSnapshot(root, state, target);
            } catch (error) {
              state.restoringWorkingDraftCid = '';
              endDraftSyncSuppression(state, expectedCid, runNumber);
              state.editorOpening = false;
              destroyEditor(state);
              setDialogView(root, 'setup');
              renderWorkingDrafts(root, state);
              setBusy(root, false);
              setDesignLibraryOpen(root, state, true, true);
              setStatus(
                root,
                error.message || 'This design could not be positioned safely.',
                'setup',
                true
              );
              resolve(false);
              return;
            }
            state.restoringWorkingDraftCid = '';
            endDraftSyncSuppression(state, expectedCid, runNumber);
            if (syncCurrentPolicyAfterRestore) {
              var currentPolicyApplied = false;
              try {
                if (!variantBindingMatches(state.currentVariantContract, currentContractAtOpen)) {
                  throw new Error('Product options changed while opening this design.');
                }
                currentPolicyApplied = withTemporarilyEnabledCropper(
                  state,
                  function () {
                    syncVariantRenderPolicy(root, state, currentContractAtOpen);
                    var synchronizedDraft = workingDraftByCid(state, expectedCid);
                    if (!synchronizedDraft || state.editorInvalid) return false;
                    synchronizedDraft.renderPolicy = cloneRenderPolicy(
                      state.activeRenderPolicy
                    );
                    synchronizedDraft.variantBinding = currentContractAtOpen;
                    assertRestorableWorkingDraft(root, synchronizedDraft);
                    return true;
                  }
                );
              } catch (_variantRestoreError) {
                currentPolicyApplied = false;
              }
              if (!currentPolicyApplied) {
                state.editorOpening = false;
                destroyEditor(state);
                setDialogView(root, 'setup');
                renderWorkingDrafts(root, state);
                setBusy(root, false);
                setDesignLibraryOpen(root, state, true, true);
                setStatus(
                  root,
                  'This design could not be adjusted to the selected product shape. Choose it again after confirming the product option.',
                  'setup',
                  true
                );
                resolve(false);
                return;
              }
              target.renderPolicy = cloneRenderPolicy(state.activeRenderPolicy);
              target.variantBinding = currentContractAtOpen;
            }
            state.editorOpening = false;
            state.editorRecipeSnapshot = cloneRecipe(target.recipe);
            setBusy(root, false);
            if (target.persistError) {
              setAutoSaveStatus(
                root,
                'Secure save paused. Make another adjustment to retry, or delete this preview.',
                true
              );
            } else if (
              Number(target.persistedLocalRevision || 0) <
              Number(target.revision || 0)
            ) {
              scheduleDraftSave(root, state, target);
            } else {
              setAutoSaveStatus(root, 'All changes saved', false);
            }
            setStatus(
              root,
              target.status === 'PREVIEW_FINAL_FAILED'
                ? (target.styleLabel || 'AI preview') +
                  ' restored. Your adjustments are safe; choose “Add this design to cart” to retry.'
                : (target.styleLabel || 'AI preview') +
                  ' restored. Adjust it or use it when ready.',
              'editor',
              target.status === 'PREVIEW_FINAL_FAILED'
            );
            var heading = root.querySelector('[data-ai-editor-heading]');
            if (heading) focusWithoutScrolling(heading);
            resolve(true);
          }
        );
      });
    }).catch(function (error) {
      if (state.restoringWorkingDraftCid === expectedCid) {
        state.restoringWorkingDraftCid = '';
        endDraftSyncSuppression(state, expectedCid, runNumber);
      }
      if (
        runNumber !== state.runNumber
      ) return false;
      state.editorOpening = false;
      if (error && error.code === 'AI_EDITOR_DEFERRED') {
        setBusy(root, false);
        return false;
      }
      destroyEditor(state);
      state.cid = '';
      state.token = '';
      state.originalKey = '';
      state.originalUrl = '';
      state.jobStyleId = '';
      state.jobStyleLabel = '';
      state.activeWorkingDraftCid = '';
      renderWorkingDrafts(root, state);
      setBusy(root, false);
      setDialogView(root, 'setup');
      setStatus(root, error && error.message ? error.message : 'This preview could not be opened.', 'setup', true);
      return false;
    });
  }

  function restoreFallbackWorkingDraft(root, state, error) {
    var fallbackCid = String(state.generationFallbackDraftCid || '');
    var fallback = workingDraftByCid(state, fallbackCid);
    if (!fallback) return false;
    state.token = '';
    state.generationFallbackDraftCid = '';
    setBusy(root, false);
    switchToWorkingDraft(root, state, fallback.cid).then(function (restored) {
      if (!restored) return;
      setStatus(
        root,
        'The new style could not be created. Your previous securely saved preview was restored.',
        'editor',
        true
      );
    });
    if (error && error.message) console.warn('GiftInOne AI draft generation error:', error.message);
    return true;
  }

  function recoverableEditorLoadError(error) {
    return Boolean(error && [
      'AI_EDITOR_IMAGE_LOAD_TIMEOUT',
      'AI_EDITOR_IMAGE_LOAD_FAILED',
      'AI_EDITOR_DECODE_TIMEOUT',
      'AI_EDITOR_DECODE_FAILED',
      'AI_EDITOR_CROPPER_IMAGE_LOAD_FAILED',
      'AI_EDITOR_CROPPER_READY_TIMEOUT'
    ].indexOf(String(error.code || '')) >= 0);
  }

  function editorDeferredError() {
    var deferred = new Error('AI_EDITOR_DEFERRED');
    deferred.code = 'AI_EDITOR_DEFERRED';
    return deferred;
  }

  function openEditor(root, state, imageUrl, runNumber) {
    var cancelled = false;
    var currentAttemptAbort = null;
    var outerAbort = function () {
      if (cancelled) return;
      cancelled = true;
      var abort = currentAttemptAbort;
      currentAttemptAbort = null;
      if (abort) abort();
    };
    var abortLease = {
      set: function (abort) {
        currentAttemptAbort = abort;
        if (cancelled && abort) abort();
      },
      clear: function (abort) {
        if (currentAttemptAbort === abort) currentAttemptAbort = null;
      }
    };
    state.abortEditorOpen = outerAbort;

    function assertEditorOpen() {
      if (
        cancelled || runNumber !== state.runNumber ||
        !dialogIsOpen(root.querySelector('[data-ai-dialog]'))
      ) throw editorDeferredError();
    }

    return ensureCropperAssets(root).then(function () {
      assertEditorOpen();
      var attempt = 0;
      function openAttempt() {
        assertEditorOpen();
        return openEditorWithCropper(
          root,
          state,
          imageUrl,
          runNumber,
          abortLease
        ).catch(
          function (error) {
            if (
              attempt >= EDITOR_AUTO_RETRY_LIMIT ||
              runNumber !== state.runNumber ||
              !recoverableEditorLoadError(error)
            ) throw error;
            attempt += 1;
            console.warn('GiftInOne AI editor load retry:', {
              code: String(error.code || 'AI_EDITOR_LOAD_FAILED'),
              cid: String(state.cid || ''),
              attempt: attempt
            });
            // Rebuild only the browser editor around the same generated URL.
            // This path never calls /preview and cannot consume another quota.
            return delay(250).then(function () {
              assertEditorOpen();
              return openAttempt();
            });
          }
        );
      }
      return openAttempt();
    }).catch(function (error) {
      if (
        cancelled || runNumber !== state.runNumber ||
        !dialogIsOpen(root.querySelector('[data-ai-dialog]'))
      ) throw editorDeferredError();
      throw error;
    }).finally(function () {
      currentAttemptAbort = null;
      if (state.abortEditorOpen === outerAbort) state.abortEditorOpen = null;
    });
  }

  function openEditorWithCropper(root, state, imageUrl, runNumber, abortLease) {
    return new Promise(function (resolve, reject) {
      if (typeof window.Cropper !== 'function') {
        reject(new Error('The image editor is unavailable.'));
        return;
      }
      destroyEditor(state);
      state.rotation = 0;
      state.flipX = false;
      state.editorDraftInteraction = false;
      updateTransformSummary(root, state);

      var editor = root.querySelector('[data-ai-editor]');
      var image = root.querySelector('[data-ai-editor-image]');
      setDialogView(root, 'editor');
      // Choose/Generate and Adjust share the same scrolling body. Always clear
      // the previous view's position before the body becomes overflow:hidden.
      resetDialogViewport(root);
      openAiDialog(root, state);
      var settled = false;
      var imageHandled = false;
      var dialog = root.querySelector('[data-ai-dialog]');
      var timer = null;
      var cropperImage = null;
      var cropperImageErrorHandler = null;
      var attemptAbort = null;

      function clearPhaseTimer() {
        if (timer) window.clearTimeout(timer);
        timer = null;
      }

      function cleanup() {
        clearPhaseTimer();
        if (
          cropperImage && cropperImageErrorHandler &&
          typeof cropperImage.removeEventListener === 'function'
        ) cropperImage.removeEventListener('error', cropperImageErrorHandler);
        cropperImage = null;
        cropperImageErrorHandler = null;
        image.onload = null;
        image.onerror = null;
        if (abortLease && attemptAbort) abortLease.clear(attemptAbort);
      }

      function fail(error) {
        if (settled) return;
        settled = true;
        cleanup();
        destroyEditor(state);
        image.removeAttribute('src');
        reject(error || new Error('The AI preview image could not be loaded.'));
      }

      function startPhaseTimer(milliseconds, code, message) {
        clearPhaseTimer();
        timer = window.setTimeout(function () {
          var timeoutError = new Error(message);
          timeoutError.code = code;
          fail(timeoutError);
        }, milliseconds);
      }

      startPhaseTimer(
        EDITOR_IMAGE_LOAD_TIMEOUT_MS,
        'AI_EDITOR_IMAGE_LOAD_TIMEOUT',
        'The AI preview image took too long to load.'
      );

      attemptAbort = function () { fail(editorDeferredError()); };
      if (abortLease) abortLease.set(attemptAbort);

      function initializeCropper() {
        if (settled) return;
        if (runNumber !== state.runNumber) {
          fail();
          return;
        }
        editor.hidden = false;
        // Cropper 必须在 dialog 进入 top layer 且完成布局后初始化，
        // 否则隐藏容器会让画布尺寸为 0。
        window.requestAnimationFrame(function () {
          window.requestAnimationFrame(function () {
            if (
              settled || runNumber !== state.runNumber ||
              !dialogIsOpen(dialog)
            ) {
              fail();
              return;
            }
            try {
              startPhaseTimer(
                EDITOR_CROPPER_READY_TIMEOUT_MS,
                'AI_EDITOR_CROPPER_READY_TIMEOUT',
                'The image editor took too long to prepare the preview.'
              );
              state.cropper = new window.Cropper(image, {
                viewMode: 1,
                aspectRatio: activeEditorAspectRatio(state),
                dragMode: 'move',
                // Use the full image as the initial FREEFORM crop. Constrained
                // products are explicitly fitted to the largest target-ratio
                // rectangle that stays completely inside the image below.
                autoCropArea: 1,
                background: false,
                responsive: true,
                restore: true,
                // static.giftinone.com intentionally serves display-only AI
                // previews without ACAO. This editor reads geometry only; all
                // pixel rendering happens in Lambda. Keep Cropper from issuing
                // anonymous-CORS clones or EXIF XHRs that cannot succeed.
                checkCrossOrigin: false,
                checkOrientation: false,
                movable: true,
                zoomable: true,
                zoomOnTouch: true,
                zoomOnWheel: true,
                wheelZoomRatio: 0.05,
                cropBoxMovable: true,
                cropBoxResizable: true,
                toggleDragModeOnDblclick: false,
                // Cropper.js applies CSS transforms to the low-resolution AI
                // preview, so rotation/flip are visible in the main crop frame.
                // recipeFromEditor maps the transformed crop back to the source
                // image before sending rotation/flip to the backend.
                rotatable: true,
                scalable: true,
                cropstart: function () {
                  if (!state.suppressDraftSync && !state.busy) {
                    state.editorDraftInteraction = true;
                  }
                },
                crop: function () {
                  if (state.cropper) {
                    syncEditorRecipeSnapshot(
                      root,
                      state,
                      state.editorDraftInteraction
                    );
                  }
                },
                cropend: function () {
                  if (state.cropper && state.editorDraftInteraction) {
                    syncEditorRecipeSnapshot(root, state, true);
                  }
                  state.editorDraftInteraction = false;
                },
                zoom: function (event) {
                  if (
                    state.suppressDraftSync || state.busy ||
                    !event || !event.detail || !event.detail.originalEvent
                  ) return;
                  state.editorDraftInteraction = true;
                  window.requestAnimationFrame(function () {
                    if (state.cropper && state.editorDraftInteraction) {
                      syncEditorRecipeSnapshot(root, state, true);
                    }
                    state.editorDraftInteraction = false;
                  });
                },
                ready: function () {
                  if (settled) return;
                  // Cropper has emitted ready; layout settlement is no longer
                  // part of the network/readiness timeout budget.
                  clearPhaseTimer();
                  window.requestAnimationFrame(function () {
                    if (settled) return;
                    try {
                      if (!fitInitialEditorCropToImage(
                        state.cropper,
                        activeEditorAspectRatio(state)
                      )) {
                        throw new Error('The image could not be fitted safely inside the editor.');
                      }
                      mountEditorBrowserOverlay(root, state);
                      resetDialogViewport(root);
                      syncEditorRecipeSnapshot(root, state);
                      settled = true;
                      cleanup();
                      resolve();
                    } catch (error) {
                      fail(error);
                    }
                  });
                }
              });
              cropperImage = state.cropper && state.cropper.image;
              if (
                cropperImage &&
                typeof cropperImage.addEventListener === 'function'
              ) {
                cropperImageErrorHandler = function () {
                  var cropperLoadError = new Error(
                    'The image editor could not reload the generated preview.'
                  );
                  cropperLoadError.code = 'AI_EDITOR_CROPPER_IMAGE_LOAD_FAILED';
                  fail(cropperLoadError);
                };
                cropperImage.addEventListener(
                  'error',
                  cropperImageErrorHandler,
                  { once: true }
                );
                if (
                  cropperImage.complete &&
                  Number(cropperImage.naturalWidth) <= 0
                ) window.setTimeout(cropperImageErrorHandler, 0);
              }
            } catch (error) {
              fail(error);
            }
          });
        });
      }

      function handleImageLoad() {
        if (settled || imageHandled) return;
        imageHandled = true;
        if (runNumber !== state.runNumber) {
          fail();
          return;
        }
        startPhaseTimer(
          EDITOR_DECODE_TIMEOUT_MS,
          'AI_EDITOR_DECODE_TIMEOUT',
          'The AI preview image took too long to decode.'
        );
        var decodePromise;
        try {
          decodePromise = typeof image.decode === 'function'
            ? image.decode()
            : Promise.resolve();
        } catch (decodeCallError) {
          decodePromise = Promise.reject(decodeCallError);
        }
        Promise.resolve(decodePromise).then(function () {
          if (settled) return;
          if (
            Number(image.naturalWidth) <= 0 ||
            Number(image.naturalHeight) <= 0
          ) {
            var decodeError = new Error('The AI preview image could not be decoded.');
            decodeError.code = 'AI_EDITOR_DECODE_FAILED';
            fail(decodeError);
            return;
          }
          clearPhaseTimer();
          initializeCropper();
        }).catch(function () {
          if (settled) return;
          // Safari can reject decode() for a fully loaded displayable image.
          // Positive natural dimensions are the safe compatibility signal.
          if (
            Number(image.naturalWidth) > 0 &&
            Number(image.naturalHeight) > 0
          ) {
            clearPhaseTimer();
            initializeCropper();
            return;
          }
          var decodeError = new Error('The AI preview image could not be decoded.');
          decodeError.code = 'AI_EDITOR_DECODE_FAILED';
          fail(decodeError);
        });
      }
      image.onload = handleImageLoad;
      image.onerror = function () {
        var loadError = new Error('The AI preview image could not be loaded.');
        loadError.code = 'AI_EDITOR_IMAGE_LOAD_FAILED';
        fail(loadError);
      };
      image.removeAttribute('crossorigin');
      image.removeAttribute('src');
      image.decoding = 'async';
      image.loading = 'eager';
      if ('fetchPriority' in image) image.fetchPriority = 'high';
      image.referrerPolicy = 'no-referrer';
      image.src = imageUrl;
      if (image.complete && Number(image.naturalWidth) > 0) {
        window.setTimeout(handleImageLoad, 0);
      }
    });
  }

  function clamp(value, minimum, maximum) {
    return Math.min(maximum, Math.max(minimum, value));
  }

  function normalized(value) {
    return Math.round(value * 100000000) / 100000000;
  }

  function normalizeQuarterRotation(value) {
    var rotation = Math.round(Number(value) || 0) % 360;
    if (rotation > 180) rotation -= 360;
    if (rotation <= -180) rotation += 360;
    if (rotation === -180) rotation = 180;
    if (rotation !== -90 && rotation !== 0 && rotation !== 90 && rotation !== 180) {
      throw new Error('The image rotation is invalid. Please reset and try again.');
    }
    return rotation;
  }

  function largestContainedCropBox(canvas, aspectRatio) {
    var left = Number(canvas && canvas.left);
    var top = Number(canvas && canvas.top);
    var width = Number(canvas && canvas.width);
    var height = Number(canvas && canvas.height);
    if (
      !Number.isFinite(left) || !Number.isFinite(top) ||
      !(width > 0) || !(height > 0)
    ) return null;

    var targetRatio = Number(aspectRatio);
    if (!(targetRatio > 0) || !Number.isFinite(targetRatio)) {
      return { left: left, top: top, width: width, height: height };
    }

    var boxWidth = width;
    var boxHeight = height;
    if (width / height > targetRatio) {
      boxWidth = height * targetRatio;
    } else {
      boxHeight = width / targetRatio;
    }
    return {
      left: left + (width - boxWidth) / 2,
      top: top + (height - boxHeight) / 2,
      width: boxWidth,
      height: boxHeight
    };
  }

  function fitEditorCropToImage(cropper, aspectRatio) {
    if (!cropper) return false;
    var container = cropper.getContainerData();
    var currentCanvas = cropper.getCanvasData();
    var containerWidth = Number(container && container.width);
    var containerHeight = Number(container && container.height);
    var naturalWidth = Number(currentCanvas && currentCanvas.naturalWidth);
    var naturalHeight = Number(currentCanvas && currentCanvas.naturalHeight);
    if (
      !(containerWidth > 0) || !(containerHeight > 0) ||
      !(naturalWidth > 0) || !(naturalHeight > 0)
    ) return false;

    var containScale = Math.min(
      containerWidth / naturalWidth,
      containerHeight / naturalHeight
    );
    var fitWidth = naturalWidth * containScale;
    var fitHeight = naturalHeight * containScale;
    var fitLeft = Number(container.left || 0) + (containerWidth - fitWidth) / 2;
    var fitTop = Number(container.top || 0) + (containerHeight - fitHeight) / 2;

    // Cropper viewMode 1 correctly prevents the crop box from leaving the
    // image. Temporarily shrink it so the canvas itself can first be fitted
    // with contain semantics, then expand it to the largest safe crop.
    cropper.setCropBoxData({
      left: fitLeft + fitWidth / 2,
      top: fitTop + fitHeight / 2,
      width: 1,
      height: 1
    });
    cropper.setCanvasData({ left: fitLeft, top: fitTop, width: fitWidth });
    var fittedCanvas = cropper.getCanvasData();
    var cropBox = largestContainedCropBox(fittedCanvas, aspectRatio);
    if (!cropBox) return false;
    cropper.setCropBoxData(cropBox);
    cropper.setDragMode('move');
    return true;
  }

  function fitInitialEditorCropToImage(cropper, aspectRatio) {
    if (!fitEditorCropToImage(cropper, aspectRatio)) return false;
    var image = cropper.getImageData() || {};
    var centeredSourceCrop = centeredInitialCropData(
      image.naturalWidth,
      image.naturalHeight,
      aspectRatio
    );
    if (!centeredSourceCrop) return false;
    // Make the untouched source-space crop explicit. The general fit helper is
    // also used after rotate/reset and must not overwrite those transforms.
    cropper.setData(centeredSourceCrop);
    return true;
  }

  function rotateAndRefitEditor(root, state, delta) {
    var cropper = state.cropper;
    if (!cropper) return false;
    var nextRotation = normalizeQuarterRotation(state.rotation + delta);
    cropper.rotateTo(nextRotation);
    if (!fitEditorCropToImage(cropper, activeEditorAspectRatio(state))) {
      cropper.rotateTo(state.rotation);
      return false;
    }
    state.rotation = nextRotation;
    updateTransformSummary(root, state);
    syncEditorRecipeSnapshot(root, state, true);
    setStatus(
      root,
      'Image rotated and fitted to the editor. You can still drag, zoom, or resize it.',
      'editor'
    );
    return true;
  }

  // Cropper.js getData() reports x/y/width/height in the canvas after its
  // rotate/scale transform. The production renderer instead extracts from the
  // untransformed source and then performs flop -> rotate. For quarter turns,
  // the inverse of Cropper's R * S matrix maps that displayed rectangle back
  // to one axis-aligned source rectangle without resampling any image pixels.
  function sourceCropFromTransformedCrop(
    crop,
    naturalWidth,
    naturalHeight,
    rotation,
    flipX
  ) {
    var sourceWidth = Number(naturalWidth);
    var sourceHeight = Number(naturalHeight);
    var angle = normalizeQuarterRotation(rotation);
    var displayWidth = Math.abs(angle) === 90 ? sourceHeight : sourceWidth;
    var displayHeight = Math.abs(angle) === 90 ? sourceWidth : sourceHeight;
    var left = Number(crop.x);
    var top = Number(crop.y);
    var right = left + Number(crop.width);
    var bottom = top + Number(crop.height);
    if (
      !(sourceWidth > 0) || !(sourceHeight > 0) ||
      !Number.isFinite(left) || !Number.isFinite(top) ||
      !Number.isFinite(right) || !Number.isFinite(bottom) ||
      right <= left || bottom <= top
    ) {
      throw new Error('The crop selection is invalid.');
    }

    var sourceXs = [];
    var sourceYs = [];
    [left, right].forEach(function (displayX) {
      [top, bottom].forEach(function (displayY) {
        var dx = displayX - displayWidth / 2;
        var dy = displayY - displayHeight / 2;
        var unrotatedX;
        var unrotatedY;
        if (angle === 90) {
          unrotatedX = dy;
          unrotatedY = -dx;
        } else if (angle === -90) {
          unrotatedX = -dy;
          unrotatedY = dx;
        } else if (angle === 180) {
          unrotatedX = -dx;
          unrotatedY = -dy;
        } else {
          unrotatedX = dx;
          unrotatedY = dy;
        }
        // Canvas/Cropper apply scale first and rotation second. A horizontal
        // flip is its own inverse, so it is applied after inverse rotation.
        if (flipX) unrotatedX = -unrotatedX;
        sourceXs.push(unrotatedX + sourceWidth / 2);
        sourceYs.push(unrotatedY + sourceHeight / 2);
      });
    });

    var sourceLeft = Math.min.apply(Math, sourceXs);
    var sourceTop = Math.min.apply(Math, sourceYs);
    var sourceRight = Math.max.apply(Math, sourceXs);
    var sourceBottom = Math.max.apply(Math, sourceYs);
    return {
      x: sourceLeft,
      y: sourceTop,
      width: sourceRight - sourceLeft,
      height: sourceBottom - sourceTop
    };
  }

  function rectangleCanvas(root, cropWidth, cropHeight, rotation) {
    var outputLongEdge = Number(root.dataset.outputLongEdge || 6000);
    if (!Number.isInteger(outputLongEdge) || outputLongEdge < 64) {
      throw new Error('The production canvas configuration is invalid.');
    }
    var width = Number(cropWidth);
    var height = Number(cropHeight);
    if (Math.abs(Math.abs(Number(rotation) || 0) - 90) < 0.001) {
      var swap = width;
      width = height;
      height = swap;
    }
    if (!(width > 0) || !(height > 0)) {
      throw new Error('The crop selection is invalid.');
    }
    var aspect = width / height;
    return aspect >= 1
      ? {
          width: outputLongEdge,
          height: Math.max(1, Math.round(outputLongEdge / aspect))
        }
      : {
          width: Math.max(1, Math.round(outputLongEdge * aspect)),
          height: outputLongEdge
        };
  }

  function editorDraftValidationError(reason, message) {
    var error = new Error(message || 'This AI preview can no longer be restored.');
    error.code = 'AI_EDITOR_DRAFT_INVALID';
    error.layoutReason = reason;
    return error;
  }

  // Capture one source-space snapshot. Display rounding may only correct a
  // tiny edge overshoot; it must never become an out-of-bounds saved draft.
  // The recipe and its production canvas are derived from this SAME data.
  function captureEditorDraft(root, state, initialCentered) {
    if (!state.cropper) throw new Error('Please finish editing your preview.');
    var image = state.cropper.getImageData();
    var naturalWidth = Number(image.naturalWidth);
    var naturalHeight = Number(image.naturalHeight);
    var transformedCrop = initialCentered
      ? centeredInitialCropData(naturalWidth, naturalHeight, activeEditorAspectRatio(state))
      : cloneEditorValue(state.cropper.getData(false));
    if (!transformedCrop) {
      throw editorDraftValidationError('source-dimensions');
    }
    var rotation = normalizeQuarterRotation(transformedCrop.rotate);
    var flipX = Number(transformedCrop.scaleX) < 0;
    var scaleX = transformedCrop.scaleX === undefined ? 1 : Number(transformedCrop.scaleX);
    var scaleY = transformedCrop.scaleY === undefined
      ? 1
      : Number(transformedCrop.scaleY);
    if (
      rotation !== state.rotation || flipX !== state.flipX ||
      Math.abs(scaleX) !== 1 || scaleY !== 1
    ) {
      throw editorDraftValidationError('transform-mismatch',
        'The editor transform is out of sync. Please reset and try again.');
    }
    var width = Number(transformedCrop.width);
    var height = Number(transformedCrop.height);
    var x = Number(transformedCrop.x);
    var y = Number(transformedCrop.y);
    var rotatedWidth = Math.abs(rotation) === 90 ? naturalHeight : naturalWidth;
    var rotatedHeight = Math.abs(rotation) === 90 ? naturalWidth : naturalHeight;
    if (
      !Number.isFinite(naturalWidth) || !(naturalWidth > 0) ||
      !Number.isFinite(naturalHeight) || !(naturalHeight > 0) ||
      !Number.isFinite(x) || !Number.isFinite(y) ||
      !Number.isFinite(width) || !(width > 0) ||
      !Number.isFinite(height) || !(height > 0)
    ) throw editorDraftValidationError('invalid-crop');

    var tolerance = editorSourceToleranceForCropper(state.cropper);
    if (
      x < -tolerance || y < -tolerance ||
      x + width > rotatedWidth + tolerance ||
      y + height > rotatedHeight + tolerance
    ) throw editorDraftValidationError('crop-out-of-bounds');

    // Preserve the customer's aspect ratio when an edge-rounding correction
    // needs to reduce size. Interior crops retain their exact fractional data.
    if (width > rotatedWidth || height > rotatedHeight) {
      var fit = Math.min(1, rotatedWidth / width, rotatedHeight / height);
      width = Math.min(rotatedWidth, width * fit);
      height = Math.min(rotatedHeight, height * fit);
    }
    var captured = {
      data: {
        x: clamp(x, 0, rotatedWidth - width),
        y: clamp(y, 0, rotatedHeight - height),
        width: width,
        height: height,
        rotate: rotation,
        scaleX: flipX ? -1 : 1,
        scaleY: 1
      },
      naturalWidth: naturalWidth,
      naturalHeight: naturalHeight
    };
    captured.recipe = recipeFromStoredDraftData(root, captured);
    if (!draftMatchesRenderPolicy({
      data: captured.data, recipe: captured.recipe, renderPolicy: state.activeRenderPolicy
    })) throw editorDraftValidationError('render-policy-mismatch');
    return captured;
  }

  function recipeFromEditor(root, state) {
    return captureEditorDraft(root, state, false).recipe;
  }

  function clearOrderProperties(root) {
    ['[data-ai-design-version-property]', '[data-ai-preview-schema-property]', '[data-ai-hd-property]'].forEach(function (selector) {
      var input = root.querySelector(selector);
      if (input) { input.value = ''; input.disabled = true; }
    });
    var previewInput = root.querySelector('[data-ai-preview-property]');
    var cidInput = root.querySelector('[data-ai-cid-property]');
    var originalInput = root.querySelector('[data-ai-original-property]');
    var overlayInput = root.querySelector('[data-ai-preview-overlay-property]');
    previewInput.value = '';
    previewInput.disabled = true;
    cidInput.value = '';
    cidInput.disabled = true;
    originalInput.value = '';
    originalInput.disabled = true;
    if (overlayInput) {
      overlayInput.value = '';
      overlayInput.disabled = true;
    }
    var finalOverlay = root.querySelector('[data-ai-final-overlay]');
    if (finalOverlay) finalOverlay.hidden = true;
  }

  function clearOrderResetReceipt(root, state) {
    var scope = root.closest('product-info');
    Array.prototype.forEach.call(
      scope
        ? scope.querySelectorAll('[data-ai-reset-receipt]')
        : root.querySelectorAll('[data-ai-reset-receipt]'),
      function (input) {
        input.value = '';
        input.disabled = true;
      }
    );
    if (state) {
      state.orderResetReceipt = '';
      state.orderResetReceiptExpiresAt = '';
    }
  }

  function setOrderResetReceipt(root, state, receipt) {
    var value = String(receipt || '');
    if (!/^gor1\.[A-Za-z0-9_-]{80,205}\.[A-Za-z0-9_-]{43}$/.test(value)) {
      throw new Error('The secure order receipt is invalid.');
    }
    var aiInput = root.querySelector('[data-ai-reset-receipt]');
    // 收据仅属于 AI 表单；普通上传的 appendForm 不再接收或创建此字段。
    if (aiInput) {
      aiInput.value = value;
      aiInput.disabled = false;
    }
    state.orderResetReceipt = value;
    return value;
  }

  function currentStyleMetadata(root) {
    var selectedCard = root.querySelector('[data-ai-style-choice][aria-checked="true"]');
    var styleSelect = root.querySelector('[data-ai-style]');
    var title = selectedCard
      ? selectedCard.querySelector('.giftinone-ai-style-card__title')
      : null;
    return {
      styleId: styleSelect ? String(styleSelect.value || '') : '',
      label: title && title.textContent
        ? String(title.textContent).trim()
        : styleSelect && styleSelect.options && styleSelect.selectedIndex >= 0
          ? String(styleSelect.options[styleSelect.selectedIndex].textContent || '').trim()
          : 'AI design'
    };
  }

  function readyDesignByCid(state, cid) {
    var target = String(cid || '');
    for (var index = 0; index < state.designs.length; index += 1) {
      if (state.designs[index].cid === target) return state.designs[index];
    }
    return null;
  }

  function designSlotCount(state) {
    var readyCount = state && Array.isArray(state.designs) ? state.designs.length : 0;
    var draftCount = state && Array.isArray(state.workingDrafts) ? state.workingDrafts.length : 0;
    return readyCount + draftCount;
  }

  function cloneEditorValue(value) {
    if (!value || typeof value !== 'object') return value;
    var copy = {};
    Object.keys(value).forEach(function (key) { copy[key] = value[key]; });
    return copy;
  }

  function cloneRecipe(recipe) {
    return recipe ? JSON.parse(JSON.stringify(recipe)) : null;
  }

  function draftThumbnailPlan(recipe, sourceWidth, sourceHeight, maximumEdge) {
    var crop = recipe && recipe.crop || {};
    var canvas = recipe && recipe.canvas || {};
    var imageWidth = Number(sourceWidth);
    var imageHeight = Number(sourceHeight);
    var cropX = Number(crop.x);
    var cropY = Number(crop.y);
    var cropWidth = Number(crop.width);
    var cropHeight = Number(crop.height);
    var canvasWidth = Number(canvas.width);
    var canvasHeight = Number(canvas.height);
    var edge = Number(maximumEdge || DRAFT_THUMBNAIL_MAX_EDGE);
    if (
      !recipe || recipe.schema !== 'ai-recipe/v1' || recipe.mode !== 'crop-overlay' ||
      recipe.fit !== 'cover' || !Number.isFinite(imageWidth) ||
      !Number.isFinite(imageHeight) || !(imageWidth > 0) || !(imageHeight > 0) ||
      !Number.isFinite(cropX) || !Number.isFinite(cropY) ||
      !(cropWidth > 0) || !(cropHeight > 0) ||
      cropX < 0 || cropY < 0 || cropX + cropWidth > 1.00000001 ||
      cropY + cropHeight > 1.00000001 ||
      !Number.isInteger(canvasWidth) || !Number.isInteger(canvasHeight) ||
      !(canvasWidth > 0) || !(canvasHeight > 0) ||
      !Number.isFinite(edge) || edge < 1
    ) return null;

    var sourceLeft = Math.max(0, Math.floor(cropX * imageWidth));
    var sourceTop = Math.max(0, Math.floor(cropY * imageHeight));
    var sourceRight = Math.min(
      imageWidth,
      Math.ceil((cropX + cropWidth) * imageWidth)
    );
    var sourceBottom = Math.min(
      imageHeight,
      Math.ceil((cropY + cropHeight) * imageHeight)
    );
    if (sourceRight <= sourceLeft || sourceBottom <= sourceTop) return null;

    var targetScale = Math.min(1, edge / Math.max(canvasWidth, canvasHeight));
    var targetWidth = Math.max(1, Math.round(canvasWidth * targetScale));
    var targetHeight = Math.max(1, Math.round(canvasHeight * targetScale));
    var rotation;
    try {
      rotation = normalizeQuarterRotation(recipe.rotation);
    } catch (_rotationError) {
      return null;
    }
    var extractedWidth = sourceRight - sourceLeft;
    var extractedHeight = sourceBottom - sourceTop;
    var rotatedWidth = Math.abs(rotation) === 90 ? extractedHeight : extractedWidth;
    var rotatedHeight = Math.abs(rotation) === 90 ? extractedWidth : extractedHeight;
    var coverScale = Math.max(
      targetWidth / rotatedWidth,
      targetHeight / rotatedHeight
    );
    var background = recipe.background && recipe.background.color;
    if (!/^#[0-9a-f]{6}$/i.test(String(background || ''))) background = '#ffffff';
    return {
      source: {
        left: sourceLeft,
        top: sourceTop,
        width: extractedWidth,
        height: extractedHeight
      },
      target: { width: targetWidth, height: targetHeight },
      draw: {
        width: extractedWidth * coverScale,
        height: extractedHeight * coverScale,
        rotation: rotation,
        flipX: Boolean(recipe.flipX),
        flipY: Boolean(recipe.flipY)
      },
      background: background
    };
  }

  function browserPreviewOverlayUrl(state) {
    return String(
      state && state.renderProfile && state.renderProfile.type === 'OVERLAY' &&
      state.previewOverlayStatus === 'READY' && state.currentPreviewOverlayUrl || ''
    );
  }

  function appendPreviewPlaceholder(media, label) {
    var placeholder = document.createElement('span');
    placeholder.className = 'giftinone-ai-working-draft-card__placeholder';
    placeholder.setAttribute('aria-hidden', 'true');
    placeholder.textContent = label || 'AI';
    media.appendChild(placeholder);
    return placeholder;
  }

  function renderDraftRecipeThumbnail(media, state, draft) {
    var recipe = cloneRecipe(draft && draft.recipe);
    var previewUrl = String(draft && draft.previewUrl || '');
    if (!media || !recipe || !previewUrl) return Promise.resolve(false);
    return new Promise(function (resolve) {
      var sourceImage = new window.Image();
      var settled = false;
      function finish(rendered) {
        if (settled) return;
        settled = true;
        sourceImage.onload = null;
        sourceImage.onerror = null;
        resolve(rendered);
      }
      sourceImage.onload = function () {
        var plan = draftThumbnailPlan(
          recipe,
          sourceImage.naturalWidth,
          sourceImage.naturalHeight,
          DRAFT_THUMBNAIL_MAX_EDGE
        );
        if (!plan || media.isConnected === false) {
          finish(false);
          return;
        }
        var canvas = document.createElement('canvas');
        canvas.className = 'giftinone-ai-preview-card__base';
        canvas.width = plan.target.width;
        canvas.height = plan.target.height;
        canvas.setAttribute('aria-hidden', 'true');
        var context = canvas.getContext('2d');
        if (!context) {
          finish(false);
          return;
        }
        try {
          context.fillStyle = plan.background;
          context.fillRect(0, 0, canvas.width, canvas.height);
          context.imageSmoothingEnabled = true;
          context.imageSmoothingQuality = 'high';
          context.save();
          context.translate(canvas.width / 2, canvas.height / 2);
          context.rotate(plan.draw.rotation * Math.PI / 180);
          context.scale(plan.draw.flipX ? -1 : 1, plan.draw.flipY ? -1 : 1);
          context.drawImage(
            sourceImage,
            plan.source.left,
            plan.source.top,
            plan.source.width,
            plan.source.height,
            -plan.draw.width / 2,
            -plan.draw.height / 2,
            plan.draw.width,
            plan.draw.height
          );
          context.restore();
        } catch (_drawError) {
          finish(false);
          return;
        }
        if (media.isConnected === false) {
          finish(false);
          return;
        }
        media.textContent = '';
        media.appendChild(canvas);
        appendBrowserOverlay(media, browserPreviewOverlayUrl(state), state);
        finish(true);
      };
      sourceImage.onerror = function () { finish(false); };
      sourceImage.decoding = 'async';
      sourceImage.src = previewUrl;
    });
  }

  function createPreviewCardMedia(state, options) {
    var settings = options || {};
    var media = document.createElement('span');
    media.className = 'giftinone-ai-preview-card__media ' + String(settings.className || '');
    var previewUrl = String(settings.previewUrl || '');
    if (settings.recipe && previewUrl) {
      appendPreviewPlaceholder(media, settings.placeholderLabel);
      renderDraftRecipeThumbnail(media, state, {
        previewUrl: previewUrl,
        recipe: settings.recipe
      });
      return media;
    }
    if (settings.rendered && previewUrl) {
      var image = document.createElement('img');
      image.className = 'giftinone-ai-preview-card__base';
      image.src = previewUrl;
      image.alt = '';
      image.loading = 'lazy';
      image.decoding = 'async';
      media.appendChild(image);
      // This image already includes its frozen product layer.
      return media;
    }
    appendPreviewPlaceholder(media, settings.placeholderLabel);
    return media;
  }

  function cloneRenderPolicy(policy) {
    if (!policy || typeof policy !== 'object') return freeformRenderPolicy('NO_PROFILE');
    return {
      mode: String(policy.mode || 'FREEFORM'),
      resolution: String(policy.resolution || ''),
      constrained: Boolean(policy.constrained),
      aspectWidth: policy.constrained ? Number(policy.aspectWidth) : undefined,
      aspectHeight: policy.constrained ? Number(policy.aspectHeight) : undefined,
      aspectRatio: policy.constrained ? Number(policy.aspectRatio) : NaN,
      signature: String(policy.signature || 'FREEFORM'),
      authoritative: Boolean(policy.authoritative)
    };
  }

  function cloneEditableDraft(draft) {
    if (!draft || !draft.previewUrl || !draft.data || !draft.recipe) return null;
    return {
      cid: String(draft.cid || ''),
      token: '',
      status: 'PREVIEW_READY',
      previewUrl: String(draft.previewUrl || ''),
      originalKey: String(draft.originalKey || ''),
      originalUrl: String(draft.originalUrl || ''),
      styleId: String(draft.styleId || ''),
      styleLabel: displayStyleLabel(draft.styleId, draft.styleLabel, 'AI preview'),
      rotation: Number(draft.rotation || 0),
      flipX: Boolean(draft.flipX),
      data: cloneEditorValue(draft.data),
      canvas: null,
      cropBox: null,
      container: null,
      recipe: cloneRecipe(draft.recipe),
      renderPolicy: cloneRenderPolicy(draft.renderPolicy),
      variantBinding: draft.variantBinding,
      naturalWidth: Number(draft.naturalWidth),
      naturalHeight: Number(draft.naturalHeight),
      fileObject: draft.fileObject || null,
      revision: Number(draft.revision || 1),
      serverRevision: Number(draft.serverRevision || 0),
      persistedLocalRevision: Number(draft.persistedLocalRevision || 0),
      updatedAt: Number(draft.updatedAt || Date.now()),
      restoredFromSession: Boolean(draft.restoredFromSession),
      customerAdjusted: Boolean(
        draft.customerAdjusted || Number(draft.serverRevision || 0) > 1
      ),
      autoCenteredRepair: Boolean(draft.autoCenteredRepair),
      requiresCartOwnershipCheck: Boolean(draft.requiresCartOwnershipCheck),
      persistError: false,
      persistErrorCode: ''
    };
  }

  function canonicalDraftPayload(draft) {
    return {
      data: cloneEditorValue(draft.data),
      recipe: cloneRecipe(draft.recipe),
      naturalWidth: Number(draft.naturalWidth),
      naturalHeight: Number(draft.naturalHeight)
    };
  }

  function clearDraftSaveTimer(state) {
    if (state.draftSaveTimer) window.clearTimeout(state.draftSaveTimer);
    state.draftSaveTimer = null;
    state.draftSaveTimerCid = '';
  }

  function persistWorkingDraft(root, state, draft, keepalive) {
    if (!draft || !CID_PATTERN.test(String(draft.cid || ''))) {
      return Promise.reject(new Error('This preview cannot be saved securely.'));
    }
    if (!state.sessionReady) {
      return Promise.reject(new Error('The secure design session is not ready.'));
    }
    // Generated assets already exist on the server. Never persist a provisional
    // first-open draft from a timer, pagehide, or close while layout is settling.
    if (draft.initializationComplete === false) return Promise.resolve(draft);
    if (draft.deleting) return Promise.resolve(draft);
    if (draft.persistPromise) {
      return draft.persistPromise.then(function () {
        if (draft.deleting) return draft;
        if (Number(draft.persistedLocalRevision || 0) < Number(draft.revision || 0)) {
          return persistWorkingDraft(root, state, draft, keepalive);
        }
        return draft;
      });
    }
    if (draft.persistError) {
      return Promise.reject(new Error(
        draft.persistErrorCode === 'DRAFT_REVISION_CONFLICT'
          ? 'This design changed in another tab. Reload it before continuing.'
          : 'Secure save is paused. Make another adjustment to retry, or delete this preview.'
      ));
    }
    var expectedRevision = Number(draft.serverRevision || 0);
    var localRevision = Number(draft.revision || 0);
    var payload = canonicalDraftPayload(draft);
    draft.persisting = true;
    state.draftPersistingCid = draft.cid;
    if (draft.cid === state.activeWorkingDraftCid) {
      setAutoSaveStatus(root, 'Saving…', false);
      setBusy(root, state.busy);
    }
    draft.persistPromise = apiRequest(
      root,
      '/ai/jobs/' + encodeURIComponent(draft.cid) + '/draft',
      'PUT',
      '',
      { expectedRevision: expectedRevision, draft: payload },
      // Draft payloads are deliberately small. Every draft PUT is keepalive so
      // a pagehide that occurs while an earlier autosave is already in flight
      // cannot downgrade that request to a navigation-cancellable fetch.
      { keepalive: true }
    ).then(function (response) {
      var saved = response && response.draft;
      if (
        String(response && response.cid || '') !== draft.cid ||
        !saved || !Number.isInteger(saved.revision) ||
        saved.revision <= expectedRevision ||
        !recipesEquivalent(saved.recipe, payload.recipe)
      ) throw new Error('The secure save response is invalid.');
      draft.serverRevision = saved.revision;
      draft.persistedLocalRevision = localRevision;
      draft.updatedAt = Date.parse(saved.updatedAt) || Date.now();
      draft.persistError = false;
      draft.persistErrorCode = '';
      if (draft.cid === state.activeWorkingDraftCid) {
        setAutoSaveStatus(root, 'All changes saved', false);
      }
      renderWorkingDrafts(root, state);
      return draft;
    }).catch(function (error) {
      if (error && error.status === 409 && error.code === 'DRAFT_REVISION_CONFLICT') {
        draft.persistError = true;
        draft.persistErrorCode = 'DRAFT_REVISION_CONFLICT';
        state.editorInvalid = draft.cid === state.activeWorkingDraftCid;
        setAutoSaveStatus(
          root,
          'This design changed in another tab. Restoring the latest securely saved version…',
          true
        );
        setStatus(root, 'This design changed in another tab. Restoring the latest version…', 'editor', true);
        // A stale autosave can finish while the customer is reopening this
        // design. Settle every image/decode/Cropper callback before changing
        // the run owner or destroying the shared editor state.
        if (state.editorOpening && typeof state.abortEditorOpen === 'function') {
          state.abortEditorOpen();
        }
        state.runNumber += 1;
        destroyEditor(state);
        setDraftReturnState(root, state, false);
        state.cid = '';
        state.token = '';
        state.originalKey = '';
        state.originalUrl = '';
        state.editorPreviewUrl = '';
        state.activeWorkingDraftCid = '';
        return restoreProductSession(root, state).then(function () {
          var conflict = new Error(
            'This design changed in another tab. The latest securely saved version was restored.'
          );
          conflict.code = 'DRAFT_REVISION_CONFLICT';
          setStatus(root, conflict.message, 'setup', true);
          throw conflict;
        }, function () {
          var restoreError = new Error(
            'This design changed in another tab, and the latest version could not be restored. Refresh and try again.'
          );
          restoreError.code = 'DRAFT_REVISION_CONFLICT';
          throw restoreError;
        });
      } else {
        // A non-conflict failure must stop this autosave generation. Without
        // this flag the finally block re-queues the same rejected PUT every
        // 500 ms, which also prevents an intentional Delete from proceeding.
        draft.persistError = true;
        draft.persistErrorCode = String(error && error.code || 'DRAFT_SAVE_FAILED');
        if (draft.cid === state.activeWorkingDraftCid) {
          setAutoSaveStatus(root, 'Secure save paused. We will retry after your next change.', true);
        }
        renderWorkingDrafts(root, state);
      }
      throw error;
    }).finally(function () {
      draft.persisting = false;
      draft.persistPromise = null;
      if (state.draftPersistingCid === draft.cid) state.draftPersistingCid = '';
      setBusy(root, state.busy);
      if (
        !draft.persistError && !draft.deleting &&
        workingDraftByCid(state, draft.cid) === draft &&
        Number(draft.persistedLocalRevision || 0) < Number(draft.revision || 0)
      ) {
        scheduleDraftSave(root, state, draft);
      }
    });
    return draft.persistPromise;
  }

  function scheduleDraftSave(root, state, draft) {
    if (
      !draft || draft.deleting || draft.initializationComplete === false || !state.sessionReady ||
      state.suppressDraftSync || state.restoringWorkingDraftCid
    ) return;
    clearDraftSaveTimer(state);
    draft.persistError = false;
    draft.persistErrorCode = '';
    state.draftSaveTimerCid = draft.cid;
    setAutoSaveStatus(root, 'Saving…', false);
    setBusy(root, state.busy);
    state.draftSaveTimer = window.setTimeout(function () {
      state.draftSaveTimer = null;
      state.draftSaveTimerCid = '';
      persistWorkingDraft(root, state, draft, false).catch(function () {});
    }, DRAFT_SAVE_DEBOUNCE_MS);
  }

  function flushPendingDraftSave(root, state, draft, keepalive) {
    if (!draft) return Promise.resolve(null);
    if (state.draftSaveTimerCid === draft.cid) clearDraftSaveTimer(state);
    if (draft.persistPromise || Number(draft.persistedLocalRevision || 0) < Number(draft.revision || 0)) {
      return persistWorkingDraft(root, state, draft, keepalive);
    }
    return Promise.resolve(draft);
  }

  function deletePersistedDraft(root, state, draft) {
    var cid = String(draft && draft.cid || '');
    if (!CID_PATTERN.test(cid)) {
      return Promise.reject(new Error('This preview cannot be deleted securely.'));
    }
    var workingDraft = workingDraftByCid(state, cid);
    var targetDraft = workingDraft || draft;
    if (targetDraft.deletePromise) return targetDraft.deletePromise;

    // Delete means discard. Never manufacture a PUT for unsaved local edits.
    // Cancel a queued autosave; if one is already on the wire, only wait for
    // it to settle so the DELETE can use the newest known server revision.
    targetDraft.deleting = true;
    if (state.draftSaveTimerCid === cid) clearDraftSaveTimer(state);
    var pendingSave = targetDraft.persistPromise
      ? Promise.resolve(targetDraft.persistPromise).catch(function (error) {
        // Never turn an already-detected cross-tab conflict into permission
        // to delete the freshly restored version. Other failures may proceed;
        // DELETE's If-Match still protects an unknown server outcome.
        if (
          error && (
            error.status === 409 || error.code === 'DRAFT_REVISION_CONFLICT'
          )
        ) throw error;
        return targetDraft;
      })
      : Promise.resolve(targetDraft);

    targetDraft.deletePromise = pendingSave.then(function () {
      var latestDraft = workingDraftByCid(state, cid) || targetDraft;
      var revision = Number.isInteger(latestDraft.serverRevision)
        ? latestDraft.serverRevision
        : 0;
      return apiRequest(
        root,
        '/ai/jobs/' + encodeURIComponent(cid) + '/draft',
        'DELETE',
        '',
        undefined,
        { headers: { 'If-Match': '"' + revision + '"' } }
      );
    }).catch(function (error) {
      if (error && error.code === 'DESIGN_BUSY') {
        throw new Error('This design is being prepared or has already been ordered. Keep it saved and remove a different design to free a space.');
      }
      if (
        error && (
          error.code === 'DRAFT_REVISION_CONFLICT' ||
          error.code === 'MANIFEST_REVISION_CONFLICT'
        )
      ) {
        targetDraft.persistError = true;
        targetDraft.persistErrorCode = 'DRAFT_REVISION_CONFLICT';
        throw new Error('This preview changed in another tab. Reload this product before removing it.');
      }
      throw error;
    }).finally(function () {
      targetDraft.deleting = false;
      targetDraft.deletePromise = null;
    });
    return targetDraft.deletePromise;
  }

  function finiteWithin(left, right, tolerance) {
    return typeof left === 'number' && typeof right === 'number' &&
      Number.isFinite(left) && Number.isFinite(right) &&
      Math.abs(left - right) <= tolerance;
  }

  function equivalentRecipeCanvas(left, right) {
    if (!left || !right) return false;
    var leftWidth = left.width;
    var leftHeight = left.height;
    var rightWidth = right.width;
    var rightHeight = right.height;
    if (
      typeof leftWidth !== 'number' || typeof leftHeight !== 'number' ||
      typeof rightWidth !== 'number' || typeof rightHeight !== 'number' ||
      !Number.isFinite(leftWidth) || !Number.isFinite(leftHeight) ||
      !Number.isFinite(rightWidth) || !Number.isFinite(rightHeight)
    ) return false;
    return leftWidth === rightWidth && leftHeight === rightHeight;
  }

  function recipesEquivalent(left, right) {
    if (!left || !right || !left.crop || !right.crop) return false;
    var leftBackground = left.background || {};
    var rightBackground = right.background || {};
    if (
      left.schema !== right.schema || left.mode !== right.mode ||
      left.rotation !== right.rotation ||
      left.flipX !== right.flipX ||
      left.flipY !== right.flipY ||
      left.fit !== right.fit ||
      leftBackground.mode !== rightBackground.mode ||
      leftBackground.color !== rightBackground.color ||
      !equivalentRecipeCanvas(left.canvas, right.canvas)
    ) return false;
    var leftEdges = [
      left.crop.x,
      left.crop.y,
      left.crop.x + left.crop.width,
      left.crop.y + left.crop.height
    ];
    var rightEdges = [
      right.crop.x,
      right.crop.y,
      right.crop.x + right.crop.width,
      right.crop.y + right.crop.height
    ];
    return leftEdges.every(function (edge, index) {
      return finiteWithin(edge, rightEdges[index], RECIPE_CROP_TOLERANCE);
    });
  }

  function workingDraftByCid(state, cid) {
    var target = String(cid || '');
    var drafts = state && Array.isArray(state.workingDrafts)
      ? state.workingDrafts
      : [];
    for (var index = 0; index < drafts.length; index += 1) {
      if (drafts[index].cid === target) return drafts[index];
    }
    return null;
  }

  function isEditableWorkingDraft(draft) {
    return Boolean(
      draft && draft.status !== 'PREVIEW_QUEUED' &&
      draft.status !== 'PREVIEW_RUNNING' &&
      draft.status !== 'PREVIEW_FINAL_QUEUED' &&
      draft.status !== 'PREVIEW_FINAL_RUNNING' &&
      draft.status !== 'PREVIEW_FAILED' &&
      draft.status !== 'PREVIEW_READY_PENDING_RESTORE' &&
      draft.previewUrl && draft.data && draft.recipe
    );
  }

  function scrubWorkingDraft(draft) {
    if (!draft) return;
    draft.token = '';
    draft.fileObject = null;
    draft.data = null;
    draft.canvas = null;
    draft.cropBox = null;
    draft.container = null;
    draft.recipe = null;
  }

  function removeWorkingDraft(state, cid) {
    var target = String(cid || '');
    var removed = null;
    state.workingDrafts = state.workingDrafts.filter(function (draft) {
      if (draft.cid !== target) return true;
      removed = draft;
      return false;
    });
    if (removed) scrubWorkingDraft(removed);
    if (state.activeWorkingDraftCid === target) {
      state.activeWorkingDraftCid = '';
      state.editorCanonicalCid = '';
      state.editorCanonicalRevision = 0;
      state.editorInvalid = false;
    }
    if (state.generationFallbackDraftCid === target) state.generationFallbackDraftCid = '';
    if (
      currentPhotoSource(state).kind === 'saved' &&
      currentPhotoSource(state).sourceCid === target
    ) setPhotoSource(null, state, emptyPhotoSource());
    if (state.restoringWorkingDraftCid === target) {
      state.restoringWorkingDraftCid = '';
      if (state.suppressDraftSync && state.suppressDraftSync.cid === target) {
        state.suppressDraftSync = null;
      }
    }
    return removed;
  }

  function clearWorkingDrafts(state) {
    state.workingDrafts.forEach(scrubWorkingDraft);
    state.workingDrafts = [];
    releaseWorkingDraftSelection(state);
  }

  function releaseWorkingDraftSelection(state) {
    clearDraftSaveTimer(state);
    state.activeWorkingDraftCid = '';
    state.generationFallbackDraftCid = '';
    state.finalizingDraftCid = '';
    state.restoringWorkingDraftCid = '';
    state.suppressDraftSync = null;
    state.editorDraftInteraction = false;
    state.editorCanonicalCid = '';
    state.editorCanonicalRevision = 0;
    state.editorInvalid = false;
    syncLegacyPhotoState(state);
  }

  function updateWorkingDraftFromEditor(root, state, shouldRender, updateExistingDraft, options) {
    if (
      !state.cropper || !CID_PATTERN.test(String(state.cid || '')) ||
      !state.editorPreviewUrl
    ) return null;
    var draft = workingDraftByCid(state, state.cid);
    var initialCentered = Boolean(options && options.initialCentered);
    if (state.suppressDraftSync) return draft;
    if (draft && (!updateExistingDraft || (initialCentered && draft.data && draft.recipe && (
      draft.restoredFromSession || draft.initializationComplete === true ||
      draft.customerAdjusted || Number(draft.serverRevision || 0) > 0
    )))) {
      state.editorRecipeSnapshot = cloneRecipe(draft.recipe);
      state.editorCanonicalCid = draft.cid;
      state.editorCanonicalRevision = Number(draft.revision || 1);
      state.editorInvalid = false;
      if (shouldRender) renderWorkingDrafts(root, state);
      return draft;
    }
    var captured;
    try {
      captured = captureEditorDraft(root, state, initialCentered);
    } catch (error) {
      state.editorLayoutDiagnostic = editorLayoutDiagnostic(state, draft, error);
      return null;
    }
    var recipe = captured.recipe;
    if (!draft) {
      if (designSlotCount(state) >= MAX_WORKING_DRAFTS) return null;
      draft = { cid: state.cid, createdAt: Date.now() };
      state.workingDrafts.push(draft);
    }
    draft.token = state.token;
    draft.status = state.finalizingDraftCid === draft.cid
      ? 'FINALIZING'
      : 'PREVIEW_READY';
    draft.previewUrl = state.editorPreviewUrl;
    draft.originalKey = state.originalKey;
    draft.originalUrl = state.originalUrl;
    draft.styleId = state.jobStyleId || '';
    draft.styleLabel = displayStyleLabel(
      draft.styleId,
      state.jobStyleLabel,
      'AI preview'
    );
    draft.rotation = state.rotation;
    draft.flipX = state.flipX;
    draft.data = cloneEditorValue(captured.data);
    draft.canvas = cloneEditorValue(state.cropper.getCanvasData());
    draft.cropBox = cloneEditorValue(state.cropper.getCropBoxData());
    draft.container = cloneEditorValue(state.cropper.getContainerData());
    draft.recipe = cloneRecipe(recipe);
    draft.customerAdjusted = !initialCentered && Boolean(
      draft.customerAdjusted || (updateExistingDraft && draft.revision)
    );
    if (initialCentered) draft.initializationComplete = false;
    if (draft.customerAdjusted) draft.autoCenteredRepair = false;
    draft.renderPolicy = cloneRenderPolicy(state.activeRenderPolicy);
    draft.variantBinding = state.currentVariantContract
      ? {
          productId: state.currentVariantContract.productId,
          variantId: state.currentVariantContract.variantId,
          selectedOptions: state.currentVariantContract.selectedOptions.map(function (option) {
            return { name: option.name, value: option.value };
          })
        }
      : null;
    draft.naturalWidth = captured.naturalWidth;
    draft.naturalHeight = captured.naturalHeight;
    if (!state.reviewingAlternateStyle) {
      draft.fileObject = currentPhotoSource(state).kind === 'local'
        ? currentPhotoSource(state).file || null
        : null;
    } else if (!draft.fileObject) {
      draft.fileObject = null;
    }
    draft.revision = Number(draft.revision || 0) + 1;
    if (!Number.isInteger(draft.serverRevision)) draft.serverRevision = 0;
    if (!Number.isInteger(draft.persistedLocalRevision)) draft.persistedLocalRevision = 0;
    draft.updatedAt = Date.now();
    state.activeWorkingDraftCid = draft.cid;
    state.editorRecipeSnapshot = cloneRecipe(recipe);
    state.editorCanonicalCid = draft.cid;
    state.editorCanonicalRevision = draft.revision;
    state.editorInvalid = false;
    if (!initialCentered) scheduleDraftSave(root, state, draft);
    if (shouldRender) renderWorkingDrafts(root, state);
    return draft;
  }

  function createWorkingDraftCard(draft, active, state) {
    var card = document.createElement('article');
    card.className = 'giftinone-ai-working-draft-card';
    card.dataset.aiActive = active ? 'true' : 'false';

    var select = document.createElement('button');
    select.type = 'button';
    select.className = 'giftinone-ai-working-draft-card__select';
    var pending = draft.status === 'PREVIEW_QUEUED' || draft.status === 'PREVIEW_RUNNING';
    var finalizing =
      draft.status === 'PREVIEW_FINAL_QUEUED' ||
      draft.status === 'PREVIEW_FINAL_RUNNING';
    var failed = draft.status === 'PREVIEW_FAILED';
    var settlementPending = failed && draft.canRetryPreview === false;
    var uploaded = draft.status === 'UPLOADED';
    var uploadPending = draft.status === 'UPLOAD_PENDING';
    var waitingForDraft = draft.status === 'PREVIEW_READY_PENDING_RESTORE';
    var synthesizedDraft = Boolean(
      draft.restoredFromSession && Number(draft.serverRevision || 0) === 0 &&
      draft.data && draft.recipe
    );
    if (pending || finalizing || settlementPending) select.dataset.aiRestoredJobPoll = draft.cid;
    else if (failed || uploaded) select.dataset.aiRestoredJobRetry = draft.cid;
    else if (waitingForDraft) select.dataset.aiRestoredJobOpen = draft.cid;
    else if (!uploadPending) select.dataset.aiWorkingDraftSelect = draft.cid;
    if (uploadPending) select.disabled = true;
    select.setAttribute('aria-pressed', active ? 'true' : 'false');
    select.setAttribute(
      'aria-label',
      (draft.styleLabel || 'AI preview') + (
        settlementPending ? ', finalizing refund, check again shortly' :
          finalizing ? ', finalizing, check progress' :
          pending ? ', generating, check progress' :
          uploaded ? ', uploaded, continue generation' :
            uploadPending ? ', upload interrupted' :
          failed ? ', generation failed, retry' :
            waitingForDraft ? ', ready, open preview' :
              synthesizedDraft ? ', ready to adjust' :
                active ? ', current securely saved preview' : ', open securely saved preview'
      )
    );
    select.appendChild(createPreviewCardMedia(state, {
      className: 'giftinone-ai-working-draft-card__media',
      previewUrl: draft.previewUrl,
      recipe: draft.recipe,
      placeholderLabel: failed ? 'Retry' : uploadPending ? 'Upload' : 'AI'
    }));
    var copy = document.createElement('span');
    copy.className = 'giftinone-ai-working-draft-card__copy';
    var title = document.createElement('strong');
    title.textContent = draft.styleLabel || 'AI preview';
    var detail = document.createElement('span');
    detail.textContent = settlementPending
      ? 'Finalizing refund…'
      : finalizing
      ? 'Finalizing…'
      : pending
        ? 'Generating…'
        : uploaded
        ? 'Continue generation'
        : uploadPending
          ? 'Upload interrupted'
      : failed
        ? (draft.errorRetryable === false ? 'Could not generate' : 'Retry preview')
        : waitingForDraft
          ? 'Ready to open'
          : draft.persistError
            ? 'Save paused — edit to retry'
          : (draft.persisting ||
            Number(draft.persistedLocalRevision || 0) < Number(draft.revision || 0))
            ? 'Saving…'
            : synthesizedDraft
              ? 'Ready to adjust'
              : 'Saved';
    copy.appendChild(title);
    copy.appendChild(detail);
    select.appendChild(copy);
    var badge = document.createElement('em');
    badge.className = 'giftinone-ai-working-draft-card__badge';
    badge.textContent = settlementPending
      ? 'Please wait'
      : finalizing || draft.status === 'FINALIZING'
      ? 'Saving'
      : draft.status === 'PREVIEW_FINAL_FAILED'
      ? 'Retry add'
        : pending
          ? 'Generating'
          : uploaded
            ? 'Continue'
            : uploadPending
              ? 'Interrupted'
          : failed
            ? 'Retry'
            : active ? 'Editing' : 'Preview';
    select.appendChild(badge);
    card.appendChild(select);

    var remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'giftinone-ai-working-draft-card__remove';
    remove.dataset.aiWorkingDraftRemove = draft.cid;
    remove.setAttribute('aria-label', 'Delete ' + (draft.styleLabel || 'AI preview'));
    remove.textContent = '\u00d7';
    card.appendChild(remove);
    return card;
  }

  function renderWorkingDrafts(root, state) {
    var count = state.workingDrafts.length;
    var totalCount = designSlotCount(state);
    var activeCid = String(state.activeWorkingDraftCid || '');
    var editor = root.querySelector('[data-ai-editor]');
    if (editor) editor.dataset.aiDraftCount = String(count);
    Array.prototype.forEach.call(
      root.querySelectorAll('[data-ai-working-drafts]'),
      function (section) {
        var editorRail = section.dataset.aiWorkingDrafts === 'editor';
        section.dataset.aiDraftCount = String(count);
        // A single active image needs no comparison rail. Resize handling
        // below reconstructs the Cropper view from the canonical recipe when
        // the rail appears or disappears at the 1↔2 boundary.
        section.hidden = count === 0 || (editorRail && count < 2);
        if (editorRail) {
          var heading = section.querySelector('h3');
          if (heading) heading.textContent = count > 1 ? 'Compare designs' : 'Current preview';
        } else {
          var setupHeading = section.querySelector('h3');
          if (setupHeading) {
            setupHeading.textContent = count > 1 ? 'Compare your AI previews' : 'Current preview saved';
          }
        }
      }
    );
    Array.prototype.forEach.call(
      root.querySelectorAll('[data-ai-working-draft-count]'),
      function (node) {
        node.textContent = totalCount + '/' + MAX_WORKING_DRAFTS;
      }
    );
    Array.prototype.forEach.call(
      root.querySelectorAll('[data-ai-clear-drafts]'),
      function (node) { node.hidden = count < 2; }
    );
    Array.prototype.forEach.call(
      root.querySelectorAll('[data-ai-working-draft-limit]'),
      function (node) {
        node.dataset.aiLimitReached = totalCount >= MAX_WORKING_DRAFTS ? 'true' : 'false';
        node.textContent = totalCount >= MAX_WORKING_DRAFTS
          ? designLimitReachedMessage()
          : designCapacityMessage();
      }
    );
    Array.prototype.forEach.call(
      root.querySelectorAll('[data-ai-working-draft-list]'),
      function (list) {
        list.textContent = '';
        state.workingDrafts.forEach(function (draft) {
          list.appendChild(createWorkingDraftCard(draft, draft.cid === activeCid, state));
        });
      }
    );
    syncDesignLibraryUi(root, state);
    syncEditorPrimaryAction(root, state);
    scheduleEditorViewportResize(root, state, false);
  }

  function renderDesignGallery(root, state) {
    if (!state || !Array.isArray(state.designs)) return;
    var list = root.querySelector('[data-ai-design-list]');
    var modeHelp = root.querySelector('[data-ai-mode-help]');
    var savedSection = root.querySelector('[data-ai-saved-designs]');
    var draftSection = root.querySelector(
      '[data-ai-designs-view] [data-ai-working-drafts="setup"]'
    );
    var libraryItems = root.querySelector('[data-ai-designs-view] .giftinone-ai-library__items');
    var designCount = root.querySelector('[data-ai-design-count]');
    var standardSummary = root.querySelector('[data-ai-standard-summary]');
    var createSummary = root.querySelector('[data-ai-create-summary]');
    var standardModeButton = root.querySelector('[data-ai-mode-standard]');
    var aiModeButton = root.querySelector('[data-ai-mode-ai]');
    var regularMode = root.dataset.aiMode === 'regular';
    var selectedCid = regularMode ? '' : String(state.selectedDesignCid || '');
    var aiReady = !regularMode && root.dataset.aiState === 'ready' && Boolean(selectedCid);
    var aiAdded = !regularMode && root.dataset.aiState === 'added';

    if (standardModeButton) {
      standardModeButton.setAttribute('aria-pressed', regularMode ? 'true' : 'false');
    }
    if (aiModeButton) {
      aiModeButton.setAttribute('aria-pressed', regularMode ? 'false' : 'true');
    }
    if (standardSummary) standardSummary.hidden = !regularMode;
    if (createSummary) createSummary.hidden = regularMode || aiReady || aiAdded;
    if (savedSection) savedSection.hidden = state.designs.length === 0;
    if (libraryItems && savedSection && draftSection) {
      // When the customer opens My designs from a selected finished design,
      // keep that current choice at the top. While editing a draft, keep the
      // draft rail first instead.
      if (selectedCid) libraryItems.insertBefore(savedSection, draftSection);
      else libraryItems.insertBefore(draftSection, savedSection);
    }
    if (designCount) {
      designCount.textContent = state.designs.length === 1
        ? '1 saved design'
        : state.designs.length + ' saved designs';
    }

    if (list) {
      list.textContent = '';
      var orderedDesigns = state.designs.slice().sort(function (left, right) {
        if (left.cid === selectedCid && right.cid !== selectedCid) return -1;
        if (right.cid === selectedCid && left.cid !== selectedCid) return 1;
        return 0;
      });
      orderedDesigns.forEach(function (design, index) {
        var designInCart = Boolean(state.cartCustomizationCids[design.cid]);
        var item = document.createElement('article');
        item.className = 'giftinone-ai-design-item';
        item.dataset.aiSelected = design.cid === selectedCid ? 'true' : 'false';
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'giftinone-ai-design-card';
        button.dataset.aiDesignCid = design.cid;
        button.setAttribute('aria-pressed', design.cid === selectedCid ? 'true' : 'false');
        button.setAttribute(
          'aria-label',
          (design.styleLabel || 'AI design') +
            (designInCart
              ? ', already in cart'
              : design.editableDraft
                ? ', edit design'
                : ', ready to select')
        );

        var media = createPreviewCardMedia(state, {
          className: 'giftinone-ai-design-card__media',
          previewUrl: design.previewFinalUrl,
          rendered: true
        });
        button.appendChild(media);

        var copy = document.createElement('span');
        copy.className = 'giftinone-ai-design-card__copy';
        var title = document.createElement('strong');
        title.textContent = design.styleLabel || 'AI design ' + (index + 1);
        var detail = document.createElement('span');
        detail.textContent = designInCart
          ? 'In cart'
          : design.editableDraft
            ? 'Edit design'
            : design.cid === selectedCid
              ? 'Currently selected'
              : 'Ready';
        copy.appendChild(title);
        copy.appendChild(detail);
        button.appendChild(copy);

        var check = document.createElement('span');
        check.className = 'giftinone-ai-design-card__check';
        check.setAttribute('aria-hidden', 'true');
        check.textContent = '\u2713';
        button.appendChild(check);
        item.appendChild(button);

        var remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'giftinone-ai-design-card__remove';
        remove.dataset.aiDesignRemove = design.cid;
        remove.setAttribute('aria-label', 'Remove ' + (design.styleLabel || 'AI design') + ' from My designs');
        remove.textContent = '\u00d7';
        item.appendChild(remove);
        list.appendChild(item);
      });
    }

    if (modeHelp) {
      if (regularMode) {
        modeHelp.textContent = 'Standard upload remains selected until you explicitly choose a saved AI design.';
      } else if (selectedCid) {
        modeHelp.textContent = 'This AI design is selected. Product options can still be changed before adding to cart.';
      } else {
        modeHelp.textContent = 'Choose a saved result, or create another AI style.';
      }
    }
    renderWorkingDrafts(root, state);
  }

  function setReadyDesignProperties(root, design) {
    if (!isCompleteFinal(design) || root.dataset.aiMode !== 'ai') { clearOrderProperties(root); return; }
    [['[data-ai-preview-property]', design.previewFinalUrl],
     ['[data-ai-hd-property]', expectedProductionUrl(design.cid)],
     ['[data-ai-cid-property]', design.cid],
     ['[data-ai-design-version-property]', design.designVersion],
     ['[data-ai-preview-schema-property]', COMPLETE_PREVIEW_SCHEMA]].forEach(function (pair) {
      var input = root.querySelector(pair[0]);
      if (!input) throw new Error('The AI order fields are unavailable. Refresh this page.');
      input.value = pair[1]; input.disabled = false;
    });
    ['[data-ai-original-property]', '[data-ai-preview-overlay-property]'].forEach(function (selector) {
      var input = root.querySelector(selector);
      if (input) { input.value = ''; input.disabled = true; }
    });
  }

  function applyReadyDesign(root, state, design, options) {
    if (!isCompleteFinal(design)) return false;
    var scope = root.closest('product-info');
    var legacy = scope ? scope.querySelector('[data-gio-legacy-customizer]') : null;
    state.selectedDesignCid = design.cid;
    var selectedContract = state.currentVariantContract;
    var selectedPolicy = (
      selectedContract && design.variantBinding &&
      variantBindingMatches(design.variantBinding, selectedContract)
    )
      ? cloneRenderPolicy(design.renderPolicy)
      : effectiveRenderPolicyForContract(state, selectedContract);
    state.activeRenderPolicy = cloneRenderPolicy(selectedPolicy);
    state.variantNeedsReadjustment = Boolean(
      design.renderPolicy && !sameRenderShape(design.renderPolicy, selectedPolicy)
    );
    root.dataset.aiMode = 'ai';
    root.dataset.aiState = 'ready';
    if (legacy) legacy.hidden = true;
    setReadyDesignProperties(root, design);
    var finalImage = root.querySelector('[data-ai-final-image]');
    if (finalImage) finalImage.src = design.previewFinalUrl;
    syncBrowserPreviewOverlay(
      root,
      state,
      document.getElementById(root.dataset.productFormId),
      false
    );
    setFinalState(root, 'ready');
    var finalTitle = root.querySelector('[data-ai-final-title]');
    if (finalTitle) finalTitle.textContent = design.styleLabel || 'AI artwork';
    var finalMessage = root.querySelector('[data-ai-final-message]');
    if (finalMessage) finalMessage.textContent = '';
    var addButton = root.querySelector('[data-ai-add]');
    if (addButton) addButton.textContent = 'Add to cart';
    var launcher = root.querySelector('[data-ai-open]');
    if (launcher) launcher.hidden = true;
    renderDesignGallery(root, state);
    syncSelectedDesignCartState(root, state);
    setStatus(
      root,
      state.selectedDesignInCart
        ? 'Already in cart.'
        : '',
      'page'
    );
    setBusy(root, state.busy);
    syncVariantAdjustmentUi(root, state);
    if (!options || options.announce !== false) {
      var modeHelp = root.querySelector('[data-ai-mode-help]');
      if (modeHelp) modeHelp.textContent = (design.styleLabel || 'AI design') + ' selected.';
    }
    return true;
  }

  function applyRegularMode(root, state) {
    var scope = root.closest('product-info');
    var legacy = scope ? scope.querySelector('[data-gio-legacy-customizer]') : null;
    root.dataset.aiMode = 'regular';
    root.dataset.aiState = 'regular';
    setDraftReturnState(root, state, false);
    clearOrderProperties(root);
    setFinalState(root, 'hidden');
    var launcher = root.querySelector('[data-ai-open]');
    if (launcher) launcher.hidden = true;
    if (legacy) legacy.hidden = false;
    trackAiEvent(root, 'fallback_standard', { source: 'customer_choice' });
    closeAiDialog(root, state, false);
    renderDesignGallery(root, state);
    setStatus(root, '', 'page');
    setBusy(root, false);
    var legacyButton = legacy
      ? legacy.querySelector('#customButton, #inputImage, .product-info_atc_btn')
      : null;
    if (legacyButton && typeof legacyButton.focus === 'function') {
      window.requestAnimationFrame(function () { focusWithoutScrolling(legacyButton); });
    }
    return true;
  }

  function chooseRegularMode(root, state) {
    if (root.dataset.aiMode === 'regular') return false;
    if (state.busy || state.finalizingDraftCid || !state.sessionReady) return false;
    setBusy(root, true);
    persistProductSelection(root, state, 'STANDARD').then(function () {
      setBusy(root, false);
      rememberPhotoMode(root, 'regular');
      applyRegularMode(root, state);
    }).catch(function (error) {
      setBusy(root, false);
      setStatus(root, error.message || 'Standard upload could not be selected.', 'page', true);
    });
    return true;
  }

  function focusProductOptions(root) {
    var scope = root.closest('product-info');
    if (!scope) return;
    var picker = scope.querySelector('variant-selects');
    var target = picker
      ? picker.querySelector('input:not([disabled]), select:not([disabled]), button:not([disabled])')
      : null;
    if (!target) {
      target = scope.querySelector('.quantity__input:not([disabled])');
      picker = target ? target.closest('.product-form__quantity') : null;
    }
    if (picker && typeof picker.scrollIntoView === 'function') {
      picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    if (target) window.setTimeout(function () { focusWithoutScrolling(target); }, 350);
  }

  function setFinalState(root, mode) {
    var final = root.querySelector('[data-ai-final]');
    var readyActions = root.querySelector('[data-ai-ready-actions]');
    var addedActions = root.querySelector('[data-ai-added-actions]');
    var checkCart = root.querySelector('[data-ai-check-cart]');
    var badge = root.querySelector('[data-ai-final-badge]');
    var title = root.querySelector('[data-ai-final-title]');
    var message = root.querySelector('[data-ai-final-message]');
    if (mode === 'ready') {
      final.hidden = false;
      readyActions.hidden = false;
      addedActions.hidden = true;
      if (checkCart) checkCart.hidden = true;
      badge.textContent = 'Ready';
      title.textContent = 'AI artwork';
      message.textContent = '';
      return;
    }
    if (mode === 'added') {
      final.hidden = false;
      readyActions.hidden = true;
      addedActions.hidden = false;
      if (checkCart) checkCart.hidden = true;
      badge.textContent = 'In cart';
      title.textContent = 'Added to cart';
      message.textContent = 'This design is in your cart.';
      return;
    }
    final.hidden = true;
    readyActions.hidden = false;
    addedActions.hidden = true;
    if (checkCart) checkCart.hidden = true;
  }

  function resetForNewDesign(root, state, openDialog, trigger) {
    updateVariantAvailability(root, state, document.getElementById(root.dataset.productFormId));
    if (state.variantUnavailable) { setBusy(root, state.busy); return false; }
    if (state.busy || state.finalizingDraftCid || state.cartSubmitting || state.cartUnknown) return false;
    var selected = readyDesignByCid(state, state.selectedDesignCid);
    state.runNumber += 1;
    var runNumber = state.runNumber;
    abortActiveUpload(state);
    destroyEditor(state);
    setDraftReturnState(root, state, false);
    revokeFilePreview(state);
    // Starting another design changes only transient editor state. Persisted
    // cards remain in the capacity-limited server manifest until the customer uses
    // an explicit Delete action for that CID.
    releaseWorkingDraftSelection(state);
    // A deferred old file selection must not repopulate the new-photo flow.
    state.deferredPhotoSelection = null;
    state.deferredPhotoSequence = Number(state.deferredPhotoSequence || 0) + 1;
    state.cid = '';
    state.token = '';
    state.originalKey = '';
    state.originalUrl = '';
    state.jobStyleId = '';
    state.jobStyleLabel = '';
    state.rotation = 0;
    state.flipX = false;
    state.previewAttempt = 0;
    state.previewFailed = false;
    state.previewSettlementPending = false;
    state.previewFinalFailed = false;
    state.cartSubmitting = false;
    state.cartUnknown = false;
    delete root.dataset.aiCartState;
    state.pendingPreviewUrl = '';
    state.pendingPreviewRunNumber = 0;
    state.editorOpening = false;
    state.expectedPreviewSeconds = DEFAULT_EXPECTED_PREVIEW_SECONDS;
    state.slowAfterSeconds = DEFAULT_SLOW_AFTER_SECONDS;
    state.publicPhase = '';
    state.publicPhaseStartedAt = 0;
    state.timing = null;
    var fileInput = root.querySelector('[data-ai-file]');
    if (fileInput) fileInput.value = '';
    setPhotoSource(root, state, emptyPhotoSource());
    root.dataset.aiMode = 'ai';
    var scope = root.closest('product-info');
    var legacy = scope ? scope.querySelector('[data-gio-legacy-customizer]') : null;
    if (legacy) legacy.hidden = true;
    var finalImage = root.querySelector('[data-ai-final-image]');
    var editorImage = root.querySelector('[data-ai-editor-image]');
    if (finalImage && !selected) finalImage.removeAttribute('src');
    if (editorImage) editorImage.removeAttribute('src');
    var launcher = root.querySelector('[data-ai-open]');
    if (launcher) {
      launcher.hidden = false;
      launcher.textContent = 'Create AI artwork';
    }
    root.querySelector('[data-ai-generate]').textContent = 'Generate preview';
    state.previewReadyPending = false;
    setDesignLibraryOpen(root, state, false, false);
    endEstimatedProgress(root, state, false);
    setDialogView(root, 'setup');
    setStatus(root, '', 'setup');
    if (selected) {
      applyReadyDesign(root, state, selected, { announce: false });
      setStatus(root, '', 'page');
    } else {
      clearOrderProperties(root);
      root.dataset.aiState = 'active';
      setFinalState(root, 'hidden');
      setStatus(root, '', 'page');
      renderDesignGallery(root, state);
    }
    setBusy(root, false);
    if (openDialog) {
      openAiDialog(root, state, trigger || launcher);
      settleResetSetupDialog(root, state, runNumber, Boolean(trigger));
    }
    return true;
  }

  function resetPersonalizationQuantity(root) {
    var productForm = document.getElementById(root.dataset.productFormId);
    var scope = root.closest('product-info');
    var quantity = scope && scope.querySelector('.quantity__input[name="quantity"]');
    if (!quantity && productForm) quantity = productForm.querySelector('[name="quantity"]');
    if (!quantity) return;
    // The rule minimum is the valid starting quantity (normally one). It also
    // respects products whose Shopify rule starts above one.
    var minimum = Math.max(1, Number(quantity.dataset.min || quantity.min || 1));
    if (!Number.isFinite(minimum)) minimum = 1;
    quantity.value = String(minimum);
    quantity.dispatchEvent(new window.Event('change', { bubbles: true }));
  }

  function preserveDetachedJobForNewDesign(root, state) {
    var cid = String(state.cid || '');
    if (!CID_PATTERN.test(cid) || workingDraftByCid(state, cid) || readyDesignByCid(state, cid)) {
      return Promise.resolve();
    }
    // A generated/failed job may not have opened its editor yet. Recover its
    // existing saved card before releasing transient state, so its slot and
    // preview remain visible. This GET never generates or deletes an image.
    var expectedRun = state.runNumber;
    return apiRequest(root, '/ai/session/products/' +
      encodeURIComponent(String(root.dataset.productId || '')), 'GET').then(function (manifest) {
      assertManifestCapacity(manifest);
      if (!state.sessionReady || state.sessionFailed || state.runNumber !== expectedRun ||
          state.cid !== cid || !Number.isInteger(manifest.revision) || manifest.revision < 0 ||
          String(manifest.productId || '') !== String(root.dataset.productId || '')) {
        throw new Error('Your saved designs changed. Reopen My designs before starting another.');
      }
      var saved = null;
      manifest.designs.forEach(function (item) {
        if (String(item.cid || '') === cid) saved = normalizeSessionDesign(root, item);
      });
      if (!saved) {
        throw new Error('Your current preview is still being saved. Open it or refresh before starting another.');
      }
      if (saved.previewFinalUrl) state.designs.push(saved);
      else state.workingDrafts.push(saved);
      renderDesignGallery(root, state);
    });
  }

  function startAnotherPersonalization(root, state, trigger) {
    if (!state || state.busy || state.finalizingDraftCid || state.cartSubmitting ||
        state.cartUnknown || state.cartChecking || state.sessionRestoring || !state.sessionReady) {
      return Promise.resolve(false);
    }
    updateVariantAvailability(root, state, document.getElementById(root.dataset.productFormId));
    if (state.variantUnavailable) {
      setBusy(root, state.busy);
      return Promise.resolve(false);
    }
    var draft = workingDraftByCid(state, state.activeWorkingDraftCid);
    if (state.cropper && draft && !draft.persistError) {
      draft = updateWorkingDraftFromEditor(root, state, false) || draft;
    }
    // A paused unsaved draft remains in My designs. Only its existing pending
    // writes are awaited; no saved preview or cart item is deleted here.
    var save = draft && !draft.persistError
      ? flushPendingDraftSave(root, state, draft, false)
      : Promise.resolve();
    setBusy(root, true);
    return save.then(function () {
      return preserveDetachedJobForNewDesign(root, state);
    }).then(function () {
      setBusy(root, false);
      if (!resetForNewDesign(root, state, true, trigger)) return false;
      resetPersonalizationQuantity(root);
      if (designSlotCount(state) >= MAX_WORKING_DRAFTS) {
        setDesignLibraryOpen(root, state, true, true);
        setStatus(root, designLimitReachedMessage() +
          ' Deleting a saved design does not remove an item from your cart.', 'setup');
      } else {
        setStatus(root, 'Choose a new photo. Your other designs and cart items stay saved.', 'setup');
        // The settled dialog focuses the real file input, which remains
        // keyboard accessible even when its visible upload label is styled.
      }
      return true;
    }).catch(function (error) {
      setBusy(root, false);
      setStatus(root, error.message || 'Your current preview could not be saved. Please retry.',
        dialogIsOpen(root.querySelector('[data-ai-dialog]')) ? 'editor' : 'page', true);
      return false;
    });
  }

  function prepareAnotherStyle(root, state, openDialog, startUnselectedAi, dialogTrigger) {
    if (state.busy || state.finalizingDraftCid) return false;
    // Switching from Standard to the Studio must not silently restore an older
    // AI result. Existing callers that are already in AI mode keep their
    // selected design protected while a new style is attempted.
    var preserveRegularMode = root.dataset.aiMode === 'regular' && !startUnselectedAi;
    state.runNumber += 1;
    var runNumber = state.runNumber;
    abortActiveUpload(state);
    destroyEditor(state);
    endEstimatedProgress(root, state, false);
    state.cid = '';
    state.token = '';
    state.originalKey = '';
    state.originalUrl = '';
    state.jobStyleId = '';
    state.jobStyleLabel = '';
    state.rotation = 0;
    state.flipX = false;
    state.previewAttempt = 0;
    state.previewFailed = false;
    state.previewSettlementPending = false;
    state.previewFinalFailed = false;
    state.pendingPreviewUrl = '';
    state.pendingPreviewRunNumber = 0;
    state.previewReadyPending = false;
    state.editorOpening = false;
    setDraftReturnState(root, state, false);
    state.expectedPreviewSeconds = DEFAULT_EXPECTED_PREVIEW_SECONDS;
    state.slowAfterSeconds = DEFAULT_SLOW_AFTER_SECONDS;
    state.publicPhase = '';
    state.publicPhaseStartedAt = 0;
    state.timing = null;
    if (startUnselectedAi) state.selectedDesignCid = '';
    var scope = root.closest('product-info');
    var legacy = scope ? scope.querySelector('[data-gio-legacy-customizer]') : null;
    var selected = readyDesignByCid(state, state.selectedDesignCid);
    var source = currentPhotoSource(state);
    if (source.kind === 'local') {
      try {
        validateOriginalFile(source.file);
        setPhotoSource(root, state, source, { incrementRevision: false });
      } catch (_error) {
        setPhotoSource(root, state, emptyPhotoSource());
      }
    } else if (selected && !startUnselectedAi) {
      // A chosen design is the authoritative saved-photo source for "another
      // style" unless the customer has explicitly picked a newer local file.
      setPhotoSourceFromDraft(root, state, selected);
    } else if (source.kind === 'saved' && !photoSourceDraft(state, source)) {
      setPhotoSource(root, state, emptyPhotoSource());
    } else if (source.kind === 'saved') {
      renderPhotoSourceUi(root, state);
    } else {
      renderPhotoSourceUi(root, state);
    }
    if (preserveRegularMode) {
      root.dataset.aiMode = 'regular';
      root.dataset.aiState = 'regular';
      clearOrderProperties(root);
      setFinalState(root, 'hidden');
      if (legacy) legacy.hidden = false;
    } else if (selected && !startUnselectedAi) {
      applyReadyDesign(root, state, selected, { announce: false });
    } else {
      root.dataset.aiMode = 'ai';
      clearOrderProperties(root);
      root.dataset.aiState = 'active';
      setFinalState(root, 'hidden');
      if (legacy) legacy.hidden = true;
    }
    var editorImage = root.querySelector('[data-ai-editor-image]');
    if (editorImage) editorImage.removeAttribute('src');
    var generate = root.querySelector('[data-ai-generate]');
    if (generate) generate.textContent = 'Generate preview';
    setDialogView(root, 'setup');
    setStatus(root, '', 'setup');
    setStatus(root, '', 'page');
    renderDesignGallery(root, state);
    setBusy(root, false);
    if (openDialog) {
      openAiDialog(
        root,
        state,
        dialogTrigger || document.activeElement || root.querySelector('[data-ai-open]')
      );
      settleResetSetupDialog(root, state, runNumber);
    }
    return true;
  }

  function reviewAnotherStyleAfterSave(root, state) {
    if (!state.cropper || state.busy || state.cartSubmitting || state.cartUnknown) return;
    var sourceDraft = updateWorkingDraftFromEditor(root, state, true);
    captureEditorViewSnapshot(root, state);
    sourceDraft = sourceDraft || workingDraftByCid(state, state.activeWorkingDraftCid);
    setPhotoSourceFromDraft(root, state, sourceDraft);
    setDraftReturnState(root, state, true);
    setDialogView(root, 'setup');
    var generate = root.querySelector('[data-ai-generate]');
    if (generate) generate.textContent = 'Generate preview';
    setStatus(
      root,
      designSlotCount(state) >= MAX_WORKING_DRAFTS
        ? designLimitReachedMessage()
        : photoSourceIsReady(state)
        ? ''
        : 'Open a saved preview to continue.',
      'setup'
    );
    setBusy(root, false);
    if (photoSourceIsReady(state)) revealSetupAction(root, state, false);
    settleResetSetupDialog(root, state, state.runNumber);
  }

  function reviewAnotherStyle(root, state) {
    if (!state.cropper || state.busy || state.cartSubmitting || state.cartUnknown) return;
    var draft = updateWorkingDraftFromEditor(root, state, true);
    if (!draft) return;
    setBusy(root, true);
    flushPendingDraftSave(root, state, draft, false).then(function () {
      setBusy(root, false);
      reviewAnotherStyleAfterSave(root, state);
    }).catch(function (error) {
      setBusy(root, false);
      setStatus(root, error.message || 'This preview could not be saved securely.', 'editor', true);
    });
  }

  function returnToCurrentPreview(root, state) {
    if (!state.cropper || state.busy || state.cartSubmitting || state.cartUnknown) return;
    var runNumber = state.runNumber;
    var expectedCid = state.cid;
    var returnDraft = workingDraftByCid(state, state.activeWorkingDraftCid);
    if (returnDraft) setPhotoSourceFromDraft(root, state, returnDraft);
    setDraftReturnState(root, state, false);
    setDialogView(root, 'editor');
    resetDialogViewport(root);
    setStatus(
      root,
      'Restoring your securely saved preview and adjustments…',
      'editor'
    );
    setBusy(root, true);
    var heading = root.querySelector('[data-ai-editor-heading]');
    beginDraftSyncSuppression(state, expectedCid, runNumber);
    window.requestAnimationFrame(function () {
      try {
        if (!returnDraft || returnDraft.cid !== expectedCid) {
          throw new Error('The securely saved preview is unavailable.');
        }
        assertRestorableWorkingDraft(root, returnDraft);
      } catch (error) {
        endDraftSyncSuppression(state, expectedCid, runNumber);
        destroyEditor(state);
        setDraftReturnState(root, state, false);
        setDialogView(root, 'setup');
        renderWorkingDrafts(root, state);
        setBusy(root, false);
        setStatus(
          root,
          (error.message || 'The preview could not be restored.') +
            ' Your securely saved preview is still available below—select it to reopen.',
          'setup',
          true
        );
        settleResetSetupDialog(root, state, runNumber);
        return;
      }
      settleWorkingDraftEditorViewport(
        root,
        state,
        runNumber,
        expectedCid,
        function (settled, settleReason) {
          if (!settled) {
            endDraftSyncSuppression(state, expectedCid, runNumber);
            if (runNumber === state.runNumber) {
              if (settleReason === 'deferred') {
                setBusy(root, false);
                updateLauncher(root, state);
                return;
              }
              destroyEditor(state);
              setDraftReturnState(root, state, false);
              setDialogView(root, 'setup');
              renderWorkingDrafts(root, state);
              setBusy(root, false);
              setDesignLibraryOpen(root, state, true, true);
              setStatus(
                root,
                'The editor layout was not stable. Select the saved design to try opening it again.',
                'setup',
                true
              );
            }
            return;
          }
          try {
            restoreWorkingDraftSnapshot(root, state, returnDraft);
            state.editorViewSnapshot = null;
          } catch (error) {
            endDraftSyncSuppression(state, expectedCid, runNumber);
            destroyEditor(state);
            setDraftReturnState(root, state, false);
            setDialogView(root, 'setup');
            renderWorkingDrafts(root, state);
            setBusy(root, false);
            setDesignLibraryOpen(root, state, true, true);
            setStatus(
              root,
              error.message || 'The preview could not be positioned safely.',
              'setup',
              true
            );
            return;
          }
          endDraftSyncSuppression(state, expectedCid, runNumber);
          setBusy(root, false);
          setStatus(
            root,
            'Securely saved preview restored. Continue adjusting it or use it when ready.',
            'editor'
          );
          focusWithoutScrolling(heading);
        }
      );
    });
  }

  function restoreSelectedDesignAfterFailure(root, state, error) {
    var selected = readyDesignByCid(state, state.selectedDesignCid);
    if (!selected || root.dataset.aiMode === 'regular') return false;
    abortActiveUpload(state);
    endEstimatedProgress(root, state, false);
    destroyEditor(state);
    setDraftReturnState(root, state, false);
    state.pendingPreviewUrl = '';
    state.pendingPreviewRunNumber = 0;
    state.previewReadyPending = false;
    state.editorOpening = false;
    state.cid = '';
    state.token = '';
    state.originalKey = '';
    state.originalUrl = '';
    state.jobStyleId = '';
    state.jobStyleLabel = '';
    state.previewFailed = false;
    state.previewFinalFailed = false;
    applyReadyDesign(root, state, selected, { announce: false });
    closeAiDialog(root, state, false);
    setStatus(
      root,
      'We could not create the new preview. Your previously selected AI design is still saved and selected.',
      'page',
      true
    );
    setStatus(root, error && error.message ? error.message : 'The new preview could not be created.', 'setup');
    setBusy(root, false);
    return true;
  }

  function recoverRuntimeFailure(root, state, error) {
    trackAiEvent(root, 'ai_error', { reason: 'runtime' });
    if (restoreSelectedDesignAfterFailure(root, state, error)) return true;
    state.runNumber += 1;
    abortActiveUpload(state);
    endEstimatedProgress(root, state, false);
    destroyEditor(state);
    setDraftReturnState(root, state, false);
    state.cid = '';
    state.token = '';
    state.originalKey = '';
    state.originalUrl = '';
    state.jobStyleId = '';
    state.jobStyleLabel = '';
    state.previewAttempt = 0;
    state.previewFailed = false;
    state.previewFinalFailed = false;
    state.pendingPreviewUrl = '';
    state.pendingPreviewRunNumber = 0;
    state.previewReadyPending = false;
    state.editorOpening = false;
    state.cartSubmitting = false;
    state.cartUnknown = false;
    delete root.dataset.aiCartState;

    var source = currentPhotoSource(state);
    if (source.kind === 'local') {
      try {
        validateOriginalFile(source.file);
      } catch (_fileError) {
        setPhotoSource(root, state, emptyPhotoSource());
      }
    } else if (source.kind === 'saved' && !photoSourceDraft(state, source)) {
      setPhotoSource(root, state, emptyPhotoSource());
    }
    renderPhotoSourceUi(root, state);

    var scope = root.closest('product-info');
    var legacy = scope ? scope.querySelector('[data-gio-legacy-customizer]') : null;
    clearOrderProperties(root);
    setFinalState(root, 'hidden');
    if (root.dataset.aiMode === 'regular') {
      root.dataset.aiState = 'regular';
      if (legacy) legacy.hidden = false;
    } else {
      root.dataset.aiMode = 'ai';
      root.dataset.aiState = 'active';
      if (legacy) legacy.hidden = true;
    }
    root.hidden = false;
    setDialogView(root, 'setup');
    var generate = root.querySelector('[data-ai-generate]');
    if (generate) generate.textContent = 'Try again';
    renderDesignGallery(root, state);
    setStatus(
      root,
      'We could not finish this preview. Your saved designs are safe. Try again or switch to Standard photo upload.',
      'setup',
      true
    );
    setStatus(
      root,
      'The new preview was not completed. Your saved designs are safe.',
      'page',
      true
    );
    setBusy(root, false);
    if (error && error.message) console.warn('GiftInOne AI runtime error:', error.message);
    return true;
  }

  function fallbackToLegacy(root, message, focusNotice) {
    trackAiEvent(root, 'fallback_standard', { source: 'system_fallback' });
    if (message) trackAiEvent(root, 'ai_error', { reason: 'bootstrap' });
    var state = root._giftInOneAiState;
    if (state) {
      state.runNumber += 1;
      abortActiveUpload(state);
      endEstimatedProgress(root, state, false);
      // Synchronously close an inner destructive-action layer before closing
      // the native dialog. This prevents its rejected Promise from re-enabling
      // hidden buttons or stealing focus during the dialog close event.
      dismissActionConfirmation(root, false, null, true);
      closeAiDialog(root, state, false);
      destroyEditor(state);
      setDraftReturnState(root, state, false);
      clearWorkingDrafts(state);
      state.cid = '';
      state.token = '';
      state.originalKey = '';
      state.originalUrl = '';
      state.deferredPhotoSelection = null;
      state.deferredPhotoSequence = Number(state.deferredPhotoSequence || 0) + 1;
      setPhotoSource(root, state, emptyPhotoSource(), { render: false });
      state.sessionReady = false;
      state.sessionRestoring = false;
    }
    clearOrderResetReceipt(root, state);
    clearOrderProperties(root);
    var scope = root.closest('product-info');
    var legacy = scope ? scope.querySelector('[data-gio-legacy-customizer]') : null;
    if (legacy) {
      legacy.hidden = false;
      if (message) {
        var oldNotice = scope.querySelector('[data-gio-ai-fallback-notice]');
        if (oldNotice) oldNotice.remove();
        var notice = document.createElement('p');
        notice.className = 'giftinone-ai-fallback-notice';
        notice.setAttribute('data-gio-ai-fallback-notice', '');
        notice.setAttribute('role', 'alert');
        notice.textContent =
          'AI preview is temporarily unavailable. Standard photo upload has been restored. Refresh this page before trying AI again.';
        legacy.parentNode.insertBefore(notice, legacy);
        if (focusNotice) {
          notice.tabIndex = -1;
          window.requestAnimationFrame(function () {
            if (notice.isConnected !== false) focusWithoutScrolling(notice);
          });
        }
      }
    }
    root.hidden = true;
    root.dataset.aiState = 'fallback';
    if (message) console.warn('GiftInOne AI fallback:', message);
  }

  function syncEditorPrimaryAction(root, state) {
    var approve = root.querySelector('[data-ai-approve]');
    if (!approve) return;
    var label = approve.querySelector('[data-ai-approve-label]');
    var draft = workingDraftByCid(state, state.activeWorkingDraftCid);
    var saving = Boolean(
      draft && !draft.persistError && (
        draft.persisting || draft.persistPromise ||
        state.draftPersistingCid === draft.cid ||
        state.draftSaveTimerCid === draft.cid ||
        Number(draft.persistedLocalRevision || 0) < Number(draft.revision || 0)
      )
    );
    var savePaused = Boolean(draft && draft.persistError);
    var preparing = Boolean(state.finalizingDraftCid);
    var text = state.variantUnavailable && !preparing
      ? variantAvailabilityLabel(state)
      : state.editorInvalid
      ? 'Reload this design'
      : preparing
      ? 'Preparing design…'
      : savePaused
        ? 'Edit to retry saving'
      : saving
        ? 'Saving changes…'
        : previewOverlayBlocksAction(state, 'confirm')
          ? 'Clock preview required'
          : 'Add this design to cart';
    if (label) label.textContent = text;
    else approve.textContent = text;
    markStockBlocked(approve, state.variantUnavailable && !preparing);
    if (preparing || saving) approve.setAttribute('aria-busy', 'true');
    else approve.removeAttribute('aria-busy');
  }

  function setBusy(root, busy) {
    var state = root._giftInOneAiState || {};
    state.busy = Boolean(busy);
    var productForm = document.getElementById(root.dataset.productFormId);
    if (productForm) updateVariantAvailability(root, state, productForm);
    var activeDraftInCart = Boolean(
      state.activeWorkingDraftCid &&
      state.cartCustomizationCids &&
      state.cartCustomizationCids[state.activeWorkingDraftCid]
    );
    var hasPendingPreview = Boolean(
      state.pendingPreviewUrl && !state.cropper
    );
    ['[data-ai-style]', '[data-ai-style-choice]', '[data-ai-file]',
      '[data-ai-new]', '[data-ai-another]', '[data-ai-try-style]',
      '[data-ai-mode-ai]', '[data-ai-use-legacy]', '[data-ai-another-style]',
      '[data-ai-return-preview]', '[data-ai-zoom-out]', '[data-ai-zoom-in]',
      '[data-ai-rotate-left]', '[data-ai-rotate-right]', '[data-ai-flip-x]',
      '[data-ai-reset]', '[data-ai-working-draft-select]',
      '[data-ai-working-draft-remove]', '[data-ai-design-remove]',
      '[data-ai-restored-job-poll]', '[data-ai-restored-job-retry]',
      '[data-ai-restored-job-open]',
      '[data-ai-clear-drafts]', '[data-ai-library-toggle]',
      '[data-ai-library-close]', '[data-ai-library-create]', '[data-ai-manage-designs]',
      '[data-ai-edit]', '[data-ai-view-library]',
      '[data-ai-style-toggle]'].forEach(function (selector) {
      Array.prototype.forEach.call(root.querySelectorAll(selector), function (node) {
        var pendingSelectionLocked = Boolean(
          hasPendingPreview && (
            selector === '[data-ai-style]' ||
            selector === '[data-ai-style-choice]' ||
            selector === '[data-ai-file]' ||
            selector === '[data-ai-style-toggle]'
          )
        );
        var libraryCreateLocked = Boolean(
          node.hasAttribute && node.hasAttribute('data-ai-library-create') && (
            designSlotCount(state) >= MAX_WORKING_DRAFTS ||
            state.canGenerate === false || state.stylesReady === false ||
            state.variantUnavailable
          )
        );
        var editorTransformLocked = Boolean(
          (state.editorInvalid || activeDraftInCart) && [
            '[data-ai-zoom-out]', '[data-ai-zoom-in]',
            '[data-ai-rotate-left]', '[data-ai-rotate-right]',
            '[data-ai-flip-x]', '[data-ai-reset]'
          ].indexOf(selector) !== -1
        );
        var workingDraftLocked = Boolean(
          selector === '[data-ai-working-draft-select]' && (
            state.cartChecking ||
            state.cartCustomizationCids &&
            state.cartCustomizationCids[String(node.dataset.aiWorkingDraftSelect || '')]
          )
        );
        node.disabled = Boolean(
          busy || state.cartSubmitting || state.cartUnknown ||
          state.sessionRestoring || pendingSelectionLocked ||
          libraryCreateLocked || editorTransformLocked || workingDraftLocked ||
          (selector === '[data-ai-another]' && (state.cartChecking || state.variantUnavailable)) ||
          (selector === '[data-ai-new]' && state.variantUnavailable)
        );
      });
    });
    Array.prototype.forEach.call(
      root.querySelectorAll('[data-ai-close], [data-ai-done-later], [data-ai-open]'),
      function (node) {
        node.disabled = Boolean(state.finalizingDraftCid);
      }
    );
    var editorLocked = Boolean(
      busy || state.cartSubmitting || state.cartUnknown || state.editorInvalid ||
      activeDraftInCart
    );
    if (state.cropper) {
      if (editorLocked && typeof state.cropper.disable === 'function') {
        state.cropper.disable();
      } else if (!editorLocked && typeof state.cropper.enable === 'function') {
        state.cropper.enable();
      }
    }
    var fileInput = root.querySelector('[data-ai-file]');
    Array.prototype.forEach.call(
      root.querySelectorAll('[data-ai-file-trigger]'),
      function (node) {
        var triggerLocked = Boolean(
          busy || state.cartSubmitting || state.cartUnknown ||
          state.sessionRestoring || hasPendingPreview
        );
        node.setAttribute('aria-disabled', triggerLocked ? 'true' : 'false');
      }
    );
    var generate = root.querySelector('[data-ai-generate]');
    var manage = root.querySelector('[data-ai-manage-designs]');
    var manageNeeded = Boolean(
      !hasPendingPreview && !state.previewFailed &&
      designSlotCount(state) >= MAX_WORKING_DRAFTS
    );
    if (generate) {
      generate.hidden = manageNeeded;
      generate.disabled = Boolean(
        busy || state.cartSubmitting || state.sessionRestoring ||
        (
          !hasPendingPreview && (
            state.variantUnavailable ||
            state.stylesReady === false ||
            state.canGenerate === false ||
            state.previewSettlementPending ||
            (!state.previewFailed && !photoSourceIsReady(state))
          )
        )
      );
      markStockBlocked(generate, state.variantUnavailable && !hasPendingPreview);
    }
    if (manage) {
      manage.hidden = !manageNeeded;
      manage.disabled = Boolean(
        busy || state.cartSubmitting || state.cartUnknown ||
        state.sessionRestoring || state.finalizingDraftCid
      );
    }
    var add = root.querySelector('[data-ai-add]');
    if (add) {
      add.disabled = Boolean(
        busy || state.cartSubmitting || state.cartUnknown ||
        state.cartChecking || state.selectedDesignInCart ||
        state.variantUnavailable || previewOverlayBlocksAction(state, 'cart') ||
        root.dataset.aiState !== 'ready'
      );
    }
    var edit = root.querySelector('[data-ai-edit]');
    if (edit) {
      var readyToEdit = readyDesignByCid(state, state.selectedDesignCid);
      edit.hidden = !readyToEdit || !readyToEdit.editableDraft;
      edit.disabled = Boolean(
        busy || state.cartSubmitting || state.cartUnknown || state.cartChecking ||
        state.selectedDesignInCart || state.variantUnavailable ||
        root.dataset.aiState !== 'ready'
      );
    }
    var approve = root.querySelector('[data-ai-approve]');
    if (approve) {
      approve.disabled = Boolean(
        busy || state.cartSubmitting || state.cartUnknown || !state.cropper ||
        state.cartChecking || activeDraftInCart || state.editorInvalid || state.variantUnavailable ||
        previewOverlayBlocksAction(state, 'confirm') ||
        state.draftPersistingCid === state.activeWorkingDraftCid ||
        state.draftSaveTimerCid === state.activeWorkingDraftCid ||
        Boolean(workingDraftByCid(state, state.activeWorkingDraftCid) &&
          workingDraftByCid(state, state.activeWorkingDraftCid).persistError)
      );
    }
    var another = root.querySelector('[data-ai-another]');
    if (another) {
      another.textContent = state.variantUnavailable ? variantAvailabilityLabel(state) : 'Personalize another';
      markStockBlocked(another, state.variantUnavailable);
    }
    root.setAttribute('aria-busy', busy ? 'true' : 'false');
    syncEditorPrimaryAction(root, state);
    if (!editorLocked) scheduleEditorViewportResize(root, state, false);
    syncSetupAction(root, state);
    syncDesignLibraryUi(root, state);
    updateLauncher(root, state);
    syncVariantAdjustmentUi(root, state);
    syncVariantAvailabilityNotice(root, state);
    flushDeferredPhotoSelection(root, state);
  }

  function applySelectedPhotoFile(root, state, file, shouldFocus) {
    var fileInput = root.querySelector('[data-ai-file]');
    if (!file) {
      // Browsers may emit change after the picker is cancelled. Cancellation
      // must not erase a previously valid local or saved source.
      renderPhotoSourceUi(root, state);
      syncSetupAction(root, state);
      return false;
    }
    // A generated preview belongs to the exact photo/style/run that created it.
    // Do not let a late or programmatic file-change orphan that preview while
    // the browser is waiting to reopen the editor.
    if (state.pendingPreviewUrl && !state.cropper) {
      if (fileInput) fileInput.value = '';
      renderPhotoSourceUi(root, state);
      setStatus(
        root,
        'Open the generated preview before changing the photo or style.',
        'setup',
        true
      );
      setBusy(root, false);
      return false;
    }
    if (state.busy || state.finalizingDraftCid || state.sessionRestoring) {
      return queuePhotoFileSelection(root, state, fileInput, file);
    }
    try {
      validateOriginalFile(file);
    } catch (error) {
      if (fileInput) {
        fileInput.value = '';
        fileInput.setAttribute('aria-invalid', 'true');
      }
      // Validation happens before the canonical source changes, so the last
      // known-good photo remains visible and usable.
      renderPhotoSourceUi(root, state);
      setStatus(root, error.message, 'setup', true);
      syncSetupAction(root, state);
      return false;
    }

    if (fileInput) fileInput.setAttribute('aria-invalid', 'false');
    var selectedDesignBeforeChange = readyDesignByCid(state, state.selectedDesignCid);
    var protectSelectedDesign = Boolean(
      selectedDesignBeforeChange &&
      root.dataset.aiMode === 'ai' &&
      (root.dataset.aiState === 'ready' || root.dataset.aiState === 'added')
    );
    var protectWorkingPreview = Boolean(
      state.reviewingAlternateStyle && state.cropper
    );
    if (!protectWorkingPreview && (state.previewFailed || state.cid || state.token)) {
      state.runNumber += 1;
      state.cid = '';
      state.token = '';
      state.originalKey = '';
      state.originalUrl = '';
      state.jobStyleId = '';
      state.jobStyleLabel = '';
      state.previewAttempt = 0;
      state.previewFailed = false;
      state.previewFinalFailed = false;
      state.cartUnknown = false;
      delete root.dataset.aiCartState;
    }

    setPhotoSource(root, state, {
      kind: 'local',
      file: file,
      label: file.name || 'Selected photo'
    });
    if (protectSelectedDesign) {
      applyReadyDesign(root, state, selectedDesignBeforeChange, { announce: false });
    } else if (root.dataset.aiMode === 'regular') {
      clearOrderProperties(root);
      root.dataset.aiState = 'regular';
      setFinalState(root, 'hidden');
      renderDesignGallery(root, state);
    } else {
      clearOrderProperties(root);
      root.dataset.aiState = 'active';
      setFinalState(root, 'hidden');
      renderDesignGallery(root, state);
    }
    setStatus(root, '', 'setup');
    syncSetupAction(root, state);
    revealSetupAction(root, state, Boolean(shouldFocus));
    return true;
  }

  // A completed proof is a flattened, immutable PNG. It is never the AI source.
  var COMPLETE_PREVIEW_SCHEMA = 'gio-complete-preview/v1';
  function expectedFinalUrl(cid, version) {
    if (!CID_PATTERN.test(String(cid || '')) || !/^[a-f0-9]{64}$/.test(String(version || ''))) return '';
    return 'https://static.giftinone.com/aiOrders/' + cid + '/preview/final-' + version + '.png';
  }
  // Stable final production output written by the paid-order workflow. This
  // includes the saved crop and any HD overlay; ai/hd.png is only the AI source.
  function expectedProductionUrl(cid) {
    if (!CID_PATTERN.test(String(cid || ''))) return '';
    return 'https://static.giftinone.com/aiOrders/' + cid + '/production/final.png';
  }
  function isCompleteFinal(value) {
    return Boolean(value && value.previewComplete === true &&
      value.previewSchema === COMPLETE_PREVIEW_SCHEMA &&
      expectedFinalUrl(value.cid, value.designVersion) &&
      value.previewFinalUrl === expectedFinalUrl(value.cid, value.designVersion));
  }
  function loadCompletePreview(value) {
    if (!isCompleteFinal(value)) return Promise.reject(new Error('The complete design preview could not be verified.'));
    return new Promise(function (resolve, reject) {
      var image = new Image();
      var finished = false;
      var timer = window.setTimeout(function () { finish(false); }, 20000);
      function finish(ok) {
        if (finished) return;
        finished = true;
        window.clearTimeout(timer);
        image.onload = null;
        image.onerror = null;
        if (!ok || image.naturalWidth < 1 || image.naturalHeight < 1 ||
            Math.max(image.naturalWidth, image.naturalHeight) > 1200) {
          var error = new Error('Your design is saved, but its complete preview could not be loaded. Please retry.');
          error.code = 'COMPLETE_PREVIEW_LOAD_FAILED';
          reject(error);
        } else {
          value.previewImageLoaded = true;
          resolve(value);
        }
      }
      image.onload = function () {
        if (typeof image.decode === 'function') image.decode().then(function () { finish(true); }, function () { finish(false); });
        else finish(true);
      };
      image.onerror = function () { finish(false); };
      image.src = value.previewFinalUrl;
    });
  }
  function waitForCompletePreview(root, state, response, contract) {
    var cid = String(response && response.cid || '');
    var version = String(response && response.designVersion || '');
    var startedAt = Date.now();
    var runNumber = state.runNumber;
    if (!CID_PATTERN.test(cid) || !/^[a-f0-9]{64}$/.test(version)) {
      return Promise.reject(new Error('The complete preview response is invalid.'));
    }
    function inspect(job) {
      if (state.runNumber !== runNumber || !state.sessionReady) throw new Error('AI_REQUEST_CANCELLED');
      if (!job || job.cid !== cid || job.designVersion !== version ||
          !variantBindingMatches(job.variantBinding, contract)) {
        throw new Error('The saved design version changed. Reopen My designs before continuing.');
      }
      if (job.status === 'READY_FOR_ORDER') return loadCompletePreview(job);
      if (job.status === 'PREVIEW_FINAL_FAILED') {
        throw new Error('Your photo is saved, but its final preview could not be completed. Please retry.');
      }
      if (['PREVIEW_FINAL_ACCEPTED', 'PREVIEW_FINAL_QUEUED', 'PREVIEW_FINAL_RUNNING'].indexOf(job.status) < 0) {
        throw new Error('This design is not ready to add to cart.');
      }
      if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
        throw new Error('Your final preview is still being prepared. Reopen My designs to check it before adding to cart.');
      }
      return delay(nextPollDelay(startedAt)).then(function () {
        return apiRequest(root, '/ai/jobs/' + encodeURIComponent(cid), 'GET', '');
      }).then(inspect);
    }
    return Promise.resolve().then(function () { return inspect(response); });
  }
  // The API forks confirmed versions server-side, reusing the unlayered AI
  // photo. An old cart item always keeps its old CID and frozen recipe.
  function adoptEditorRevision(root, state, parentCid, response) {
    if (!response || response.cid === parentCid) return parentCid;
    if (response.revisionParentCid !== parentCid || !CID_PATTERN.test(String(response.cid || '')) ||
        response.aiPreviewUrl !== expectedAiPreviewUrl(response.cid)) {
      throw new Error('The new design revision could not be verified.');
    }
    var cid = response.cid;
    var draft = workingDraftByCid(state, parentCid);
    if (!draft || state.cid !== parentCid || state.activeWorkingDraftCid !== parentCid) {
      throw new Error('The active design changed before saving.');
    }
    clearDraftSaveTimer(state);
    draft.cid = cid;
    draft.token = '';
    draft.previewUrl = response.aiPreviewUrl;
    draft.originalKey = '';
    draft.serverRevision = 0;
    draft.persistedLocalRevision = Number(draft.revision || 1);
    draft.persistError = false;
    draft.requiresCartOwnershipCheck = false;
    if (response.variantBinding) draft.variantBinding = response.variantBinding;
    state.designs = state.designs.filter(function (item) { return item.cid !== parentCid; });
    state.cid = cid;
    state.token = '';
    state.originalKey = '';
    state.activeWorkingDraftCid = cid;
    state.finalizingDraftCid = cid;
    state.editorPreviewUrl = response.aiPreviewUrl;
    if (state.pendingPreviewUrl) state.pendingPreviewUrl = response.aiPreviewUrl;
    if (state.selectedDesignCid === parentCid) state.selectedDesignCid = cid;
    if (state.restoringWorkingDraftCid === parentCid) state.restoringWorkingDraftCid = cid;
    renderWorkingDrafts(root, state);
    return cid;
  }
  function rememberPhotoMode(root, mode) {
    try { window.sessionStorage.setItem('gio-photo-mode-v17:' + root.dataset.productId, mode); } catch (_error) {}
  }
  function restoredPhotoMode(root) {
    try { return window.sessionStorage.getItem('gio-photo-mode-v17:' + root.dataset.productId) || ''; } catch (_error) { return ''; }
  }
  function chooseAiMode(root, state) {
    if (root.dataset.aiMode === 'ai' || state.busy || state.finalizingDraftCid ||
        state.cartSubmitting || state.cartUnknown || !state.sessionReady) return false;
    rememberPhotoMode(root, 'ai');
    var selected = readyDesignByCid(state, state.selectedDesignCid);
    function apply() {
      if (selected) applyReadyDesign(root, state, selected, { announce: false });
      else {
        root.dataset.aiMode = 'ai';
        root.dataset.aiState = 'active';
        clearOrderProperties(root);
        setFinalState(root, 'hidden');
        var scope = root.closest('product-info');
        var legacy = scope && scope.querySelector('[data-gio-legacy-customizer]');
        if (legacy) legacy.hidden = true;
        var launcher = root.querySelector('[data-ai-open]');
        if (launcher) launcher.hidden = false;
        renderDesignGallery(root, state);
      }
      setBusy(root, false);
      setStatus(root, '', 'page');
    }
    if (selected) {
      setBusy(root, true);
      persistProductSelection(root, state, 'AI', selected.cid).then(apply).catch(function (error) {
        setBusy(root, false);
        setStatus(root, error.message || 'Your AI design could not be selected.', 'page', true);
      });
    } else apply();
    return true;
  }

  function expectedAiPreviewUrl(cid) {
    return 'https://static.giftinone.com/aiOrders/' + cid + '/ai/preview.png';
  }

  function normalizeSessionDesign(root, item) {
    var cid = String(item && item.cid || '');
    var styleId = String(item && item.styleId || '');
    // Older test manifests stored the technical styleId as styleLabel. Keep
    // those cards customer-friendly without rewriting the immutable styleId.
    var styleLabel = displayStyleLabel(
      styleId,
      String(item && item.styleLabel || '').slice(0, 120),
      'AI design'
    ).slice(0, 120);
    var status = String(item && item.status || '');
    if (!CID_PATTERN.test(cid) || !styleId || styleId.length > 120) return null;
    var renderPolicy;
    try {
      renderPolicy = normalizePublicRenderPolicy(item);
    } catch (_renderPolicyError) {
      return null;
    }
    if (status === 'READY_FOR_ORDER') {
      if (!isCompleteFinal(item)) return null;
      var readyRecipe = cloneRecipe(item.recipe || item.draft && item.draft.recipe);
      var editableDraft = null;
      var readySavedDraft = item.draft;
      if (
        item.aiPreviewUrl === expectedAiPreviewUrl(cid) && readySavedDraft &&
        Number.isInteger(readySavedDraft.revision) && readySavedDraft.revision >= 0
      ) {
        var readyServerRevision = readySavedDraft.revision;
        var readyLocalRevision = readyServerRevision === 0 ? 1 : readyServerRevision;
        editableDraft = {
          cid: cid,
          token: '',
          status: 'PREVIEW_READY',
          previewUrl: item.aiPreviewUrl,
          originalKey: '',
          originalUrl: String(item.originalUrl || ''),
          styleId: styleId,
          styleLabel: styleLabel,
          rotation: Number(readySavedDraft.data && readySavedDraft.data.rotate || 0),
          flipX: Number(readySavedDraft.data && readySavedDraft.data.scaleX) < 0,
          data: cloneEditorValue(readySavedDraft.data),
          canvas: null,
          cropBox: null,
          container: null,
          recipe: cloneRecipe(readySavedDraft.recipe),
          renderPolicy: cloneRenderPolicy(renderPolicy),
          variantBinding: item.variantBinding,
          naturalWidth: Number(readySavedDraft.naturalWidth || item.naturalWidth),
          naturalHeight: Number(readySavedDraft.naturalHeight || item.naturalHeight),
          fileObject: null,
          revision: readyLocalRevision,
          serverRevision: readyServerRevision,
          // Revision 0 is a server-authoritative legacy recipe synthesized as
          // an editable draft. Mark that snapshot locally clean so merely
          // opening or deleting it never sends a migration PUT. The first
          // real edit still increments revision and saves against server 0.
          persistedLocalRevision: readyLocalRevision,
          updatedAt: Date.parse(readySavedDraft.updatedAt || '') || Date.now(),
          restoredFromSession: true,
          customerAdjusted: readyServerRevision > 1,
          autoCenteredRepair: false,
          requiresCartOwnershipCheck: true,
          persistError: false,
          persistErrorCode: ''
        };
        repairUnadjustedDraftCrop(root, editableDraft);
        try { assertRestorableWorkingDraft(root, editableDraft); }
        catch (_readyDraftError) { editableDraft = null; }
      }
      // A finished job may retain its old final.png while a reopened design
      // has a newer securely saved draft. Never expose that stale final image
      // for cart submission: restore the newer recipe as a working draft and
      // require one more finalization after the customer finishes adjusting.
      if (
        editableDraft && readyRecipe &&
        !recipesEquivalent(readyRecipe, editableDraft.recipe)
      ) return editableDraft;
      return {
        cid: cid,
        previewFinalUrl: item.previewFinalUrl,
        designVersion: item.designVersion,
        previewSchema: item.previewSchema,
        previewComplete: true,
        originalUrl: String(item.originalUrl || ''),
        originalKey: '',
        styleId: styleId,
        styleLabel: styleLabel,
        createdAt: Date.parse(item.updatedAt || '') || Date.now(),
        serverRevision: item.draft && Number.isInteger(item.draft.revision)
          ? item.draft.revision
          : 0,
        recipe: readyRecipe,
        renderPolicy: cloneRenderPolicy(renderPolicy),
        variantBinding: item.variantBinding,
        editableDraft: editableDraft,
        boundVariantId: String(
          item.variantBinding && item.variantBinding.variantId ||
          item.variantId || item.renderContract && item.renderContract.variantId || ''
        ),
        restoredFromSession: true
      };
    }
    if (
      status === 'UPLOAD_PENDING' || status === 'UPLOADED' ||
      status === 'PREVIEW_QUEUED' || status === 'PREVIEW_RUNNING' ||
      status === 'PREVIEW_FAILED'
    ) {
      var failure = item.error && typeof item.error === 'object' ? item.error : {};
      return {
        cid: cid,
        token: '',
        status: status,
        previewUrl: '',
        originalKey: '',
        originalUrl: String(item.originalUrl || ''),
        styleId: styleId,
        styleLabel: styleLabel,
        previewAttempt: Number(item.previewAttempt || 0),
        errorCode: String(failure.code || ''),
        errorRetryable: status !== 'UPLOAD_PENDING' && failure.retryable !== false,
        canRetryPreview: item.canRetryPreview !== false,
        serverRevision: 0,
        revision: 0,
        persistedLocalRevision: 0,
        renderPolicy: cloneRenderPolicy(renderPolicy),
        variantBinding: item.variantBinding,
        restoredFromSession: true
      };
    }
    if (
      (
        status !== 'PREVIEW_READY' &&
        status !== 'PREVIEW_FINAL_QUEUED' &&
        status !== 'PREVIEW_FINAL_RUNNING' &&
        status !== 'PREVIEW_FINAL_FAILED'
      ) ||
      item.aiPreviewUrl !== expectedAiPreviewUrl(cid)
    ) return null;
    var savedDraft = item.draft;
    if (!savedDraft || !Number.isInteger(savedDraft.revision) || savedDraft.revision < 0) return null;
    var serverRevision = savedDraft.revision;
    var localRevision = serverRevision === 0 ? 1 : serverRevision;
    var draft = {
      cid: cid,
      token: '',
      status: status,
      previewUrl: item.aiPreviewUrl,
      originalKey: '',
      originalUrl: String(item.originalUrl || ''),
      styleId: styleId,
      styleLabel: styleLabel,
      rotation: Number(savedDraft.data && savedDraft.data.rotate || 0),
      flipX: Number(savedDraft.data && savedDraft.data.scaleX) < 0,
      data: cloneEditorValue(savedDraft.data),
      canvas: null,
      cropBox: null,
      container: null,
      recipe: cloneRecipe(savedDraft.recipe),
      renderPolicy: cloneRenderPolicy(renderPolicy),
      variantBinding: item.variantBinding,
      naturalWidth: Number(savedDraft.naturalWidth || item.naturalWidth),
      naturalHeight: Number(savedDraft.naturalHeight || item.naturalHeight),
      fileObject: null,
      revision: localRevision,
      serverRevision: serverRevision,
      // See READY_FOR_ORDER above: a synthesized revision-0 snapshot is
      // already secure and must remain clean until the customer really edits.
      persistedLocalRevision: localRevision,
      updatedAt: Date.parse(savedDraft.updatedAt || '') || Date.now(),
      restoredFromSession: true,
      customerAdjusted: serverRevision > 1,
      autoCenteredRepair: false,
      requiresCartOwnershipCheck: status === 'PREVIEW_FINAL_FAILED',
      persistError: false,
      persistErrorCode: ''
    };
    repairUnadjustedDraftCrop(root, draft);
    try { assertRestorableWorkingDraft(root, draft); } catch (_error) { return null; }
    return draft;
  }

  function fetchRestoredWorkingDraft(root, state, cid) {
    if (!state.sessionReady || state.sessionFailed) {
      var inactiveError = new Error('The AI session is no longer active.');
      inactiveError.code = 'AI_SESSION_STALE';
      return Promise.reject(inactiveError);
    }
    var expectedRunNumber = state.runNumber;
    var productPath = '/ai/session/products/' +
      encodeURIComponent(String(root.dataset.productId || ''));
    return apiRequest(root, productPath, 'GET').then(function (manifest) {
      if (
        !state.sessionReady || state.sessionFailed ||
        expectedRunNumber !== state.runNumber
      ) {
        var staleError = new Error('The AI session changed before the design was restored.');
        staleError.code = 'AI_SESSION_STALE';
        throw staleError;
      }
      assertManifestCapacity(manifest);
      if (
        !manifest || String(manifest.productId || '') !== String(root.dataset.productId || '') ||
        !Number.isInteger(manifest.revision) || !Array.isArray(manifest.designs)
      ) throw new Error('The saved design response is invalid.');
      state.manifestRevision = manifest.revision;
      if (typeof manifest.canGenerate === 'boolean') state.canGenerate = manifest.canGenerate;
      applyQuota(root, state, manifest);
      var source = null;
      for (var index = 0; index < manifest.designs.length; index += 1) {
        if (String(manifest.designs[index].cid || '') === String(cid || '')) {
          source = manifest.designs[index];
          break;
        }
      }
      var restored = normalizeSessionDesign(root, source);
      if (!isEditableWorkingDraft(restored)) {
        throw new Error('This preview is not ready to open yet.');
      }
      var replaced = false;
      state.workingDrafts = state.workingDrafts.map(function (existing) {
        if (existing.cid !== restored.cid) return existing;
        scrubWorkingDraft(existing);
        replaced = true;
        return restored;
      });
      if (!replaced) state.workingDrafts.push(restored);
      renderWorkingDrafts(root, state);
      setBusy(root, false);
      return restored;
    });
  }

  function pollRestoredJob(root, state, cid) {
    var targetCid = String(cid || '');
    if (!CID_PATTERN.test(targetCid)) return Promise.resolve(false);
    if (state.restoredJobPolls[targetCid]) return state.restoredJobPolls[targetCid];
    var startedAt = Date.now();
    function stop(value) {
      delete state.restoredJobPolls[targetCid];
      return value;
    }
    function settleOpenedRestoredProgress(completed) {
      if (
        state.openRestoredJobCid !== targetCid ||
        state.openRestoredJobRunNumber !== state.runNumber
      ) return false;
      state.openRestoredJobCid = '';
      state.openRestoredJobRunNumber = 0;
      endEstimatedProgress(root, state, Boolean(completed));
      setBusy(root, false);
      return true;
    }
    function poll() {
      if (!state.sessionReady || state.sessionFailed) return stop(false);
      var target = workingDraftByCid(state, targetCid);
      if (!target || (
        target.status !== 'PREVIEW_QUEUED' && target.status !== 'PREVIEW_RUNNING' &&
        target.status !== 'PREVIEW_FINAL_QUEUED' &&
        target.status !== 'PREVIEW_FINAL_RUNNING' &&
        !(target.status === 'PREVIEW_FAILED' && target.canRetryPreview === false)
      )) {
        settleOpenedRestoredProgress(false);
        return stop(false);
      }
      return apiRequest(root, '/ai/jobs/' + encodeURIComponent(targetCid), 'GET', '').then(function (job) {
        if (!state.sessionReady || state.sessionFailed) return stop(false);
        assertJobOperationIdentity(job, targetCid, target.styleId);
        applyQuota(root, state, job);
        if (job.status === 'PREVIEW_FAILED' && job.canRetryPreview === false) {
          target.status = 'PREVIEW_FAILED';
          target.canRetryPreview = false;
          target.previewAttempt = Number(job.previewAttempt || target.previewAttempt || 1);
          renderWorkingDrafts(root, state);
          setStatus(
            root,
            'Finalizing the previous preview refund. Retry will be available shortly.',
            'setup'
          );
          if (Date.now() - startedAt >= POLL_TIMEOUT_MS) {
            settleOpenedRestoredProgress(false);
            return stop(false);
          }
          return delay(1000).then(poll);
        }
        if (
          job.status === 'PREVIEW_FINAL_QUEUED' ||
          job.status === 'PREVIEW_FINAL_RUNNING'
        ) {
          target.status = job.status;
          renderWorkingDrafts(root, state);
          if (Date.now() - startedAt >= POLL_TIMEOUT_MS) {
            settleOpenedRestoredProgress(false);
            return stop(false);
          }
          return delay(nextPollDelay(startedAt)).then(poll);
        }
        if (job.status === 'READY_FOR_ORDER' && isCompleteFinal(job)) {
          stop(true);
          return restoreProductSession(root, state).then(function () {
            if (!state.sessionReady || state.sessionFailed) return false;
            settleOpenedRestoredProgress(true);
            setStatus(root, 'Your final AI design is ready and selected.', 'page');
            return true;
          });
        }
        if (job.status === 'PREVIEW_FINAL_FAILED') {
          target.status = 'PREVIEW_FINAL_FAILED';
          renderWorkingDrafts(root, state);
          stop(false);
          var shouldOpenFailedDraft =
            state.openRestoredJobCid === targetCid &&
            state.openRestoredJobRunNumber === state.runNumber;
          settleOpenedRestoredProgress(false);
          return fetchRestoredWorkingDraft(root, state, targetCid).then(function (restored) {
            if (!state.sessionReady || state.sessionFailed) return false;
            setStatus(
              root,
              'The final preview could not be completed. Your adjustments are saved; open this design to retry.',
              'setup',
              true
            );
            if (shouldOpenFailedDraft) return switchToWorkingDraft(root, state, restored.cid);
            return false;
          });
        }
        if (job.status === 'PREVIEW_QUEUED' || job.status === 'PREVIEW_RUNNING') {
          target.status = job.status;
          target.previewAttempt = Number(job.previewAttempt || target.previewAttempt || 0);
          renderWorkingDrafts(root, state);
          if (Date.now() - startedAt >= POLL_TIMEOUT_MS) {
            settleOpenedRestoredProgress(false);
            return stop(false);
          }
          return delay(nextPollDelay(startedAt)).then(poll);
        }
        if (job.status === 'PREVIEW_READY' && job.aiPreviewUrl === expectedAiPreviewUrl(targetCid)) {
          target.status = 'PREVIEW_READY_PENDING_RESTORE';
          target.previewUrl = job.aiPreviewUrl;
          target.previewAttempt = Number(job.previewAttempt || target.previewAttempt || 0);
          renderWorkingDrafts(root, state);
          stop(true);
          if (
            state.openRestoredJobCid === targetCid &&
            state.openRestoredJobRunNumber === state.runNumber
          ) {
            var expectedRunNumber = state.runNumber;
            return fetchRestoredWorkingDraft(root, state, targetCid).then(function (restored) {
              if (
                !state.sessionReady || state.sessionFailed ||
                expectedRunNumber !== state.runNumber || restored.cid !== targetCid
              ) return false;
              settleOpenedRestoredProgress(true);
              return switchToWorkingDraft(root, state, restored.cid);
            });
          }
          return true;
        }
        if (job.status === 'PREVIEW_FAILED') {
          target.status = 'PREVIEW_FAILED';
          target.canRetryPreview = job.canRetryPreview !== false;
          target.previewAttempt = Number(job.previewAttempt || target.previewAttempt || 1);
          target.errorCode = String(job.error && job.error.code || 'PREVIEW_FAILED');
          target.errorRetryable = !(job.error && job.error.retryable === false);
          renderWorkingDrafts(root, state);
          stop(false);
          if (
            state.openRestoredJobCid === targetCid &&
            state.openRestoredJobRunNumber === state.runNumber
          ) {
            settleOpenedRestoredProgress(false);
            setStatus(
              root,
              (job.previewRefunded === true ? 'This technical failure was not counted. ' : '') +
                'This preview could not be generated. Retry it or delete it.',
              'setup',
              true
            );
          }
          setBusy(root, false);
          return false;
        }
        settleOpenedRestoredProgress(false);
        return stop(false);
      }).catch(function (error) {
        stop(false);
        if (!state.sessionReady || state.sessionFailed) return false;
        settleOpenedRestoredProgress(false);
        setBusy(root, false);
        setStatus(
          root,
          error.message || 'We could not refresh this preview. Try again in a moment.',
          'setup',
          true
        );
        return false;
      });
    }
    // Product load starts no background fan-out. Once the customer explicitly
    // opens or retries a pending card, keep the original immediate fast poll.
    state.restoredJobPolls[targetCid] = poll();
    return state.restoredJobPolls[targetCid];
  }

  function retryRestoredJob(root, state, draft) {
    if (draft && draft.status === 'PREVIEW_FAILED' && draft.canRetryPreview === false) {
      setStatus(
        root,
        'Finalizing the previous preview refund. Retry will be available shortly.',
        'setup'
      );
      delete state.restoredJobPolls[draft.cid];
      return pollRestoredJob(root, state, draft.cid);
    }
    if (
      !draft || (draft.status !== 'PREVIEW_FAILED' && draft.status !== 'UPLOADED') ||
      draft.errorRetryable === false
    ) {
      state.openRestoredJobCid = '';
      state.openRestoredJobRunNumber = 0;
      setStatus(root, 'This preview cannot be retried. Delete it and create a new preview.', 'setup', true);
      return Promise.resolve(false);
    }
    state.runNumber += 1;
    state.cid = draft.cid;
    state.token = '';
    state.originalKey = '';
    state.originalUrl = draft.originalUrl || '';
    state.jobStyleId = draft.styleId;
    state.jobStyleLabel = draft.styleLabel;
    state.previewAttempt = Number(draft.previewAttempt || 0);
    state.previewFailed = false;
    state.openRestoredJobCid = draft.cid;
    state.openRestoredJobRunNumber = state.runNumber;
    draft.status = 'PREVIEW_QUEUED';
    renderWorkingDrafts(root, state);
    beginEstimatedProgress(
      root,
      state,
      'queued',
      'Retrying your preview',
      'Your photo is securely queued.',
      25
    );
    setBusy(root, true);
    return apiRequest(
      root,
      '/ai/jobs/' + encodeURIComponent(draft.cid) + '/preview',
      'POST',
      ''
    ).then(function (response) {
      rememberPolicyFromJob(
        state,
        response,
        Boolean(
          state.currentVariantContract &&
          variantBindingMatches(response.variantBinding, state.currentVariantContract)
        )
      );
      applyQuota(root, state, response);
      draft.status = response.status === 'PREVIEW_RUNNING'
        ? 'PREVIEW_RUNNING'
        : 'PREVIEW_QUEUED';
      renderWorkingDrafts(root, state);
      setBusy(root, false);
      delete state.restoredJobPolls[draft.cid];
      return pollRestoredJob(root, state, draft.cid);
    }).catch(function (error) {
      if (error && error.data) applyQuota(root, state, error.data);
      draft.status = 'PREVIEW_FAILED';
      if (error && error.code === 'PREVIEW_SETTLEMENT_PENDING') {
        draft.canRetryPreview = false;
        renderWorkingDrafts(root, state);
        endEstimatedProgress(root, state, false);
        setBusy(root, false);
        setStatus(
          root,
          'Finalizing the previous preview refund. Retry will be available shortly.',
          'setup'
        );
        delete state.restoredJobPolls[draft.cid];
        return pollRestoredJob(root, state, draft.cid);
      }
      renderWorkingDrafts(root, state);
      state.openRestoredJobCid = '';
      state.openRestoredJobRunNumber = 0;
      endEstimatedProgress(root, state, false);
      setBusy(root, false);
      setStatus(
        root,
        error.code === 'DAILY_PREVIEW_LIMIT_REACHED'
          ? 'You have used today\'s free previews. You can still edit, use, or delete saved designs.'
          : (error.message || 'This preview could not be retried.'),
        'setup',
        true
      );
      return false;
    });
  }

  function applySessionManifest(root, state, manifest, expectedPhotoSourceRevision) {
    assertManifestCapacity(manifest);
    if (
      !manifest || String(manifest.productId || '') !== String(root.dataset.productId || '') ||
      !Number.isInteger(manifest.revision) || manifest.revision < 0 ||
      (manifest.mode !== 'STANDARD' && manifest.mode !== 'AI') ||
      typeof manifest.canGenerate !== 'boolean' || !Array.isArray(manifest.designs)
    ) throw new Error('The saved design response is invalid.');
    clearWorkingDrafts(state);
    state.designs = [];
    state.manifestRevision = manifest.revision;
    state.savedMode = manifest.mode;
    state.selectedDesignCid = String(manifest.selectedCid || '');
    state.canGenerate = manifest.canGenerate;
    applyQuota(root, state, manifest);
    manifest.designs.forEach(function (item) {
      try { rememberPolicyFromJob(state, item, false); } catch (_policyError) { return; }
      var normalized = normalizeSessionDesign(root, item);
      if (!normalized) return;
      if (normalized.previewFinalUrl) state.designs.push(normalized);
      else state.workingDrafts.push(normalized);
    });
    var mayReconcilePhotoSource =
      expectedPhotoSourceRevision === undefined ||
      Number(expectedPhotoSourceRevision) === Number(state.photoSourceRevision || 0);
    if (mayReconcilePhotoSource && currentPhotoSource(state).kind === 'saved') {
      var refreshedPhotoDraft = photoSourceDraft(state);
      if (refreshedPhotoDraft) {
        setPhotoSourceFromDraft(root, state, refreshedPhotoDraft, {
          incrementRevision: false,
          render: false
        });
      } else {
        setPhotoSource(root, state, emptyPhotoSource(), {
          incrementRevision: false,
          render: false
        });
      }
    }
    var selected = readyDesignByCid(state, state.selectedDesignCid);
    var scope = root.closest('product-info');
    var legacy = scope ? scope.querySelector('[data-gio-legacy-customizer]') : null;
    if (manifest.mode === 'AI' && selected) {
      applyReadyDesign(root, state, selected, { announce: false });
    } else {
      root.dataset.aiMode = 'regular';
      root.dataset.aiState = 'regular';
      clearOrderProperties(root);
      setFinalState(root, 'hidden');
      if (legacy) legacy.hidden = false;
      renderDesignGallery(root, state);
    }
    if (restoredPhotoMode(root) === 'ai' && root.dataset.aiMode === 'regular' && !selected) {
      root.dataset.aiMode = 'ai';
      root.dataset.aiState = 'active';
      if (legacy) legacy.hidden = true;
      var restoredLauncher = root.querySelector('[data-ai-open]');
      if (restoredLauncher) restoredLauncher.hidden = false;
      renderDesignGallery(root, state);
    }
    var restoredCount = state.designs.length + state.workingDrafts.length;
    setStatus(root, restoredCount
      ? restoredCount + (restoredCount === 1 ? ' AI design restored.' : ' AI designs restored.')
      : 'Standard photo upload selected.', 'page');
    refreshCartCustomizationState(root, state, false).catch(function () {});
    // A restored pending card is refreshed only when the customer opens it.
    // Avoid one independent status loop per saved card on every product load.
    if (mayReconcilePhotoSource) renderPhotoSourceUi(root, state);
    return manifest;
  }

  function restoreProductSession(root, state, initialManifest) {
    if (!state.sessionReady || state.sessionFailed) {
      var inactiveError = new Error('The AI session is no longer active.');
      inactiveError.code = 'AI_SESSION_STALE';
      return Promise.reject(inactiveError);
    }
    var expectedRunNumber = state.runNumber;
    var expectedPhotoSourceRevision = Number(state.photoSourceRevision || 0);
    state.sessionRestoring = true;
    setRestoreStatus(root, 'Restoring your designs…', true, false);
    setBusy(root, false);
    var manifestPromise = initialManifest
      ? Promise.resolve(initialManifest)
      : apiRequest(root,
        '/ai/session/products/' + encodeURIComponent(String(root.dataset.productId || '')),
        'GET'
      );
    return manifestPromise.then(function (manifest) {
      if (
        !state.sessionReady || state.sessionFailed ||
        expectedRunNumber !== state.runNumber
      ) {
        var staleError = new Error('The AI session changed before the saved designs were restored.');
        staleError.code = 'AI_SESSION_STALE';
        throw staleError;
      }
      applySessionManifest(root, state, manifest, expectedPhotoSourceRevision);
      state.sessionRestoring = false;
      setRestoreStatus(root, '', false, false);
      setBusy(root, false);
      return manifest;
    }).catch(function (error) {
      state.sessionRestoring = false;
      if (error && error.code === 'AI_SESSION_STALE') {
        setBusy(root, false);
        throw error;
      }
      state.sessionReady = false;
      state.sessionFailed = true;
      state.canGenerate = false;
      setRestoreStatus(root,
        'We could not restore your designs. Refresh this page before using AI.',
        true, true);
      setBusy(root, false);
      throw error;
    });
  }

  function resetDraftRuntimeAfterPartialDeletion(root, state) {
    state.runNumber += 1;
    abortActiveUpload(state);
    if (state.editorOpening && typeof state.abortEditorOpen === 'function') {
      state.abortEditorOpen();
    }
    endEstimatedProgress(root, state, false);
    destroyEditor(state);
    setDraftReturnState(root, state, false);
    releaseWorkingDraftSelection(state);
    state.cid = '';
    state.token = '';
    state.originalKey = '';
    state.originalUrl = '';
    state.jobStyleId = '';
    state.jobStyleLabel = '';
    state.previewAttempt = 0;
    state.previewFailed = false;
    state.previewSettlementPending = false;
    state.previewFinalFailed = false;
    state.pendingPreviewUrl = '';
    state.pendingPreviewRunNumber = 0;
    state.previewReadyPending = false;
    state.editorOpening = false;
    state.abortEditorOpen = null;
    state.openRestoredJobCid = '';
    state.openRestoredJobRunNumber = 0;
    setDialogView(root, 'setup');
  }

  function reconcilePartialDraftDeletion(root, state, originalError, fallbackMessage) {
    // DELETE calls are intentionally serialized. If one succeeds and a later
    // one fails, the server manifest is the only safe source of truth.
    return restoreProductSession(root, state).then(function () {
      // Re-open any surviving preview from its refreshed card. Never leave the
      // customer inside an editor whose CID may already have been deleted.
      resetDraftRuntimeAfterPartialDeletion(root, state);
      renderWorkingDrafts(root, state);
      renderDesignGallery(root, state);
      renderPhotoSourceUi(root, state);
      setBusy(root, false);
      setStatus(
        root,
        originalError && originalError.message || fallbackMessage,
        'setup',
        true
      );
      throw originalError;
    }, function (restoreError) {
      // The server may have committed a DELETE whose response was lost. If we
      // also cannot refresh the manifest, every local CID is untrusted. Close
      // the Studio and require a page refresh instead of exposing stale cards.
      state.sessionReady = false;
      state.sessionFailed = true;
      resetDraftRuntimeAfterPartialDeletion(root, state);
      fallbackToLegacy(
        root,
        restoreError && restoreError.message ||
          'The saved design list could not be refreshed after deletion.',
        true
      );
      var fatalError = new Error(
        'We could not safely refresh your AI designs. Refresh this page before using AI again.'
      );
      fatalError.code = 'AI_SESSION_RECONCILE_FAILED';
      throw fatalError;
    });
  }

  function requestStudioBootstrap(root) {
    var timeZone = browserTimeZone();
    return apiRequest(root, '/ai/session/bootstrap', 'POST', '', {
      timeZone: timeZone,
      productId: String(root.dataset.productId || '')
    }).catch(function (error) {
      var legacyContract = Boolean(
        error && error.status === 400 &&
        (error.code === 'UNEXPECTED_FIELD' || error.code === 'INVALID_REQUEST')
      );
      if (!legacyContract) throw error;
      // A cached new theme can briefly meet the previous Lambda during a
      // rolling deployment. Retry once with the v16.3 request shape; its
      // response then uses the existing manifest/styles GET fallbacks.
      return apiRequest(root, '/ai/session/bootstrap', 'POST', '', {
        timeZone: timeZone
      });
    });
  }

  function bootstrapAndRestoreSession(root, state) {
    return requestStudioBootstrap(root).then(function (response) {
      if (!response || !response.session || !response.session.expiresAt) {
        throw new Error('The secure design session could not be started.');
      }
      var receiptExpiresAt = Date.parse(response.orderResetReceiptExpiresAt || '');
      if (!Number.isFinite(receiptExpiresAt) || receiptExpiresAt <= Date.now()) {
        throw new Error('The secure order receipt is expired.');
      }
      setOrderResetReceipt(root, state, response.orderResetReceipt);
      state.orderResetReceiptExpiresAt = response.orderResetReceiptExpiresAt;
      state.sessionReady = true;
      state.sessionFailed = false;
      var initialManifest = response.initialState && response.initialState.manifest;
      return restoreProductSession(root, state, initialManifest).then(function () {
        return response;
      });
    });
  }

  function applyInitialStyleCatalog(root, state, styleSelect, scope, response) {
    if (!response || !response.styles || !response.styles.length) {
      throw new Error('No AI style is currently available.');
    }
    var styles = mergeStylePresentation(root, response.styles);
    if (!styles.length) throw new Error('No AI style is currently available.');
    renderStyleChoices(root, styleSelect, styles);
    state.stylesReady = true;
    var notice = scope.querySelector('[data-gio-ai-fallback-notice]');
    if (notice) notice.remove();
    setStatus(root, '', 'setup');
    renderDesignGallery(root, state);
    setBusy(root, false);
    return styles;
  }

  function markInitialStyleCatalogUnavailable(root, state, error) {
    state.stylesReady = false;
    setStatus(
      root,
      'New AI generation is temporarily unavailable. Your saved designs remain available.',
      'setup',
      true
    );
    setBusy(root, false);
    if (error && error.message) {
      console.warn('GiftInOne AI styles unavailable:', error.message);
    }
    return false;
  }

  function initializeStyleCatalog(root, state, styleSelect, scope, bootstrapResponse) {
    var initialState = bootstrapResponse && bootstrapResponse.initialState;
    var bundledCatalog = initialState && initialState.styleCatalog;
    var catalogPromise;
    if (bundledCatalog && bundledCatalog.available === true) {
      catalogPromise = Promise.resolve({ styles: bundledCatalog.styles });
    } else if (bundledCatalog && bundledCatalog.available === false) {
      var bundledError = new Error('The AI style catalog is temporarily unavailable.');
      bundledError.code = String(bundledCatalog.code || 'STYLE_CATALOG_UNAVAILABLE');
      catalogPromise = Promise.reject(bundledError);
    } else {
      // Rolling-deploy fallback: an older Lambda does not return initialState.
      catalogPromise = apiRequest(root, '/ai/styles', 'GET');
    }
    return catalogPromise.then(function (response) {
      return applyInitialStyleCatalog(root, state, styleSelect, scope, response);
    }).catch(function (error) {
      return markInitialStyleCatalogUnavailable(root, state, error);
    });
  }

  function persistProductSelection(root, state, mode, cid) {
    var productPath = '/ai/session/products/' +
      encodeURIComponent(String(root.dataset.productId || ''));
    return apiRequest(root, productPath, 'GET').then(function (manifest) {
      assertManifestCapacity(manifest);
      if (!manifest || !Number.isInteger(manifest.revision)) {
        throw new Error('The saved design response is invalid.');
      }
      state.manifestRevision = manifest.revision;
      var body = { expectedRevision: manifest.revision, mode: mode };
      if (mode === 'AI') body.cid = String(cid || '');
      return apiRequest(root, productPath + '/selection', 'PUT', '', body);
    }).then(function (response) {
      if (
        !response || String(response.productId || '') !== String(root.dataset.productId || '') ||
        !Number.isInteger(response.revision) || response.revision <= state.manifestRevision ||
        response.mode !== mode ||
        (mode === 'AI' && String(response.selectedCid || '') !== String(cid || ''))
      ) throw new Error('The design selection response is invalid.');
      state.manifestRevision = response.revision;
      state.savedMode = response.mode;
      state.selectedDesignCid = String(response.selectedCid || state.selectedDesignCid || '');
      return response;
    }).catch(function (error) {
      if (error && error.status === 409 && error.code === 'MANIFEST_REVISION_CONFLICT') {
        setStatus(root, 'Your designs changed in another tab. Restoring the latest selection…', 'page', true);
        return restoreProductSession(root, state).then(function () { throw error; });
      }
      throw error;
    });
  }

  function originalPhotoFromKey(key, cid) {
    var normalizedKey = String(key || '');
    if (!CID_PATTERN.test(String(cid || ''))) {
      throw new Error('The customization response is invalid.');
    }
    var expectedPrefix = 'aiOrders/' + cid + '/source/original.';
    var extension = normalizedKey.slice(expectedPrefix.length);
    if (
      normalizedKey.indexOf(expectedPrefix) !== 0 ||
      (extension !== 'jpg' && extension !== 'png' && extension !== 'webp')
    ) {
      throw new Error('The original photo upload response is invalid.');
    }
    return {
      key: normalizedKey,
      url: 'https://static.giftinone.com/' + normalizedKey
    };
  }

  function originalPhotoFromUpload(upload, cid) {
    if (!upload || !upload.fields || typeof upload.fields !== 'object') {
      throw new Error('The original photo upload response is invalid.');
    }
    return originalPhotoFromKey(upload.fields.key, cid);
  }

  function markReady(root, state, job) {
    if (
      job.status !== 'READY_FOR_ORDER' ||
      !CID_PATTERN.test(state.cid) ||
      job.cid !== state.cid ||
      !isCompleteFinal(job)
    ) {
      throw new Error('The final preview response is invalid.');
    }
    var previewInput = root.querySelector('[data-ai-preview-property]');
    var cidInput = root.querySelector('[data-ai-cid-property]');
    var originalInput = root.querySelector('[data-ai-original-property]');
    if (!previewInput || !cidInput || !originalInput) {
      throw new Error('The AI order fields are unavailable.');
    }
    var readySource = currentPhotoSource(state);
    var reusableFile = readySource.kind === 'local'
      ? readySource.file || null
      : null;
    var completedDraft = workingDraftByCid(state, state.cid);
    var editableDraft = cloneEditableDraft(completedDraft);
    var readyRenderPolicy = rememberPolicyFromJob(state, job, true);
    if (editableDraft) {
      editableDraft.renderPolicy = cloneRenderPolicy(readyRenderPolicy);
      editableDraft.variantBinding = job.variantBinding;
    }
    var design = {
      cid: state.cid,
      previewFinalUrl: job.previewFinalUrl,
      designVersion: job.designVersion,
      previewSchema: job.previewSchema,
      previewComplete: true,
      previewImageLoaded: job.previewImageLoaded === true,
      originalUrl: state.originalUrl || '',
      originalKey: state.originalKey || '',
      styleId: state.jobStyleId || '',
      styleLabel: displayStyleLabel(
        state.jobStyleId,
        state.jobStyleLabel,
        'AI design'
      ),
      recipe: cloneRecipe(
        state.editorRecipeSnapshot || job.recipe || job.draft && job.draft.recipe
      ),
      renderPolicy: cloneRenderPolicy(readyRenderPolicy),
      variantBinding: job.variantBinding,
      editableDraft: editableDraft,
      boundVariantId: String(
        job.variantBinding && job.variantBinding.variantId ||
        state.finalizingVariantContract && state.finalizingVariantContract.variantId || ''
      ),
      createdAt: Date.now(),
      serverRevision: Number(workingDraftByCid(state, state.cid) &&
        workingDraftByCid(state, state.cid).serverRevision || 0)
    };
    var existing = readyDesignByCid(state, design.cid);
    if (existing) {
      Object.keys(design).forEach(function (key) { existing[key] = design[key]; });
      design = existing;
    } else {
      state.designs.push(design);
    }
    state.selectedDesignCid = design.cid;
    removeWorkingDraft(state, design.cid);
    state.finalizingDraftCid = '';
    state.restoringWorkingDraftCid = '';
    state.cid = '';
    state.token = '';
    state.originalKey = '';
    state.originalUrl = '';
    state.jobStyleId = '';
    state.jobStyleLabel = '';
    state.editorPreviewUrl = '';
    root.querySelector('[data-ai-editor]').hidden = true;
    destroyEditor(state);
    setDraftReturnState(root, state, false);
    state.cartSubmitting = false;
    state.cartUnknown = false;
    delete root.dataset.aiCartState;
    if (reusableFile) {
      setPhotoSource(root, state, {
        kind: 'local',
        file: reusableFile,
        label: reusableFile.name || 'Selected photo'
      });
    } else {
      setPhotoSourceFromDraft(root, state, design);
    }
    if (root.dataset.aiMode === 'regular') {
      clearOrderProperties(root);
      root.dataset.aiState = 'regular';
      setFinalState(root, 'hidden');
      renderDesignGallery(root, state);
      setStatus(root, 'Your AI design is ready and saved. Select it above whenever you want to use it.', 'page');
    } else {
      applyReadyDesign(root, state, design);
    }
    closeAiDialog(root, state, false);
    if (root.dataset.aiState === 'ready') focusReadySummary(root);
    return design;
  }

  function pollActivePreviewSettlement(root, state, runNumber, cid, styleId) {
    var targetCid = String(cid || '');
    if (!CID_PATTERN.test(targetCid) || !styleId) return Promise.resolve(false);
    if (state.previewSettlementPolls[targetCid]) {
      return state.previewSettlementPolls[targetCid];
    }
    var startedAt = Date.now();
    function stop(value) {
      delete state.previewSettlementPolls[targetCid];
      return value;
    }
    function poll() {
      if (runNumber !== state.runNumber || targetCid !== state.cid) return stop(false);
      return apiRequest(root, '/ai/jobs/' + encodeURIComponent(targetCid), 'GET', '').then(function (job) {
        assertJobOperationIdentity(job, targetCid, styleId);
        applyQuota(root, state, job);
        if (job.status !== 'PREVIEW_FAILED') return stop(false);
        if (job.canRetryPreview === false) {
          state.previewSettlementPending = true;
          setStatus(
            root,
            'Finalizing the previous preview refund. Retry will be available shortly.',
            'setup'
          );
          if (Date.now() - startedAt >= POLL_TIMEOUT_MS) return stop(false);
          return delay(1000).then(poll);
        }
        state.previewSettlementPending = false;
        state.previewFailed = true;
        var generate = root.querySelector('[data-ai-generate]');
        if (generate) generate.textContent = 'Retry preview';
        setStatus(
          root,
          (job.previewRefunded === true ? 'This technical failure was not counted. ' : '') +
            'The previous attempt is settled. You can retry the preview now.',
          'setup',
          true
        );
        setBusy(root, false);
        return stop(true);
      }).catch(function (error) {
        stop(false);
        state.previewSettlementPending = true;
        setBusy(root, false);
        setStatus(root, error.message || 'Refund status could not be refreshed. Try again shortly.', 'setup', true);
        return false;
      });
    }
    state.previewSettlementPolls[targetCid] = poll();
    return state.previewSettlementPolls[targetCid];
  }

  function showPreviewFailure(root, state, error) {
    endEstimatedProgress(root, state, false);
    if (error && error.data) applyQuota(root, state, error.data);
    state.pendingPreviewUrl = '';
    state.pendingPreviewRunNumber = 0;
    state.editorOpening = false;
    var generate = root.querySelector('[data-ai-generate]');
    if (error.code === 'DAILY_PREVIEW_LIMIT_REACHED') {
      trackAiEvent(root, 'ai_error', { reason: 'daily_limit' });
      state.canGenerate = false;
      setDialogView(root, 'setup');
      setStatus(
        root,
        'You have used today\'s free previews. You can still edit or use any saved design.',
        'setup',
        true
      );
      setBusy(root, false);
      return true;
    }
    if (error.code === 'PREVIEW_FAILED') {
      trackAiEvent(root, 'ai_error', { reason: 'preview_failed' });
      state.previewAttempt = Number(error.attempt || state.previewAttempt || 1);
      state.previewFailed = true;
      generate.textContent = 'Retry preview';
      setDialogView(root, 'setup');
      if (error.data && error.data.canRetryPreview === false) {
        state.previewSettlementPending = true;
        setStatus(
          root,
          'Finalizing the previous preview refund. Retry will be available shortly.',
          'setup'
        );
        setBusy(root, false);
        pollActivePreviewSettlement(
          root,
          state,
          state.runNumber,
          state.cid,
          state.jobStyleId
        );
        return true;
      }
      state.previewSettlementPending = false;
      setStatus(root,
        (error.data && error.data.previewRefunded === true
          ? 'This technical failure was not counted. '
          : '') +
        'Preview failed. Click “Retry preview” to make attempt ' +
        (state.previewAttempt + 1) + '.', 'setup', true);
      setBusy(root, false);
      revealSetupAction(root, state, true);
      return true;
    }
    if (error.code === 'PREVIEW_SETTLEMENT_PENDING') {
      trackAiEvent(root, 'ai_error', { reason: 'settlement_pending' });
      state.previewFailed = true;
      state.previewSettlementPending = true;
      generate.textContent = 'Retry preview';
      setDialogView(root, 'setup');
      setStatus(
        root,
        'Finalizing the previous preview refund. Retry will be available shortly.',
        'setup'
      );
      setBusy(root, false);
      pollActivePreviewSettlement(
        root,
        state,
        state.runNumber,
        state.cid,
        state.jobStyleId
      );
      return true;
    }
    if (error.code === 'PREVIEW_FINAL_FAILED') {
      trackAiEvent(root, 'ai_error', { reason: 'final_failed' });
      state.previewFinalFailed = true;
      setStatus(root, 'Final preview failed. Click “Add this design to cart” to retry.', 'editor', true);
      setBusy(root, false);
      return true;
    }
    return false;
  }

  function logEditorReadyTiming(state) {
    if (!state.timing) return;
    state.timing.editorReadyAt = Date.now();
    console.info('GiftInOne AI preview timing', {
      totalMs: state.timing.editorReadyAt - state.timing.startedAt,
      jobMs: state.timing.jobCreatedAt
        ? state.timing.jobCreatedAt - state.timing.startedAt
        : null,
      uploadMs: state.timing.uploadCompletedAt && state.timing.jobCreatedAt
        ? state.timing.uploadCompletedAt - state.timing.jobCreatedAt
        : null,
      workflowAndEditorMs: state.timing.previewAcceptedAt
        ? state.timing.editorReadyAt - state.timing.previewAcceptedAt
        : null
    });
  }

  function retainGeneratedPreviewForReopen(state) {
    var draft = workingDraftByCid(state, state.cid);
    if (!draft || draft.initializationComplete !== false) return;
    if (state.draftSaveTimerCid === draft.cid) clearDraftSaveTimer(state);
    // Keep the generated image visible, while all reopens go through verified
    // initialization again. This also covers closing the dialog mid-setup.
    draft.status = 'PREVIEW_READY_PENDING_RESTORE';
    draft.data = null;
    draft.recipe = null;
    draft.canvas = null;
    draft.cropBox = null;
    draft.container = null;
  }

  function preservePendingPreviewForEditorRetry(root, state, error, runNumber) {
    if (
      runNumber !== state.runNumber || !state.pendingPreviewUrl ||
      error.code === 'AI_EDITOR_DEFERRED' ||
      error.message === 'AI_REQUEST_CANCELLED'
    ) return false;
    var incompleteDraft = workingDraftByCid(state, state.cid);
    var diagnostic = state.editorLayoutDiagnostic ||
      editorLayoutDiagnostic(state, incompleteDraft, error);
    retainGeneratedPreviewForReopen(state);
    destroyEditor(state);
    renderWorkingDrafts(root, state);
    state.editorOpening = false;
    state.previewReadyPending = true;
    if (state.progressTimer) window.clearInterval(state.progressTimer);
    state.progressTimer = null;
    state.generationActive = false;
    delete root.dataset.aiGenerating;
    var progress = root.querySelector('[data-ai-progress]');
    if (progress) progress.hidden = true;
    setDialogView(root, 'setup');
    setStatus(
      root,
      'Your AI image was generated, but the editor did not finish loading. ' +
        'Click “Open generated preview” to load the same image again. This will not use another preview.',
      'setup',
      true
    );
    setStatus(
      root,
      'Your AI image is generated and ready to reopen.',
      'page'
    );
    setBusy(root, false);
    console.error('GiftInOne AI editor load failed; generated image retained:', {
      code: String(error.code || 'AI_EDITOR_LOAD_FAILED'),
      stage: String(error.layoutStage || 'unknown'),
      cid: String(state.cid || ''),
      previewUrl: String(state.pendingPreviewUrl || ''),
      layout: diagnostic.actual,
      diagnostic: diagnostic
    });
    return true;
  }

  function loadPendingEditor(root, state) {
    if (!state.pendingPreviewUrl || state.editorOpening) return Promise.resolve(false);
    var runNumber = state.pendingPreviewRunNumber;
    var imageUrl = state.pendingPreviewUrl;
    state.editorLayoutDiagnostic = null;
    state.editorOpening = true;
    state.previewReadyPending = false;
    setBusy(root, true);
    updateLauncher(root, state);
    setStatus(root, 'Loading your AI preview…', 'page');
    return openEditor(root, state, imageUrl, runNumber).then(function () {
      if (runNumber !== state.runNumber) return false;
      state.editorPreviewUrl = imageUrl;
      state.activeWorkingDraftCid = state.cid;
      setStatus(root, 'Crop or zoom the image, then save this design.', 'editor');
      setStatus(root, 'Your AI preview is ready to position.', 'page');
      var heading = root.querySelector('[data-ai-editor-heading]');
      var dialog = root.querySelector('[data-ai-dialog]');
      var expectedCid = state.cid;
      beginDraftSyncSuppression(state, expectedCid, runNumber);
      return new Promise(function (resolve, reject) {
        settleInitialEditorViewport(
          root,
          state,
          runNumber,
          expectedCid,
          function (initialSettled, initialSettleReason) {
            if (!initialSettled || runNumber !== state.runNumber) {
              endDraftSyncSuppression(state, expectedCid, runNumber);
              if (runNumber === state.runNumber) {
                state.editorOpening = false;
                if (initialSettleReason === 'deferred') {
                  reject(editorDeferredError());
                  return;
                }
                var layoutError = new Error('The image editor layout could not be prepared.');
                layoutError.code = 'AI_EDITOR_LAYOUT_FAILED';
                layoutError.layoutStage = 'initial-' + (initialSettleReason || 'fit');
                reject(layoutError);
                return;
              }
              resolve(false);
              return;
            }
            endDraftSyncSuppression(state, expectedCid, runNumber);
            // Fresh initial coordinates come from the image dimensions, never
            // from Cropper's rounded display. Existing saved/customer drafts
            // are retained by updateWorkingDraftFromEditor and restored below.
            var createdDraft = updateWorkingDraftFromEditor(root, state, true, true, {
              initialCentered: true
            });
            if (!createdDraft) {
              state.editorOpening = false;
              var draftError = new Error('The AI preview could not be prepared.');
              draftError.code = 'AI_EDITOR_LAYOUT_FAILED';
              draftError.layoutStage = 'draft-capture';
              reject(draftError);
              return;
            }
            state.generationFallbackDraftCid = '';
            beginDraftSyncSuppression(state, expectedCid, runNumber);
            settleWorkingDraftEditorViewport(
              root,
              state,
              runNumber,
              expectedCid,
              function (settled, settleReason) {
                if (runNumber !== state.runNumber) {
                  endDraftSyncSuppression(state, expectedCid, runNumber);
                  resolve(false);
                  return;
                }
                if (!settled) {
                  endDraftSyncSuppression(state, expectedCid, runNumber);
                  state.editorOpening = false;
                  if (settleReason === 'deferred') {
                    reject(editorDeferredError());
                    return;
                  }
                  var settleError = new Error('The image editor layout did not settle.');
                  settleError.code = 'AI_EDITOR_LAYOUT_FAILED';
                  settleError.layoutStage = 'draft-' + (settleReason || 'restore');
                  reject(settleError);
                  return;
                }
                try {
                  restoreWorkingDraftSnapshot(root, state, createdDraft);
                  createdDraft.initializationComplete = true;
                } catch (error) {
                  endDraftSyncSuppression(state, expectedCid, runNumber);
                  state.editorOpening = false;
                  reject(error);
                  return;
                }
                endDraftSyncSuppression(state, expectedCid, runNumber);
                if (Number(createdDraft.persistedLocalRevision || 0) < Number(createdDraft.revision || 0)) {
                  scheduleDraftSave(root, state, createdDraft);
                }
                state.pendingPreviewUrl = '';
                state.pendingPreviewRunNumber = 0;
                state.editorOpening = false;
                logEditorReadyTiming(state);
                endEstimatedProgress(root, state, true);
                setBusy(root, false);
                if (heading && dialogIsOpen(dialog)) {
                  focusWithoutScrolling(heading);
                }
                resolve(true);
              }
            );
          }
        );
      });
    }).catch(function (error) {
      if (runNumber === state.runNumber) state.editorOpening = false;
      if (error.code === 'AI_EDITOR_DEFERRED') {
        if (runNumber !== state.runNumber) return false;
        // A close during first-open settlement is a pause, not a saved editor.
        // Discard its transient viewport so opening cannot take the shortcut
        // for an already-open draft and leave initialization permanently false.
        retainGeneratedPreviewForReopen(state);
        destroyEditor(state);
        renderWorkingDrafts(root, state);
        state.previewReadyPending = true;
        setDialogView(root, 'setup');
        updateLauncher(root, state);
        setBusy(root, false);
        return false;
      }
      if (preservePendingPreviewForEditorRetry(root, state, error, runNumber)) {
        return false;
      }
      throw error;
    });
  }

  function finishAiPreview(root, state, runNumber, job) {
    state.previewFailed = false;
    state.previewAttempt = Number(job.previewAttempt || state.previewAttempt || 1);
    state.pendingPreviewUrl = job.aiPreviewUrl;
    state.pendingPreviewRunNumber = runNumber;
    state.previewReadyPending = true;
    trackAiEvent(root, 'preview_ready', { source: 'generation' });
    root.querySelector('[data-ai-generate]').textContent = 'Generate preview';
    setProgressStage(
      root,
      state,
      'loading',
      'Preview ready',
      'Open the designer to load and position your preview.',
      95
    );
    setStatus(root, 'Your AI preview is ready. Continue to position it.', 'page');
    if (state.progressTimer) window.clearInterval(state.progressTimer);
    state.progressTimer = null;
    updateLauncher(root, state);
    var dialog = root.querySelector('[data-ai-dialog]');
    if (!dialogIsOpen(dialog)) return Promise.resolve(false);
    return loadPendingEditor(root, state);
  }

  function startOrRetryPreview(root, state, runNumber) {
    state.previewSettlementPending = false;
    return sessionFirstJobRequest(
      root,
      '/ai/jobs/' + encodeURIComponent(state.cid) + '/preview',
      'POST',
      state.token
    ).then(function (response) {
      rememberPolicyFromJob(
        state,
        response,
        Boolean(
          state.currentVariantContract &&
          variantBindingMatches(response.variantBinding, state.currentVariantContract)
        )
      );
      applyQuota(root, state, response);
      if (state.timing) state.timing.previewAcceptedAt = Date.now();
      var usedPublicPhase = syncPreviewProgress(root, state, response);
      if (!usedPublicPhase) {
        setProgressStage(
          root,
          state,
          'queued',
          'Waiting for AI',
          'Your photo is securely queued for generation.',
          25
        );
      }
      return pollJob(root, state, runNumber, false);
    }).then(function (job) {
      return finishAiPreview(root, state, runNumber, job);
    });
  }

  function cartCustomizationIds(cart) {
    var ids = Object.create(null);
    var items = cart && Array.isArray(cart.items) ? cart.items : [];
    items.forEach(function (item) {
      var properties = item && item.properties && typeof item.properties === 'object'
        ? item.properties
        : {};
      var cid = String(properties._CustomizationId || '');
      if (CID_PATTERN.test(cid)) ids[cid] = true;
    });
    return ids;
  }

  function cartItemsByCustomizationId(cart) {
    var result = Object.create(null);
    (cart && Array.isArray(cart.items) ? cart.items : []).forEach(function (item) {
      var cid = String(item && item.properties && item.properties._CustomizationId || '');
      if (!CID_PATTERN.test(cid)) return;
      if (!result[cid]) result[cid] = [];
      result[cid].push(item);
    });
    return result;
  }

  function syncFinalDesignDetails(root, state, design) {
    var node = root.querySelector('[data-ai-final-details]');
    if (!node) return;
    var rows = state.cartItemsByCustomizationCid && state.cartItemsByCustomizationCid[design.cid] || [];
    var options = design.variantBinding && design.variantBinding.selectedOptions || [];
    if (rows.length && Array.isArray(rows[0].options_with_values)) {
      options = rows[0].options_with_values;
    }
    var details = options.map(function (option) {
      return String(option && option.value || '');
    }).filter(function (value) { return Boolean(value && value !== 'Default Title'); });
    if (state.selectedDesignInCart && rows.length) {
      var quantity = rows.reduce(function (total, row) { return total + Number(row.quantity || 0); }, 0);
      if (quantity > 0) details.push('Quantity in cart: ' + quantity);
    }
    node.textContent = details.join(' · ');
    node.hidden = details.length === 0;
  }

  function syncSelectedDesignCartState(root, state) {
    var selectedCid = String(state.selectedDesignCid || '');
    state.selectedDesignInCart = Boolean(
      selectedCid && state.cartCustomizationCids[selectedCid]
    );
    var design = readyDesignByCid(state, selectedCid);
    // The same immutable cart snapshot has one presentation, regardless of
    // whether it came from cart/add, a restored session, or another tab.
    // An uncertain write stays locked until a successful authoritative GET.
    if (design && root.dataset.aiMode === 'ai' && !state.cartUnknown && !state.cartSubmitting &&
        (root.dataset.aiState === 'ready' || root.dataset.aiState === 'added')) {
      var nextState = state.selectedDesignInCart ? 'added' : 'ready';
      var previousState = root.dataset.aiState;
      root.dataset.aiState = nextState;
      setFinalState(root, nextState);
      var title = root.querySelector('[data-ai-final-title]');
      if (title) title.textContent = design.styleLabel || 'AI artwork';
      var add = root.querySelector('[data-ai-add]');
      if (add) add.textContent = 'Add to cart';
      if (state.selectedDesignInCart) clearOrderProperties(root);
      else setReadyDesignProperties(root, design);
      syncFinalDesignDetails(root, state, design);
      if (previousState !== nextState) {
        setStatus(root, state.selectedDesignInCart
          ? 'This design is in your cart.'
          : 'This design is no longer in your cart. You can add it again.', 'page');
      }
      renderDesignGallery(root, state);
    }
    setBusy(root, state.busy);
    return state.selectedDesignInCart;
  }

  function refreshCartCustomizationState(root, state, announceError) {
    if (state.cartRefreshPromise) return state.cartRefreshPromise;
    var routeRoot = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? String(window.Shopify.routes.root)
      : '/';
    state.cartChecking = true;
    var mutationSequence = Number(state.cartMutationSequence || 0);
    setBusy(root, state.busy);
    state.cartRefreshPromise = window.fetch(routeRoot + 'cart.js', {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    }).then(function (response) {
      if (!response || !response.ok) throw new Error('The cart could not be checked.');
      return response.json();
    }).then(function (cart) {
      if (!cart || !Array.isArray(cart.items)) {
        throw new Error('The cart returned an invalid response.');
      }
      state.cartChecking = false;
      state.cartRefreshPromise = null;
      // A read started before a local cart mutation cannot overwrite the
      // acknowledged result. Recheck after the mutation has settled.
      if (mutationSequence !== Number(state.cartMutationSequence || 0)) {
        return refreshCartCustomizationState(root, state, announceError);
      }
      state.cartCustomizationCids = cartCustomizationIds(cart);
      state.cartItemsByCustomizationCid = cartItemsByCustomizationId(cart);
      if (state.cartUnknown) {
        state.cartUnknown = false;
        delete root.dataset.aiCartState;
        setStatus(root, state.cartCustomizationCids[state.selectedDesignCid]
          ? 'Your cart has been confirmed. This design is in your cart.'
          : 'Your cart has been checked. This design is saved and ready to add.', 'page');
      }
      return syncSelectedDesignCartState(root, state);
    }).catch(function (error) {
      state.cartChecking = false;
      state.cartRefreshPromise = null;
      setBusy(root, state.busy);
      if (announceError) {
        setStatus(
          root,
          'We could not verify your cart. Refresh the page before adding this design.',
          'page',
          true
        );
      }
      throw error;
    });
    return state.cartRefreshPromise;
  }

  function readyRecipeForRebind(root, design) {
    if (design && design.recipe && typeof design.recipe === 'object') {
      return Promise.resolve(cloneRecipe(design.recipe));
    }
    return apiRequest(
      root,
      '/ai/jobs/' + encodeURIComponent(String(design && design.cid || '')),
      'GET',
      ''
    ).then(function (job) {
      if (
        !job || job.status !== 'READY_FOR_ORDER' ||
        String(job.cid || '') !== String(design.cid || '') ||
        !isCompleteFinal(job)
      ) throw new Error('The saved AI design could not be verified.');
      var recipe = cloneRecipe(job.recipe || job.draft && job.draft.recipe);
      if (!recipe) throw new Error('The saved AI design is missing its crop recipe.');
      design.recipe = cloneRecipe(recipe);
      return recipe;
    });
  }

  /** A changed option creates a new immutable proof, without a new AI call. */
  function rebindReadyDesignVariant(root, state, productForm) {
    if (state.variantRebindPromise) return state.variantRebindPromise;
    var design = readyDesignByCid(state, state.selectedDesignCid);
    if (!design) return Promise.reject(new Error('Select an AI design before adding to cart.'));
    var parentCid = design.cid;
    var contract;
    try { contract = selectedVariantContract(root, state, productForm); }
    catch (error) { return Promise.reject(error); }
    state.variantRebindPromise = readyRecipeForRebind(root, design).then(function (recipe) {
      if (!variantBindingMatches(selectedVariantContract(root, state, productForm), contract)) {
        throw new Error('Product options changed while saving. Please try again.');
      }
      return apiRequest(root, '/ai/jobs/' + encodeURIComponent(parentCid) + '/recipe', 'PUT', '', {
        recipe: recipe, productId: contract.productId, variantId: contract.variantId,
        selectedOptions: contract.selectedOptions
      });
    }).then(function (response) {
      if (!response || (response.cid !== parentCid && response.revisionParentCid !== parentCid)) {
        throw new Error('The new design version could not be verified.');
      }
      return waitForCompletePreview(root, state, response, contract);
    }).then(function (response) {
      if (state.selectedDesignCid !== parentCid ||
          !variantBindingMatches(selectedVariantContract(root, state, productForm), contract)) {
        throw new Error('Product options changed while saving. Please try again.');
      }
      var policy = rememberAuthoritativeRenderPolicy(state, response, contract, true);
      if (!sameRenderShape(design.renderPolicy, policy)) {
        state.variantNeedsReadjustment = true;
        syncVariantAdjustmentUi(root, state);
        throw new Error('This option uses a different shape. Review the crop before adding to cart.');
      }
      design.previousCid = parentCid;
      design.cid = response.cid;
      design.designVersion = response.designVersion;
      design.previewSchema = response.previewSchema;
      design.previewComplete = true;
      design.previewImageLoaded = true;
      design.previewFinalUrl = response.previewFinalUrl;
      design.recipe = cloneRecipe(response.recipe || design.recipe);
      design.boundVariantId = contract.variantId;
      design.variantBinding = response.variantBinding;
      design.renderPolicy = cloneRenderPolicy(policy);
      if (design.editableDraft) {
        design.editableDraft.cid = response.cid;
        design.editableDraft.token = '';
        design.editableDraft.previewUrl = expectedAiPreviewUrl(response.cid);
        design.editableDraft.originalKey = '';
        if (response.cid !== parentCid) {
          design.editableDraft.serverRevision = 0;
          design.editableDraft.persistedLocalRevision = Number(design.editableDraft.revision || 1);
        }
        design.editableDraft.variantBinding = response.variantBinding;
        design.editableDraft.renderPolicy = cloneRenderPolicy(policy);
      }
      state.currentVariantContract = contract;
      state.selectedDesignCid = design.cid;
      applyReadyDesign(root, state, design, { announce: false });
      return design;
    });
    state.variantRebindPromise = state.variantRebindPromise.then(function (value) {
      state.variantRebindPromise = null;
      return value;
    }, function (error) {
      state.variantRebindPromise = null;
      if (error && error.data && error.data.renderPolicy &&
          variantBindingMatches(error.data.variantBinding, contract)) {
        try {
          var stillCurrent = variantBindingMatches(state.currentVariantContract, contract);
          var rejected = rememberAuthoritativeRenderPolicy(state, error.data, contract, stillCurrent);
          if (stillCurrent && !sameRenderShape(design.renderPolicy, rejected)) {
            state.variantNeedsReadjustment = true;
            syncVariantAdjustmentUi(root, state);
          }
        } catch (_policyError) {}
      }
      throw error;
    });
    return state.variantRebindPromise;
  }

  function readyDesignEditStatusTarget(root) {
    var dialog = root.querySelector('[data-ai-dialog]');
    return dialogIsOpen(dialog) ? 'setup' : 'page';
  }

  function restoreReadyDesignAfterEditFailure(root, state, design, message, trigger) {
    destroyEditor(state);
    if (state.draftSaveTimerCid === design.cid) clearDraftSaveTimer(state);
    removeWorkingDraft(state, design.cid);
    if (!readyDesignByCid(state, design.cid)) state.designs.push(design);
    state.selectedDesignCid = design.cid;
    state.variantNeedsReadjustment = false;
    setPhotoSourceFromDraft(root, state, design);
    applyReadyDesign(root, state, design, { announce: false });
    setBusy(root, false);
    openSavedDesignLibrary(root, state, trigger);
    setStatus(
      root,
      message || 'This design could not be opened safely. Select it to try again.',
      'setup',
      true
    );
    return false;
  }

  /**
   * A finished design retains its canonical editable draft. Reopen that draft
   * directly instead of selecting it through the session API or generating a
   * new preview. The saved canvas is restored under its original render shape;
   * switchToWorkingDraft applies the current Variant only when it differs.
   */
  function reopenReadyDesign(root, state, productForm, requestedDesign, trigger) {
    if (state.busy || state.cartSubmitting || state.cartUnknown) {
      return Promise.resolve(false);
    }
    var design = requestedDesign || readyDesignByCid(state, state.selectedDesignCid);
    var statusTarget = readyDesignEditStatusTarget(root);
    var designMovedToDraft = false;
    if (!design || !design.editableDraft) {
      setStatus(
        root,
        'This saved design cannot be reopened. Create another AI preview for this product shape.',
        statusTarget,
        true
      );
      return Promise.resolve(false);
    }
    try {
      syncBrowserPreviewOverlay(root, state, productForm, true, true);
      var editContract = selectedVariantContract(root, state, productForm);
      if (!variantBindingMatches(state.currentVariantContract, editContract)) {
        throw new Error('Product options changed while opening this design.');
      }
    } catch (error) {
      setStatus(root, error.message, statusTarget, true);
      return Promise.resolve(false);
    }

    setBusy(root, true);
    return refreshCartCustomizationState(root, state, false).then(function () {
      if (state.cartCustomizationCids[design.cid]) {
        setBusy(root, false);
        setStatus(
          root,
          'This exact design is already in your cart. Remove it from the cart before editing it.',
          statusTarget,
          true
        );
        return false;
      }
      var draft = cloneEditableDraft(design.editableDraft);
      if (!draft) throw new Error('This saved design is missing its editable crop.');
      draft.status = 'PREVIEW_READY';
      draft.requiresCartOwnershipCheck = true;
      draft.renderPolicy = cloneRenderPolicy(draft.renderPolicy || design.renderPolicy);

      state.designs = state.designs.filter(function (item) {
        return item.cid !== design.cid;
      });
      removeWorkingDraft(state, draft.cid);
      state.workingDrafts.push(draft);
      designMovedToDraft = true;
      state.selectedDesignCid = '';
      state.selectedDesignInCart = false;
      state.variantNeedsReadjustment = false;
      clearOrderProperties(root);
      setPhotoSourceFromDraft(root, state, draft);
      root.dataset.aiMode = 'ai';
      root.dataset.aiState = 'active';
      var scope = root.closest('product-info');
      var legacy = scope ? scope.querySelector('[data-gio-legacy-customizer]') : null;
      if (legacy) legacy.hidden = true;
      setFinalState(root, 'hidden');
      setDesignLibraryOpen(root, state, false, false);
      renderDesignGallery(root, state);

      // switchToWorkingDraft rejects calls while busy. Release the cart-check
      // lock immediately before the synchronous switch call; the switch takes
      // its own editor-opening lock before yielding.
      setBusy(root, false);
      return switchToWorkingDraft(root, state, draft.cid, false).then(function (opened) {
        if (!opened || !state.cropper) {
          return restoreReadyDesignAfterEditFailure(
            root,
            state,
            design,
            'This design could not be opened safely. It remains saved in My designs.',
            trigger
          );
        }
        setStatus(
          root,
          (design.styleLabel || 'AI design') +
            ' restored. Adjust it or add it to your cart when ready.',
          'editor'
        );
        return true;
      });
    }).catch(function (error) {
      if (designMovedToDraft && !readyDesignByCid(state, design.cid)) {
        return restoreReadyDesignAfterEditFailure(
          root,
          state,
          design,
          error && error.message || 'This design could not be opened.',
          trigger
        );
      }
      setBusy(root, false);
      setStatus(
        root,
        error && error.message || 'This design could not be opened.',
        statusTarget,
        true
      );
      return false;
    });
  }

  function reopenReadyDesignForCurrentVariant(root, state, productForm, trigger) {
    return reopenReadyDesign(
      root,
      state,
      productForm,
      readyDesignByCid(state, state.selectedDesignCid),
      trigger
    );
  }

  function submitReadyDesign(root, state, productForm, options) {
    if (
      root.dataset.aiState !== 'ready' ||
      state.cartSubmitting ||
      state.cartUnknown
    ) return false;
    var submitCid = String(state.selectedDesignCid || '');
    if (!CID_PATTERN.test(submitCid) || !readyDesignByCid(state, submitCid)) return false;
    if (
      window.GiftinoneVariantSync &&
      typeof window.GiftinoneVariantSync.ensureCurrentVariantFromSelections === 'function' &&
      window.GiftinoneVariantSync.ensureCurrentVariantFromSelections({ forceEnableSubmit: true }) === false
    ) {
      setStatus(root, 'Choose an available product option before adding to cart.', 'page', true);
      return false;
    }
    try {
      selectedVariantContract(root, state, productForm);
      syncBrowserPreviewOverlay(root, state, productForm, true, true);
    } catch (variantError) {
      setStatus(root, variantError.message, 'page', true);
      return false;
    }
    var submitButton = productForm
      ? productForm.querySelector('[type="submit"][name="add"]')
      : null;
    if (!productForm || !submitButton || submitButton.disabled) {
      setStatus(root, 'Your design is saved. Choose an available option, then retry add to cart.', 'page');
      setBusy(root, false);
      return false;
    }
    var freshContract = options && options.freshContract;
    setBusy(root, true);
    refreshCartCustomizationState(root, state, true).then(function () {
      if (state.selectedDesignCid !== submitCid) {
        throw new Error('The selected design changed before the cart was updated. Please try again.');
      }
      if (state.cartCustomizationCids[submitCid]) {
        setBusy(root, false);
        setStatus(
          root,
          'This design is already in your cart. Adjust quantity in the cart, or create or use another design.',
          'page',
          true
        );
        return null;
      }
      var design = readyDesignByCid(state, submitCid);
      var currentContract = selectedVariantContract(root, state, productForm);
      var canUseFreshFinalization = Boolean(
        design && freshContract && !state.variantNeedsReadjustment &&
        variantBindingMatches(design.variantBinding, freshContract) &&
        variantBindingMatches(currentContract, freshContract) &&
        isCompleteFinal(design)
      );
      if (canUseFreshFinalization) {
        state.currentVariantContract = currentContract;
        setStatus(root, 'Adding your finished design to cart…', 'page');
        return design;
      }
      setStatus(root, 'Securing the selected size and clock face…', 'page');
      return rebindReadyDesignVariant(root, state, productForm).then(function (saved) {
        if (saved.cid !== submitCid && saved.previousCid !== submitCid) throw new Error('The selected design changed.');
        submitCid = saved.cid;
        return saved;
      });
    }).then(function (design) {
      if (!design) return null;
      if (state.selectedDesignCid !== submitCid) {
        throw new Error('The selected design changed before the cart was updated. Please try again.');
      }
      var latestContract = selectedVariantContract(root, state, productForm);
      if (!variantBindingMatches(design.variantBinding, latestContract)) {
        setStatus(root, 'Product options changed. Securing the latest selection…', 'page');
        return rebindReadyDesignVariant(root, state, productForm).then(function (saved) {
        if (saved.cid !== submitCid && saved.previousCid !== submitCid) throw new Error('The selected design changed.');
        submitCid = saved.cid;
        return saved;
      });
      }
      return design;
    }).then(function (design) {
      return design ? loadCompletePreview(design) : null;
    }).then(function (design) {
      if (!design) return;
      if (
        window.GiftinoneVariantSync &&
        typeof window.GiftinoneVariantSync.ensureCurrentVariantFromSelections === 'function' &&
        window.GiftinoneVariantSync.ensureCurrentVariantFromSelections({ forceEnableSubmit: true }) === false
      ) throw new Error('Choose an available product option before adding to cart.');
      var confirmedContract = selectedVariantContract(root, state, productForm);
      if (
        design.cid !== submitCid || state.selectedDesignCid !== submitCid ||
        !variantBindingMatches(design.variantBinding, confirmedContract)
      ) throw new Error('Product options changed before the cart was updated. Please try again.');

      if (submitButton.disabled) {
        throw new Error('Choose an available product option before adding to cart.');
      }
      setBusy(root, false);
      if (typeof productForm.requestSubmit === 'function') {
        productForm.requestSubmit(submitButton);
      } else {
        submitButton.click();
      }
    }).catch(function (error) {
      setBusy(root, false);
      if (state.variantNeedsReadjustment) {
        return reopenReadyDesignForCurrentVariant(root, state, productForm);
      }
      setStatus(
        root,
        error && error.message || 'The selected product options could not be secured.',
        'page',
        true
      );
    });
    return true;
  }

  function initialize(root) {
    if (root.dataset.aiInitialized === 'true') return;
    root.dataset.aiInitialized = 'true';
    var scope = root.closest('product-info');
    var legacy = scope ? scope.querySelector('[data-gio-legacy-customizer]') : null;
    if (
      !scope || !legacy ||
      !document.getElementById(root.dataset.productFormId)
    ) return;

    var renderProfile;
    var variantsById;
    try {
      renderProfile = readRenderProfile(root);
      variantsById = readProductVariants(root, renderProfile);
    } catch (configurationError) {
      root.hidden = false;
      root.dataset.aiState = 'configuration-error';
      root.dataset.aiMode = 'regular';
      legacy.hidden = false;
      var aiModeButton = root.querySelector('[data-ai-mode-ai]');
      if (aiModeButton) {
        aiModeButton.disabled = true;
        aiModeButton.setAttribute('aria-disabled', 'true');
      }
      var standardButton = root.querySelector('[data-ai-mode-standard]');
      if (standardButton) standardButton.setAttribute('aria-pressed', 'true');
      var restoreStatus = root.querySelector('[data-ai-restore-status]');
      if (restoreStatus) restoreStatus.hidden = true;
      var configurationStatus = root.querySelector('[data-ai-page-status]');
      if (configurationStatus) {
        configurationStatus.textContent =
          'AI artwork is temporarily unavailable for this product. Standard photo upload is still available.';
        configurationStatus.dataset.aiError = 'true';
      }
      console.warn('GiftInOne AI Render Profile:', configurationError.message);
      return;
    }

    var state = {
      root: root,
      renderProfile: renderProfile,
      variantsById: variantsById,
      renderPoliciesByVariant: Object.create(null),
      activeRenderPolicy: freeformRenderPolicy('INITIAL'),
      variantNeedsReadjustment: false,
      currentVariantContract: null,
      currentPreviewOverlayUrl: '',
      previewOverlayUnavailable: false,
      previewOverlayStatus: renderProfile.type === 'OVERLAY'
        ? 'MISSING'
        : 'NOT_REQUIRED',
      previewOverlayLoadSequence: 0,
      previewOverlayLoadPromise: null,
      previewOverlayAttemptCancel: null,
      previewOverlayRetryTimer: null,
      previewOverlayRetryResolve: null,
      variantRebindPromise: null,
      finalizingVariantContract: null,
      runNumber: 0,
      cid: '',
      token: '',
      originalKey: '',
      originalUrl: '',
      jobStyleId: '',
      jobStyleLabel: '',
      designs: [],
      selectedDesignCid: '',
      manifestRevision: 0,
      savedMode: 'STANDARD',
      sessionReady: false,
      sessionRestoring: true,
      sessionFailed: false,
      stylesReady: false,
      canGenerate: false,
      quotaRemaining: null,
      quotaResetsAt: '',
      orderResetReceipt: '',
      orderResetReceiptExpiresAt: '',
      workingDrafts: [],
      activeWorkingDraftCid: '',
      generationFallbackDraftCid: '',
      finalizingDraftCid: '',
      restoringWorkingDraftCid: '',
      suppressDraftSync: null,
      editorDraftInteraction: false,
      editorCanonicalCid: '',
      editorCanonicalRevision: 0,
      editorInvalid: false,
      draftSaveTimer: null,
      draftSaveTimerCid: '',
      draftPersistingCid: '',
      forkSourceCid: '',
      photoSource: emptyPhotoSource(),
      photoSourceRevision: 0,
      deferredPhotoSelection: null,
      deferredPhotoSequence: 0,
      restoredJobPolls: {},
      previewSettlementPolls: {},
      openRestoredJobCid: '',
      openRestoredJobRunNumber: 0,
      activeFileObject: null,
      pendingFileObject: null,
      cropper: null,
      cropperAssetsPromise: null,
      editorResizeObserver: null,
      editorResizeScheduled: false,
      editorViewportWidth: 0,
      editorViewportHeight: 0,
      editorViewportRestorePending: false,
      editorViewportRetryCount: 0,
      editorViewportFailure: false,
      handleEditorWindowResize: null,
      editorRecipeSnapshot: null,
      editorViewSnapshot: null,
      editorPreviewUrl: '',
      reviewingAlternateStyle: false,
      rotation: 0,
      flipX: false,
      previewAttempt: 0,
      previewFailed: false,
      previewSettlementPending: false,
      previewFinalFailed: false,
      cartSubmitting: false,
      cartUnknown: false,
      cartChecking: false,
      cartRefreshPromise: null,
      cartCustomizationCids: Object.create(null),
      cartItemsByCustomizationCid: Object.create(null),
      cartMutationSequence: 0,
      selectedDesignInCart: false,
      variantUnavailable: false,
      fileObjectUrl: '',
      fileValid: false,
      selectedFileName: '',
      busy: false,
      designLibraryOpen: false,
      dialogTrigger: null,
      nextDialogTrigger: null,
      restoreFocusOnClose: false,
      timing: null,
      generationActive: false,
      previewReadyPending: false,
      pendingPreviewUrl: '',
      pendingPreviewRunNumber: 0,
      editorOpening: false,
      abortEditorOpen: null,
      activeUpload: null,
      progressTimer: null,
      progressStartedAt: 0,
      progressPhaseStartedAt: 0,
      progressPhase: '',
      progressValue: 0,
      uploadProgress: 0,
      progressAdvisoryLevel: 0,
      expectedPreviewSeconds: DEFAULT_EXPECTED_PREVIEW_SECONDS,
      slowAfterSeconds: DEFAULT_SLOW_AFTER_SECONDS,
      publicPhase: '',
      publicPhaseStartedAt: 0
    };
    root._giftInOneAiState = state;
    initializeActionConfirmation(root);
    var styleSelect = root.querySelector('[data-ai-style]');
    var fileInput = root.querySelector('[data-ai-file]');
    var generateButton = root.querySelector('[data-ai-generate]');
    var approveButton = root.querySelector('[data-ai-approve]');
    var addButton = root.querySelector('[data-ai-add]');
    var anotherButton = root.querySelector('[data-ai-another]');
    var openButton = root.querySelector('[data-ai-open]');
    var aiModeButton = root.querySelector('[data-ai-mode-ai]');
    var dialog = root.querySelector('[data-ai-dialog]');
    var productForm = document.getElementById(root.dataset.productFormId);

    state.cropperAssetsPromise = ensureCropperAssets(root);
    state.cropperAssetsPromise.catch(function (error) {
      console.warn('GiftInOne AI editor asset preload:', error.message);
    });

    var editorFrame = root.querySelector('.giftinone-ai-editor-frame');
    if (window.ResizeObserver && editorFrame) {
      state.editorResizeObserver = new window.ResizeObserver(function () {
        scheduleEditorViewportResize(root, state, false);
      });
      state.editorResizeObserver.observe(editorFrame);
    } else {
      state.handleEditorWindowResize = function () {
        scheduleEditorViewportResize(root, state, false);
      };
      window.addEventListener('resize', state.handleEditorWindowResize);
    }

    // Improve the customer-facing label only for products that initialized this
    // AI control. The legacy element IDs and click/upload behavior stay intact,
    // and non-AI products never execute this branch.
    var legacyButton = legacy.querySelector(
      '#customButton.product-info_atc_btn, #inputImage.product-info_atc_btn'
    );
    if (legacyButton) {
      legacyButton.dataset.availableLabel = 'Upload & customize photo';
      var legacyButtonLabel = legacyButton.querySelector('#customText');
      if (legacyButtonLabel && !legacyButton.classList.contains('is-sold-out')) {
        legacyButtonLabel.textContent = legacyButton.dataset.availableLabel;
      }
    }

    function syncVariantAvailability(retryFailedOverlay) {
      updateVariantAvailability(root, state, productForm);
      var overlayReady = syncBrowserPreviewOverlay(
        root,
        state,
        productForm,
        true,
        retryFailedOverlay === true
      );
      state.previewOverlayUnavailable = Boolean(
        renderProfile.type === 'OVERLAY' && !overlayReady
      );
      // The final recipe is immutable once finalization starts. Variant UI
      // may still update the current contract/overlay, but must not refit or
      // rewrite the in-flight editable draft. The cart path revalidates and
      // rebinds the finished design against the latest Variant afterward.
      if (!state.finalizingDraftCid) {
        syncVariantRenderPolicy(root, state, state.currentVariantContract);
      }
      setBusy(root, state.busy);
      var cartDesign = readyDesignByCid(state, state.selectedDesignCid);
      if (root.dataset.aiState === 'added' && cartDesign) {
        syncFinalDesignDetails(root, state, cartDesign);
        var differentCartOptions = Boolean(state.currentVariantContract &&
          !variantBindingMatches(cartDesign.variantBinding, state.currentVariantContract));
        setStatus(root, state.variantUnavailable
          ? ''
          : differentCartOptions
            ? 'Your cart item keeps the options shown above. Select Personalize another to use the new options with a new photo.'
            : '', 'page');
      } else if (state.variantUnavailable && root.dataset.aiState !== 'fallback') {
        setStatus(root, '', 'page');
      } else if (state.previewOverlayUnavailable) {
        setStatus(
          root,
          state.previewOverlayStatus === 'LOADING'
            ? PREVIEW_OVERLAY_LOADING_MESSAGE
            : PREVIEW_OVERLAY_UNAVAILABLE_MESSAGE,
          'page',
          state.previewOverlayStatus !== 'LOADING'
        );
        var overlayStatus = root.querySelector('[data-ai-page-status]');
        if (overlayStatus) overlayStatus.dataset.aiOverlayNotice = 'true';
      } else if (state.variantNeedsReadjustment && root.dataset.aiState === 'ready') {
        setStatus(
          root,
          'This option uses a different shape. Review the AI crop before adding it to your cart.',
          'page'
        );
      } else if (
        root.dataset.aiState === 'ready' &&
        !state.cartSubmitting && !state.cartUnknown
      ) {
        setStatus(root, '', 'page');
      }
    }

    if (window.MutationObserver && productForm) {
      state.variantObserver = new window.MutationObserver(syncVariantAvailability);
      state.variantObserver.observe(productForm, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['disabled', 'value', 'aria-disabled']
      });
    }
    if (productForm) {
      productForm.addEventListener('change', function () {
        syncVariantAvailability(true);
      });
    }

    setDialogView(root, 'setup');
    if (dialog) {
      dialog.addEventListener('cancel', function (event) {
        if (actionConfirmationIsOpen(root)) {
          event.preventDefault();
          cancelActionConfirmation(root);
          return;
        }
        if (state.finalizingDraftCid) {
          event.preventDefault();
          setStatus(root, 'Saving this design. Please wait for it to finish.', 'editor');
          return;
        }
        var draft = workingDraftByCid(state, state.activeWorkingDraftCid);
        if (draft && !draft.persistError && (
          draft.persistPromise ||
          state.draftSaveTimerCid === draft.cid ||
          Number(draft.persistedLocalRevision || 0) < Number(draft.revision || 0)
        )) {
          event.preventDefault();
          flushPendingDraftSave(root, state, draft, false).then(function () {
            closeAiDialog(root, state, true);
          }).catch(function (error) {
            setStatus(root, error.message || 'The design could not be saved securely.', 'editor', true);
          });
          return;
        }
        state.restoreFocusOnClose = true;
      });
      dialog.addEventListener('close', function () {
        afterDialogClose(root, state);
      });
    }

    openButton.addEventListener('click', function (event) {
      syncVariantAvailability();
      if (state.variantUnavailable && !hasExistingAiWork(state)) return;
      if (state.finalizingDraftCid || root.dataset.aiMode !== 'ai') return;
      setDialogView(
        root,
        state.cropper && !state.reviewingAlternateStyle ? 'editor' : 'setup'
      );
      var dialogTrigger = state.nextDialogTrigger || event.currentTarget || openButton;
      state.nextDialogTrigger = null;
      openAiDialog(root, state, dialogTrigger);
      if (state.pendingPreviewUrl && !state.cropper) {
        loadPendingEditor(root, state).catch(function (error) {
          if (error.message === 'AI_REQUEST_CANCELLED') return;
          recoverRuntimeFailure(root, state, error);
        });
        return;
      }
      window.requestAnimationFrame(function () {
        var target = state.generationActive
          ? root.querySelector('[data-ai-progress-title]')
          : state.cropper && !state.reviewingAlternateStyle
          ? root.querySelector('[data-ai-editor-heading]')
          : styleSelect.hidden
            ? root.querySelector('[data-ai-style-choice]') || fileInput
            : styleSelect;
        if (target) target.focus();
      });
    });
    if (aiModeButton) {
      aiModeButton.addEventListener('click', function () { chooseAiMode(root, state); });
    }
    Array.prototype.forEach.call(
      root.querySelectorAll('[data-ai-close], [data-ai-done-later]'),
      function (button) {
        button.addEventListener('click', function (event) {
          if (state.finalizingDraftCid) return;
          var draft = workingDraftByCid(state, state.activeWorkingDraftCid);
          if (!draft) {
            closeAiDialog(root, state, true);
            return;
          }
          if (draft.persistError) {
            closeAiDialog(root, state, true);
            return;
          }
          button.disabled = true;
          flushPendingDraftSave(root, state, draft, false).then(function () {
            button.disabled = false;
            closeAiDialog(root, state, true);
          }).catch(function (error) {
            button.disabled = false;
            setStatus(root, error.message || 'The design could not be saved securely.', 'editor', true);
          });
        });
      }
    );
    Array.prototype.forEach.call(
      root.querySelectorAll('[data-ai-library-toggle], [data-ai-manage-designs]'),
      function (button) {
        button.addEventListener('click', function () {
          if (
            state.busy || state.finalizingDraftCid ||
            state.cartSubmitting || state.cartUnknown ||
            designSlotCount(state) === 0
          ) return;
          setDialogView(root, 'setup');
          setDesignLibraryOpen(root, state, true, true);
        });
      }
    );
    Array.prototype.forEach.call(
      root.querySelectorAll('[data-ai-library-close]'),
      function (button) {
        button.addEventListener('click', function () {
          if (state.busy || state.finalizingDraftCid) return;
          setDesignLibraryOpen(root, state, false, true);
        });
      }
    );
    var tryAnotherStyleButton = root.querySelector('[data-ai-try-style]');
    if (tryAnotherStyleButton) {
      tryAnotherStyleButton.addEventListener('click', function () {
        reviewAnotherStyle(root, state);
      });
    }
    var returnPreviewButton = root.querySelector('[data-ai-return-preview]');
    if (returnPreviewButton) {
      returnPreviewButton.addEventListener('click', function () {
        returnToCurrentPreview(root, state);
      });
    }

    // Session recovery is independent from the generation switch and style
    // catalog. A temporary /styles failure must never hide server-restored
    // designs or prevent editing, deleting, finalizing, or using them.
    legacy.hidden = false;
    root.hidden = false;
    root.dataset.aiMode = 'regular';
    root.dataset.aiState = 'regular';
    clearOrderProperties(root);
    setStatus(root, 'Restoring your designs…', 'page');
    setStatus(root, 'Loading available AI styles…', 'setup');
    renderDesignGallery(root, state);
    setBusy(root, false);

    bootstrapAndRestoreSession(root, state).then(function (response) {
      // New Lambda versions bundle session, current product manifest, and the
      // style catalog into this one response. Older versions are supported by
      // initializeStyleCatalog's GET fallback during a rolling deployment.
      initializeStyleCatalog(root, state, styleSelect, scope, response);
    }).catch(function (error) {
      state.sessionFailed = true;
      fallbackToLegacy(root, error.message);
    });

    fileInput.addEventListener('change', function () {
      var file = fileInput.files && fileInput.files[0];
      fileInput.removeAttribute('aria-invalid');
      applySelectedPhotoFile(root, state, file, true);
    });

    generateButton.addEventListener('click', function () {
      if (state.busy || state.finalizingDraftCid) return;
      updateVariantAvailability(root, state, productForm);
      if (state.pendingPreviewUrl && !state.cropper) {
        setBusy(root, true);
        setStatus(root, 'Opening your generated AI preview…', 'setup');
        setStatus(root, 'Opening your generated AI preview…', 'page');
        loadPendingEditor(root, state).catch(function (error) {
          if (error.message === 'AI_REQUEST_CANCELLED') return;
          recoverRuntimeFailure(root, state, error);
        });
        return;
      }
      if (state.variantUnavailable) {
        setStatus(root,
          'Choose an available size or product option before generating a preview.',
          'setup', true);
        return;
      }
      if (state.canGenerate === false) {
        setStatus(root,
          'You have no free previews remaining today. You can still edit or use your saved designs.',
          'setup', true);
        return;
      }
      var selectedStyle = currentStyleMetadata(root);
      var generationVariantContract;
      try {
        if (
          window.GiftinoneVariantSync &&
          typeof window.GiftinoneVariantSync.ensureCurrentVariantFromSelections === 'function' &&
          window.GiftinoneVariantSync.ensureCurrentVariantFromSelections({ forceEnableSubmit: true }) === false
        ) throw new Error('Choose an available product option before continuing.');
        generationVariantContract = selectedVariantContract(root, state, productForm);
      } catch (variantError) {
        setStatus(root, variantError.message, 'setup', true);
        return;
      }
      state.activeRenderPolicy = effectiveRenderPolicyForContract(
        state,
        generationVariantContract
      );
      if (
        state.previewFailed && state.cid &&
        selectedStyle.styleId === state.jobStyleId
      ) {
        trackAiEvent(root, 'preview_generate', { retry: true });
        state.runNumber += 1;
        var retryRun = state.runNumber;
        state.previewFailed = false;
        state.timing = { startedAt: Date.now(), uploadCompletedAt: Date.now() };
        setBusy(root, true);
        beginEstimatedProgress(
          root,
          state,
          'queued',
          'Retrying your preview',
          'Attempt ' + (state.previewAttempt + 1) + ' is securely queued.',
          25
        );
        setStatus(root, 'Retrying your AI preview...', 'setup');
        setStatus(root, 'Retrying your AI preview…', 'page');
        startOrRetryPreview(root, state, retryRun).catch(function (error) {
          if (retryRun !== state.runNumber || error.message === 'AI_REQUEST_CANCELLED') return;
          if (
            !showPreviewFailure(root, state, error)
          ) recoverRuntimeFailure(root, state, error);
        });
        return;
      }

      if (designSlotCount(state) >= MAX_WORKING_DRAFTS) {
        renderWorkingDrafts(root, state);
        setStatus(
          root,
          'You already have ' + MAX_WORKING_DRAFTS +
            ' AI designs. Delete one before generating another preview.',
          'setup',
          true
        );
        setBusy(root, false);
        return;
      }

      if (state.cropper) updateWorkingDraftFromEditor(root, state, true);
      state.generationFallbackDraftCid = state.activeWorkingDraftCid || '';

      var generationSource = currentPhotoSource(state);
      var file = generationSource.kind === 'local'
        ? generationSource.file || null
        : null;
      var forkSourceDraft = generationSource.kind === 'saved'
        ? photoSourceDraft(state, generationSource)
        : null;
      var contentType;
      if (file) {
        try {
          contentType = validateOriginalFile(file);
        } catch (error) {
          fileInput.setAttribute('aria-invalid', 'true');
          setStatus(root, error.message, 'setup', true);
          return;
        }
      } else if (!forkSourceDraft) {
        if (generationSource.kind === 'saved') {
          setPhotoSource(root, state, emptyPhotoSource());
        }
        setStatus(root, 'Choose a photo or select a saved photo to continue.', 'setup', true);
        return;
      }
      trackAiEvent(root, 'preview_generate', {
        source: forkSourceDraft ? 'saved_photo' : 'new_photo',
        retry: false
      });
      setDraftReturnState(root, state, false);
      state.runNumber += 1;
      var runNumber = state.runNumber;
      state.cid = '';
      state.token = '';
      state.originalKey = forkSourceDraft ? forkSourceDraft.originalKey || '' : '';
      state.originalUrl = forkSourceDraft ? forkSourceDraft.originalUrl || '' : '';
      state.jobStyleId = selectedStyle.styleId;
      state.jobStyleLabel = selectedStyle.label;
      state.previewAttempt = 0;
      state.previewFailed = false;
      state.previewSettlementPending = false;
      state.previewFinalFailed = false;
      state.cartUnknown = false;
      delete root.dataset.aiCartState;
      state.expectedPreviewSeconds = DEFAULT_EXPECTED_PREVIEW_SECONDS;
      state.slowAfterSeconds = DEFAULT_SLOW_AFTER_SECONDS;
      state.publicPhase = '';
      state.publicPhaseStartedAt = 0;
      state.timing = { startedAt: Date.now() };
      state.activeWorkingDraftCid = '';
      // generationSource is the immutable request snapshot. Keep the same
      // canonical source selected so retries and another-style actions render
      // exactly the source that will be reused.
      syncLegacyPhotoState(state);
      destroyEditor(state);
      renderWorkingDrafts(root, state);
      var selectedDesign = readyDesignByCid(state, state.selectedDesignCid);
      if (selectedDesign && root.dataset.aiMode === 'ai') {
        applyReadyDesign(root, state, selectedDesign, { announce: false });
      } else if (root.dataset.aiMode === 'regular') {
        clearOrderProperties(root);
        setFinalState(root, 'hidden');
        root.dataset.aiState = 'regular';
      } else {
        clearOrderProperties(root);
        setFinalState(root, 'hidden');
        root.dataset.aiState = 'active';
      }
      setDialogView(root, 'setup');
      setBusy(root, true);
      beginEstimatedProgress(
        root,
        state,
        forkSourceDraft ? 'queued' : 'preparing',
        forkSourceDraft ? 'Preparing another style' : 'Preparing your photo',
        forkSourceDraft ? 'Reusing your securely saved photo.' : 'Creating a secure upload.',
        forkSourceDraft ? 25 : 5
      );
      setStatus(root, forkSourceDraft ? 'Preparing another style…' : 'Creating a secure upload...', 'setup');
      setStatus(root, 'Preparing your AI preview…', 'page');

      var createPreviewPromise;
      if (forkSourceDraft) {
        createPreviewPromise = apiRequest(
          root,
          '/ai/jobs/' + encodeURIComponent(forkSourceDraft.cid) + '/variations',
          'POST',
          '',
          {
            styleId: selectedStyle.styleId,
            productId: generationVariantContract.productId,
            variantId: generationVariantContract.variantId,
            selectedOptions: generationVariantContract.selectedOptions
          }
        ).then(function (job) {
          if (runNumber !== state.runNumber) throw new Error('AI_REQUEST_CANCELLED');
          if (
            !CID_PATTERN.test(String(job && job.cid || '')) || job.status !== 'UPLOADED' ||
            !job.style || String(job.style.styleId || '') !== selectedStyle.styleId
          ) {
            throw new Error('The style variation response is invalid.');
          }
          if (!variantBindingMatches(job.variantBinding, generationVariantContract)) {
            throw new Error('The selected product options could not be secured.');
          }
          rememberAuthoritativeRenderPolicy(
            state,
            job,
            generationVariantContract,
            true
          );
          state.cid = job.cid;
          state.token = '';
          state.jobStyleId = String(job.style.styleId);
          state.jobStyleLabel = displayStyleLabel(
            state.jobStyleId,
            job.style.label || selectedStyle.label,
            'AI design'
          );
          state.timing.jobCreatedAt = Date.now();
          state.timing.uploadCompletedAt = Date.now();
          setProgressStage(root, state, 'queued', 'Waiting for AI',
            'Your saved photo is securely queued with the new style.', 25);
          setStatus(root, 'Generating your AI preview…', 'setup');
          setStatus(root, 'Generating your AI preview…', 'page');
          return startOrRetryPreview(root, state, runNumber);
        });
      } else {
        createPreviewPromise = apiRequest(root, '/ai/jobs', 'POST', '', {
          styleId: styleSelect.value,
          productId: generationVariantContract.productId,
          variantId: generationVariantContract.variantId,
          selectedOptions: generationVariantContract.selectedOptions,
          file: { name: file.name, contentType: contentType, size: file.size }
        }).then(function (job) {
          if (runNumber !== state.runNumber) throw new Error('AI_REQUEST_CANCELLED');
          if (state.timing) state.timing.jobCreatedAt = Date.now();
          applyProgressResponse(state, job);
          if (!CID_PATTERN.test(String(job.cid || ''))) {
            throw new Error('The customization response is invalid.');
          }
          if (!variantBindingMatches(job.variantBinding, generationVariantContract)) {
            throw new Error('The selected product options could not be secured.');
          }
          rememberAuthoritativeRenderPolicy(
            state,
            job,
            generationVariantContract,
            true
          );
          var original = originalPhotoFromUpload(job.originalUpload, job.cid);
          state.cid = job.cid;
          state.token = job.customizationToken || '';
          state.originalKey = original.key;
          state.originalUrl = original.url;
          setProgressStage(root, state, 'uploading', 'Uploading your photo',
            'Your original photo is being stored securely.', 5);
          setStatus(root, 'Uploading your photo...', 'setup');
          setStatus(root, 'Uploading your photo…', 'page');
          return uploadOriginal(job.originalUpload, file, state, function (fraction) {
            if (runNumber !== state.runNumber) return;
            state.uploadProgress = fraction;
            updateProgressUi(root, state);
          });
        }).then(function () {
          if (state.timing) state.timing.uploadCompletedAt = Date.now();
          state.uploadProgress = 1;
          setProgressStage(root, state, 'queued', 'Waiting for AI',
            'Your photo is uploaded and securely queued.', 25);
          setStatus(root, 'Generating your AI preview...', 'setup');
          setStatus(root, 'Generating your AI preview…', 'page');
          return startOrRetryPreview(root, state, runNumber);
        });
      }
      createPreviewPromise.catch(function (error) {
        if (runNumber !== state.runNumber || error.message === 'AI_REQUEST_CANCELLED') return;
        if (
          !showPreviewFailure(root, state, error)
        ) {
          if (!restoreFallbackWorkingDraft(root, state, error)) {
            recoverRuntimeFailure(root, state, error);
          }
        }
      });
    });

    approveButton.addEventListener('click', function () {
      updateVariantAvailability(root, state, productForm);
      if (state.variantUnavailable) {
        setBusy(root, state.busy);
        setStatus(root, 'Choose an available size or product option before adding to cart.', 'editor', true);
        return;
      }
      if (previewOverlayBlocksAction(state, 'confirm')) {
        setStatus(root, PREVIEW_OVERLAY_UNAVAILABLE_MESSAGE, 'editor', true);
        var overlayEditorStatus = root.querySelector('[data-ai-editor-status]');
        if (overlayEditorStatus) overlayEditorStatus.dataset.aiOverlayNotice = 'true';
        return;
      }
      if (
        !state.cid || !state.cropper ||
        state.busy || state.finalizingDraftCid ||
        state.suppressDraftSync || state.editorDraftInteraction ||
        state.cartSubmitting || state.cartUnknown ||
        state.activeWorkingDraftCid !== state.cid
      ) return;
      var runNumber = state.runNumber;
      var finalizeCid = state.cid;
      var finalizeToken = '';
      var finalizeStyleId = String(state.jobStyleId || '');
      var recipe;
      var finalVariantContract;
      var requiresCartOwnershipCheck = false;
      try {
        if (
          window.GiftinoneVariantSync &&
          typeof window.GiftinoneVariantSync.ensureCurrentVariantFromSelections === 'function' &&
          window.GiftinoneVariantSync.ensureCurrentVariantFromSelections({ forceEnableSubmit: true }) === false
        ) throw new Error('Choose an available product option before continuing.');
        finalVariantContract = selectedVariantContract(root, state, productForm);
        var canonicalDraft = workingDraftByCid(state, finalizeCid);
        if (
          !canonicalDraft || !canonicalDraft.recipe || state.editorInvalid ||
          canonicalDraft.persistError || canonicalDraft.persistPromise ||
          Number(canonicalDraft.persistedLocalRevision || 0) !== Number(canonicalDraft.revision || 0) ||
          canonicalDraft.cid !== state.activeWorkingDraftCid ||
          state.editorCanonicalCid !== canonicalDraft.cid ||
          Number(state.editorCanonicalRevision) !== Number(canonicalDraft.revision || 1)
        ) {
          throw new Error('This preview is not ready to use. Wait until saving finishes, then try again.');
        }
        assertRestorableWorkingDraft(root, canonicalDraft);
        requiresCartOwnershipCheck = Boolean(
          canonicalDraft.requiresCartOwnershipCheck
        );
        // Every real crop/zoom/tool interaction commits the working draft.
        // Responsive display reconstruction does not. Finalization therefore
        // sends the active canonical recipe without comparing it to Cropper's
        // current container-dependent geometry.
        finalizeToken = canonicalDraft.token;
        recipe = cloneRecipe(canonicalDraft.recipe);
        state.editorRecipeSnapshot = cloneRecipe(recipe);
      } catch (error) {
        setStatus(root, error.message, undefined, true);
        return;
      }
      state.finalizingDraftCid = finalizeCid;
      state.finalizingVariantContract = finalVariantContract;
      var finalizingDraft = workingDraftByCid(state, finalizeCid);
      if (finalizingDraft) finalizingDraft.status = 'FINALIZING';
      renderWorkingDrafts(root, state);
      setBusy(root, true);
      setStatus(
        root,
        requiresCartOwnershipCheck
          ? 'Checking your cart before preparing this design…'
          : 'Preparing your final preview...',
        'editor'
      );
      var cartOwnershipCheck = requiresCartOwnershipCheck
        ? refreshCartCustomizationState(root, state, true).catch(function () {
            var cartCheckError = new Error(
              'Your cart could not be verified. Refresh the page before finishing this design.'
            );
            cartCheckError.code = 'FINALIZE_CART_CHECK_FAILED';
            throw cartCheckError;
          })
        : Promise.resolve();
      cartOwnershipCheck.then(function () {
        if (
          runNumber !== state.runNumber || finalizeCid !== state.cid ||
          finalizeCid !== state.activeWorkingDraftCid ||
          finalizeCid !== state.finalizingDraftCid
        ) throw new Error('AI_REQUEST_CANCELLED');
        if (state.cartCustomizationCids[finalizeCid]) {
          var inCartError = new Error(
            'This exact design is already in your cart. Remove it from the cart before editing it.'
          );
          inCartError.code = 'FINALIZE_DESIGN_IN_CART';
          throw inCartError;
        }
        if (
          window.GiftinoneVariantSync &&
          typeof window.GiftinoneVariantSync.ensureCurrentVariantFromSelections === 'function' &&
          window.GiftinoneVariantSync.ensureCurrentVariantFromSelections({ forceEnableSubmit: true }) === false
        ) {
          var unavailableError = new Error('Choose an available product option before continuing.');
          unavailableError.code = 'FINALIZE_VARIANT_UNAVAILABLE';
          throw unavailableError;
        }
        var currentFinalizeContract;
        try {
          currentFinalizeContract = selectedVariantContract(root, state, productForm);
        } catch (variantError) {
          variantError.code = 'FINALIZE_VARIANT_UNAVAILABLE';
          throw variantError;
        }
        if (!variantBindingMatches(currentFinalizeContract, finalVariantContract)) {
          var changedVariantError = new Error(
            'Product options changed. Review the updated crop before adding this design to cart.'
          );
          changedVariantError.code = 'FINALIZE_VARIANT_CHANGED';
          throw changedVariantError;
        }
        syncBrowserPreviewOverlay(root, state, productForm, true, true);
        if (previewOverlayBlocksAction(state, 'confirm')) {
          var overlayError = new Error(PREVIEW_OVERLAY_UNAVAILABLE_MESSAGE);
          overlayError.code = 'FINALIZE_OVERLAY_UNAVAILABLE';
          throw overlayError;
        }
        trackAiEvent(root, 'use_design', { source: 'editor' });
        setStatus(root, state.previewFinalFailed
          ? 'Retrying your final preview...'
          : 'Preparing your final preview...');
        state.previewFinalFailed = false;
        return apiRequest(
          root,
          '/ai/jobs/' + encodeURIComponent(finalizeCid) + '/recipe',
          'PUT',
          finalizeToken,
          {
            recipe: recipe,
            productId: finalVariantContract.productId,
            variantId: finalVariantContract.variantId,
            selectedOptions: finalVariantContract.selectedOptions
          }
        );
      }).then(function (response) {
        finalizeCid = adoptEditorRevision(root, state, finalizeCid, response);
        finalizeToken = state.token;
        if (response && response.status === 'READY_FOR_ORDER') {
          assertJobOperationIdentity(response, finalizeCid, finalizeStyleId);
        }
        rememberAuthoritativeRenderPolicy(
          state,
          response,
          finalVariantContract,
          true
        );
        // A fast backend response may already contain the finished contract.
        // Otherwise keep the existing active-AI polling cadence unchanged.
        if (response && response.status === 'READY_FOR_ORDER') return response;
        return pollJob(
          root,
          state,
          runNumber,
          true,
          finalizeCid,
          finalizeToken
        );
      }).then(loadCompletePreview).then(function (job) {
        if (
          runNumber !== state.runNumber || finalizeCid !== state.cid ||
          finalizeCid !== state.activeWorkingDraftCid ||
          finalizeCid !== state.finalizingDraftCid
        ) return;
        if (!variantBindingMatches(job.variantBinding, finalVariantContract)) {
          throw new Error('The selected product options could not be secured.');
        }
        state.finalizingDraftCid = '';
        var readyDesign = markReady(root, state, job);
        state.finalizingVariantContract = null;
        setBusy(root, false);
        if (readyDesign) {
          submitReadyDesign(root, state, productForm, {
            freshContract: finalVariantContract
          });
        }
      }).catch(function (error) {
        if (runNumber !== state.runNumber || error.message === 'AI_REQUEST_CANCELLED') return;
        state.finalizingDraftCid = '';
        state.finalizingVariantContract = null;
        var retryableDraft = workingDraftByCid(state, finalizeCid);
        if (retryableDraft) retryableDraft.status = 'PREVIEW_READY';
        renderWorkingDrafts(root, state);
        if (
          error && [
            'FINALIZE_CART_CHECK_FAILED',
            'FINALIZE_DESIGN_IN_CART',
            'FINALIZE_VARIANT_UNAVAILABLE',
            'FINALIZE_VARIANT_CHANGED',
            'FINALIZE_OVERLAY_UNAVAILABLE',
            'COMPLETE_PREVIEW_LOAD_FAILED'
          ].indexOf(error.code) >= 0
        ) {
          setBusy(root, false);
          syncVariantAvailability(true);
          setStatus(root, error.message, 'editor', true);
          return;
        }
        if (
          !showPreviewFailure(root, state, error)
        ) recoverRuntimeFailure(root, state, error);
      });
    });

    root.querySelector('[data-ai-zoom-out]').addEventListener('click', function () {
      if (state.cropper && !state.busy && !state.editorInvalid) {
        state.cropper.zoom(-0.1);
        syncEditorRecipeSnapshot(root, state, true);
      }
    });
    root.querySelector('[data-ai-zoom-in]').addEventListener('click', function () {
      if (state.cropper && !state.busy && !state.editorInvalid) {
        state.cropper.zoom(0.1);
        syncEditorRecipeSnapshot(root, state, true);
      }
    });
    root.querySelector('[data-ai-rotate-left]').addEventListener('click', function () {
      if (state.busy || state.editorInvalid) return;
      rotateAndRefitEditor(root, state, -90);
    });
    root.querySelector('[data-ai-rotate-right]').addEventListener('click', function () {
      if (state.busy || state.editorInvalid) return;
      rotateAndRefitEditor(root, state, 90);
    });
    root.querySelector('[data-ai-flip-x]').addEventListener('click', function () {
      if (state.busy || state.editorInvalid) return;
      state.flipX = !state.flipX;
      if (state.cropper) state.cropper.scaleX(state.flipX ? -1 : 1);
      updateTransformSummary(root, state);
      syncEditorRecipeSnapshot(root, state, true);
    });
    root.querySelector('[data-ai-reset]').addEventListener('click', function () {
      if (state.busy || state.editorInvalid) return;
      if (state.cropper) {
        state.cropper.reset();
        fitEditorCropToImage(
          state.cropper,
          activeEditorAspectRatio(state)
        );
      }
      state.rotation = 0;
      state.flipX = false;
      updateTransformSummary(root, state);
      syncEditorRecipeSnapshot(root, state, true);
      setStatus(root, 'Editor reset. You can drag, zoom, rotate, or resize the crop.', 'editor');
    });

    var editReadyButton = root.querySelector('[data-ai-edit]');
    if (editReadyButton) {
      editReadyButton.addEventListener('click', function (event) {
        reopenReadyDesignForCurrentVariant(
          root,
          state,
          productForm,
          event.currentTarget || editReadyButton
        );
      });
    }

    addButton.addEventListener('click', function (event) {
      if (state.variantNeedsReadjustment) {
        reopenReadyDesignForCurrentVariant(
          root,
          state,
          productForm,
          event.currentTarget || addButton
        );
        return;
      }
      submitReadyDesign(root, state, productForm);
    });

    var reviewOptionsButton = root.querySelector('[data-ai-review-options]');
    if (reviewOptionsButton) {
      reviewOptionsButton.addEventListener('click', function () {
        focusProductOptions(root);
      });
    }

    var designGallery = root.querySelector('[data-ai-design-gallery]');
    if (designGallery) {
      designGallery.addEventListener('click', function (event) {
        var removeButton = event.target && event.target.closest
          ? event.target.closest('[data-ai-design-remove]')
          : null;
        if (removeButton && designGallery.contains(removeButton)) {
          event.preventDefault();
          if (
            state.busy || state.finalizingDraftCid ||
            state.cartSubmitting || state.cartUnknown
          ) return;
          var removeCid = String(removeButton.dataset.aiDesignRemove || '');
          var designToRemove = readyDesignByCid(state, removeCid);
          if (!designToRemove) return;
          var deletingSelected = removeCid === state.selectedDesignCid;
          var removeIndex = state.designs.indexOf(designToRemove);
          openActionConfirmation(root, {
            trigger: removeButton,
            title: 'Remove from My designs?',
            message: displayStyleLabel(
              designToRemove.styleId,
              designToRemove.styleLabel,
              'This AI design'
            ) +
              ' will be removed from My designs to free one saved-design space. Items already in your cart will keep their photos and options.',
            cancelLabel: 'Keep design',
            acceptLabel: 'Remove saved design',
            pendingLabel: 'Removing…',
            pendingStatus: 'Removing from My designs',
            errorMessage: function (error) {
              return error && error.message || 'The design could not be deleted. Please try again.';
            },
            action: function () {
              setBusy(root, true);
              return deletePersistedDraft(root, state, designToRemove).then(function () {
                state.designs = state.designs.filter(function (design) {
                  return design.cid !== removeCid;
                });
                clearPhotoSourceForCid(root, state, removeCid);
                if (deletingSelected) {
                  var scope = root.closest('product-info');
                  var legacy = scope ? scope.querySelector('[data-gio-legacy-customizer]') : null;
                  state.selectedDesignCid = '';
                  state.savedMode = 'STANDARD';
                  state.selectedDesignInCart = false;
                  var continueAi = root.dataset.aiMode === 'ai';
                  root.dataset.aiState = continueAi ? 'active' : 'regular';
                  if (continueAi) rememberPhotoMode(root, 'ai');
                  clearOrderProperties(root);
                  setFinalState(root, 'hidden');
                  if (legacy) legacy.hidden = continueAi;
                  var launcher = root.querySelector('[data-ai-open]');
                  if (launcher) launcher.hidden = !continueAi;
                  setStatus(root,
                    'Saved-design space freed. Your cart items are unchanged. You can personalize another.',
                    'page');
                } else {
                  setStatus(root, 'Saved-design space freed. You can create another preview now.', 'setup');
                }
                renderDesignGallery(root, state);
                setBusy(root, false);
                return function () {
                  var remaining = designGallery.querySelectorAll('[data-ai-design-cid]');
                  return remaining.length
                    ? remaining[Math.min(Math.max(removeIndex, 0), remaining.length - 1)]
                    : (state.designLibraryOpen && designLibraryItemFocusTarget(root)) ||
                      root.querySelector('[data-ai-style-choice]') || styleSelect;
                };
              }).catch(function (error) {
                setBusy(root, false);
                setStatus(
                  root,
                  error.message || 'The design could not be deleted.',
                  'setup',
                  true
                );
                throw error;
              });
            }
          });
          return;
        }
        var button = event.target && event.target.closest
          ? event.target.closest('[data-ai-design-cid]')
          : null;
        if (!button || !designGallery.contains(button)) return;
        if (
          state.busy || state.finalizingDraftCid ||
          state.cartSubmitting || state.cartUnknown
        ) return;
        var design = readyDesignByCid(state, button.dataset.aiDesignCid);
        if (design) {
          if (state.cartCustomizationCids[design.cid]) {
            setBusy(root, true);
            persistProductSelection(root, state, 'AI', design.cid).then(function () {
              rememberPhotoMode(root, 'ai');
              applyReadyDesign(root, state, design, { announce: false });
              setBusy(root, false);
              closeAiDialog(root, state, false);
              focusReadySummary(root);
              refreshCartCustomizationState(root, state, false).catch(function () {});
            }).catch(function (error) {
              setBusy(root, false);
              setStatus(root, error.message || 'This design could not be selected.', 'setup', true);
            });
          } else {
            reopenReadyDesign(root, state, productForm, design, button);
          }
        }
      });
    }

    root.addEventListener('click', function (event) {
      var restoredPollButton = event.target && event.target.closest
        ? event.target.closest('[data-ai-restored-job-poll]')
        : null;
      var restoredRetryButton = event.target && event.target.closest
        ? event.target.closest('[data-ai-restored-job-retry]')
        : null;
      var restoredOpenButton = event.target && event.target.closest
        ? event.target.closest('[data-ai-restored-job-open]')
        : null;
      var restoredButton = restoredPollButton || restoredRetryButton || restoredOpenButton;
      if (restoredButton && root.contains(restoredButton)) {
        event.preventDefault();
        if (
          state.busy || state.cartSubmitting || state.cartUnknown ||
          state.cartChecking
        ) return;
        var restoredCid = String(
          restoredButton.dataset.aiRestoredJobPoll ||
          restoredButton.dataset.aiRestoredJobRetry ||
          restoredButton.dataset.aiRestoredJobOpen || ''
        );
        var restoredDraft = workingDraftByCid(state, restoredCid);
        if (!restoredDraft) return;
        setDesignLibraryOpen(root, state, false, false);
        if (restoredRetryButton) {
          retryRestoredJob(root, state, restoredDraft);
          return;
        }
        state.runNumber += 1;
        var restoredRunNumber = state.runNumber;
        state.openRestoredJobCid = restoredCid;
        state.openRestoredJobRunNumber = restoredRunNumber;
        state.cid = restoredCid;
        state.token = '';
        state.originalKey = '';
        state.originalUrl = restoredDraft.originalUrl || '';
        state.jobStyleId = restoredDraft.styleId;
        state.jobStyleLabel = restoredDraft.styleLabel;
        if (restoredOpenButton) {
          setBusy(root, true);
          fetchRestoredWorkingDraft(root, state, restoredCid).then(function (draft) {
            if (
              restoredRunNumber !== state.runNumber ||
              draft.cid !== state.openRestoredJobCid
            ) return false;
            state.openRestoredJobCid = '';
            state.openRestoredJobRunNumber = 0;
            setBusy(root, false);
            return switchToWorkingDraft(root, state, draft.cid);
          }).catch(function (error) {
            if (restoredRunNumber !== state.runNumber) return;
            state.openRestoredJobCid = '';
            state.openRestoredJobRunNumber = 0;
            setBusy(root, false);
            setStatus(root, error.message || 'This preview could not be opened.', 'setup', true);
          });
          return;
        }
        beginEstimatedProgress(
          root,
          state,
          restoredDraft.status === 'PREVIEW_RUNNING' ? 'running' : 'queued',
          'Restoring your preview',
          'Generation continues securely even when you leave this product.',
          restoredDraft.status === 'PREVIEW_RUNNING' ? 35 : 25
        );
        setStatus(root, 'Your preview is still generating. It will open when ready.', 'setup');
        pollRestoredJob(root, state, restoredCid);
        return;
      }

      var clearDraftsButton = event.target && event.target.closest
        ? event.target.closest('[data-ai-clear-drafts]')
        : null;
      if (clearDraftsButton && root.contains(clearDraftsButton)) {
        event.preventDefault();
        if (state.busy || !state.workingDrafts.length) return;
        var previewCount = state.workingDrafts.length;
        openActionConfirmation(root, {
          trigger: clearDraftsButton,
          title: 'Delete all previews?',
          message: previewCount + (previewCount === 1
            ? ' in-progress preview will be permanently deleted. Finished designs will remain.'
            : ' in-progress previews will be permanently deleted. Finished designs will remain.'),
          cancelLabel: 'Keep previews',
          acceptLabel: 'Delete previews',
          pendingLabel: 'Deleting…',
          pendingStatus: 'Deleting previews',
          errorMessage: function (error) {
            return error && error.message || 'Not every preview could be deleted. Please try again.';
          },
          action: function () {
            var draftsToDelete = state.workingDrafts.slice();
            var deleteQueue = Promise.resolve();
            setBusy(root, true);
            draftsToDelete.forEach(function (draft) {
              deleteQueue = deleteQueue.then(function () {
                return deletePersistedDraft(root, state, draft);
              }).then(function () {
                removeWorkingDraft(state, draft.cid);
              });
            });
            return deleteQueue.then(function () {
              state.runNumber += 1;
              destroyEditor(state);
              state.cid = '';
              state.token = '';
              state.originalKey = '';
              state.originalUrl = '';
              setDraftReturnState(root, state, false);
              setDialogView(root, 'setup');
              renderWorkingDrafts(root, state);
              renderPhotoSourceUi(root, state);
              setBusy(root, false);
              setStatus(root, 'All AI previews for this product were deleted.', 'setup');
              return function () {
                return (state.designLibraryOpen && designLibraryItemFocusTarget(root)) ||
                  root.querySelector('[data-ai-style-choice]') || styleSelect;
              };
            }).catch(function (error) {
              return reconcilePartialDraftDeletion(
                root,
                state,
                error,
                'Not every preview could be deleted.'
              );
            });
          }
        });
        return;
      }

      var selectDraft = event.target && event.target.closest
        ? event.target.closest('[data-ai-working-draft-select]')
        : null;
      if (selectDraft && root.contains(selectDraft)) {
        event.preventDefault();
        if (state.busy || state.cartSubmitting || state.cartUnknown) return;
        setDesignLibraryOpen(root, state, false, false);
        switchToWorkingDraft(root, state, selectDraft.dataset.aiWorkingDraftSelect);
        return;
      }

      var removeDraft = event.target && event.target.closest
        ? event.target.closest('[data-ai-working-draft-remove]')
        : null;
      if (!removeDraft || !root.contains(removeDraft)) return;
      event.preventDefault();
      var removeCid = String(removeDraft.dataset.aiWorkingDraftRemove || '');
      if (
        state.busy || state.cartSubmitting || state.cartUnknown ||
        removeCid === state.finalizingDraftCid
      ) return;
      var draft = workingDraftByCid(state, removeCid);
      if (!draft) return;
      var deletingActive = removeCid === state.activeWorkingDraftCid;
      var draftIndex = state.workingDrafts.indexOf(draft);
      openActionConfirmation(root, {
        trigger: removeDraft,
        title: 'Delete this preview?',
        message: displayStyleLabel(
          draft.styleId,
          draft.styleLabel,
          'This AI preview'
        ) +
          ' will be permanently deleted. This frees one design space.',
        cancelLabel: 'Keep preview',
        acceptLabel: 'Delete preview',
        pendingLabel: 'Deleting…',
        pendingStatus: 'Deleting preview',
        errorMessage: function (error) {
          return error && error.message || 'The preview could not be deleted. Please try again.';
        },
        action: function () {
          setBusy(root, true);
          return deletePersistedDraft(root, state, draft).then(function () {
            if (deletingActive) {
              state.runNumber += 1;
              destroyEditor(state);
              state.cid = '';
              state.token = '';
              state.originalKey = '';
              state.originalUrl = '';
              setDraftReturnState(root, state, false);
              setDialogView(root, 'setup');
            }
            removeWorkingDraft(state, removeCid);
            renderWorkingDrafts(root, state);
            renderPhotoSourceUi(root, state);
            setBusy(root, false);
            setStatus(root, 'AI preview deleted.', deletingActive ? 'setup' : 'editor');
            return function () {
              var list = root.querySelector(
                '[data-ai-working-draft-list="' + (deletingActive ? 'setup' : 'editor') + '"]'
              );
              var remaining = list
                ? list.querySelectorAll('[data-ai-working-draft-select]')
                : [];
              return remaining.length
                ? remaining[Math.min(Math.max(draftIndex, 0), remaining.length - 1)]
                : (state.designLibraryOpen && designLibraryItemFocusTarget(root)) ||
                  root.querySelector('[data-ai-style-choice]') || styleSelect;
            };
          }).catch(function (error) {
            setBusy(root, false);
            setStatus(root, error.message || 'The preview could not be deleted.',
              deletingActive ? 'editor' : 'setup', true);
            throw error;
          });
        }
      });
    });

    Array.prototype.forEach.call(
      root.querySelectorAll('[data-ai-another-style]'),
      function (button) {
        button.addEventListener('click', function (event) {
          if (
            state.busy || state.finalizingDraftCid ||
            state.cartSubmitting || state.cartUnknown
          ) return;
          if (openSavedDesignLibrary(root, state, event.currentTarget)) return;
          if (
            state.generationActive || state.previewReadyPending ||
            state.pendingPreviewUrl || state.cropper || state.editorOpening
          ) {
            var preserveRegularMode = root.dataset.aiMode === 'regular';
            if (preserveRegularMode) {
              root.dataset.aiState = 'regular';
              clearOrderProperties(root);
              legacy.hidden = false;
              renderDesignGallery(root, state);
            } else {
              root.dataset.aiMode = 'ai';
              var selected = readyDesignByCid(state, state.selectedDesignCid);
              if (selected) applyReadyDesign(root, state, selected, { announce: false });
              else {
                legacy.hidden = true;
                root.dataset.aiState = 'active';
                renderDesignGallery(root, state);
              }
            }
            openButton.hidden = false;
            state.nextDialogTrigger = event.currentTarget;
            openButton.click();
            return;
          }
          prepareAnotherStyle(root, state, true, false, event.currentTarget);
        });
      }
    );

    Array.prototype.forEach.call(root.querySelectorAll('[data-ai-new]'), function (button) {
      button.addEventListener('click', function (event) {
        if (
          state.busy || state.finalizingDraftCid ||
          state.cartSubmitting || state.cartUnknown
        ) return;
        var hasAiPreviews = state.workingDrafts.length > 0 || Boolean(state.cid);
        if (!hasAiPreviews) {
          resetForNewDesign(root, state, true);
          return;
        }
        var changePhotoPreviewCount = Math.max(
          state.workingDrafts.length,
          state.cid ? 1 : 0
        );
        openActionConfirmation(root, {
          trigger: event.currentTarget || button,
          title: 'Use a different photo?',
          message: changePhotoPreviewCount + (changePhotoPreviewCount === 1
            ? ' in-progress preview will be permanently deleted. Finished designs will remain.'
            : ' in-progress previews will be permanently deleted. Finished designs will remain.'),
          cancelLabel: 'Keep current photo',
          acceptLabel: 'Use new photo',
          pendingLabel: 'Changing…',
          pendingStatus: 'Preparing a new photo',
          errorMessage: function (error) {
            return error && error.message || 'The previews could not be deleted. Please try again.';
          },
          action: function () {
            if (!state.workingDrafts.length) {
              resetForNewDesign(root, state, true);
              return function () { return setupFocusTarget(root); };
            }
            var draftsToDelete = state.workingDrafts.slice();
            var deleteQueue = Promise.resolve();
            setBusy(root, true);
            draftsToDelete.forEach(function (draft) {
              deleteQueue = deleteQueue.then(function () {
                return deletePersistedDraft(root, state, draft);
              }).then(function () {
                removeWorkingDraft(state, draft.cid);
              });
            });
            return deleteQueue.then(function () {
              setBusy(root, false);
              resetForNewDesign(root, state, true);
              return function () { return setupFocusTarget(root); };
            }).catch(function (error) {
              return reconcilePartialDraftDeletion(
                root,
                state,
                error,
                'The previews could not be deleted.'
              );
            });
          }
        });
      });
    });

    if (anotherButton) anotherButton.addEventListener('click', function (event) {
      startAnotherPersonalization(root, state, event.currentTarget);
    });
    root.addEventListener('giftinone:personalize-another', function (event) {
      var detail = event.detail || {};
      if (root.dataset.aiMode === 'ai' &&
          (root.dataset.aiState === 'ready' || root.dataset.aiState === 'added') &&
          state.sessionReady && !state.busy && !state.finalizingDraftCid &&
          !state.cartSubmitting && !state.cartUnknown && !state.cartChecking &&
          !state.sessionRestoring && (!detail.cid || detail.cid === state.selectedDesignCid)) {
        detail.accepted = true;
        startAnotherPersonalization(root, state, detail.trigger || anotherButton || openButton);
      }
    });
    Array.prototype.forEach.call(root.querySelectorAll('[data-ai-view-library]'), function (button) {
      button.addEventListener('click', function () {
        if (state.busy || state.cartSubmitting || state.cartUnknown || state.finalizingDraftCid) return;
        openSavedDesignLibrary(root, state, button);
      });
    });

    root.addEventListener('giftinone:ai-cart-submit', function (event) {
      var detail = event.detail || {};
      if (
        root.dataset.aiState !== 'ready' || detail.cid !== state.selectedDesignCid ||
        state.cartSubmitting || state.cartUnknown
      ) return;
      state.cartMutationSequence += 1;
      state.cartSubmitting = true;
      state.cartUnknown = false;
      delete root.dataset.aiCartState;
      addButton.textContent = 'Adding…';
      setStatus(root, 'Adding your design to cart…', 'page');
      setBusy(root, true);
    });

    root.addEventListener('giftinone:ai-cart-error', function (event) {
      var detail = event.detail || {};
      if (detail.cid !== state.selectedDesignCid || root.dataset.aiState !== 'ready') return;
      state.cartSubmitting = false;
      state.cartUnknown = false;
      delete root.dataset.aiCartState;
      addButton.textContent = 'Retry add to cart';
      root.querySelector('[data-ai-final-message]').textContent =
        'Your design is saved. The cart request failed, so no new preview is needed.';
      setBusy(root, false);
      setStatus(root, 'Your design is still saved. Please try adding it again.', 'page');
    });

    root.addEventListener('giftinone:ai-cart-unknown', function (event) {
      var detail = event.detail || {};
      if (detail.cid !== state.selectedDesignCid || root.dataset.aiState !== 'ready') return;
      state.cartMutationSequence += 1;
      state.cartSubmitting = false;
      state.cartUnknown = true;
      root.dataset.aiCartState = 'unknown';
      addButton.textContent = 'Check cart before retrying';
      var checkCart = root.querySelector('[data-ai-check-cart]');
      if (checkCart) checkCart.hidden = false;
      root.querySelector('[data-ai-final-badge]').textContent = 'Check cart';
      root.querySelector('[data-ai-final-title]').textContent = 'Confirmation needed';
      root.querySelector('[data-ai-final-message]').textContent =
        'The cart response was interrupted, so this item may already be in your cart. Do not retry yet.';
      setStatus(root,
        'Do not retry or start a new design. Open or refresh your cart to confirm this item first.',
        'page');
      setBusy(root, false);
    });

    root.addEventListener('giftinone:ai-cart-added', function (event) {
      var detail = event.detail || {};
      if (detail.cid !== state.selectedDesignCid || root.dataset.aiState !== 'ready') return;
      trackAiEvent(root, 'add_to_cart', { source: 'ai_design' });
      state.cartMutationSequence += 1;
      state.cartCustomizationCids[String(detail.cid)] = true;
      state.selectedDesignInCart = true;
      state.runNumber += 1;
      closeAiDialog(root, state, false);
      destroyEditor(state);
      revokeFilePreview(state);
      // Cart success does not delete the server manifest. Keep every saved or
      // in-progress card locally as well, so the visible capacity
      // cannot drift from the next server request.
      releaseWorkingDraftSelection(state);
      state.cid = '';
      state.token = '';
      state.originalKey = '';
      state.originalUrl = '';
      state.jobStyleId = '';
      state.jobStyleLabel = '';
      state.rotation = 0;
      state.flipX = false;
      state.previewAttempt = 0;
      state.previewFailed = false;
      state.previewFinalFailed = false;
      state.cartSubmitting = false;
      state.cartUnknown = false;
      delete root.dataset.aiCartState;
      state.timing = null;
      endEstimatedProgress(root, state, false);
      fileInput.value = '';
      setPhotoSource(root, state, emptyPhotoSource());
      clearOrderProperties(root);
      var editorImage = root.querySelector('[data-ai-editor-image]');
      if (editorImage) editorImage.removeAttribute('src');
      root.dataset.aiState = 'added';
      root.dataset.aiMode = 'ai';
      openButton.hidden = true;
      addButton.textContent = 'Add to cart';
      syncSelectedDesignCartState(root, state);
      refreshCartCustomizationState(root, state, false).catch(function () {});
      setStatus(root, 'Your design was added to cart.', 'page');
      renderDesignGallery(root, state);
      setBusy(root, false);
    });

    Array.prototype.forEach.call(
      root.querySelectorAll('[data-ai-use-legacy]'),
      function (button) {
        button.addEventListener('click', function () {
          if (
            state.busy || state.finalizingDraftCid ||
            state.cartSubmitting || state.cartUnknown
          ) return;
          chooseRegularMode(root, state);
        });
      }
    );

    state.flushDraftOnPageExit = function () {
      var draft = workingDraftByCid(state, state.activeWorkingDraftCid);
      if (!draft || draft.persistError) return;
      flushPendingDraftSave(root, state, draft, true).catch(function () {});
    };
    state.flushDraftOnVisibilityChange = function () {
      if (document.visibilityState === 'hidden') {
        state.flushDraftOnPageExit();
      } else if (state.sessionReady) {
        refreshCartCustomizationState(root, state, false).catch(function () {});
      }
    };
    state.refreshCartOnReturn = function () {
      if (state.sessionReady) {
        refreshCartCustomizationState(root, state, false).catch(function () {});
      }
    };
    window.addEventListener('pagehide', state.flushDraftOnPageExit);
    window.addEventListener('pageshow', state.refreshCartOnReturn);
    window.addEventListener('focus', state.refreshCartOnReturn);
    document.addEventListener('visibilitychange', state.flushDraftOnVisibilityChange);
    document.addEventListener('cart:updated', state.refreshCartOnReturn);
    if (
      typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object' &&
      PUB_SUB_EVENTS && PUB_SUB_EVENTS.cartUpdate
    ) {
      state.unsubscribeCartUpdate = subscribe(
        PUB_SUB_EVENTS.cartUpdate,
        state.refreshCartOnReturn
      );
    }

    syncVariantAvailability();
  }

  function initializeAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-gio-ai-form]');
    Array.prototype.forEach.call(roots, initialize);
  }

  if (window.__GIFTINONE_AI_TEST__ === true) {
    window.GiftInOneAiProfileTest = {
      readSelectedVariantAvailability: readSelectedVariantAvailability,
      updateVariantAvailability: updateVariantAvailability,
      updateLauncher: updateLauncher,
      setBusy: setBusy,
      selectedVariantContract: selectedVariantContract,
      syncVariantAdjustmentUi: syncVariantAdjustmentUi,
      resetForNewDesign: resetForNewDesign,
      startAnotherPersonalization: startAnotherPersonalization,
      preserveDetachedJobForNewDesign: preserveDetachedJobForNewDesign,
      resetPersonalizationQuantity: resetPersonalizationQuantity,
      setFinalState: setFinalState,
      syncSelectedDesignCartState: syncSelectedDesignCartState,
      refreshCartCustomizationState: refreshCartCustomizationState,
      cartCustomizationIds: cartCustomizationIds,
      cartItemsByCustomizationId: cartItemsByCustomizationId,
      afterDialogClose: afterDialogClose,
      applySelectedPhotoFile: applySelectedPhotoFile,
      waitForCompletePreview: waitForCompletePreview,
      setReadyDesignProperties: setReadyDesignProperties,
      applyReadyDesign: applyReadyDesign,
      rebindReadyDesignVariant: rebindReadyDesignVariant,
      isCompleteFinal: isCompleteFinal,
      expectedFinalUrl: expectedFinalUrl,
      loadCompletePreview: loadCompletePreview,
      chooseAiMode: chooseAiMode,
      chooseRegularMode: chooseRegularMode,
      createPreviewCardMedia: createPreviewCardMedia,
      adoptEditorRevision: adoptEditorRevision,
      normalizeVariantRatioRules: normalizeVariantRatioRules,
      readRenderProfile: readRenderProfile,
      clientRenderPolicyForContract: clientRenderPolicyForContract,
      normalizePublicRenderPolicy: normalizePublicRenderPolicy,
      sameRenderShape: sameRenderShape,
      previewOverlayBlocksAction: previewOverlayBlocksAction,
      recordPreviewOverlayStatus: recordPreviewOverlayStatus,
      loadPreviewOverlayImage: loadPreviewOverlayImage,
      beginPreviewOverlayLoad: beginPreviewOverlayLoad,
      cancelPreviewOverlayLoad: cancelPreviewOverlayLoad,
      ensureBrowserPreviewOverlay: ensureBrowserPreviewOverlay,
      draftThumbnailPlan: draftThumbnailPlan,
      cidPattern: CID_PATTERN,
      largestContainedCropBox: largestContainedCropBox,
      fitEditorCropToImage: fitEditorCropToImage,
      fitInitialEditorCropToImage: fitInitialEditorCropToImage,
      persistWorkingDraft: persistWorkingDraft,
      deletePersistedDraft: deletePersistedDraft,
      displayStyleLabel: displayStyleLabel,
      renderStyleChoices: renderStyleChoices,
      mergeStylePresentation: mergeStylePresentation,
      assertManifestCapacity: assertManifestCapacity,
      emptyPhotoSource: emptyPhotoSource,
      currentPhotoSource: currentPhotoSource,
      photoSourceIsReady: photoSourceIsReady,
      setPhotoSource: setPhotoSource,
      centeredInitialCropData: centeredInitialCropData,
      repairUnadjustedDraftCrop: repairUnadjustedDraftCrop,
      restoreWorkingDraftViewport: restoreWorkingDraftViewport,
      editorSourceToleranceForCropper: editorSourceToleranceForCropper,
      defaultEditorViewportMatches: defaultEditorViewportMatches,
      workingDraftViewportMatches: workingDraftViewportMatches,
      draftMatchesRenderPolicy: draftMatchesRenderPolicy,
      assertRestorableWorkingDraft: assertRestorableWorkingDraft,
      settleInitialEditorViewport: settleInitialEditorViewport,
      settleWorkingDraftEditorViewport: settleWorkingDraftEditorViewport,
      scheduleEditorViewportResize: scheduleEditorViewportResize,
      openSavedDesignLibrary: openSavedDesignLibrary,
      nextPollDelay: nextPollDelay,
      recoverableEditorLoadError: recoverableEditorLoadError,
      requestStudioBootstrap: requestStudioBootstrap,
      sessionFirstJobRequest: sessionFirstJobRequest,
      captureEditorDraft: captureEditorDraft,
      editorLayoutDiagnostic: editorLayoutDiagnostic
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initializeAll(document);
    });
  } else {
    initializeAll(document);
  }
  document.addEventListener('shopify:section:load', function (event) {
    initializeAll(event.target);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    var roots = event.target.querySelectorAll('[data-gio-ai-form]');
    Array.prototype.forEach.call(roots, function (root) {
      var state = root._giftInOneAiState;
      if (!state) return;
      if (state.flushDraftOnPageExit) state.flushDraftOnPageExit();
      clearDraftSaveTimer(state);
      cancelPreviewOverlayLoad(state);
      state.runNumber += 1;
      abortActiveUpload(state);
      endEstimatedProgress(root, state, false);
      closeAiDialog(root, state, false);
      destroyEditor(state);
      revokeFilePreview(state);
      clearWorkingDrafts(state);
      clearOrderResetReceipt(root, state);
      state.deferredPhotoSelection = null;
      setPhotoSource(null, state, emptyPhotoSource());
      if (state.flushDraftOnPageExit) {
        window.removeEventListener('pagehide', state.flushDraftOnPageExit);
      }
      if (state.refreshCartOnReturn) {
        window.removeEventListener('pageshow', state.refreshCartOnReturn);
        window.removeEventListener('focus', state.refreshCartOnReturn);
        document.removeEventListener('cart:updated', state.refreshCartOnReturn);
      }
      if (state.flushDraftOnVisibilityChange) {
        document.removeEventListener('visibilitychange', state.flushDraftOnVisibilityChange);
      }
      if (state.variantObserver) state.variantObserver.disconnect();
      if (state.editorResizeObserver) state.editorResizeObserver.disconnect();
      if (state.handleEditorWindowResize) {
        window.removeEventListener('resize', state.handleEditorWindowResize);
      }
      if (typeof state.unsubscribeCartUpdate === 'function') state.unsubscribeCartUpdate();
    });
  });
})(window, document);
